// AUTO-GENERATED by app/web/scripts/build-guides-manifest.mjs
// Do not edit by hand. Edit the .md files under app/web/src/guides/<product>/ instead.

import { TGuideProductId } from "./manifest";

export type TGeneratedGuide = {
    product: TGuideProductId;
    slug: string;
    title: string;
    one_line_description: string;
    category: string;
    order: number;
    last_screenshotted_at: string | null;
    tags: Array<string>;
    content_hash: string;
    body_markdown: string;
};

export const generatedGuides: ReadonlyArray<TGeneratedGuide> = [
    {
        product: "legal",
        slug: "bank-account-verification",
        title: "Bank account verification — submission, the call, and the lock",
        one_line_description: "How clients submit bank details, how staff verify and lock them, and the fraud attack to defend against.",
        category: "communications",
        order: 10,
        last_screenshotted_at: "2026-05-29",
        tags: ["bank-account","verification","fraud","push-payment","video-call","completion","redemption"],
        content_hash: "0ee81c1869fa298cea75ddd4c961c63c64d0be37822fa52da1763b3b887b34c6",
        body_markdown: `## What it is

The set of controls around the bank accounts on a case — where the client's money comes from, where it goes back to, and how we stop a fraudster from quietly redirecting it. There are three moving parts: **client-side submission** in the portal, **staff-side verification** with a call, and the **case-level lock** that prevents further changes once verified.

The lock is the single most important control on a sale or remortgage near completion. Once it's on, the client can no longer change their bank details in the portal — only staff can, and only after re-verification. **Push-payment fraud** (the most common conveyancing fraud by some distance) almost always pivots on getting a member of staff to update bank details without going back through this loop.

## When you'd use it

- A client has just submitted bank details in the portal and you need to verify them.
- A client wants to change their already-locked bank account.
- Funds are leaving us next week and you want to confirm the destination is locked, verified, and right.
- You're investigating a suspected fraud attempt around bank details.

## Walkthrough

### How the system sees a bank account on a case

Every bank account row on a case carries a **verification status**:

| Verification status | What it means |
| --- | --- |
| **Unsubmitted** | The client has entered it but not submitted it yet. The portal shows it as a draft. |
| **Pending** | The client has submitted the list for verification. Sits in the staff queue until the verification call confirms each account. |
| **Approved** | Staff have verified the account on a call. This is the only status that lets money move to it. |
| **Rejected** | Staff verified and rejected it (mismatch, suspected fraud, etc.). |
| **Not verified as will not be used** | Approved-equivalent for an account we know will never be used (a redundant historic entry). |

The case as a whole also carries a separate **client changes to bank accounts locked** flag — a timestamp that records when the lock came on. While set, the client cannot edit any account from the portal.

### Stage 1: client submits

The client enters one or more bank accounts on the portal step (*Tell us which bank account to send money to*). They can edit freely until they hit *submit for verification*. On submit:

- Every account whose status isn't already **approved** flips to **pending**.
- The **client changes to bank accounts locked** flag turns on against the case.
- The system records who submitted and when.

The client can't undo this from the portal. After submission, the only path to a change is through staff.

### When the money goes back to the introducer

On a case whose **introducer** has bank details on file, the account-holder dropdown on this step offers the introducer by name alongside the people on the case. This is how probate and similar cases route funds back to the introducer rather than to the client — the client is still asked the question, but they don't hand over their own bank details.

When the client picks the introducer:

- The bank-detail fields are hidden — the client sees a short note that funds will be returned to the introducer, and enters nothing.
- The account is recorded against the introducer and saved straight to **approved**, so it **skips the verification call**. The introducer's details were already entered and checked by staff in the CRM; re-running the client-facing check on our own known-good details would be pointless and would show the client the wrong "we're verifying your bank" message.

The introducer's bank details live on the **introducer record** (CRM → Introducers → Bank details), not on the case. Every member of staff can *see* them, but **editing** is restricted to specifically-authorised individuals — granted per person, the same way roadblock sign-off authority is — and every change is written to an audit log. This is deliberate: changing an introducer's bank details would silently redirect funds on every case routed to that introducer, so it sits behind a tighter gate than ordinary introducer config.

**Staff can route the payout to the introducer directly**, without waiting for the client. In the CRM case details, the Client bank accounts panel has a **Send proceeds to** dropdown. On a case whose introducer has bank details on file, pick the introducer and — after the confirmation — the system does the same thing the client-facing step would: it records an approved introducer account, marks any other account on the case as not used, and **locks bank-detail changes**. That **clears the bank-accounts roadblock without a verification call**. Use it when the money is going to the introducer and you don't want to depend on the client finishing onboarding — for example a probate sale where the proceeds go to the executor. Any staff member can set this; the tighter gate stays on *editing the introducer's actual account numbers*, not on choosing an already-checked introducer as the recipient. Switch the dropdown back to the client's own account to unlock and hand the step back to the client.

### Stage 2: the verification call

This is the real defence. The system has no way to know whether the sort code and account number the client typed are theirs — only a human conversation can establish that. The conveyancer or assigned staff member:

1. Calls the client on the **phone number already on file**. Not the one in any recent email, not the one the client mentioned in passing — the number on the case record. If the client doesn't pick up on that number, you do not have a verified caller; reschedule.
2. Confirms the bank name, sort code, and account number against what the client submitted. Read each digit aloud and have the client read them back.
3. Asks the client to confirm **why** this account is being used — proceeds destination, mortgage redemption, completion monies.
4. Approves the account in the CRM, which flips its row to **approved**.

For mortgage fee cases, matrimonial transfers, cash purchases, unregistered titles, or any case where extra video verification is policy, the verification call is a **video call**, not a phone call. See [Anti-fraud and verification](/crm/guides/legal/anti-fraud-and-verification) for the full list.

### Stage 3: lock it and leave it

Once every relevant account is **approved** and the **client changes to bank accounts locked** flag is on, the destination is settled. Don't unlock unless there is a compelling reason and you are prepared to repeat the verification call.

### Changing a bank account after the lock is on

This is where push-payment fraud lives. The pattern is invariably:

1. Email from the client (or what looks like the client) explaining they've changed bank — old account closed, switched to a new bank, etc. Often emotionally plausible.
2. Request to send funds to a new sort code and account number.
3. Some urgency hook — completion is coming, they need it before a deadline.

The correct response, every time:

- Do **not** edit the bank account from the email. Reading the email is fine; acting on it is the trap.
- Trigger a **video call** with the client. Use the phone number on file to schedule it. Do not use the email's signature, do not use a new number quoted in the email.
- On the video call, confirm: who they are (face check, ID still valid), why the change, the new details digit-by-digit.
- Only then update the bank account row. The system audit log records you as the user who did it.
- Re-submit for verification — the new account goes back to **pending** until you approve it.
- The lock stays on; you do not turn it off and back on.

If anything on the call feels off — coached responses, evasive answers about *why*, mismatch with what's on the original ID — fail the call and escalate to the conveyancer or HOLP.

### What you do per account

- Read the digits aloud. Hearing a digit difference is faster than spotting it on screen.
- Confirm the bank name matches the sort code's bank. Sort code → bank is public; if the named bank doesn't match the sort code, that's a fail.
- For sale proceeds going to a third party (e.g. another solicitor for an onward purchase), apply the same call discipline to the third party — Law Society / CLC check, written confirmation through verified channels.
- If the account is in a different name from the client, you need a written explanation and conveyancer / HOLP sign-off before you approve.

## How it works under the hood

- Submission **does two things at once**: it flips eligible rows from *unsubmitted* / *rejected* to *pending*, and it sets the case-level lock. Already-approved rows are not touched.
- The **lock is a timestamp**, not a boolean. Once set, the client portal will not show the *edit bank details* surface — there's no "lock toggle" in the client UI, only the staff-side action that clears it.
- **Verification is per-row.** Approving one account doesn't approve another. A case can have an approved redemption account and a pending refund account at the same time.
- The audit log records every submission and every verification action with the staff member who took it.
- The system has **no automatic re-verification** — once approved, an account stays approved until staff explicitly mark it otherwise.

## Edge cases & gotchas

- **Approving an account does not unlock the case.** The case stays locked until you deliberately unlock it. Approving the final pending row is not the same action.
- **"The client emailed from their normal address."** Email accounts get compromised — sometimes weeks before the fraud lands. A real-address email asking for a bank change is not a safer signal than a look-alike address. Apply the same routine.
- **The account name doesn't match the client name** is a stop-and-think moment. Joint names, maiden names, and trading names all have legitimate explanations, but you need to hear the explanation and document it before approval.
- **Sort codes can hint at the bank.** If the client says they've moved to "their new HSBC account" but the sort code is a Barclays code, that's a fail without further conversation.
- **Mortgage redemption accounts** come from the redemption statement, not the client. Cross-check the statement against the figures on file before sending money. The client cannot redirect the lender's redemption account.
- **"I'll just unlock it for one quick change."** This is exactly the gap the fraud pattern exploits. The unlock-edit-relock workflow without a verification call is the failure mode. Do the call.
- **A rejected account isn't dead** — the client can re-submit. If they do, treat the new submission with the same scrutiny.
- **Refund / proceeds destination is the highest-stakes account.** That's where money actually leaves the firm; treat it with the most care.

## Related guides

- [Anti-fraud and verification](/crm/guides/legal/anti-fraud-and-verification)
- [Risk management](/crm/guides/legal/risk-management)
- [Client portal — onboarding steps](/crm/guides/legal/client-portal-onboarding-steps)
- [Sale process](/crm/guides/legal/sale-process)
- [Remortgage process](/crm/guides/legal/remortgage-process)
`,
    },
    {
        product: "legal",
        slug: "case-view-overview",
        title: "Case view overview",
        one_line_description: "Every panel on the case page and what it's for.",
        category: "case-view",
        order: 10,
        last_screenshotted_at: "2026-05-29",
        tags: ["case-view","overview","getting-started"],
        content_hash: "e837d29b5db47d17accef10b0c2289d5d7f0d13c7c6154f362ced36f7148906b",
        body_markdown: `## What it is

The case page at **/crm/legal/cases/&lt;id&gt;** is the home of a single case. Everything we know about it lives here, organised into a tab bar across the top and three columns underneath.

## When you'd use it

- You're picking up a case for the first time and want the lie of the land.
- A client emails about *their case* and you need somewhere to anchor the conversation.
- You need to find a specific bit of information — a document, an enquiry, the handler — and don't yet know which tab it sits in.

## Walkthrough

### The page at a glance

![The case page showing the tab bar, the address header, and the three columns: Details, Overview, Roadblocks](/static/guides/legal/case-view-overview/case-page.png)

Three things to clock the moment you open a case:

1. **The address header** — full property address with the status pills on the right (e.g. **Instructed**, **purchase**). The copy icon next to it copies the address to clipboard. When a case is in chase, the status pill reads **Instructed (in chase)** — the reasons sit in the chase banner in the Overview column.
2. **The tab bar** — **DETAILS** is the default. The other tabs (**DOCUMENTS**, **ENQUIRIES**, **EMAILS**, **SATISFACTION**, **LEDGER**, **CONFLICTS**, **ONBOARDING**) each switch the body to a different view of the case.
3. **Three columns** — *Details*, *Overview*, *Roadblocks* — live underneath. They're independent of the tab bar; switching tabs swaps the body but the columns persist.

### The Details column (left)

Where you set things and read the case configuration.

- **Adhoc jobs** — one-off tasks you create against this case. Each shows up in the day view of whoever it's assigned to.
- **Case management** — case handler, onboarding manager, esigning toggle, and the rollback-instruction escape hatch.
- **Dual rep** — link to the other side of a dual-rep deal.
- **Risk management** — the case risk assessment.

### The Overview column (middle)

Where you read the case at a glance.

- **Chase banner** — when the case is in chase, a purple banner sits at the top of this column. It lists the reasons why, like *Enquiry replies (management company) awaited*. It also shows how long the case has been in chase, so you can see at a glance which ones have been stuck longest. The **Review / update** link opens the chase settings, where you change the reasons or switch chase off.
- **Linked case banner** — a grey banner shows when this case is one half of a pair. If the sale fell through and the work moved on, it says so and links to the new case. On the new case it points back to the old one. **Both halves always link to each other**, so you never have to search a client's name to find the other one.
- **Auction pack banner** — on an auction case this appears once **the auction is less than a week away and the pack has not been marked as sent**. It is amber until the day of the auction, then red, and it stays up until the pack goes out. Both the auction date and the sent flag live in the Auction section of the Contracts tab.
- **AI estimate** — the case read at a glance. It leads with a plain-English headline and a **likely completion month**, then breaks down into: **Blocked** — what's outstanding, grouped by who the ball is with (*the client*, *the other side*, *a third party*, or *us*), with chase dates and the handler's initials on our own items; **Do next** — the concrete next actions, each tagged with who should take it; **Recently completed** — the last few things actually done on the case, with dates; and a short digest of the latest emails plus when we last heard from, and last wrote to, each side. **Every Blocked row is a link** — click *Exchange Ready* and you land on that roadblock's checklist; click *2 overdue documents* and you land on the Documents tab. They **open in a new tab**, so you keep your place. The amber *Out of date* pill refers to the headline and the written prose; the Blocked list is **worked out fresh every time you open the case**, so it never points you at something already finished.
- **eSigning panel** — when esigning is on, this links straight to the live Yoti envelope.
- **Timeline** — the case's note feed, replacing the old Staff notes box. It opens on **Notes** — just the notes staff have written, plus the big case moments (instructed, exchanged, completed), **newest first**. Flip to **Everything** for the full audit trail: roadblock opens, assignments, \`#await\`s and the like. Add a note in the box at the **top** — a roadblock is **already picked for you** (wherever the case is up to), and you can change it from the dropdown. Chat commands like \`#assign\` and \`#await\` work here too. **Pin** a note to lift it into a Pinned band at the top for everyone; **edit** your own notes any time (they show a quiet *edited* tag). The grey pill on each note names its roadblock — click it to jump into that roadblock's chat.
- **Instruction card** — the line that goes on every internal record: "Instructed (purchase): &lt;address&gt; — Klyant Matter ID: &lt;id&gt;".

### The Roadblocks column (right)

The current blocker list for the case. Each row is a thing standing between us and the next milestone. Empty list = no live blockers (sometimes the case really is clear; sometimes it means we haven't loaded the right data in yet).

Click a roadblock to expand its checklist. See [Roadblocks: how they work](/crm/guides/legal/roadblocks-how-they-work).

### The tabs

| Tab | What it gives you |
| --- | --- |
| **Details** | The default — the three columns described above. |
| **Documents** | The document store for this case: what we have, what's missing, who uploaded it, status of each. |
| **Enquiries** | Legal enquiries raised and answered (both sides). |
| **Emails** | The email thread filed to the case. |
| **Satisfaction** | NPS responses if any are in. |
| **Ledger** | Money in and money out — quote, fees, disbursements, refunds. |
| **Conflicts** | The conflicts-of-interest check status. |
| **Onboarding** | The client's progress through the onboarding steps. |

## Edge cases & gotchas

- The right-most icon next to the address header (the document with "0" badge) is the **file attachments overlay**. It only lights up when you have attachments queued in the inbox to file against this case.
- The columns' headers (*Details*, *Overview*, *Roadblocks*) collapse if you click them. Useful when you're focusing on one thing and want the other two out of the way.
- An empty Roadblocks column on a case that *should* have blockers usually means the data hasn't been pulled through to the engine. Check the Documents tab first — missing required documents are the most common cause.

## Related guides

- [Dashboard overview](/crm/guides/legal/dashboard-overview) — where you find the case in the first place.
- [Roadblocks: how they work](/crm/guides/legal/roadblocks-how-they-work).
`,
    },
    {
        product: "legal",
        slug: "compliance-policies-index",
        title: "Compliance policies — the index and where the signed versions live",
        one_line_description: "The CLC-facing policies the practice maintains, what each one covers, and where the canonical signed copy is held.",
        category: "compliance-and-policies",
        order: 10,
        last_screenshotted_at: "2026-07-07",
        tags: ["compliance","clc","policies","aml","complaints","conflicts","index"],
        content_hash: "25d7485b28729fa1e4ea218cba9abbbd32c8ae266dd413de4a68b533155fc6ec",
        body_markdown: `> First draft for the CLC audit — canonical signed policy is held by compliance; this page is the staff-facing summary.

## What it is

The landing page for Sail Legal's regulatory policies. Under the CLC's Code of Conduct the practice has to hold, follow, and keep current a set of written policies. This page lists them, says in one line what each covers, and points to where the **signed, dated, canonical version** lives. Everything linked from here is a staff-facing summary; it does not replace the signed policy.

## When you'd use it

- You need to find the current policy on a topic and aren't sure which one applies.
- An auditor or the CLC has asked which policies the practice maintains.
- You're onboarding and want the map of the compliance framework in one place.

## The policies we maintain

| Policy | Covers | CLC code it maps to |
| --- | --- | --- |
| **Anti-Money Laundering & CTF** | Client due diligence, risk assessment, ongoing monitoring, suspicious activity reporting. | AML/CTF Code; Money Laundering Regulations |
| **Conflicts of interest** | Identifying, recording, and managing conflicts between clients and the practice. | Conflicts of Interest Code |
| **Complaints handling** | How a formal complaint is acknowledged, investigated, and answered. | Complaints Code |
| **Client account & accounts** | Handling client money, reconciliation, and residual balances. | Accounts Code |
| **Estimates & Terms of Engagement** | Giving costs information and issuing a clear TOE before acting. | Estimates & TOE Code |

## Where the canonical versions live

- The **signed, dated master** of each policy is held by compliance, not in this hub.
- Each policy has a named owner, a review date, and a version number recorded on the master.
- If a staff-facing summary here and the signed master disagree, **the signed master wins** — flag the drift to compliance.

## How these guides are structured

Each policy guide in this category follows the same shape:

- A one-line note that it is a staff-facing summary of a signed master.
- What the policy is for and which CLC code it sits under.
- The process staff actually follow, step by step.
- The records that have to exist for an audit trail.
- Escalation routes and the roles involved.

## Edge cases & gotchas

- **These pages are summaries, not the policy.** Never treat a hub page as the version of record.
- **Review dates matter.** A policy past its review date is an audit finding even if the content is fine — check the master's review date.
- **Don't paste firm-specific facts here** (thresholds, bank details, named individuals) unless compliance has confirmed them; keep the hub about process.

## Related guides

- Anti-Money Laundering & CTF policy
- Conflicts of interest policy
- Formal complaints — handling, escalation, and the client response
`,
    },
    {
        product: "legal",
        slug: "dashboard-overview",
        title: "Dashboard overview",
        one_line_description: "The case list, the day work list, the week strip, and how to switch between them.",
        category: "getting-started",
        order: 10,
        last_screenshotted_at: "2026-08-19",
        tags: ["getting-started","dashboard","day-view","cases"],
        content_hash: "7f0c70c14b8d895eccd5682f465ab6545b957c2902e4dcb26d48c565df83d134",
        body_markdown: `## What it is

The dashboard at **/crm/legal/cases** is where most of the team starts the day. It hosts two distinct views of the same world — a **Case view** for browsing the whole live caseload, and a **Day view** for planning your own work for a given date.

**It remembers the view you last used** and opens straight back to it. The first time, or before you've picked, **it opens on the Day view** so you land on your own work for today. **Switch views** any time with the toggle at the top right, under *Viewing tasks by*.

## When you'd use it

- You're starting your day and want to see what's on your plate for today.
- You're looking for a specific case by status, chase mode, or who introduced it.
- You're checking how many cases are currently in chase, or who's holding what.
- You want to spot overdue work before the rest of the team notices it.
- You want to see how loaded each day of your week is, so you can spread your work out.
- You have a pile of overdue tasks and want to reschedule them all in one go.

## Walkthrough

### Case view

Use the Case view to browse the full caseload with filters.

![Case view with filter row showing status, chase mode, assigned, introduced by, and SH listings only](/static/guides/legal/dashboard-overview/case-view.png)

The filter row from left to right:

1. **Status** — the case-status bucket. *Instructed* is the default; you can pick *Pre-instructed*, *Completed*, etc.
2. **Chase mode** — show only cases currently being chased, or all of them.
3. **Assigned to** — *Anyone*, or a specific team member.
4. **Introduced by** — *Anyone*, or a specific introducer.
5. **SH listings only** — tick to show only cases that came through a Sail Homes agency listing. A case only counts here once the Sail Homes listing has been added to it on the case's property section. If a sale you know Sail Homes handled is missing, that's usually why.

The columns are *Address*, *Clients*, *Type*, *Comment*, *Status*, *Next date*, *Worked*, *Contacted*, and *Handler*. Click a row to open the case in-line — you get the comment box and the case action right there without leaving the dashboard.

*Worked* and *Contacted* are both day counts, and they deliberately answer different questions. **Worked is the days since the case last moved forward** — a checklist item ticked, a roadblock closed, or a note added to one. **Contacted is the days since the last email**, in either direction. A case with a low *Contacted* and a high *Worked* is one you're talking about but not progressing; the other way round is one you're progressing quietly. Sort by either by clicking its header. **Neither column ever shows 0 for a case nothing has happened on** — both count from the instruction date instead, so a case nobody has touched climbs to the top rather than sitting at the bottom looking freshly worked.

### Day view

The Day view is where you land the first time, and any time you last left it here. Use the *Viewing tasks by* dropdown at the top right to switch between Day and Case.

![The Day view: the week strip at the top, the overdue banner under it, then one combined list grouped under band headers](/static/guides/legal/dashboard-overview/day-view.png)

At the top of the Day view is **your week strip**. It shows **one card for each remaining day** of the working week, plus an **Overdue** card on the left and a **Next week** card on the right.

![The week strip: Overdue, each remaining weekday with a count and a small load bar, and a Next week rollup](/static/guides/legal/dashboard-overview/week-strip.png)

Each day card shows **everything landing on that day** — your tasks, key dates, and roadblocks together. The small bar gives you a feel for how heavy the day is next to the others. **Click a day card to see that day's list.** Click **Overdue** to see everything past its date. The strip is the fastest way to answer "which day is overloaded?" and move work before it becomes a problem.

The header line on the right — *N of M cleared today* — is your progress for the day. It counts both **tasks you've ticked** and **roadblocks that resolved** today.

The strip **follows the Assigned to filter**. Leave it on yourself for your own week, or pick a teammate to see theirs.

Under the strip sits the **overdue banner**. It appears whenever anything is past its date.

![The overdue banner: 3 overdue items, 2 tasks can be rescheduled, 1 needs action — with Review overdue and Reschedule buttons](/static/guides/legal/dashboard-overview/overdue-banner.png)

It splits your overdue pile honestly: **tasks can be rescheduled** with one click, but **roadblocks and key dates need action** — there is no date to push on those. Two buttons:

- **Review overdue** — jumps the list to the overdue pile so you can work through it.
- **Reschedule N tasks** — moves every overdue task at once. Pick **Spread across this week** to share them over your lightest days, or *Tomorrow*, *Next Monday*, or a date of your choice. After the move, the banner offers **Undo** — one click puts every task back on its old date.

Below the banner is **one combined list** for the selected day. Tasks, key dates, and roadblocks all sit in it together, with the work that matters most at the top **by default**.

The list is split by coloured headings — *Needs sign off*, *Under offer*, *Not yet under offer*. **The heading a row sits under is why it sits where it does**: on an under-offer case the sale is agreed, so completion is waiting on the legal work. A case can be both under offer and in chase, and **its rows still sit under the Under offer heading**, carrying the *In chase* tag as well.

Under each heading, **all the work on one case stays together**. The case takes its place in the list from its most urgent item, and the rest of that case's work follows right below it. Only the first row shows the address. The rest show **↳ same case**. So you **open a file once and clear all of it**, instead of meeting the same address again forty rows later.

Each row follows the same shape — what to do, which case, who owns it:

- A **checkbox** on the left means it's an ad-hoc task. Tick it when done, click the calendar icon to move it to another day, click the avatar to hand it to someone else, or click the text to edit it. **Moving it to another day, or to another person, takes it off this list** — you get a few seconds and an **Undo** before it goes.
- A **clock icon** means it's a key date (exchange, completion, deadlines). These are facts about the case — nothing to tick.
- A **dotted icon** means it's a roadblock. The row names the type and the next thing to do. Clicking it opens the case with the roadblock expanded — you do the work there, not here. A roadblock whose case is in chase carries a small **In chase** tag, so you can tell which blockers belong to paused cases.

The date column **changes with the list**. On Overdue, each row shows **its own date** — click it to move the task. On a single day every row would show the same date, so it becomes a small **calendar button** that does the same job. On a roadblock, that control is how you mark it as **waiting on someone outside** — or **park** it, when nobody outside is holding it up and you're simply pacing your own week. A parked row reads *Parked* rather than *Chase:*, and caps at five business days.

At the foot of the list sits a **Parked by us** band holding everything you've parked. It stays shut while you still have live work to do. **Clear the list and it opens on its own** — so finishing your day gives you your own backlog to pick from instead of an empty screen. Parking never makes a deadline go away; it only decides what you look at today.

Next to the list heading is a **type filter**. Use it to narrow the list to **Tasks only**, **Key dates only**, or **Roadblocks only** — or drill into a single **roadblock type** (e.g. *Client Verification*), or one step further into its **checklist items** with the **›** arrow. **Any type** clears it. The list loads thirty rows at a time; use **See more** to page through the rest.

Beside it is **Sort**, which reorders the whole list by a date on the case — **Expected exchange**, **Expected completion**, **Exchange deadline**, or **Completion deadline**, soonest first. Use it to work your day by **what is closest to landing** rather than by what is most overdue. Picking one **overrides the usual order entirely**, sign-off included, so the coloured headings disappear while it is on. Cases with no such date **drop to the bottom** in the normal order — nothing ever leaves the list. **Priority puts it back**, and so does reloading the page: the sort lasts for the session only.

The Day view's own filter row still works as before:

- **Showing tasks for** — *Today*, *Overdue*, or a specific date (handy for weekend dates the strip doesn't show).
- **Assigned to** — the team member whose work you want to see.

## Edge cases & gotchas

- Roadblock rows in the day list are **read-only**. You don't tick a roadblock off here — you click through to the case, do the work, and the roadblock disappears on the next refresh.
- **Overdue is grouped by case, not by age alone.** A case with one very late item brings its newer items up with it, so you can clear the whole file in one visit. Hunting the single oldest thing in the pile? Read the dates, not the order.
- **A case can show up twice** when one of its roadblocks needs a sign-off. Sign-off work is pinned to the very top, so it sits above that case's other rows instead of travelling with them.
- **Spread skips today on purpose.** Rescheduled tasks land from tomorrow onwards — today is already planned. If the week is nearly over, they spill into next week's days.
- **Undo lasts until you move on.** It offers to reverse the last bulk move only — once you filter, reschedule again, or leave the page, the moment has passed.
- *Chase mode* defaults to *Chase mode* only. If you're hunting a case you can't find, that's the first filter to widen.
- The *SH listings only* tick is sticky — it stays on between sessions. Worth a glance when a case "doesn't show up" but you know it exists.
- "Showing 0" with all your filters at their defaults usually means *Chase mode* + *SH listings only* are both restrictive. Untick at least one.

## Related guides

- [Case view overview](/crm/guides/legal/case-view-overview) — the case page panels.
- [Roadblocks: how they work](/crm/guides/legal/roadblocks-how-they-work) — what the Day view is queuing for you to clear.
`,
    },
    {
        product: "legal",
        slug: "complaints-process",
        title: "Formal complaints — handling, escalation, and the client response",
        one_line_description: "What conveyancers and complaint handlers do when a formal complaint comes in, plus the client acknowledgement letter.",
        category: "service-and-complaints",
        order: 10,
        last_screenshotted_at: "2026-05-29",
        tags: ["complaints","escalation","legal-ombudsman","clc","manager"],
        content_hash: "0f1182e9cf16bb6a7c17e1455b2f6c471dd8221c7dda52d0f9b45e560e54df4d",
        body_markdown: `## What it is

The formal complaints process for Sail Legal. Three perspectives:

1. **The conveyancer on the file** — what to do the moment a complaint lands.
2. **The complaint handler / manager** — how to investigate and respond.
3. **The client** — the acknowledgement letter they receive within 7 days.

Aligned to CLC guidance and the firm's published complaints procedure.

## When you'd use it

- A client has emailed something that looks like a formal complaint and you're not sure of the next step.
- You're the complaint handler / manager and the file has just been escalated to you.
- You need to draft the 7-day acknowledgement letter.

## Walkthrough — for the conveyancer on the file

### Step 1: Immediate escalation and file integrity

- **Immediately notify** the complaint handler (the manager) that a formal complaint has been received.
- **Cease substantive correspondence** with the client on complaint issues unless expressly authorised by the manager.
- Make sure the **entire file is fully up to date**, including:
  - Chronology
  - Attendance notes
  - Evidence of title review (or absence thereof)
  - Copies of all correspondence with buyer's solicitors
  - Internal handover notes when conduct transferred

### Step 2: Written factual report to manager

Provide a written report covering:

- Date conduct was transferred and what checks were undertaken at takeover.
- When the title register was reviewed and what was identified.
- What can and cannot be evidenced about earlier handling.
- Communications with the clients regarding buyer inactivity.
- Probate documentation received, when, and how it was assessed.
- Explanation for any failure to return calls / reliance on email.

**Factual, not defensive.** Supported by file evidence.

### Step 3: Remedial action on the live file

- Proactively progress the transaction under the manager's supervision.
- Implement the agreed communication plan (including telephone contact).
- Provide **weekly written updates** to the manager until exchange.

## Walkthrough — for the complaint handler / manager

### Step 1: Formal acknowledgement (within 7 days)

- Confirm the complaint is accepted under the firm's complaints procedure.
- Confirm you are the complaint handler.
- Confirm the investigation timescale (normally **28 days under CLC guidance**).
- Confirm the right to escalate to the **Legal Ombudsman** if unresolved.
- **Record it on the case.** On the Satisfaction tab, choose *Mark complaint as "acknowledged"* — see
  [Recording a complaint](/crm/guides/legal/recording-a-complaint). This is what evidences the 7-day
  deadline on the Complaints Report.

### Step 2: Investigation

Review the entire file independently. Identify:

- Whether the title was obtained and reviewed at instruction.
- Whether file records meet audit requirements.
- Whether buyer inactivity was identified but not communicated.
- Whether probate requirements were identified early enough.
- Whether communication standards were met.

**Crucially, assess this as a firm-level issue, not just an individual performance issue.**

### Step 3: Findings and outcome

Prepare a written findings report addressing each complaint head:

- Upheld / partially upheld / not upheld.
- Clear explanation for each conclusion.
- Any redress offered (where appropriate).
- Any learning outcomes or procedural improvements.

### Step 4: Final response (within 28 days)

- Provide a reasoned written outcome.
- **Apologise where service fell below expected standards** (without unnecessary admissions).
- Explain improvements being made.
- Confirm Legal Ombudsman rights (time limits included).
- **Record it on the case.** On the Satisfaction tab, choose *Mark complaint as "resolved"*. This is
  what evidences the 28-day deadline on the Complaints Report.

## Client acknowledgement letter — template

> Dear [Client],
>
> Thank you for your email. I confirm that I am treating your correspondence as a formal complaint under Sail Legal's complaints procedure.
>
> I will be handling this complaint personally in my capacity as the firm's manager, and I appreciate you setting out your concerns in such a clear and structured way.
>
> I am now undertaking a full review of the file and the issues you have raised, including the matters relating to file management, communication, and the handling of the title and probate documentation. This will involve a review of the firm's records and supervision arrangements, as well as the conduct of the file at different stages of the transaction.
>
> Under our complaints procedure, I aim to provide you with a full written response within 28 days of this acknowledgement. If, for any reason, it becomes clear that additional time is required, I will let you know and explain why.
>
> In the meantime, I have ensured that the file is being actively supervised to progress matters as efficiently as possible. I also note your comments regarding communication, including requests for telephone contact, and I will ensure that this is addressed going forward.
>
> Once my investigation is complete, I will write to you with my findings and the outcome of the complaint. If you remain dissatisfied at that stage, you will have the right to refer the matter to the Legal Ombudsman, details of which will be provided in my final response.
>
> Thank you for bringing these concerns to my attention.
>
> Yours sincerely

## Edge cases & gotchas

- **The 7-day acknowledgement is a hard deadline.** Don't let it slip; that's the moment the client sees the firm taking the complaint seriously. The Satisfaction tab shows the due date the moment a complaint is recorded, and turns it red once it passes.
- **Mark both stamps the day the letter goes out.** They record the day you click, so a late tick shows as a breach the report can't tell apart from a real one.
- **Cease substantive correspondence** while the complaint handler is investigating — don't try to "fix" it directly with the client, as it can prejudice the investigation.
- **Assess at firm level, not individual level** — even if one conveyancer is clearly the focus, the investigation has to consider supervision, processes, and audit trail.
- **"Apologise without unnecessary admissions"** is the standard CLC line. Acknowledge the impact of poor service without conceding a regulatory breach that hasn't been investigated yet.

## Related guides

- [Recording a complaint on the satisfaction tab](/crm/guides/legal/recording-a-complaint)
`,
    },
    {
        product: "legal",
        slug: "glossary-of-acronyms",
        title: "Glossary — the acronyms we use in ad-hoc jobs and on the CRM",
        one_line_description: "The shorthand the team uses on the CRM and what each letter actually stands for.",
        category: "reference",
        order: 10,
        last_screenshotted_at: "2026-05-29",
        tags: ["glossary","reference","acronyms","shorthand"],
        content_hash: "2407a908650c7026a48bf17715a779561c71e3da1ad33ca87e5beed81dcf8064",
        body_markdown: `## What it is

The shorthand the legal team uses in ad-hoc job titles, comments on the CRM, and internal correspondence. A new joiner sees half of these and bounces off; this is the lookup table.

## When you'd use it

- You're new and someone's just emailed you about "the BSL's CT and MRC".
- You're skimming a case comment that's mostly letters and need to translate.
- You're writing an ad-hoc job and want to use the standard shorthand for consistency.

## Walkthrough

| Acronym | Meaning |
| --- | --- |
| **BINS** | Buildings Insurance |
| **BSL's** | Buyer's Solicitors |
| **CDD** | Client Due Diligence |
| **CIF** | Client Information Form |
| **COT** | Certificate of Title |
| **CT** | Contract |
| **EAP** | Estate Agents on Purchase |
| **EAS** | Estate Agents on Sale |
| **ENQ'** | Enquiry |
| **ERP** | Early Redemption Penalty |
| **FFC** | Fixtures, Fittings & Contents Form |
| **FH** | Freehold |
| **HTB** | Help to Buy |
| **ID** | Identification |
| **ILA** | Independent Legal Advice |
| **LAS** | Local Search |
| **LH** | Leasehold |
| **LIF** | Leasehold Information Form |
| **LLD** | Landlord |
| **MAC** | Management Company |
| **MD** | Mortgage Deed |
| **MEMO** | Sales Memorandum |
| **MO** | Mortgage Offer |
| **MRC** | Mortgage Report Confirmation Form |
| **MTG** | Mortgage |
| **OE's** | Office Copies |
| **PIF** | Property Information Form |
| **RMT** | Remortgage |
| **ROT** | Report on Title Declaration |
| **RS** | Redemption Statement |
| **SDLT** | Stamp Duty Land Tax |
| **SSL's** | Seller's Solicitors |
| **STF** | Stock Transfer Form |
| **ToE** | Transfer of Equity |
| **TP1** | Transfer of Part |
| **TR1** | Transfer Deed |

## Edge cases & gotchas

- **BSL's / SSL's** — the apostrophe-s is the team's habit, not strict grammar. Search for both with and without the apostrophe when looking through old comments.
- **CT** is *Contract*, not anything legal-Latin. Don't read into the shorthand.
- **TP1 vs TR1** — Transfer of Part vs Transfer Deed. TP1 is when only part of a registered title is being transferred; TR1 is the whole title. Confusing the two on a Land Registry submission is a costly mistake.

## Related guides

- [Onboarding checklist](/crm/guides/legal/onboarding-checklist) — uses several of these.
- [Sale process](/crm/guides/legal/sale-process)
- [Purchase process](/crm/guides/legal/purchase-process)
`,
    },
    {
        product: "legal",
        slug: "title-register-entries",
        title: "Land Registry register entries — catalogue",
        one_line_description: "Reference for every entry type you'll meet on an HMLR Official Copy, and which ones we check automatically.",
        category: "title-check",
        order: 10,
        last_screenshotted_at: "2026-10-13",
        tags: ["title-check","reference","hmlr","land-registry"],
        content_hash: "5f9242b73d3dcc1120a8d531d559d6c178fcab9b8a7f932ca0bd8d956cd4b3a3",
        body_markdown: `## What it is

A lookup for the entries you'll see on an HMLR Official Copy. It covers all three sections of the
register, what each entry type means, and which ones we check for you automatically.

Based on Land Registry Practice Guides 23 and 24, and on the wording of 3,549 real Official Copies
we hold.

## When you'd use it

- A title check has flagged something and you want to know what the register entry actually says.
- You're reading an Official Copy by hand and hit an entry you don't recognise.
- You want to know whether a check is one we answer for you, or one you answer yourself.

## Every check now tells you where to look

You don't need this catalogue to answer a single check. Each check in the Title Check panel has a
help icon beside it. Click it and you get two things: which part of the register to open, and the
exact phrase to search the PDF for. Use this catalogue when you want the wider picture — what a
whole section contains, or what an unfamiliar entry means.

## Where to open the register

You don't need to go hunting through the document store. Open the case, go to a **Title Checks**
block, and open the title you're working on. The register, the title plan and any deeds we hold are
listed at the top under **Title and deeds**. Each one opens in a new tab. Keep the checks on one
screen and the Official Copy on the other.

On a case with several titles you only see that title's own register. We match it on the title
number in the file name. Some documents don't have a title number in the name — a deed someone
uploaded by hand usually won't. Those show under every title, so you never lose the one you need.

![Title and deeds, and the documents the register refers to](/static/guides/legal/title-register-entries/title-and-deeds.png)

### The documents the register refers to

Below that sits **Documents referred to on this title**. These are the conveyances, transfers,
leases and deeds named in the register's own entries. Land Registry tells us they exist when we
fetch the Official Copy, so the list fills itself in. Getting hold of each one is a separate step:

- **Available** — not ordered yet. **Order** fetches it from Land Registry for **£7**, charged to
  our account.
- **Ordered** — we hold it. **Open** opens the PDF in a new tab.
- **Manual referral** — Land Registry won't send this one electronically. The row says why.
- **Unavailable** — Land Registry has no copy to send.

*Available* means **nobody has ordered it yet**. It does not mean we know we can get it. You only
learn that when an order comes back. That's when a row turns into *Manual referral* or *Unavailable*.

## Where the automatic answers come from

Most checks are now answered from **the Land Registry's own summary of the title**, not from us
reading the entries. Every Official Copy carries flags that say whether the title has a charge, a
caution, a bankruptcy entry, and so on. We take those at their word.

That makes the answers steadier. They no longer depend on matching wording that changes from title
to title. But **a check can say No when you can see the thing in the register**. It is uncommon. It
does happen — we found 49 titles where the flag said there was no green-out, and the entry plainly
described one.

So **treat a No as an opinion, not proof**. If you can see it in the register, believe the register.
Raise it as an other title issue.

## Walkthrough

The register comes in three sections. Each holds a different kind of entry.

**The class of title is not an entry.** *Absolute*, *Good leasehold*, *Qualified* and *Possessory*
appear in the heading at the top of the register. They are not in the Section B list. Go hunting for
a "title class" entry and you won't find one.

### Section A — Property register

What the property **is**.

| Entry type | Notes |
| --- | --- |
| **Property description** | The address plus the tenure base. Always entry 1. |
| **Plan related** | e.g. *"A new title plan based on the latest revision of the Ordnance Survey Map has been prepared."* |
| **Provisions** | Covenants or easements affecting the property — usually a quoted extract from the original conveyance. The big-text ones. |
| **Beneficial / subjective easements** | Rights granted **to** this property — a right of way over a neighbour, for example. |
| **Note** | Informational only — *"the title includes any legal easements referred to in clause LR11.1 of the registered lease…"* |
| **Rentcharge** | Annual payment encumbering the land. Often £1 or recorded as extinguished. |
| **Mines and minerals** | *"Excepting all mines and minerals…"*. Also worded *"excluded"* or *"not included in the registration"* — all three mean the same thing. We check this one for you. |
| **Manorial rights** | Pre-1925 lord-of-the-manor reservations. |
| **Chancel repair liability** | Statutory liability — rare but expensive when present. |
| **Housing / Leasehold Reform Acts, not parent title** | Statutory leasehold-enfranchisement carve-out. |
| **Copyhold** | Legacy from pre-1925 land law — almost never seen now. |
| **Cancelled references** | *"Land tinted pink… cancelled — references should be ignored."* Housekeeping, nothing to action. |
| **Green out** | Land edged and numbered in green on the plan has been removed from this title and registered under other title numbers. The property is smaller than the plan first suggests. We check this one for you. |
| **Registered lease — easements** | Leasehold-only: easements granted by the lease. |
| **Schedule of multiple registered leases** | When the title includes multiple sub-leases (garage, bin store, etc.). |
| **Court order** | Rare — affects boundaries or rights. |

### Section B — Proprietorship register

Who **owns** it.

| Entry type | Notes |
| --- | --- |
| **Proprietor** | Names, addresses, partnership status. Joint proprietors are the precondition for a Form A restriction. The date on this entry is the one that drives the *last transfer under six months* rule. |
| **Restriction** | Form A through Form Z plus bespoke. **Form A is the tenants-in-common one and we check for it.** Most read *"except a trust corporation"*. Older ones read *"not being a trust corporation"*. Both count. Form K is the personal-representative restriction. Form P is the lender's standard. |
| **Price paid** | *"£X on date Y"* — required for transactions over £40k post-April-2000. Useful context, but **it is not what the six-month rule reads**. It is missing altogether on roughly half of all titles. |
| **Notice** | Bankruptcy notice, trust beneficiary notice. |
| **Caution against first registration** | Pre-registration land. |
| **Inhibition** | Legacy LRA 1925 — pre-2003. |

### Section C — Charges register

What it is **subject to**.

| Entry type | Notes |
| --- | --- |
| **Charge** | Mortgage. Modern format: *"Charge dated [date] to secure the moneys including the further advances therein mentioned"* plus chargee details. |
| **Restriction** | Lender's standard (Form P) to prevent disposal without consent. |
| **Notice** (unilateral or agreed) | Third-party interest registered against the title. A *Beneficiary of Unilateral Notice* entry is a different thing from an agreed notice — read the entry type, not just the word "notice". |
| **Lease** | Where this title is **subject to** an out-lease (the burdening side). |
| **Schedule of notices of leases** | List of lease notices, usually leasehold flats. |
| **Restrictive covenant** | Old-deed covenants. Wording varies a lot: *"contains covenants"*, *"contains the following covenants:-"*, *"contains restrictive stipulations"*. |
| **Indemnity covenants** | Protection for the original covenantor's successors. |
| **Easement** | This title is **burdened by** an easement (someone has a right of way over it). |
| **Equitable charge** | Charging order from court. |
| **Discount charge** | Right-to-buy discount, under section 156 of the Housing Act 1985. |
| **Home rights notice** | Spouse's home rights under the Family Law Act. |

## Edge cases & gotchas

- **Wording isn't consistent.** "Restriction" might appear as *Restriction*, *RESTRICTION*, or only
  implicitly in the text. Search on the words in the entry, not on the entry type alone.
- **Many entries have no date.** Provisions and most notes carry no entry date, so the registration
  date is sometimes blank.
- **Entry numbers restart in every section.** There is an entry 1 in A, in B and in C. Always say
  which section you mean.
- **Schedules sit at the section level**, not in the main entry list. Two are affected: the
  *schedule of multiple registered leases* in Section A, and the *schedule of notices of leases* in
  Section C. The schedule-of-leases check now answers for you, but **it only says a schedule exists,
  never what is in it**. Open it and read it.
- **A deed we can't get hold of shows up in two places.** The register entry may carry a note about
  it. Look for wording that no copy was supplied on first registration, or that neither the original
  deed nor a certified copy or examined abstract was produced. So read the notes on any covenant
  entry. The **Documents referred to on this title** list is the quicker route. It names every
  document the register refers to. A *Manual referral* or *Unavailable* row is Land Registry telling
  you straight out that it can't send one.
- **Green out does not mean cancelled** — it means land has been removed from this title and
  registered under another title number. The property is smaller than the filed plan first suggests.
  Worth a look every time. Cancelled *pink* tinting is the housekeeping one you can skip.

## Related guides

- More title-check guides will land here as the catalogue grows — common false positives, and the
  manual override flow.
`,
    },
    {
        product: "legal",
        slug: "roadblocks-how-they-work",
        title: "Roadblocks: how they work",
        one_line_description: "Where to find them, what the statuses mean, and the # command flow.",
        category: "roadblocks",
        order: 10,
        last_screenshotted_at: "2026-07-21",
        tags: ["roadblocks","case-view","day-view","getting-started","cheat-sheet"],
        content_hash: "7f07ddf62575fe4b24ba40447299e390ebd3a5149d0d20af0b15e03304ad61d6",
        body_markdown: `## What it is

A roadblock is one specific thing standing between us and the next milestone on a case — *ID check not done*, *Property questionnaire not returned*, *Title check pending senior signoff*. Each one carries a checklist of the actions that would clear it, plus a chat thread for back-and-forth.

The engine that maintains them is the most important thing to understand: **you don't tick a roadblock off**. The engine recomputes the roadblock list on every meaningful write to the case, and when the underlying state is right, the roadblock disappears on its own.

## When you'd use it

- You've opened a case and want to know what's blocking it.
- You're planning your day and want to see what's landing on your desk today.
- You're chasing a senior to sign something off.
- You've done the work and want to confirm the system has noticed.
- You need to hand a roadblock to a teammate or mark it as waiting on a third party.

## Walkthrough

### Where to find roadblocks

Three places:

1. **The jobs button** — press **J** anywhere in the CRM to open the jobs popout. It has three sub-tables: **Roadblocks**, **Contextual jobs**, and **All other jobs**. The roadblocks tab is a single scrollable list of everything live for you.
2. **On a case** — open the header on any block of the case page; inside it you'll find that case's roadblocks.
3. **The case Timeline** — the Overview column's Timeline card shows every roadblock chat on the case in one feed. Each entry carries a grey pill naming its roadblock — click it to jump straight into that roadblock. See [Case view overview](/crm/guides/legal/case-view-overview).

![The case page showing the Details / Overview / Roadblocks columns. Click the Roadblocks header to expand the list.](/static/guides/legal/roadblocks-how-they-work/case-roadblocks-panel.png)

### Who a roadblock lands with

Every roadblock sits with someone on the legal team. By default that's one of two people on the case:

- The **onboarding manager** picks up the early onboarding roadblocks: **Client Verification**, **Quote**, **Chase Instruction**, **Title Checks**, **Contract Ready Pack**, and **Pre-Completion Ready**.
- The **case handler** picks up everything else.

Anything waiting on a sign-off still goes to a senior, as before.

These defaults are set for each type of case under **Admin → Case Auto Assignment**. You can set different people there for a specific introducer too. So if a whole type of work keeps landing with the wrong person, that's where you fix it. For a one-off, hand the roadblock to a teammate with the **assign** command (below).

### Roadblock statuses — what they mean

| Status | What it means |
| --- | --- |
| **Open** | The roadblock is ready to be worked on. |
| **Awaiting signoff** | The handler is done — it needs an authoriser to sign it off before it counts as resolved. |
| **Resolved** | The work is complete. You can ignore the row; the engine will tidy it on the next refresh. |
| **Blocked** | Waiting on someone outside the team to come back to us — a client, the other side, a lender, the council. Set by the *await* command or the pause icon (see below). The row names who. |
| **Parked** | The same mechanism pointed at us rather than a third party: nobody external is holding it up, you've pushed it later in your own week. It reads *Parked*, never *Chase*, because nobody is being chased. |
| **Suspended** | Temporarily frozen until another roadblock resolves (or the relevant state changes). The engine sets this on its own. |

### The two views inside a roadblock

Each roadblock has two views. Use the **viz** button to switch between them.

- **Checklist** — the default. The list of items to be done. Each item is either *manual* (you do the work) or *automatic* (resolves itself when the related case work lands).
- **Chat** — discussion + the audit trail of every status change.

Click the **status pill** to flip back from chat to checklist quickly.

### Reading a checklist item

Each item tells you three things at a glance.

**Who needs to do it.** A small pill on the right shows the party: **Client**, **Us**, or **Third party**. The list is sorted by this — client items first, then our work, then things we're waiting on a third party for. Done items drop to the bottom.

**Whether it's really done.** Under the item is its **Evidence** — the live case facts that back up the tick. You'll see a short summary:

- Green **All confirmed** — every fact is in place.
- Red **N missing** — that many facts are still outstanding.
- Grey **Details** — nothing is outstanding, but a fact needs reading rather than ticking.

Click the summary to open it. You get one row per person or title with the actual value — so you can trust it's done without leaving the roadblock. This is the bit that replaces keeping your own checklist in a spreadsheet.

Most rows carry a tick or a cross. A **grey dot** means the item accepted that fact without the thing being present — a deed that was never lodged at the Land Registry and so can't be obtained, a case with no estate agent. The item still clears; the row is there so you know what you're signing off.

A red row names exactly what's missing and for whom — for example *Priya Patel — No valid ID check*. Some rows have a **link**: click it to jump straight to where that data lives (the Members section, the mortgage offer, and so on) without losing your place.

The **Resolved by** link, on the right of the same row, explains in plain words how the item clears.

### Opening a form related to the roadblock

Click the **form icon** at the top right of the roadblock. A popup asks which column you want to open the form in — pick *Details*, *Overview*, or *Roadblocks*. The form opens in that column without losing your place in the roadblock.

### Chat — keyboard shortcuts

The chat box is full Slack-style. You can format text with:

- **Bold** — \`B\`
- *Italic* — \`I\`
- Underline — \`U\`
- Numbered list — \`1\`
- Bullet list — \`L\`

Press **Enter** to send. **Shift + Enter** for a new line.

### The \`#\` command flow

The chat box is also the action surface. Type \`#\` to open the command menu, then use the arrow keys to pick what you want to do. The same commands work from the case Timeline's note box — they act on whichever roadblock the Timeline's dropdown points at. Three commands matter:

#### Assign a roadblock

1. In the chat box, press \`#\`.
2. Arrow down to **assign**. Press Enter.
3. Arrow down to the teammate you want to assign to. Press Enter.
4. A command pill is added to the message draft. Add an optional comment and press Enter to submit.

A system message acknowledges the assignment in the audit trail.

You can also reassign without opening the roadblock at all: on the case's roadblocks list, click the assignee avatar on any open roadblock and pick a teammate from the dropdown. It's the same staff list as the day view. Resolved roadblocks show who they sat with but can't be reassigned this way.

#### Mark a roadblock as waiting on someone else

1. In the chat box, press \`#\`.
2. Arrow down to **await**. Press Enter.
3. Arrow down to the party you're waiting on. Press Enter.
4. Add an optional comment and submit.

Each party comes back after a set number of days by default — the gap reflects how long that party realistically takes, so HM Land Registry waits far longer than a client does. Want a different gap? Type a number after the party — like \`#await:Client:5\` to wait 5 days. The list shows the option as you type.

You don't have to type the party's full name. Most have a short alias: \`#await:lr\` finds HM Land Registry, \`#await:council\` finds Local authority, \`#await:mgmt\` finds the management company.

The roadblock's status flips to **Blocked** and the system message captures who and why.

You don't need to open the roadblock to do this. On the case's roadblocks list — or the day-view roadblocks table — click the pause icon on any row. **The menu groups every party by side** — our side, client side, other side, third parties — and **pins the ones you picked most recently to the top**. **Start typing to narrow it down**; the same aliases work here. Pick a party and **the roadblock waits that party's usual number of days**, showing who you're waiting on and until when.

Need a different gap? **The days box at the bottom fills in with whichever party you're pointing at.** Change it — anything from 1 to 365 for a third party, up to 5 for a park — and **the line underneath tells you the date it will land on** before you commit.

![The case's Roadblocks column with the pause icon clicked, showing the await menu grouped by side with a search box and a days control.](/static/guides/legal/roadblocks-how-they-work/case-roadblocks-await-popout.png)

#### Park it instead — when the answer is "us"

Sometimes nobody external is holding a roadblock up. You could do it; you've just got more on today than the day will take. Naming a party you aren't actually chasing puts a lie into the chase data, so the picker keeps **our own side** as its own group — pick *Us* there.

Parking works like any other wait, with three differences worth knowing:

- **It caps at 5 business days**, not 365. Parking is "not today, but this week" — so whatever you park is back in front of you within the week. Ask for longer and you'll be told, rather than quietly given five.
- **The row reads *Parked*, never *Chase:*** — there's no party to name.
- **It doesn't clear the deadline.** Parking moves work off today's list; it doesn't make a breached deadline un-breached, and it never counts toward *cleared today*. The Day view is for planning your week; the deadline still stands underneath.

Already waiting on someone? The same pause icon shows the current wait and a **Cancel await** button. One click sends the roadblock back to active, ready to work again. It's the quick way back if the third party comes through early, before the wait was due to end.

#### Add an ad-hoc checklist item

When the predefined checklist doesn't cover a one-off thing you need to track on this specific roadblock:

1. In the chat box, press \`#\`.
2. Arrow down to **add-checklist-item**. Press Enter.
3. Type the checklist item. **Shift + Enter** to add a new line. **Enter** to submit.

The item shows up in the checklist view and counts toward the roadblock being resolved.

### On the day view

The Day view at **/crm/legal/cases** (with *Day* selected in the *Viewing tasks by* dropdown) shows **one combined work list** — your roadblocks sit in the same list as your tasks and key dates. Anything **waiting on a sign-off comes first** so it can't be missed, then everything on a case that is **under offer**, then the rest, **oldest first** within each group.

Under offer beats age, so a roadblock that breached its deadline weeks ago can sit **below** a fresher one on a case where the sale is already agreed. That's deliberate: the agreed sale is the one with a completion riding on it. Rows on an under-offer case carry a green **Under offer** tag, so the order is always explainable.

![The Day view combined list, with roadblock rows (Pre Completion Ready, Client Verification) interleaved with task rows](/static/guides/legal/roadblocks-how-they-work/day-view-roadblocks.png)

A roadblock row shows the **type and the next thing to do** (e.g. *Client Verification — Next: initial verification call*). The rows are **read-only**: clicking one opens the case page with the roadblock expanded — you do the work there, not here. The row vanishes from the Day view on the next refresh.

Roadblocks also count toward the **week strip** at the top of the Day view. A day card's number includes the roadblocks landing that day, so the strip shows your real load — not just your scheduled tasks. Overdue roadblocks pool into the **Overdue** card and the overdue banner. They can't be bulk-rescheduled like tasks — a roadblock is overdue because its deadline passed, so the way out is to clear it, mark it as waiting with the *await* command, or **park** it one row at a time.

Anything you park drops into a **Parked by us** band at the foot of the work list. It stays shut while you still have live work. Clear the list and it opens on its own, so finishing your day hands you your own backlog rather than an empty screen. A parked roadblock rejoins the main list as normal work on the day you parked it to.

The combined list is filtered by:

- The date selector (*Today* / *Overdue* / a specific date), or a click on any week-strip card.
- The *Assigned to* selector.
- The **type filter** next to the heading. Pick **Roadblocks only** to hide tasks; pick a **roadblock type** to narrow to it; or click the **›** arrow beside a type to slide into its **checklist items** and narrow to a single outstanding task — e.g. *every roadblock that still needs an initial verification call*. **Back** steps up a level; **Any type** clears it.

## How it works under the hood

Worth knowing because it explains a lot of "wait, why is it doing that?" moments:

- **Roadblocks refresh on every case write.** Touch the case in any meaningful way and the engine reconsiders the list.
- **Awaiting signoff** rows hide from the doer's day view until the senior reviews.
- **Blocked** roadblocks are deferred via the *await* command or the pause icon on the row — they reappear when the party comes back, the wait lapses, or you cancel it. A **parked** roadblock is the same mechanism pointed at us instead of a third party, so it comes back the same way.
- **Suspended** is the engine's call, not yours. The case being closed, abandoned, or completed mutes non-post-completion roadblocks because they're no longer load-bearing. **A case closed awaiting a new one mutes everything**, post-completion included — it never completed, so there's no housekeeping left to do.
- **The engine is the source of truth**, not the UI. If you think a roadblock should be cleared and it isn't, the data underneath hasn't reached the state the engine expects.

## Edge cases & gotchas

- **"I cleared the document but the roadblock is still there."** The engine refreshes on case writes — but the *kind* of write matters. A document upload that doesn't set the status will not flip an automatic checklist item. Check the document status separately.
- **An empty Roadblocks column on a case that should have blockers.** Most often a missing document or a status field not yet filled in. The engine isn't broken; the data feeding it is incomplete.
- **A roadblock you can't see on the day view.** It might be in *awaiting signoff* state and sitting with the senior — pinned at the top of *their* day list, not yours. Check the case page directly to see all states.
- **A completed or abandoned case still shows roadblocks.** Only non-post-completion roadblocks auto-suspend on terminal cases. Post-completion roadblocks (e.g. *NPS feedback chase*) stay live deliberately. The exception is a case **closed awaiting a new one, where everything suspends** and the work moves to the replacement case.
- **The Jobs Overlay also shows roadblocks** in its combined view. The day view is the canonical surface for daily planning; the overlay is being retired from that role.
- **\`#\` commands need exact spelling once selected.** The arrow-key picker is the safe path — typing the command name directly is fragile.

## Related guides

- [Case view overview](/crm/guides/legal/case-view-overview) — where the per-case Roadblocks column lives.
- [Dashboard overview](/crm/guides/legal/dashboard-overview) — where the day-view roadblocks table lives.
`,
    },
    {
        product: "legal",
        slug: "sale-process",
        title: "Sale process — what the system gates and what you confirm",
        one_line_description: "How the Contract Ready / Exchange Ready / Pre-Completion roadblocks drive a sale, with the manual context around them.",
        category: "legal-practice",
        order: 10,
        last_screenshotted_at: "2026-05-29",
        tags: ["sale","conveyancing","contract-ready","exchange-ready","probate","leasehold","pif","baspi","limited-title"],
        content_hash: "3e0f23c363384b4a771fbd86b88632d11fab6f2edfd0da70e26de549d2afcaac",
        body_markdown: `## What it is

The end-to-end sale process, anchored to the **roadblocks the engine actually fires** rather than a paper checklist. Where the system has a hard gate (a roadblock that must clear before the next stage), that's flagged. Where the rest of the work is judgement / SOP knowledge that the system doesn't see, that's flagged too.

## When you'd use it

- You're running a sale and want to know what's gating progress right now.
- A file is stuck and you're trying to work out which roadblock is the blocker.
- You're new and learning the standard sale flow.

## Walkthrough

### How the system sees a sale

Three load-bearing roadblocks fire on a sale, in this rough order:

| Roadblock | Gates | Clears when |
| --- | --- | --- |
| **Client Verification** | Anything client-side. | ID checks pass, verification call done, TOE agreed, etc. |
| **Contract Ready Pack** | Drafting the contract pack to the buyer's side. | Every required document for this title is in (see list below). |
| **Exchange Ready** | Exchange itself. | All enquiries resolved or marked not relevant, **ready-for-exchange review complete** (authoriser sign-off). |
| **Pre-Completion Ready** | Completion-day. | All pre-completion items in (redemption, authority, indemnities). |

You don't tick these off — you do the work; the engine notices the underlying state and clears each checklist item. See [Roadblocks: how they work](/crm/guides/legal/roadblocks-how-they-work).

### Stage 1: File opening and client onboarding

- Finish opening the file once instructed. Create the Klyant ID. Confirm AML compliance.
- Send the client onboarding email (portal login, ID check, onboarding questionnaire).
- Ask the client to complete the property questionnaire. On cases with the new **in-portal PIF** turned on, that's the PIF in the portal — one form, not three — see [Internal PIF](/crm/guides/legal/internal-pif). On older cases it's **TA6** and **TA10** (plus **TA7** if leasehold).

**System view** — at this point **Client Verification** has fired with items for ID checks, verification call, TOE agreement. Run the verification call → that item clears. The property questionnaire — the PIF, or the TA6 / TA7 / TA10 receipts on older cases — feeds Contract Ready Pack later.

**Probate sales nuance** — obtain Grant of Probate (testate) or Letters of Administration (intestate). Will copy required. **Form A restriction** on the title → line up a second trustee for overreaching (full name, email, mobile + ID check).

### Stage 2: Compliance and initial set-up

- Upload the sales memorandum from estate agents.
- Note estate agent's commission.
- Law Society Lawyer Check / CLC Check on the buyer's firm.
- Input buyer's solicitor info.
- First contact with the buyer's solicitors; get full client names.
- Chase monies on account; receipt them.
- Review ID search result. Fail or negative entry → conveyancer or HOLP.

**System view** — onboarding payments feed the ledger; ID-check status feeds **Client Verification**.

### Stage 3: Title and Contract Ready Pack

This is where **Contract Ready Pack** lives. Its checklist (varies by case shape):

- All documents referred to in titles and deeds obtained.
- EPC obtained.
- LPE1 ordered, then LPE1 received (leasehold with a management company).
- FME1 received (freehold with management company).
- AST / tenancy agreement received (cases sold subject to an AST).
- PIF received — a completed in-portal PIF (BASPI). On cases with the new PIF flow, this single item **replaces** the three TA items below.
- TA6 received (full-title-guarantee property) — older, non-PIF cases.
- TA7 received (full-title-guarantee leasehold) — older, non-PIF cases.
- TA10 received (full-title-guarantee property) — older, non-PIF cases.
- Local authority search received (where searches ordered).
- Environment search received (where searches ordered).
- Drainage search received (where searches ordered).
- Management company details obtained (where management company exists).
- Plus other case-specific items.

**Workflow** — request office copies; run the title check (paralegal initial pass, escalate to conveyancer or HOLP for complicated titles); request redemption statement if a charge is on the title; for leasehold, contact the landlord / management company for the Leasehold Sales Pack (cost to client, then pay for pack).

**Management companies** — on the **Details** tab → **Management companies** block, **Add** a company: search for one we've used before (so its standard data — type, whether it handles ground rent / service charge, how/where to apply for the LPE1/FME1 pack and the cost, consent instructions — is reused) or create a new one. A case can have more than one. The company-level details edit in a popup (shared everywhere that company is used); the per-case details (the contact for this file, apportionment, pack ordered) stay on the case.

**Probate-only nuance** — on a probate sale the executor has no personal knowledge of the property, so the old TA6 / TA7 / TA10 forms aren't used. On cases with the new PIF flow, the executor instead completes a **reduced in-portal PIF** that still produces a BASPI — the questions they can't answer print as "not known" with a cover note for the buyer's side (see [Internal PIF](/crm/guides/legal/internal-pif)). On older, non-PIF probate cases the property is sold as seen — executors can complete TA10 if there are items in the property, and in the absence of TA6 / TA7 you offer **Limited Title Indemnity Insurance** at the seller's cost.

### Stage 4: Contract drafted, sent, enquiries

- Conveyancer completes a detailed title check. Update clients and estate agents on any issues.
- Update the ledger with extras; send the client a completion statement explaining additions.
- Draft contract to the buyer's solicitors.
- Update estate agents and clients that contracts have been issued.
- Run the **fee check**; draft the completion statement (if there's a mortgage but no redemption statement, use the client's figure).

**System view** — **Contract Ready Pack** now **resolves without a senior sign-off**: when the last checklist item clears, the roadblock resolves itself. Red items mean a document is still missing.

### Stage 5: Enquiries and Exchange Ready

- Review buyer-side enquiries; notify estate agents.
- Receipt enquiries; approve the TR1.
- Answer what we can; loop in the client where we can't.
- Send to client: contract, TR1, title plan, draft completion statement, enquiries.
- Update estate agents; respond to buyer-side enquiries.

**System view** — **Exchange Ready** is now live. Its items:

- **All enquiries resolved or marked not relevant** — every enquiry has to be cleared.
- **Replies-to-enquiries report received** (purchase only — sales don't see this item).
- **Ready-for-exchange review complete** — a sign-off by an authoriser, recorded in the system. This is the system's record that the file has been reviewed for exchange. Not a clerical box-tick — without it, the engine won't let exchange progress.

### Stage 6: Pre-exchange

- Redemption statement received → check, copy to client, update completion statement.
- Signed documents from client in (contract, TR1, title plan).
- Agree completion date with client + buyer's conveyancer.
- Prepare TA13.
- Run the Clear Day List.

### Stage 7: Exchange

- Take client's authority to exchange (see [Authority to exchange](/crm/guides/legal/authority-to-exchange) for the script).
- Exchange contracts.
- Obtain final redemption statement.
- Send exchanged contract + completed TA13 to buyer's conveyancer.
- Notify client and estate agents.
- Put indemnity policies on risk.
- Leasehold — prepare completion statement for apportionments.
- Send exchange letters.

### Stage 8: Pre-completion (Pre-Completion Ready)

**System view** — **Pre-Completion Ready** fires with completion-day prerequisites. Items include redemption statement, client authority, indemnity policies on risk, etc.

- Prepare completion pack; send to accounts.
- Slips: receipt of funds, mortgage payments, estate agent's fees, service charge arrears, balance due to client, indemnity policies.

### Stage 9: Completion

- Await funds from buyer's conveyancer.
- When funds arrive, complete.
- Update client and estate agents; release keys.
- Send completion letters, including TR1 to buyer's solicitors (signed copy on file).
- Redeem the mortgage.
- Pay estate agents.
- Pay leasehold monies if due.
- Balance to client.
- Reconcile file accounts.
- Close file.

## Help to Buy

Help to Buy loan on the property → **consent from Help to Buy is required before exchange**. The system doesn't compute this; it's an off-engine gate. Treat it like a manual roadblock until the consent letter is filed.

## Sail Homes sales

When Sail Homes are the estate agents, they handle some of the initial onboarding. They typically upload:

- Office copies, EPC, Grant of Probate / Letters of Administration if available.
- They request the local, drainage, and environmental searches.

**Search-cost rule** — typically passed on to the buyer. If the buyer's conveyancer rejects our searches, the cost falls on the seller. Notify the seller immediately. The Sail-Legal-prepared contract notes that searches are paid by the buyer; add to the ledger.

## Sale review — risk

- Complete the risk checklist; run sols / CLC checks.
- Review file risk level regularly.
- Report escalated risks; report ID issues; confirm AML compliance.
- Escalate unresolved risks; escalate non-standard undertakings to HOLP.
- ID that hasn't passed → Money Laundering Officer (or deputy).

## Sale accounts completion pack — 48 hours before completion

- Completion statement, invoices, receipt of sales proceeds, redemption statement.
- CHAPS slip for mortgage redemption.
- Estate agent's invoice + slip for fees.
- Slips for indemnity policies / leasehold fees if applicable.
- Slips for balance to client.
- Slip to transfer costs of accounts invoice.

**Sail Homes file** — also transfer the cost of the search pack.

## Edge cases & gotchas

- **The roadblocks are the source of truth.** If you've "done" something but the roadblock won't clear, the underlying case field isn't where the engine expects. Don't argue with the engine; find the field.
- **Probate sales and the contract pack.** On cases with the new PIF flow, the **Contract Ready Pack** roadblock looks for a single **PIF received** item (a completed BASPI) instead of TA6 / TA7 / TA10 — that one item covers both full-title and probate sales. On older, non-PIF cases the TA items still apply on full-title-guarantee titles, and for probate (limited title) cases they aren't created in the first place — if you're seeing them on an older probate file, the title guarantee field is wrong.
- **Ready-for-exchange review complete** is an authoriser sign-off — not a clerical box-tick. Without it, the engine won't let exchange progress.
- **Help to Buy consent** is an off-engine gate. Start it early.
- **Completion pack is 48 hours before completion**, not 24. Accounts won't bend on this without a good reason.

## Related guides

- [Onboarding — what the system asks](/crm/guides/legal/onboarding-checklist)
- [Roadblocks: how they work](/crm/guides/legal/roadblocks-how-they-work)
- [Purchase process](/crm/guides/legal/purchase-process)
- [Authority to exchange contracts](/crm/guides/legal/authority-to-exchange)
- [Glossary — acronyms](/crm/guides/legal/glossary-of-acronyms)
`,
    },
    {
        product: "legal",
        slug: "service-levels",
        title: "Service level guide — residential conveyancing",
        one_line_description: "The standard timeframes Sail Legal commits to for each stage of a residential conveyancing transaction.",
        category: "service-and-complaints",
        order: 10,
        last_screenshotted_at: "2026-05-29",
        tags: ["service-levels","sla","response-times","chasers"],
        content_hash: "e55778a93418f9b8d8f527236912f78927c6aafe3e4943304e1e53b81216626d",
        body_markdown: `## What it is

The published service-level expectations Sail Legal aims to meet for residential conveyancing — initial contact times, chaser cadences, and stage turnaround. Think of it as the firm's commitment to the client and the bar that complaints will be measured against.

This is **process knowledge, not currently driven by the case engine**. Roadblocks track what's outstanding but don't enforce SLA timings — the cadence is on the team.

## When you'd use it

- A client (or referrer) asks how quickly something will happen.
- You're handling a complaint and need to know what the published commitment was.
- You're planning your week and want to know which chasers are due.

## Walkthrough

### Client communication

| Event | Target |
| --- | --- |
| Initial contact with client (phone or email) after instructions | Within **2 working days** |

### File opening and onboarding

| Stage | Target |
| --- | --- |
| Initial title check | **3 working days** from file opening |
| Onboarding checks | **3 working days** |
| Chasers for missing onboarding info | Every **3 working days** until received |

### Contract preparation

| Stage | Target |
| --- | --- |
| Draft contract papers issued once Sales Memorandum on file | **2 working days** |
| Chasers for completed protocol forms | Every **4 working days** if not received |

### Enquiries handling

| Action | Target |
| --- | --- |
| Acknowledge enquiries on receipt | **2 working days** |
| Send enquiries requiring client input to the client | **2 working days** |
| Respond with replies to enquiries (once all info to hand) | **4 working days** |
| Chase buyer's solicitor for outstanding responses | Every **3 working days** |

### Client document management

| Action | Target |
| --- | --- |
| Chase the client for signed documents | Every **3 working days** until received |

## Edge cases & gotchas

- **"Working days" excludes weekends and bank holidays.** Don't beat yourself up if a 2-working-day target sneaks into a Tuesday after a long weekend — it's on time.
- **The chaser cadences are floors, not ceilings.** If a case is genuinely stuck, chase harder, not on the published rhythm.
- **Onboarding chasers run every 3 working days** but the **buyer-solicitor enquiry chasers also run every 3 working days** — diary them so they don't both land on the client at once.

## Related guides

- [Internal complaints process for conveyancers](/crm/guides/legal/complaints-process)
- [Onboarding checklist](/crm/guides/legal/onboarding-checklist)
- [Sale process](/crm/guides/legal/sale-process)
`,
    },
    {
        product: "legal",
        slug: "training-intro-to-conveyancing",
        title: "Training — Introduction to conveyancing (Session 1)",
        one_line_description: "What conveyancing is, the two systems (registered and unregistered), the five stages, and the Sail Legal approach.",
        category: "training-sessions",
        order: 10,
        last_screenshotted_at: "2026-05-29",
        tags: ["training","foundation","registered-land","unregistered-land","conveyancing-protocol"],
        content_hash: "0fb3ecf22a9b37be6c4504d6ffc0ba882fb50cff51cd9dda70820bc64bf6fdf4",
        body_markdown: `## What it is

The opening session of the Sail Legal conveyancing foundation course. Sets the scene: what conveyancing actually is, the two parallel systems still in use in England and Wales, and how Sail Legal cuts time out of the traditional flow.

This is **training material, not currently driven by the case engine** — it's background context for the rest of the curriculum.

## When you'd use it

- A new joiner needs to ground their thinking before tackling specific stages.
- You want a quick refresher on the basics — registered vs unregistered, the five stages, the SL three-stage approach.
- A client or referrer asks "what is conveyancing actually?".

## Walkthrough

### What conveyancing actually is

The legal mechanism by which estates or interests in land are transferred from one person to another. Almost all land in England and Wales belongs ultimately to the Crown — when people talk about buying land, they're buying an **estate** (freehold or leasehold) or an **interest** (legal easement, legal mortgage) in that land.

Estates and interests pass by **deed**. Conveyancing is the system around producing, exchanging, and registering those deeds.

The law and procedures are the same whether the property is a semi in Southport or a farm in Devon.

### Two parallel systems

| System | How title is proved | When you'll see it |
| --- | --- | --- |
| **Registered** | Registration itself at HM Land Registry — the register is conclusive of ownership. | Most cases. |
| **Unregistered** | Documentary chain of conveyances, charges and grants ("title deeds") going back at least **15 years**. | Around 20% of land in England and Wales — usually older, never sold or charged since the 1990 compulsory-registration cutoff. |

On any sale, transfer or mortgage of unregistered land today, the transaction **must be registered** at HM Land Registry — gradually shrinking the unregistered pool.

### The five stages of a transaction

1. **Pre-contract** — receipt of initial instructions, draft contract drawn by seller's conveyancer and approved by buyer's, enquiries raised, searches carried out.
2. **Contract** — the parties enter into a legally binding agreement (exchange of contracts).
3. **Pre-completion** — buyer investigates title, raises requisitions, prepares the Transfer deed, runs final searches.
4. **Completion** — buyer pays the purchase price (less deposit) and gets keys; seller passes the Transfer.
5. **Post-completion** — SDLT submitted, Transfer sent to HM Land Registry for registration.

### The Law Society Conveyancing Protocol

A "preferred practice" framework (in force since 1 April 2011) intended to streamline the conveyancing process. Key points:

- **Not mandatory.** If a firm adopts it, they notify the other side.
- Sets out the steps to be followed.
- Aims to put as much information as possible into the buyer's hands when the draft contract is submitted — narrowing the gap between offer and exchange.
- Helps reduce gazumping and gazundering.
- Requires standard forms (Seller's Property Information Form, etc.).
- Evolves over time as the law changes.

### The Sail Legal approach — three reports

Sail Legal is a direct conveyancing firm. The traditional "face-to-face interview to sign the contract, then a follow-up report" approach is collapsed into three reports, sent at the points where the client actually needs to read something:

| Stage | What goes out |
| --- | --- |
| **Stage 1** | Report on the draft contract papers; contract sent for signature (pending mortgage offer). Title-examination queries raised with the seller's conveyancer. |
| **Stage 2** | Detailed report on the local search; other necessary searches identified. |
| **Stage 3** | Report on the mortgage offer and its conditions. |

Once all three are done and the file review confirms readiness, the file moves to exchange.

Video calls handle ID verification and client conversation — meeting in an office isn't itself a fraud-prevention measure; the enhanced due diligence is what counts.

## Edge cases & gotchas

- **Unregistered land hasn't gone away.** ~20% of land in England and Wales is still unregistered. Know how to deduce title from an epitome.
- **Post-1990 compulsory registration** means that on any sale, transfer or mortgage the title becomes registered — the unregistered pool only shrinks, never grows.
- **The Protocol is not mandatory** but the standard forms it requires are deeply embedded in market practice. Other firms will assume you're using them.

## Related guides

- [Training — Essential background law (Session 2)](/crm/guides/legal/training-essential-background-law)
- [Training — Registered and unregistered land (Session 3)](/crm/guides/legal/training-registered-and-unregistered-land)
- [Sale process](/crm/guides/legal/sale-process)
- [Purchase process](/crm/guides/legal/purchase-process)
`,
    },
    {
        product: "legal",
        slug: "aml-policy",
        title: "Anti-Money Laundering & CTF policy — the staff-facing summary",
        one_line_description: "The practice's approach to client due diligence, risk assessment, ongoing monitoring, and suspicious activity reporting.",
        category: "compliance-and-policies",
        order: 20,
        last_screenshotted_at: "2026-07-07",
        tags: ["aml","ctf","cdd","mlro","sar","risk-assessment","clc","compliance"],
        content_hash: "4a58c32d95a1ff7cabb2301b2a25491404e2e44652c8f483b22fcb300a67f480",
        body_markdown: `> First draft for the CLC audit — canonical signed policy is held by compliance; this page is the staff-facing summary.

## What it is

The summary of how Sail Legal meets its anti-money-laundering and counter-terrorist-financing obligations. It sits under the CLC's AML/CTF Code and the Money Laundering Regulations. The signed master sets the detail — thresholds, named officers, and any firm-specific triggers — this page is the process staff follow day to day.

## When you'd use it

- You're taking on a new client and need to know what due diligence is required before acting.
- Something on a file looks off and you're deciding whether to escalate.
- An auditor asks how the practice assesses and monitors money-laundering risk.

## The framework

- The practice appoints a **Money Laundering Reporting Officer (MLRO)** who receives internal reports and decides whether to report onward.
- The practice keeps a **firm-wide risk assessment** covering its clients, services, geographies, and delivery channels.
- Every matter carries a **matter-level risk rating** that drives how much due diligence is done.

## What staff actually do

### Step 1: Client due diligence (CDD)

- Identify the client and verify that identity from a reliable, independent source.
- Where there is a beneficial owner behind the client, identify and where required verify them.
- Understand the purpose and intended nature of the retainer.
- Complete CDD **before** acting, unless a permitted exception applies.

### Step 2: Risk-rate the matter

- Apply the matter risk rating using the practice's criteria.
- Where the rating is high, apply **enhanced due diligence** and record why.
- Simplified due diligence only where the signed policy permits it.

### Step 3: Source of funds and source of wealth

- Establish where the money for the transaction is coming from.
- Where risk warrants it, establish how the client came to hold that wealth.
- Record the evidence relied on and the reasoning.

### Step 4: Ongoing monitoring

- Keep CDD current through the life of the retainer.
- Scrutinise transactions for consistency with what is known about the client.
- Re-run checks when circumstances change (new party, new source of funds, PEP or sanctions signal).

### Step 5: Reporting a suspicion

- If you know or suspect money laundering, make an **internal report to the MLRO** — do not decide it yourself.
- **Do not tip off** the client or any third party that a report has been or may be made.
- The MLRO decides whether to submit a report to the relevant authority and whether consent is needed to proceed.

## Records that must exist

- The CDD evidence and the matter risk rating for every client.
- Source-of-funds evidence and reasoning where obtained.
- Any internal report to the MLRO and the MLRO's decision.
- Training records showing staff have been trained on AML.

## Edge cases & gotchas

- **CDD comes before acting.** Starting substantive work before due diligence is complete is the most common finding.
- **Suspicion is a low bar and reporting is not optional.** Route it to the MLRO; never sit on it or resolve it with the client.
- **Tipping off is a criminal offence** — say nothing to the client about a report.
- **Thresholds and named officers live in the signed master.** Don't quote a number from memory; check the canonical policy.

## Related guides

- Compliance policies — the index and where the signed versions live
- Anti-fraud and verification — the system gates and the human ones
- [Source of wealth checks — funders, Armalytix, and signing off](/crm/guides/legal/source-of-wealth-checks)
- Conflicts of interest policy
`,
    },
    {
        product: "legal",
        slug: "email-quality-score",
        title: "Email Quality Score — the send-time grade on outbound email",
        one_line_description: "The AI grade that runs the moment you press send, what each verdict means, and when it's skipped.",
        category: "communications",
        order: 20,
        last_screenshotted_at: "2026-05-29",
        tags: ["email-coach","quality-score","clear-review","ai","send-time","verdict","override"],
        content_hash: "16cab8f01d432ba93feae8af345cb52121b57eb205370351b04d95e945d3e9e7",
        body_markdown: `## What it is

The **Email Quality Score** is a quick AI grade that fires the moment you press send on an outbound legal email. It rates the draft on three dimensions, derives a single verdict, and offers 1–3 short suggestions. If the verdict is green, the email just goes; if it's amber or red, you see a modal and choose between revising or sending anyway.

It is **not** the same tool as **CLEAR Review**. CLEAR is the mid-draft *rewrite* tool — you reach for it deliberately, get a rewritten body back, and decide whether to take the rewrite. The Quality Score is automatic, runs at send-time, and **never rewrites** — it scores and suggests. CLEAR rewrites. Quality Score grades.

## When you'd use it

- You've written a tricky email to a client and you want a sanity check before it leaves.
- You've inherited an old reply chain and your draft might be carrying tone from earlier in the thread.
- You're writing on autopilot late in the day and want a second pair of eyes that doesn't need a colleague.
- You're trying to understand why a teammate's send was flagged and what the **Send anyway** override on the audit trail means.

You don't *invoke* the Quality Score — it runs on every send. But it's worth knowing when it'll have something to say.

## Walkthrough

### The three dimension scores

Every scored email gets three 0–10 scores. Each tracks one of Sail's communication principles:

| Dimension | What it's grading |
| --- | --- |
| **Clarity & next steps** | Plain language. Concrete actions the recipient is being asked to take. No jargon dump. |
| **Tone** | Warm, human, respectful. No archaic salutations, no curtness, no passive aggression. Empathy calibrated to the recipient. |
| **Ownership** | Are we taking responsibility, or pushing work back at the recipient (*"speak to your agent yourself"*) without offering help? |

The scores aren't averaged. The verdict is derived from the **lowest** of the three.

### The verdict — what each one means

| Verdict | Triggered when | What happens |
| --- | --- | --- |
| **Send confidently** | Every dimension is 7 or higher. | The send goes through silently. No modal. |
| **Worth a tweak** | At least one dimension is 4–6, none are 3 or lower. | Modal appears. You see the dimension scores and 1–2 suggestions. You can revise or **Send anyway**. |
| **Reconsider** | Any single dimension is 3 or lower. | Modal appears with stronger framing. You can revise or **Send anyway**. |

A red **Reconsider** is most often caused by a Tone trigger — archaic salutations (*Dear Sirs*, *To Whom It May Concern*), rude phrasing (*frankly*, *as I already told you*, *obviously*), sarcasm, or blame placed on the recipient. The AI is told to score Tone harshly for these specifically.

### The recipient-role rubric

The Quality Score knows who the email is going to and calibrates accordingly. Four roles:

| Recipient role | How it shifts the grading |
| --- | --- |
| **Client** | Full empathy bar. All three principles apply at full weight. |
| **Estate agent** | Professional tone bar, but the empathy bar is lower. Pass-the-parcel still counts. |
| **Other-side conveyancer** | Same as estate agent — professional, not warm. |
| **Other** | General clarity / tone / ownership only. Lowest bar. |

For multi-recipient emails the **dominant** role wins, in that priority order. A reply that's CC'd to both a client and the other-side conveyancer is graded as a client email.

For client recipients the AI is also given the client's **ownership status** on the case — *new owner*, *remaining owner*, *departing owner*, *not an owner*. This calibrates empathy on transfer-of-equity and probate cases: the empathy expected when writing to a remaining owner on a transfer of equity is different from the empathy expected on a straight sale. **The score row doesn't store ownership status** — it's only fed to the AI at scoring time.

### When the score is silently skipped

Not every send gets a real grade. Three skip reasons exist, and a row is recorded for the send in every case:

| Score status | Why it skipped |
| --- | --- |
| **Scored** | A normal AI grading completed. Dimensions and verdict are populated. |
| **Too short** | Draft is under 15 words. Too short to meaningfully grade — usually a one-line reply. |
| **Template send** | The send includes a template attachment, so the body is mostly boilerplate the AI shouldn't grade. |
| **AI unavailable** | The grading call failed or timed out. The send still goes; the row records that we tried. |

When the status is anything other than **Scored**, the email goes silently — no modal, no friction. The row exists so the audit trail and Metabase reports are complete.

Very long drafts (over 1500 words) **are** graded, but they're truncated to 1500 words for scoring and a *Length* suggestion is automatically prepended to the AI's own suggestions list.

### The "Send anyway" override

When you press **Send anyway** on a flagged draft, the system records:

- The verdict at the time of send.
- The dimension scores.
- That the override happened.

The original draft body is **not** kept (only the sent body is); intermediate scores during draft iteration are not persisted either. The audit trail is *"what we graded, what verdict that was, and whether the sender pressed through anyway"* — not *"the full evolution of the draft"*.

There are no silent overrides. Every press of **Send anyway** is recorded against the email row, queryable in Metabase, and visible to anyone reviewing send quality. This is the point of the override audit trail — it's a coaching signal, not a punishment, but it's not invisible.

### What you should actually do when you see an amber or red verdict

The verdict is opinion, not law. The AI is a Haiku-tier model with a short context — it's a coach, not a compliance gate.

- **Read the suggestions first.** Each is at most 15 words and quotes the offending phrase where it can. The fastest read on what to change.
- **If you disagree, send anyway.** The override is recorded but unblocked. The model is calibrated to be picky; sometimes it's wrong.
- **If you agree even a bit, revise.** A 15-second rewrite to fix a "Dear Sirs" or to add the missing next step is cheap.
- **Don't ignore reds you would have stopped on without the prompt.** The most common failure mode is reflexive *Send anyway* on emails the conveyancer would otherwise have caught on a re-read.

## How it works under the hood

- The Quality Score and CLEAR Review are **different endpoints and different models** — the Quality Score is a fast grader on a single send. CLEAR rewrites a draft on demand. Don't expect the two to agree on the same draft.
- The score is **always persisted on send** — even when the AI couldn't grade it (template, too short, AI unavailable). The row records the verdict and whether the sender pressed **Send anyway**.
- **No row exists for drafts that are never sent.** A scored draft that gets abandoned is invisible to the audit trail. The score lives in browser memory until send.
- The **lowest dimension drives the verdict.** A 9-9-3 draft is **Reconsider**, not **Worth a tweak**. The system treats the weakest dimension as the ceiling.
- The Quality Score and the recipient-role grading are **case-aware** — the AI knows whether the recipients are clients on this case, the estate agent on this case, the other-side conveyancer on this case, or unknown. Emails sent without a case-link are graded as *other*.

## Edge cases & gotchas

- **"My short reply was graded as Worth a tweak."** It wasn't — short replies (under 15 words) are skipped as **too short** and go silently. If you saw a modal, the draft was longer than you thought.
- **A template send goes silently.** That's by design. Template attachments mean the body is mostly boilerplate; we don't grade boilerplate.
- **CLEAR rewrites and Quality Score grades will sometimes disagree.** CLEAR is told to rewrite to a clearer version; Quality Score is told to score against three principles. Use CLEAR when you want a draft to react to; use the Quality Score when you want a sanity check on what you've already written.
- **The verdict doesn't change between draft revisions automatically.** The score fires *at send*. If you redraft after seeing the modal, you'll see a fresh score on the next send.
- **The override pressing is logged but not gated.** No-one has to approve before you can send. The point is the audit trail, not a gate.
- **Multi-recipient drafts get the dominant-role grade.** A reply to a client that also CCs the other-side conveyancer is graded as a client email — at the higher empathy bar. Don't write to the other-side conveyancer with a client in CC unless you mean it.
- **AI-unavailable emails still send.** A timeout or transient AI failure never blocks a legitimate send. The row records the attempt.

## Related guides

- [Managing the case inbox — reply, forward, file attachments](/crm/guides/legal/managing-case-inbox)
- [Triage inbox and email filing](/crm/guides/legal/triage-and-email-filing)
- [Confidential email handling](/crm/guides/legal/confidential-email-handling)
- [Reminder emails — personal vs helper modes](/crm/guides/legal/reminder-emails-personal-vs-helper)
`,
    },
    {
        product: "legal",
        slug: "onboarding-checklist",
        title: "Onboarding — what the system asks, and what you confirm by hand",
        one_line_description: "How the roadblock engine drives onboarding, plus the pre-instruction facts you gather before the system can take over.",
        category: "getting-started",
        order: 20,
        last_screenshotted_at: "2026-05-29",
        tags: ["onboarding","instruction","roadblocks","client-verification","quote"],
        content_hash: "4c0189b1c10b3e571a34faa15a37591ddbd0fa018726f4040282cb7d554c164c",
        body_markdown: `## What it is

Onboarding a case is driven by two complementary things:

1. **Roadblocks the engine fires automatically** as soon as a case becomes instructed — these are the system's view of what's outstanding, and they clear themselves as the underlying state changes.
2. **Pre-instruction facts** the onboarding team gathers up front, before the system has enough to compute anything useful — case type, introducer, lender, deceased owners, taxes.

The right mental model is: **don't tick off a paper checklist**. Use the roadblocks panel on the case as the live to-do list, and use the pre-instruction notes below to make sure nothing about the case shape has been missed.

## When you'd use it

- A new case has just been quoted and you're about to instruct.
- You're picking up a part-onboarded file someone else started and need to see what's outstanding (open the case → look at the Roadblocks column).
- A new joiner wants to understand what onboarding is actually doing.

## Walkthrough

### The system view — roadblocks the engine fires

Once a case becomes instructed, the engine generates these roadblocks (depending on case shape). Each one carries its own checklist; an item clears when the underlying state is correct. Don't try to clear items by clicking — clear them by doing the work that produces the state.

| Roadblock | What it represents | Cleared when |
| --- | --- | --- |
| **Chase Instruction** | The case isn't instructed yet. | Case becomes instructed. |
| **Quote** | Quote sent, quote created, conveyancer allocated, etc. | All quote items are in place. |
| **Client Verification** | ID checks, initial verification call, vulnerability sign-off, email/phone validation, terms of engagement, dual-rep consent. | Each item resolves as the corresponding case write lands. |
| **Title Checks** | Title obtained and reviewed. | Title status reaches *passed*. |
| **Contract Ready Pack** | Contract pack readiness. | All required contract documents are in. |
| **Ongoing Risk Assessment** | Risk reviewed. | Risk assessment recorded. |

For a full picture of how the engine works and how to read each roadblock, see [Roadblocks: how they work](/crm/guides/legal/roadblocks-how-they-work).

### The pre-instruction facts — what you gather before the system can help

Before the case is instructed, capture these on intake. The engine can't roadblock things it doesn't know about, so this is the human-judgement layer that sits above the system view.

**Case shape**
- **Case type** — sale, purchase, remortgage, transfer of equity, transfer of equity with remortgage, assent, TR1 involving a deceased owner, death of a joint owner.
- **Number of clients.**

**Money flows**
- **Introducer** — for New Law / Affinion sales: where do the proceeds go? For transfers: who pays our fees?
- **Lender involvement** — if yes: existing mortgage account number (sale) or new lender + panel check (purchase / remortgage / transfer).

**Deceased-owner facts**
- Was there a Will? If yes, copy required.
- If no Will, details of all next of kin (all need ID'ing).
- Grant of Probate or Letters of Administration — when applied for, can a copy come on instruction?

**Property value**
- Sale or purchase: the agreed price.
- Transfer or assent: the date-of-death value.

**Tax flags**
- Inheritance Tax issues the legal team needs to know about.
- Capital Gains Tax issues.

**Other early flags**
- Debts on the title — statements obtained, copies shareable.
- Unregistered title — where are the deeds? (Send to Chester.)

### Initial calls

**Every case** gets an initial call. The "All clients passed initial verification call" checklist item under **Client Verification** is how the system tracks it.

### Video calls — required for:

These are not roadblock items; they're a separate policy list that the team applies on top of the standard verification call.

- Mortgage fee cases (conveyancer).
- Matrimonial transfers of equity (conveyancer).
- Unregistered titles (conveyancer).
- Cases where the client is suspected to be vulnerable (conveyancer).
- Any case where a client tries to change their contact information.
- Cases where the clients live abroad.
- Cash purchases.
- Any transaction involving a client we know — or suspect — is bankrupt.
- Transactions at an undervalue (parents gifting a property to children).
- Any client uncooperative on ID verification.
- Seller or developer offering incentives, allowances, or discounts such as cash back (conveyancer).
- Deposit being paid by someone other than the purchaser, or paid directly to the seller (conveyancer).
- Where a client has a bank mortgage and a Certificate of Execution is required as part of the mortgage conditions.

### Transaction-specific intake

**Sales** — TA10, TA6, and (if leasehold) TA7 in. Once memo is in, contact the buyer's solicitors (intro email on CODA), then call for full client names.

**Surplus funds (sales)** — referrer is New Law or Affinion? Proceeds usually go to NL. Other law firm? Lawyer check. Client direct? Armalytix link.

**Remortgages** — existing lender's early-redemption penalty expiry date; suggested completion date.

**Transfer of equity** — who's being added / removed? If removed and money is changing hands, how much and where is it coming from? ID link to giftors, Armalytix proof-of-funds link.

**Matrimonial transfers** — Amicable referral: court order in hand? If not, separation agreement? If neither, separation statement preparation (£75 + VAT on the ledger).

**Purchases** — first-time buyers (note on file). Gift on the deposit (donor ID + Armalytix). Client's own funds Armalytix link.

## Edge cases & gotchas

- **The system doesn't know what it isn't told.** Onboarding's job is largely about translating the human-side facts (case type, deceased owner, gift) into case fields the engine can roadblock against. Once those fields are populated, the roadblocks generate themselves.
- **Unregistered titles without deeds in hand are a non-starter.** Don't pass to the conveyancer until the deeds arrive — Title Checks won't clear.
- **Name mismatch between owner and client** is the most common reason a file stalls weeks later. Catch it at intake by reading the office copies and the ID side-by-side.
- **Tenants-in-common Form A restriction in Section B** of the office copies means a second trustee or Form A removal at some point — flag it now so the case handler isn't surprised pre-exchange.
- **Gift on a purchase** is a sequence: donor declaration → donor ID → donor Armalytix → client Armalytix. Often the slowest part of pre-exchange and worth starting at intake.
- **The legacy "Onboarding Process 2026" SOP** was written as a manual checklist before roadblocks were fully wired up. Anywhere the SOP and the live roadblocks differ, **the roadblocks win** — they're the system's current view and they reflect the rules the engine actually applies.

## Related guides

- [Roadblocks: how they work](/crm/guides/legal/roadblocks-how-they-work)
- [Sale process](/crm/guides/legal/sale-process)
- [Purchase process](/crm/guides/legal/purchase-process)
- [Transfer of equity — the process](/crm/guides/legal/transfer-of-equity-process)
- [Source of wealth checks — funders, Armalytix, and signing off](/crm/guides/legal/source-of-wealth-checks)
- [Glossary — acronyms](/crm/guides/legal/glossary-of-acronyms)
`,
    },
    {
        product: "legal",
        slug: "purchase-process",
        title: "Purchase process — what the system gates and what you confirm",
        one_line_description: "How Client Verification, Contract Ready, Exchange Ready, and Pre-Completion drive a purchase, with Help-to-Buy specifics.",
        category: "legal-practice",
        order: 20,
        last_screenshotted_at: "2026-05-29",
        tags: ["purchase","conveyancing","help-to-buy","exchange-ready","registration"],
        content_hash: "73c8d59c2f2e8234f281b5f0419ecf479c407d7ae945b0e931a6670269c94aab",
        body_markdown: `## What it is

The end-to-end purchase process, anchored to the **roadblocks the engine actually fires** rather than a paper checklist. Where the system has a hard gate, that's flagged. Where the rest is judgement / SOP knowledge, that's flagged too.

## When you'd use it

- You're running a purchase and want to know what's gating right now.
- A file is stuck and you're hunting the blocking roadblock.
- You're picking up a half-progressed purchase and want to see what's done vs outstanding.

## Walkthrough

### How the system sees a purchase

| Roadblock | Gates | Clears when |
| --- | --- | --- |
| **Client Verification** | Anything client-side. | ID checks pass, verification call done, TOE agreed, etc. |
| **Contract Ready Pack** | Receiving and reviewing the contract pack from the seller's side. | Every required document is in. |
| **Exchange Ready** | Exchange itself. | Enquiries resolved, **replies-to-enquiries report received**, **report sent to client**, **ready-for-exchange review complete**. |
| **Pre-Completion Ready** | Completion-day. | Pre-completion items in. |

You don't tick these off — you do the work; the engine notices and clears. See [Roadblocks: how they work](/crm/guides/legal/roadblocks-how-they-work).

**Note for purchases**: Exchange Ready on a purchase has two extra items vs a sale — *Replies-to-enquiries report received* and *Report sent to client*. The system distinguishes purchase vs sale here.

### Stage 1: Onboarding and ID

- Initial documents sent to client.
- Sales memorandum from estate agents.
- Law Society Lawyer Check / CLC Check on the seller's firm.
- First contact with seller's solicitors.
- Chase monies on account.
- Request Thirdfort from client. Review result; **any fail or negative entry → fee earner / manager**.
- Complete ID / Client Due Diligence Form. Do the AML bill.
- Chase draft contract papers from seller's solicitors.
- Log Client Information Form when in.
- Help to Buy ISA → Help to Buy ISA Declaration for signature.
- Check source / proof of funds; explanation + evidence in client's accounts.
- **Gift on deposit** — gift declaration to donor → donor ID → donor Thirdfort → donor AML bill. May need more monies on account.

**System view** — **Client Verification** carries the ID, verification call, TOE, dual-rep items. Each clears when the underlying field is set.

### Stage 2: Draft contract pack received

- Log the draft pack.
- Request residential searches.
- Update client and estate agents.
- Raise enquiries with seller's solicitors.
- Report on title to client: contract, TR1, SDLT forms, report on title declaration, title plan for signature.
- Fee check + draft completion statement included with the contract report.

**Leasehold without sales pack yet** — advise client a complete title report needs the pack. Offer them: wait, or accept a partial report.

**System view** — **Contract Ready Pack** for a purchase has different items vs a sale (the buyer is receiving, not preparing). The engine tracks document receipts.

### Stage 3: Mortgage

- Mortgage offer received.
- Send mortgage documents to client for signature + return.
- Gift on deposit? Report it to the lender.
- Any other issues to the lender — lease term, ground-rent escalation, etc.
- Help to Buy property? Send Help to Buy Equity Charges.
- Leasehold + first report sent without pack — send a separate report when the pack arrives.

### Stage 4: Replies and searches — Exchange Ready

- Replies to enquiries received. Review; chase outstanding. When complete, send report on enquiries with the enquiries declaration.
- Search results received. Send search report with searches declaration.
- Update seller's solicitors and estate agents.

**System view** — **Exchange Ready** is now active:

- **All enquiries resolved or marked not relevant.**
- **Replies-to-enquiries report received** (this item only exists on purchases).
- **Report sent to client** (purchase-only).
- **Ready-for-exchange review complete** — authoriser sign-off, recorded in the system. Not a clerical box-tick; without it, exchange can't progress.

### Stage 5: Pre-exchange

- Hand the file up for sign-off.
- Arrange completion dates.
- Request mortgage funds — released to us **one working day prior to completion**.
- Carry out K16 and OS1.
- Request any Help to Buy ISA funds.
- Request deposit / balance from client.
- Request buildings insurance (unless leasehold flat).
- Get client's authority to exchange (see [Authority to exchange](/crm/guides/legal/authority-to-exchange)).

### Stage 6: Exchange

- Exchange contracts.
- Send exchange letters.
- Run Lawyer Check. Anything other than "frequent" → EHD.

### Stage 7: Pre-completion (Pre-Completion Ready)

**System view** — **Pre-Completion Ready** fires with completion-day items.

- Prepare the completion pack.
- Ensure OS1 / OS2 is still in date.
- Send completion pack to accounts.
- Chase mortgage funds if necessary.

### Stage 8: Completion

- Complete.
- Update LMS Conveyancer Zone if necessary.
- Update clients and estate agents.
- Send completion letters.

### Stage 9: Post-completion

- Submit SDLT Form; pay stamp duty.
- Post-completion documents from seller's solicitors → submit AP1.
- Registration received → copy to client and lender.
- Reconcile account; close file.

## Help to Buy purchases

- Client applies to Help to Buy for authority for us to act.
- Authority in → identify exchange and completion dates.
- Send **Sols1 Form** — you cannot exchange without Help to Buy authority to exchange.
- After exchange, send **Sols2 Form** — Help to Buy funds release direct to the developer.
- Pay any leasehold monies due.

## File wallets and accounts pack

(Unchanged from the legacy SOP — these are clerical / accounts standards, not system-driven.)

**Correspondence tag, Risk Review wallet, Signed Documents wallet, ID wallet, Source of Funds wallet** — see the legacy SOP material for the per-wallet item lists. **Completion pack to accounts 48 hours before completion** — same hard rule as sales.

## Edge cases & gotchas

- **The roadblocks are the source of truth.** If you've done something but the roadblock won't clear, the underlying case field isn't where the engine expects.
- **Thirdfort fail or negative entry** is a mandatory escalation. Don't progress until seen.
- **Gift on the deposit** is a sequence — donor declaration → donor ID → donor Thirdfort → donor AML bill. Often the slowest part of pre-exchange.
- **Exchange Ready on a purchase has extra items** vs a sale (replies-to-enquiries report, report to client). Don't be surprised the engine asks for both.
- **Help to Buy authority is a hard gate** — no Sols1, no exchange. Off-engine; treat as a manual blocker.
- **Lawyer Check on exchange day** that isn't "frequent" → pause, escalate to EHD.
- **OS1 in date at completion** — re-run if expired.

## Related guides

- [Onboarding — what the system asks](/crm/guides/legal/onboarding-checklist)
- [Roadblocks: how they work](/crm/guides/legal/roadblocks-how-they-work)
- [Sale process](/crm/guides/legal/sale-process)
- [Authority to exchange contracts](/crm/guides/legal/authority-to-exchange)
- [Purchase enquiries — standard wording](/crm/guides/legal/purchase-enquiries)
- [Glossary — acronyms](/crm/guides/legal/glossary-of-acronyms)
`,
    },
    {
        product: "legal",
        slug: "recording-a-complaint",
        title: "Recording a complaint on the satisfaction tab",
        one_line_description: "How the case Satisfaction tab tracks sentiment, and how marking a complaint, its acknowledgement, its resolution or an ombudsman referral feeds the regulatory complaints log.",
        category: "service-and-complaints",
        order: 20,
        last_screenshotted_at: "2026-09-11",
        tags: ["complaints","satisfaction","sentiment","legal-ombudsman","clc","complaints-report","deadlines"],
        content_hash: "fe24b0b3c81b2d76d8a0834704a870cb8322acba81a6aa35835148297064a576",
        body_markdown: `## What it is

The **Satisfaction tab** on a case (the *smiley/frown* tab on the case page) is where the team
tracks how a client is feeling about their matter — and, when it comes to it, where you **record a
formal complaint** so it lands on the firm's regulatory complaints log.

It holds three different things, and it's worth keeping them straight from the start:

- **Sentiment** — a soft *happy* / *unhappy* read on the case. It can be set by hand, and the
  system also sets it to *unhappy* on its own when an inbound client email reads badly. Sentiment
  is a *signal*, not a complaint.
- **A formal complaint** — the moment you mark *"raised as a formal complaint"*, the case joins the
  **Complaints Report** (the log the CLC expects the firm to keep). This is the load-bearing action
  on the tab.
- **The two CLC deadlines** — once a complaint is on the record, the tab tracks whether it was
  **acknowledged within 7 days** and **resolved within 28**. A banner above the timeline shows both
  due dates, and the Complaints Report scores them.
- **An ombudsman referral** — the escalation you record when a client takes an unresolved complaint
  to the **Legal Ombudsman**.

This guide is about the *mechanics* of the tab. For what you and the complaint handler must
actually do when a complaint comes in — the 7-day acknowledgement, the investigation, the final
response — see [Formal complaints — handling, escalation, and the client
response](/crm/guides/legal/complaints-process).

## When you'd use it

- A client has emailed something that reads like a formal complaint and you need to **put it on the
  record**.
- The case has an **unhappy sentiment** and the "Review unhappy customer satisfaction" job has
  landed in your day view.
- A complaint you're handling has been **escalated to the Legal Ombudsman** and you need to note it.
- You want to leave a **note** on the satisfaction history, or hand the review to someone else.

## Walkthrough

### Getting there

Open the case and switch to the **Satisfaction tab**. You'll see a **timeline** of everything that
has touched the client's satisfaction on this case — sentiment changes, notes, re-assignments,
emails, phone calls, and any complaint or ombudsman events — newest at the top. The header carries a
**sentiment face** showing the case's current read.

The actions live in the bar at the bottom. **Add note** and **Re-assign** sit on their own; the
rest are under the **Add event** (calendar) button.

![The satisfaction tab's Add event menu open, showing set sentiment Happy/Unhappy, mark as a formal complaint, mark as raised with the ombudsman, mark the complaint acknowledged, mark it resolved, and defer the review job](/static/guides/legal/recording-a-complaint/add-event-menu.png)

### Recording a formal complaint

From **Add event**, choose **Mark as "raised as a formal complaint"** and confirm the prompt.

That single click is what matters for compliance: it stamps the case with a **complaint date**, and
that date is exactly what pulls the case onto the **Complaints Report** — the firm's CLC complaints
log. Until you mark it here, the complaint isn't on the record, however many notes and emails sit on
the file.

Marking it **doesn't send anything to the client** and **doesn't run the investigation** for you —
it records that a complaint exists. The *what to do next* is the [complaints
process](/crm/guides/legal/complaints-process): notify the complaint handler, acknowledge within 7
days, investigate, respond.

If a complaint was marked in error, you can **remove the event** from the timeline, which clears the
complaint date and takes the case back off the log. Removing it also clears any acknowledgement and
resolution dates — both deadlines hang off the complaint date, so they can't outlive it.

### Recording the acknowledgement and the final response

As soon as a complaint is on the record, a **banner appears above the timeline** with both CLC
deadlines: acknowledgement due 7 days from the complaint date, final written response due 28. An
**overdue deadline turns red**.

Two new entries appear under **Add event**:

- **Mark complaint as "acknowledged"** — record that the acknowledgement letter has gone out.
- **Mark complaint as "resolved" (final response sent)** — record that the final written response
  has gone out.

Each **stamps today's date** and then **opens the add-note box** so you can write down what actually
happened — including the real date, if you're ticking the box after the fact. Both entries only
appear while there's a complaint on file and that stamp hasn't been made yet.

### Recording an ombudsman referral

If an unresolved complaint goes to the **Legal Ombudsman**, choose **Mark as "raised with the
ombudsman"** from **Add event** and confirm. This records the referral against the case, and it
shows up as its own column on the Complaints Report alongside the original complaint. Like the
complaint event, it can be removed if recorded in error.

### Setting sentiment by hand

**Add event** also lets you set sentiment to **Happy** or **Unhappy**. Use unhappy the moment you
know a client isn't satisfied, even if it's nowhere near a formal complaint — it's the early-warning
signal, and it's what surfaces the review job (below). Set it back to happy once the concern is
resolved. Sentiment on its own never touches the complaints log — only the *formal complaint* action
does that.

### Notes, re-assignment, and deferring the review

- **Add note** — leave a dated note on the satisfaction timeline. Notes can be **pinned** so the
  important ones stay at the top.
- **Re-assign** — hand the satisfaction review to another member of staff.
- **Defer review unhappy satisfaction job** — push the review job out by 7 days when you've
  actively decided this one can wait.

## How it works under the hood

- **Unhappy sentiment can be automatic.** When an inbound client email is triaged and its tone
  scores badly enough, the system records an *unhappy* sentiment on the case for you. So a case can
  turn unhappy without anyone clicking anything — the client's own words set it.
- **Unhappy is what raises the day-view job.** An unhappy sentiment surfaces the "Review unhappy
  customer satisfaction" job, which deep-links straight back to this tab. Deferring the job (or
  resolving the concern and setting sentiment back to happy) is how it clears.
- **The formal-complaint mark is the log's source of truth.** The Complaints Report is built from
  the complaint date this action stamps — not from sentiment, not from notes. That's why *recording*
  the complaint here is the step that can't be skipped.
- **The deadlines are measured from the complaint date.** The report carries both dates, the days
  taken, and a **yes / no / pending** verdict per deadline. A complaint **still open past its deadline
  counts as no**; one **still inside its deadline reads as pending**.
- **Sentiment, complaint, and ombudsman are independent.** A case can be unhappy with no complaint,
  carry a formal complaint while sentiment reads happy again (concern resolved, record retained), or
  hold a complaint that never goes near the ombudsman. Read all three, not just the face.

## Edge cases & gotchas

- **Unhappy ≠ complaint.** An unhappy sentiment — especially an auto-set one — is a prompt to look,
  not a complaint on the record. Don't assume the log is covered just because the face is frowning.
- **A complaint isn't recorded until you mark it.** Emails and notes on the file don't put a case on
  the Complaints Report. If it's a formal complaint, mark it as one.
- **The client raises; you record.** The wording on the tab ("raised as a formal complaint") is the
  client's act — your job here is to log it accurately, then follow the complaints process.
- **Removing a complaint event takes the case off the log.** Only remove one that was genuinely
  marked in error — it clears the complaint date the report depends on.
- **Deferring hides the job, not the problem.** The review job comes back after the deferral window;
  use it to schedule the follow-up, not to make the number go away.
- **The stamps record when you click, not when you wrote.** Both deadline marks use today's date.
  Tick the box the day the letter goes out — a late tick reads as a breach on the report, and the
  note explaining it isn't something the export can read.

## Related guides

- [Formal complaints — handling, escalation, and the client response](/crm/guides/legal/complaints-process)
- [Service levels](/crm/guides/legal/service-levels)
- [Case view overview](/crm/guides/legal/case-view-overview)
- [The Risk Report — the monthly risk-grade snapshot](/crm/guides/legal/risk-report)
`,
    },
    {
        product: "legal",
        slug: "roadblock-chase-instruction",
        title: "Roadblock: Chase Instruction",
        one_line_description: "How to clear the Chase Instruction roadblock — a case quoted but not yet instructed.",
        category: "roadblocks",
        order: 20,
        last_screenshotted_at: "2026-05-29",
        tags: ["roadblocks","chase-instruction","onboarding","troubleshooting"],
        content_hash: "188e2bd02285f0e0a01dd675644110514122d144258a11c1f9868351ecc5edc9",
        body_markdown: `## What it is

Chase Instruction fires once a case has been upgraded to a quote (not a property-report-only matter) and the quote has been sent to the client — but the client hasn't yet instructed us. It's the engine's prompt that there's a live opportunity sitting unchased.

It clears the moment the case is marked as instructed.

## When you'd use this guide

- A roadblock has appeared and you can't tell what step the case is stuck at.
- A quote was sent some time ago and you want to know whether the engine has noticed.
- You're triaging a queue of pre-instruction cases.

## The checklist

### Case instructed

The single item under this roadblock. Clears automatically the moment the case's *is instructed* milestone is set.

- **Where to go**: usually this happens when the client returns the instruction in the portal, or when an operator records the instruction via the **Onboarding** block on the **Details** tab. There's no manual tick to find on the roadblock itself — you record the instruction on the case and the engine clears the item.
- **Common stuck-states**:
  - **The client confirmed by phone but no instruction record exists on the case** — the engine reads the *is instructed* milestone, not your call notes. Capture the instruction via the onboarding flow.
  - **A property-report-only case shouldn't see this roadblock**. If you do see it, check whether the case has been wrongly upgraded.

## Edge cases & gotchas

- **Chase Instruction does not block exchange** — by the time exchange is in scope, the case is already instructed and the roadblock has cleared. It exists only to keep pre-instruction cases visible.
- **It only fires if the quote has been sent.** A quote drafted but unsent won't trigger Chase Instruction; the *Quote* roadblock handles that earlier stage.
- **Once the case is instructed and Chase Instruction clears, the rest of the roadblock library wakes up** — Client Verification, Title Checks (once a title is added), Contract Ready Pack (on sales), Ongoing Risk Assessment, and the others. If a long queue of roadblocks appears suddenly, that's why.

## Related guides

- [Roadblocks: how they work](/crm/guides/legal/roadblocks-how-they-work)
- [Onboarding — what the system asks, and what you confirm by hand](/crm/guides/legal/onboarding-checklist)
- [Roadblock: Quote](/crm/guides/legal/roadblock-quote)
- [Roadblock: Client Verification](/crm/guides/legal/roadblock-client-verification)
`,
    },
    {
        product: "legal",
        slug: "training-essential-background-law",
        title: "Training — Essential background law (Session 2)",
        one_line_description: "Estates, interests, easements, covenants, trusts of land, mortgages and contract law — the legal foundation under every transaction.",
        category: "training-sessions",
        order: 20,
        last_screenshotted_at: "2026-05-29",
        tags: ["training","foundation","easements","covenants","mortgages","trusts-of-land","contract-law"],
        content_hash: "16a8f0b9610748ef7caba0dd7287ba3623e3c07c962086122fb5a95afad03cd2",
        body_markdown: `## What it is

The background legal concepts that underpin every conveyancing transaction. Most of the time these sit in the background — but when something unusual lands, you need to know what an equitable interest is, when overreaching applies, and why positive freehold covenants don't bind successors.

This is **training material, not currently driven by the case engine** — it's the legal scaffolding behind the playbooks.

## When you'd use it

- A title throws up an unfamiliar interest — equitable charge, prescriptive easement, restrictive covenant — and you want to ground your thinking.
- A new joiner is learning the difference between joint tenants and tenants in common.
- A client asks why "subject to contract" is on every email until exchange.

## Walkthrough

### The two legal estates

Under the **Law of Property Act 1925**:

- **Estate in fee simple absolute in possession** — the **freehold** estate.
- **Term of years absolute** — the **leasehold** estate.

The **Commonhold and Leasehold Reform Act 2002** added a third: **commonhold**, a hybrid freehold ownership of flats. Lenders don't love it; uncommon in practice.

### The five legal interests

1. **Legal easements and profits a prendre** (rights of way, rights to fish, etc.).
2. **Legal rentcharges** — periodic payments in respect of land.
3. **Charges by way of legal mortgage**.
4. **Rights of entry**.
5. **Franchises** — Crown-granted rights, e.g. to hold a market.

Every other property right is recognised only in **equity**. Equitable interests don't bind a bona fide purchaser for value of a legal estate **without notice** of them — though the question of notice has been largely superseded by registration.

### Easements

| Aspect | Detail |
| --- | --- |
| Common types | Right of way, right of support, right to run drains. |
| Created by | Deed (most), implication, prescription, or s62 Law of Property Act 1925. |
| Four characteristics | Dominant and servient tenement; right benefits the dominant tenement; diversity of ownership/occupation; capable of being the subject of a grant. |
| Granted vs reserved | Granted — given to another. Reserved — kept by the seller out of what they sell. |

Customary and public rights (e.g. public footpaths) are not easements — they're exercisable by anyone under general law.

### Restrictive vs positive covenants

| | Restrictive | Positive |
| --- | --- | --- |
| Nature | Negative — "shall not" | Positive — "shall do / pay" |
| Example | Don't build an extension; don't carry on trade. | Maintain a fence; pay for shared driveway. |
| Run with freehold land? | Yes, if four conditions met (negative, dominant + servient, benefits dominant, intended to run). | No (subject to limited exceptions) — historic problem in freehold. |
| Where positive covenants do run | In **leases** (both benefit and burden), and under **commonhold**. |

**Covenantor** = person who gives the promise. **Covenantee** = person who benefits.

### Trusts of land and co-ownership

Trusts of land are governed by the **Trusts of Land and Appointment of Trustees Act 1996**. They arise expressly, by statute, or automatically — e.g. when a landowner dies and title passes to executors.

The legal estate vests in the **trustees** (max four, min two for overreaching). On a trustee's death the legal estate vests automatically in survivors.

**Overreaching** — a buyer is protected if the purchase money is paid to **at least two trustees** (or a trust corporation). The buyer doesn't need to investigate how the trustees deal with the proceeds.

**Co-ownership** — when two people buy together, they create a trust of land:

| | Beneficial joint tenants | Tenants in common |
| --- | --- | --- |
| Right of survivorship | Yes — whole interest passes to survivor on death. | No — each owner has a defined share. |
| Shares | Equal only. | Can be unequal. |
| Disposable by Will? | No. | Yes. |
| Registered land signal | No restriction. | **Form A restriction** on the register. |

If a sole survivor is selling a tenancy in common (Form A on register), a **second trustee must be appointed** for overreaching.

### Mortgages

A legal mortgage must be **created by deed** — usually a charge by way of legal mortgage. A mortgage in writing but not by deed is only equitable.

The legal mortgage gives the lender statutory rights to:

- Sue for repayment.
- Exercise a power of sale (subject to conditions being met — legal date for redemption past, etc.).
- Take possession.

### Contract law for conveyancers

Under section 2 of the **Law of Property (Miscellaneous Provisions) Act 1989**, a contract for the sale of land must:

- Be in writing.
- Incorporate all expressly agreed terms in one document (or in exchanged counterparts).
- Be signed by or on behalf of each party.

Failure on any of these = the contract is **void**.

For a binding contract:

1. Offer.
2. Unconditional acceptance.
3. Consideration (price).
4. Intention to create legal relations.

**"Subject to contract"** — somewhat superfluous since the 1989 Act (which already requires formality) but still widely used for comfort. It signals neither party is bound until the formal contract is signed and exchanged.

### Third-party rights — how they're protected

| Land status | Protected by |
| --- | --- |
| **Registered** | Restriction or Agreed/Unilateral Notice on the register; reference in the Charges Register. Some bind without registration as overriding interests. |
| **Unregistered** | Class C(i)–(iv), D(ii)–(iii), F land charges at the Land Charges Registry — revealed by a **K15** search. |

Typical K15 entries:

| Class | Meaning |
| --- | --- |
| C(i) | Puisne mortgage (second charge). |
| C(ii) | Limited owner's charge (e.g. inheritance tax payable by life tenant). |
| C(iii) | General equitable charge. |
| C(iv) | Estate contract. |
| D(ii) | Restrictive covenant. |
| D(iii) | Equitable easement. |
| F | Matrimonial home rights. |

## Edge cases & gotchas

- **Positive covenants don't run with freehold land** — which is why management of shared driveways and fences is often done via leases, deeds of covenant, or commonhold instead.
- **Overreaching needs two trustees minimum** or a trust corporation. A sole-surviving tenant in common cannot give a clean receipt alone.
- **A mortgage not by deed is only equitable** — fine for some purposes, but the statutory powers don't fire.
- **"Subject to contract" doesn't fix a flawed contract.** If section 2 isn't satisfied, the contract is void no matter what label you put on it.

## Related guides

- [Training — Introduction to conveyancing (Session 1)](/crm/guides/legal/training-intro-to-conveyancing)
- [Training — Registered and unregistered land (Session 3)](/crm/guides/legal/training-registered-and-unregistered-land)
- [Land Registry register entries — catalogue](/crm/guides/legal/title-register-entries)
- [Tenants in common — one owner has died](/crm/guides/legal/will-trusts-tenants-in-common-death)
`,
    },
    {
        product: "legal",
        slug: "source-of-wealth-checks",
        title: "Source of wealth checks — funders, Armalytix, and signing off",
        one_line_description: "How to flag a funder, gather evidence through Armalytix, and mark source of wealth passed.",
        category: "compliance-and-policies",
        order: 25,
        last_screenshotted_at: "2026-07-22",
        tags: ["aml","source-of-wealth","source-of-funds","armalytix","funder","giftor","roadblocks","compliance"],
        content_hash: "2909926073d9b5b3e6035570f0e16d2d3e9f650b95efffffc03cd636cb5240bf",
        body_markdown: `## What it is

Before a purchase completes, we have to be happy about the money paying for it. Where is it coming from? And how did the person paying it come to have it? That is the **source of wealth check**. It runs per funder — everyone putting money into the deal gets checked, and a conveyancer signs each one off by hand once they're happy.

Two terms sit close together, so keep them apart:

- **Source of funds** is the money for *this* deal — the deposit, the sale proceeds, the gift. Knowing it came from a UK bank account is not enough. We need to know how that money got there.
- **Source of wealth** is the bigger picture — how the person built up their money in the first place. Think salary, an inheritance, selling a business, or a divorce settlement.

On a purchase you usually look at both on the same evidence. That's why the check in the CRM is just called **Source of wealth**. We gather most of the evidence through **Armalytix**, our source-of-funds provider. The client connects their bank through open banking, and Armalytix builds a report we review.

## When you'd use it

- You're onboarding a purchase and a client, a giftor, or another party is putting money in.
- A gifted deposit turns up and you need to check the donor as well as the buyer.
- The funds change shape late on — a mortgage swapped for a private loan, a new lump sum, money from overseas — and you need to re-check where it came from.

## Walkthrough

1. **Open the client on the case and find the Source of wealth section.** It sits on each party member in the **Clients** section, under their ID checks. It only shows for people who can actually fund a deal — clients, giftors, executors, personal representatives, an attorney under an LPA, and third parties registered as paying money in. You won't see it on a beneficiary, because money flows out to them, not in.

   ![The Source of wealth section on a client, with the Needs source of wealth check box unticked](/static/guides/legal/source-of-wealth-checks/needs-sow-check.png)

2. **Tick "Needs source of wealth check" for each funder who needs one.** This is your call — the system doesn't guess who to flag. Tick it for every person whose money is going into the purchase. Ticking it opens a **Source of wealth satisfied** line for that person and puts the check on the case roadblocks.

   ![The box ticked, showing the Source of wealth satisfied line and the Mark source of wealth passed button](/static/guides/legal/source-of-wealth-checks/mark-source-of-wealth-passed.png)

3. **Send the client the Armalytix request.** In **Compose email**, pick the **Request SOW** template. It asks the client to send a source-of-funds report through Armalytix. It also reassures them that Armalytix is FCA-regulated, can't move money, and only shares what they choose. A giftor gets their own request — the person giving the money gets the same checks as the buyer.

   ![The Request SOW email template selected in the template picker](/static/guides/legal/source-of-wealth-checks/request-sow-email-template.png)

4. **Read the report when it lands, and file it on the case.** Save the Armalytix report as the **Source of Wealth Report** document. Then actually read it — this is the part that matters. Does what you're seeing match what you know about the client? Follow the money back to where it really started. If a lump sum came from an inheritance two years ago, the evidence you want is the inheritance paperwork — not just six months of statements.

5. **Write down your reasoning on the file.** A report sitting on the case is not a decision. It's evidence. Note briefly why you're happy, or what you chased and how it was sorted. If the file is ever pulled for a CLC audit, your note is what shows you understood the client — not just that you collected some statements.

6. **Mark source of wealth passed.** Once you're genuinely happy, click **Mark source of wealth passed** on that funder. The system records who signed it off and when.

   ![The funder now showing Passed with a date and an Unmark button](/static/guides/legal/source-of-wealth-checks/source-of-wealth-passed.png)

7. **The roadblock clears once every flagged funder is passed.** The **Source of wealth satisfied** item on **Exchange ready** (and **Pre-completion ready**) only clears when all the funders you flagged are signed off. Until then it stays open and holds exchange.

   ![The Source of wealth satisfied roadblock item, showing a funder not yet satisfied](/static/guides/legal/source-of-wealth-checks/roadblock-source-of-wealth-satisfied.png)

## How it works under the hood

- **A report on file never passes the check on its own.** The Armalytix report is evidence. A person still has to look at it and decide. This is on purpose — the same as the way a PEP result never clears itself. The tool lays out the information; you make the call.
- **It's per funder, not per case.** Say a couple are buying with a gift from a parent. That's three checks — both buyers and the donor — and the roadblock won't clear until every one you flagged is passed.
- **Passing is recorded and reversible.** The sign-off saves your name and the date. If something changes, **Unmark** reopens it and the roadblock comes back.
- **There is no "fail" button.** If you're not happy, you simply don't pass it. The roadblock stays open and holds the case. A concern that looks like money laundering is not something you sort out in this screen — see below.

## Edge cases & gotchas

- **Gifts are a chain, and the slowest part of pre-exchange.** A gifted deposit runs donor declaration → donor ID → donor's Armalytix → the client's Armalytix. Start it at intake, not the week before exchange.
- **The client can't or won't connect their bank.** Armalytix has a backup — statements can be uploaded or scanned, and it still runs the checks. A flat refusal to show where funds came from is itself a signal, not just a delay.
- **A bank connection doesn't last forever.** Open-banking consent runs out after about 90 days. On a long deal you may need a fresh pull rather than leaning on an old one.
- **Watch for the usual red flags** — large cash deposits, money from overseas (especially higher-risk countries), third-party payments nobody mentioned, funds that don't fit what you know of the client, and last-minute changes to how the purchase is being paid for. The Armalytix report points out things that don't add up; treat them as prompts to ask, not boxes to tick.
- **If you know or suspect money laundering, stop and report it to the MLRO.** Don't mark the check passed. Don't try to sort it out with the client. And don't tell them a report is being made — tipping off is a crime. Suspicion is a low bar, and reporting is not a choice.

## Related guides

- [Anti-Money Laundering & CTF policy — the staff-facing summary](/crm/guides/legal/aml-policy)
- [Onboarding — what the system asks, and what you confirm by hand](/crm/guides/legal/onboarding-checklist)
- [Risk management — grading a case and re-grading as it changes](/crm/guides/legal/risk-management)
- [Bank account verification — submission, the call, and the lock](/crm/guides/legal/bank-account-verification)
`,
    },
    {
        product: "legal",
        slug: "authority-to-exchange",
        title: "Authority to exchange — scripts for sale and purchase",
        one_line_description: "What to say to the client on the exchange-authority call, sale and purchase versions.",
        category: "legal-practice",
        order: 30,
        last_screenshotted_at: "2026-05-29",
        tags: ["exchange","scripts","sale","purchase","client-call"],
        content_hash: "409eaffe49a8692df1b7eb92ce1fb6bf8c0c723d7816b4004c4b96b82ba5c408",
        body_markdown: `## What it is

The verbatim scripts for taking a client's express authority to exchange contracts. Two flavours: one for sales, one for purchases. Each covers the legal consequences of exchange and the client's responsibilities up to completion.

The point is to make sure the client has heard, on the call, exactly what they're agreeing to before we exchange — and that we can evidence having said it.

**System view** — the authority call itself doesn't directly clear a roadblock. What it does is unblock the **Exchange Ready** roadblock's *Ready-for-exchange review complete* item by giving the authoriser the final piece they need to sign off. Don't take authority before Exchange Ready's other items are green: enquiries resolved, replies-to-enquiries report received (purchases), report to client sent (purchases). If any of those are still open, the engine isn't ready, and neither are you.

## When you'd use it

- You're about to exchange contracts and need the client's express authority on a recorded call.
- You're new and want the words to use so you don't ad-lib your way through this conversation.

## Walkthrough — sale

> *"We have contacted the buyer's solicitors to see if they are ready to exchange contracts today. In the meantime, I need to take your express authority to exchange once confirmation is received from the other side."*

**Details of your transaction:**

- Sale price: £[insert]
- Deposit to be paid by buyer on exchange: £[insert]
- Proposed completion date: [insert]

**Important points about exchange:**

- Up until exchange, either party can withdraw without penalty.
- Once contracts are exchanged, the transaction becomes legally binding.
- You will be legally committed to sell at the agreed price, and the buyer will be legally committed to buy.
- The buyer is responsible for insuring the property.
- The agreed completion date will be fixed in the contract.

**Between exchange and completion:**

- Request any final redemption statements (if applicable).
- The buyer's solicitor will request their mortgage funds (if applicable).
- On the day of completion, once the full purchase price is received, we will release the keys to the buyer via the estate agent.
- The sale proceeds will be paid to you (or your nominated account), less any redemptions, legal fees, and estate agent fees.
- You should ensure you are ready to vacate the property by the agreed completion date.

**Authority:**

> *"Please can you confirm now that you give me authority to exchange contracts on your behalf on the basis of the sale price, deposit, and completion date I have just outlined?"*

## Walkthrough — purchase

> *"I am calling to let you know that we have contacted the seller's solicitors to see if they are able to exchange contracts today. In the meantime, please may I have your express authority."*

**Details of your transaction:**

- Purchase price: £[insert]
- Deposit to be paid on exchange: £[insert]
- Proposed completion date: [insert]

**By exchanging contracts your purchase becomes legally binding.** Up until exchange, either party can withdraw without penalty. Once contracts are exchanged:

- You are legally committed to buy the property for the agreed price.
- The seller is legally committed to sell to you.
- The deposit will be sent to the seller's solicitor.
- The agreed completion date will be fixed in the contract.

**Your responsibilities after exchange:**

- Ensure that the balance of purchase funds (including any mortgage advance, the remainder of your deposit, and any additional sums due) is available in time for completion.
- Arrange for buildings insurance to start from the date of exchange — unless buying a leasehold flat where the building is insured by the freeholder / management company; we will confirm if that applies.
- Make moving arrangements — book removals, transfer utilities.

**What happens between exchange and completion:**

- We carry out final pre-completion checks.
- We request mortgage funds from your lender (if applicable).
- You send us the balance of funds in good time before completion.
- On completion, we transfer the purchase money to the seller's solicitor, and you can collect the keys from the estate agent.

**Authority:**

> *"Please can you confirm that I have your authority to exchange?"*

## Edge cases & gotchas

- **Insurance at exchange** is a purchase-side requirement except for leasehold flats. If you're not sure whether the building is freeholder-insured, say so on the call and confirm by email afterwards.
- **"Today" language** in the script presumes you're calling to exchange same-day. If exchange is scheduled for a named future date, swap in that date so the client doesn't think they're committing to exchanging *right now*.
- **Reading the figures aloud** matters. Mis-stating the sale price or deposit on the call would invalidate the authority. Read from the screen, slowly.

## Related guides

- [Sale process](/crm/guides/legal/sale-process)
- [Purchase process](/crm/guides/legal/purchase-process)
`,
    },
    {
        product: "legal",
        slug: "conflicts-of-interest-policy",
        title: "Conflicts of interest policy — the staff-facing summary",
        one_line_description: "How the practice identifies, records, and manages conflicts between clients, and between a client and the practice.",
        category: "compliance-and-policies",
        order: 30,
        last_screenshotted_at: "2026-07-07",
        tags: ["conflicts","conflict-of-interest","clc","compliance","own-interest","client-conflict"],
        content_hash: "cc62c2c35e25e2c0196d37c8b9176c1d59831d95e64b57ab82558b0b705e9211",
        body_markdown: `> First draft for the CLC audit — canonical signed policy is held by compliance; this page is the staff-facing summary.

## What it is

The summary of how Sail Legal spots and handles conflicts of interest, under the CLC's Conflicts of Interest Code. A conflict is any situation where the practice's duty to one client is at odds with its duty to another, or with the practice's own interest. The signed master governs; this page is what staff do.

## When you'd use it

- You're opening a matter and need to run the conflict check.
- You act for one party and are asked to also act for, or against, someone connected.
- A referral fee, personal connection, or firm interest touches a matter and you're unsure whether it's a problem.

## The two kinds of conflict

- **Client conflict** — a conflict between two or more clients, or a significant risk of one.
- **Own-interest conflict** — a conflict between a client's interest and the practice's own interest (including staff interests and financial arrangements).

## What staff actually do

### Step 1: Check at the outset

- Run a conflict check against existing and former clients and connected parties before accepting instructions.
- Consider own-interest factors: referral arrangements, personal relationships, financial stakes.
- Record that the check was done and what it found.

### Step 2: Decide whether you can act

- If there is a conflict or a significant risk of one, the default is **do not act**.
- Only proceed where a recognised exception applies and the conditions for it are met.
- Where in doubt, escalate rather than proceed.

### Step 3: If a conflict emerges mid-matter

- Stop and assess as soon as the conflict is spotted.
- Where the practice can no longer act fairly for a client, cease acting for the affected party.
- Protect confidential information — do not let it flow across the conflict line.

### Step 4: Record and review

- Record the conflict, the decision, and the reasoning on the matter.
- Where an exception was relied on, record which one and why the conditions were met.

## Records that must exist

- The outset conflict check and its result for every matter.
- Any conflict identified later, the decision taken, and the reasoning.
- Where the practice acted despite a potential conflict, the exception relied on and the conditions satisfied.

## Edge cases & gotchas

- **The default is not to act.** Acting through a conflict needs a proper exception, not just a busy diary.
- **Own-interest conflicts are easy to miss** — referral fees and personal connections count, not just two-client situations.
- **Confidential information is part of the conflict test.** Even without a live conflict, holding a former client's confidential information can bar the practice from acting against them.
- **The recognised exceptions live in the signed master.** Don't rely on memory of what's permitted; check the canonical policy.

## Related guides

- Compliance policies — the index and where the signed versions live
- Anti-Money Laundering & CTF policy
- Formal complaints — handling, escalation, and the client response
`,
    },
    {
        product: "legal",
        slug: "sms-login-codes",
        title: "Login codes — when one doesn't arrive",
        one_line_description: "Which second factor someone is on, why a text might not have sent, and what to do when a staff member loses their authenticator app.",
        category: "getting-started",
        order: 30,
        last_screenshotted_at: "2026-08-05",
        tags: ["login","two-factor","authenticator","client-portal","troubleshooting"],
        content_hash: "3ea7c3f6ea7e57af557aee2d744b42b0b466dfe1499892162aadcc56aafd376e",
        body_markdown: `## What it is

Everyone types a six-digit code before they can get in. Where that code comes from
depends on who they are.

**Clients always get a text.** **Staff get a text until they set up an authenticator
app**, and after that the code comes from the app instead. **Nothing is sent to someone
on an authenticator** — there is no text to chase and no **Resend code?** link on their
screen.

So the first question on any "I can't get in" call is which of the two they're on. The
screen tells you: if it names a phone number, they're on texts; if it says *"Enter the
6-digit code from your authenticator app"*, they're not.

## When you'd use it

- A client rings to say they're stuck on the code screen and nothing has come through.
- You're locked out yourself and no code arrives.
- Someone has tapped **Resend code?** over and over, been told each time that the code was sent, and still has nothing.
- A case came in from an introducer email and the client can't get in at all.
- A colleague has lost, wiped or replaced the phone their authenticator app was on.
- Someone has typed the wrong code several times and is now being told to wait.

## Someone on texts, and nothing arrives

1. **Ask when they opened the page, not when they tried to log in.** The code
   sends as soon as the code screen loads. There's no button to press to get the
   first one. If they opened the page twenty minutes ago and then went to find
   their phone, that's the time that matters.

2. **Ask them to read the screen to you.** The wording tells you which problem
   you have.

   - "A SMS has been sent to …" with part of a number — we did just send one. Check
     the digits you can see against the phone they're holding. Often it's a work
     mobile they've left at the office, or an old number we still have on file.
   - "We sent a code to … a few minutes ago." — we did **not** send a new one, because
     the last one is still current. Get them to scroll back through their texts. A
     fresh one only comes after five minutes, or from **Resend code?**.
   - "You appear to have no record of a valid phone number with us." — we have no
     number we can use, so nothing was ever sent. Go to step 5.

3. **Get them to scroll back, but only ten minutes.** A code stops working ten
   minutes after we sent it. Older than that and the screen says it has expired —
   ask for a new one rather than hunting for the text.

4. **Leave a full minute between resends.** If they tap **Resend code?** within a
   minute of the last one, we don't send it. The screen tells them so: "We already
   sent one. Wait a minute, then try again." Ask them to wait sixty seconds, tap
   once, then watch the phone.

5. **Add a number if there isn't one.** Put the client's mobile on the case and
   mark it as their main number. We only ever use the main one. A second number
   sitting under it never gets tried. Then ask them to reload the code screen,
   which sends a fresh code right away.

6. **Check delivery in the message log if you have access.** In Twilio, go to
   **Messaging → Logs** and filter by **Recipient Number**. This settles it. If
   the row says Delivered, the text reached the phone, and the problem is at
   their end.

   ![Twilio message logs listing outbound messages with a Delivered status](/static/guides/legal/sms-login-codes/twilio-message-logs.png)

   Two things catch people out here. Type the number with the country code, like
   +447700900123, not 07700900123, or you'll get no rows back. And the times on
   screen are US Pacific. A UK evening at 19:30 shows up as 11:30, so take eight
   hours off UK time to find the row you want.

7. **Pass it to engineering if none of that explains it.** Give them the phone
   number and roughly when the person opened the page. That's enough to find the
   text — or to show there wasn't one.

## A staff member who can't use their authenticator app

There's no text to fall back on once someone has set the app up, so **the only way back
in is an admin clearing it for them**. **They then go back to codes by text**, and can
set the app up again whenever they like.

1. **Check it really is the app.** Their screen should ask for the code from the app and
   show no phone number. If it names a number, they're still on texts — use the section
   above.

2. **Ask an admin to clear it.** Any admin opens **Settings → Edit Staff Users**, finds
   the person, and uses **Reset** next to *Authenticator app: set up*. It asks once more
   before doing it.

3. **Have them reload the code screen.** A text goes out as it loads, exactly as it used
   to, so they're straight back in.

4. **Point them at Security when they're ready to set it up again.** It's in the menu
   next to Logout, and they'll need to pass a code by text first.

If the only admin is the person who's locked out, that's one for engineering — there's a
way in from the server side.

## Setting up an authenticator app

Staff do this themselves from **Security** in the menu. It asks for a code by text first,
then shows a QR code, a setup key and a setup link. Scan the QR with a phone app, or
paste the setup link into 1Password if you're on a computer. **Type the code the app
shows to finish** — until you do, nothing has changed and you're still on texts.

## Edge cases & gotchas

- **Codes expire after ten minutes.** Older codes used to keep working indefinitely; they
  don't now. "That code has expired" is a different message from "Code is incorrect", so
  the screen tells you which you're dealing with.
- **Five wrong codes in a row means a fifteen-minute wait.** This covers both texts and
  the app, and a correct code is refused during the wait. It clears as soon as one goes
  through, so four wrong ones followed by a right one costs nothing. An admin reset also
  clears it.
- **Resending inside a minute sends nothing**, and says so. Reloading the page
  inside five minutes also sends nothing, and now says so too — the wording on the
  page tells them to use the code we sent a few minutes ago.
- **A new code stops the old one working.** If someone reads out a code from an
  earlier text after a newer one has landed, they'll be told the code is
  incorrect, even though it looks fine to them.
- **The same app code can't be used twice**, even within the thirty seconds it's on
  screen. Someone pasting a code they already used will be told it's incorrect.
- **We only use the main number.** A client can have three numbers on the case and
  still get nothing, because none is marked as the main one. There's no error and
  no failed text. We just skip the send.
- **Cases built from introducer emails often come in with no phone at all.** If we
  can't make sense of the number in the email, we save the case without one rather
  than throw the whole thing out. That's what causes the "no record of a valid
  phone number" message.
- **"Please enter a valid phone number" comes from the phone network, not us.** The
  number on file is wrong or can't be reached. Correct it rather than trying again.
- **Staff get asked again every week, clients every three days.** "Why is it asking
  me again, I only just did this" is usually the window running out.
- **Clients can't use an authenticator app.** It's staff only, on purpose — passing a
  code by text is also how we confirm we have a working number for a client, and an app
  can't tell us that.
- **Someone who works for two of our brands sets the app up twice.** Sail Homes and Sail
  Legal hold separate staff records, so each needs its own entry in the app.
- **Nothing sends outside the live system.** The code on test setups is always
  000000 — for texts and for the app — and no text goes out, so only real client
  traffic shows in the message log.

## Related guides

- [Client portal — onboarding steps and what staff can see](/crm/guides/legal/client-portal-onboarding-steps)
- [Anti-fraud and verification](/crm/guides/legal/anti-fraud-and-verification)
`,
    },
    {
        product: "legal",
        slug: "reminder-emails-personal-vs-helper",
        title: "Reminder emails — personal vs helper modes",
        one_line_description: "Why one outstanding step is shown not all, daily cadence vs 5-day cooldown, how to pause a client who's away, and what to tell a confused helper.",
        category: "communications",
        order: 30,
        last_screenshotted_at: "2026-05-29",
        tags: ["reminders","onboarding","id-check","helper-mode","cooldown","client-portal","pause"],
        content_hash: "2596ea0f029c2dd204897b6363b8b34a305590f219e02c7293708a76e18ae4ce",
        body_markdown: `## What it is

The chase emails that go out automatically to clients (and sometimes to other people on the case) when an onboarding step has been outstanding too long. Two modes exist — **personal** and **helper** — and they look and feel different on purpose.

The most important thing to internalise is that **the email shows exactly one outstanding step**, not a list of everything outstanding. Not because the worker doesn't know the full list (it does), but because the client portal only reveals onboarding steps **one at a time** — if the email enumerated three future steps, the client would click the link, log in, and see only the first one. The reminder mirrors the portal.

## When you'd use it

- A client says they're "getting emails every day about their ID check" and you want to confirm that's expected behaviour, not a bug.
- A helper writes back saying *"I keep getting emails about Sarah's ID — I don't know what I'm supposed to do"* and you need to explain.
- An onboarding-team colleague asks why the reminder names only one outstanding step when several are open.
- A client tells you they're away — on holiday, in hospital, or dealing with a death — and you want to stop chasing them until they're back.
- You're stopping reminders going to a client because of a death, complaint, or bereavement-sensitive moment on the case.

## Walkthrough

### The two modes — what each one is

| Mode | The recipient is… | Cadence | Subject line shape |
| --- | --- | --- | --- |
| **Personal** | The actual blocker. Their own action is what's outstanding. | Daily. | Names the recipient and the blocker. *"Sarah, your ID check is the last thing standing…"* |
| **Helper** | Not blocking anything themselves, but has a capability over someone else's blocker (e.g. an executor / joint owner who can complete an ID check on someone else's behalf). | **5-day cooldown** between helper sends to the same person on the same case. | Centres on the blocker's owner, not the helper. *"Could you nudge Sarah to finish their ID check?"* |

Personal beats helper when both could apply to the same recipient — we don't double-up. The system picks one mode per recipient per send.

Helper mode is **narrow** today. It only kicks in on **ID check** and **ID proof of address** reminders, because those are the only two blocker types where the system can identify who has a capability to act on someone else's behalf. Every other reminder (e.g. agree to TOE, complete the PIF, pay money on account) is **always personal** — there's no helper variant.

### Why the email only names one step

The client portal reveals onboarding steps **one at a time** in a defined order — the client does not see future steps until they finish the current one. The reminder email picks the **single step** matching what the portal would reveal *next* for that client, even when several blockers are technically outstanding underneath.

Two consequences worth knowing:

- The blocker named in the email is the one the client will land on when they click through. It will match the portal experience.
- A reminder isn't a summary — it's a nudge on the **next** thing. If the client has five outstanding steps, they'll see five different reminder subjects over the next several days, not one summary email listing all five.

This isn't a bug or under-disclosure. It's deliberate — surface-level honest, in-portal logically sequenced. If a client says *"I thought I'd done everything except ID — why am I still getting chased on agree-to-TOE?"*, the answer is the portal hadn't revealed the next step to them yet; once they finish the current one, the next reveal happens, and the reminder topic moves on.

### What a helper-mode email actually says

A helper email reframes the ask completely. Instead of *"You haven't done X"*, it reads as a favour: *"We've reminded Sarah she needs her ID check. If you can nudge her, that would help the case along."*

The body doesn't enumerate Sarah's full step list. It mentions only the one blocker. The CTA is *help Sarah*, not *log in and do something yourself*. The helper has nothing to do in the portal as themselves — for now, helper-mode reminders are pure social-pressure assists.

If the helper has any personal blockers of their own, they get a **personal** email instead, naming their own outstanding step. We do not dilute attention by mixing modes in a single send.

### The 5-day helper cooldown

Personal reminders go daily — once a day, every day the step stays outstanding. Helper reminders are throttled to **once every five days** per (case, helper) pair. A helper getting a daily ping for someone else's missing ID would be the fastest way to burn the goodwill we need from them.

Cooldown is enforced by looking at the reminder send log — if the most recent helper reminder to that person on that case is less than five days old, the send is skipped silently. The next send is queued for whenever the cooldown lapses.

The cooldown is **per case and per helper**. So someone helping on two cases will hear from both cases on independent five-day rhythms.

### Pausing reminders for a client who's away

When a client tells you they're away, you can stop their chase emails until a date you pick. Open the case, go to the client's tab under the party section, and set a date under **Pause reminder emails until**.

Four things to know about it:

- **It follows the person, not the case.** If they're selling one home and buying another, both go quiet. You set it once. You don't have to remember the second case.
- **It starts again by itself.** On the date you picked, chasing resumes. There's nothing to come back and undo. If they ring you sooner, clear the date and chasing starts again straight away.
- **It stops helper emails about them too.** If Sarah is paused, we stop asking her joint owner to nudge her. We'd otherwise be telling him *"we've reminded Sarah"* when we hadn't, and pushing the chasing onto him instead of stopping it.
- **It only stops reminder emails.** They'll still get magic links when they try to log in, and any email you send them by hand. It doesn't touch your work — the case stays in your day view, jobs stay in your list, and roadblock dates keep running. A pause is about not pestering the client, not about parking the case.

You'll see a small clock icon next to a paused client's name in the party tab list, so you can tell at a glance who's quiet and why.

**Pause the person, or pause the case?** Pausing the person is right when one client is away and the rest of the case carries on. Pausing the case — the chase reason *client asked to pause work* — is right when the client has asked us to stop working on the whole thing. The case-level one stops everything for everyone and has no end date, so somebody has to remember to undo it.

### What you do when a helper is confused

A helper writes in and says some version of *"I've been getting these emails about Sarah and I have no idea what they want from me."* You need to explain:

- The system has flagged the helper as someone who **could** help (joint owner / executor / capable signer for an ID check).
- We're not asking the helper to do anything in the portal as themselves.
- We're asking the helper to **gently chase Sarah** to finish the step we've named.
- The helper isn't being chased daily — only once every five days. (If it feels more frequent, they might be on multiple cases.)
- If the helper genuinely can't help, that's fine — the personal reminders to Sarah continue regardless. We're not relying on the helper.
- If the helper wants the emails to stop entirely (e.g. they've fallen out with Sarah, or they're not actually in a position to help), set a pause date on **the helper**. That mutes them without touching the personal reminders still going to Sarah.

### What you do as staff

- **Don't promise a client that their reminders will stop the moment they do one step.** The portal will reveal the next step and reminders will continue on that. The "you're done" moment is when *every* onboarding step is complete.
- **Read the helper email's framing before forwarding it on.** A copied-and-pasted reminder out of context reads like nagging. Add a one-line wrapper if you're forwarding internally.
- **When a client is away, pause them rather than letting the daily chase run.** Set a date under Pause reminder emails until. If the whole case needs to stop, use the case-level chase reason instead.
- **Cross-reference the reminder subject against the portal step.** If they don't match, that's a signal worth flagging — they should always agree.

## How it works under the hood

- The reminder worker computes the **full** list of outstanding steps for every user on every active case. Then it **picks one** — the highest-priority step matching the portal's reveal order — and that's the one the email names.
- The portal step priority list inside the reminder worker is **kept in sync by hand** with the portal's reveal order. If the portal changes its sequence, the reminder priority is updated alongside it.
- **Personal beats helper on the same recipient** before any cooldown check runs. So a helper-mode email is only ever sent to someone who has zero personal blockers of their own.
- **The cooldown log records every send**, including skipped ones, so the cooldown logic can read off it directly. There's no separate cooldown table.
- **Helper mode is limited to two blocker types** today — ID check and ID proof of address. Other blocker types (e.g. PIF, TOE agreement, money on account) never spawn a helper email; they're always personal.
- **A pause is stored against the person**, not the case, which is why it covers every case they're on. The worker drops paused people when it works out who to email, and also drops them from the list of people it says are blocking — that second part is what stops helper emails going out about them.

## Edge cases & gotchas

- **"Why is the email talking about agreeing to TOE — I thought we were waiting on their ID?"** Because the portal would reveal TOE before ID in the step sequence, and ID is further down the list. The next visible thing wins.
- **A helper getting daily emails.** They're on more than one case. The cooldown is per case; the union of two case cadences can land near-daily.
- **The reminder names a step the client says they've already done.** Usually a document upload that didn't flip the status field, or an ID check that's been submitted but not yet approved. Check the underlying status before assuming the engine is wrong.
- **Helper-mode emails are blank on the *helper personal action* front by design.** The helper has nothing of their own to do; they're being asked to nudge. Don't treat the absence of an in-portal action as a UI gap.
- **Cooldown applies even when the previous send was a skipped one.** If the previous helper email was suppressed for some unrelated reason, the cooldown timer still runs from the attempted send.
- **Reminders continue on cases that are closed for the wrong reason.** Aborts, on-hold, abandoned — make sure the case status is correct so the reminder worker stops looking at it.
- **Bereavement-sensitive moments.** If a client has died, or the executor has just told us about a death, pause that recipient straight away. Without a pause the cadence is daily and will not stop on its own.
- **A pause that's lapsed looks like no pause at all.** Once the date passes, the control goes back to reading *Set pause date* and the clock icon disappears. That's on purpose — a lapsed pause is the normal end state, and chasing has already resumed. If you need longer, set a new date.
- **A paused client still shows up in your day view.** The pause only silences their emails. If you're wondering why a case with a paused client is still on your list, that's why — and it's deliberate, so paused cases don't quietly drift.
- **The payment reminder has three different versions.** It chases three kinds of money — upfront costs at the start, the deposit before exchange, and the balance before completion — and says something different for each. When more than one is unpaid, the email talks about the latest-stage money, because that's the one with a fixed date attached. So a payment chase on an exchanged case talks about completion funds, not upfront costs. If you're cross-referencing the subject against the portal step, the portal shows the full breakdown of every kind outstanding, while the email names only the one it leads with.
- **Setting the case's ownership answer doesn't stop the joint-ownership chase — except on trust.** The joint-ownership reminder reads each client's own answer, not the case field, so a handler who records the answer on the case after a phone call will watch the chase carry on. The one exception is **on trust**: set *joint ownership held as* to **on trust** and both the portal step and the reminder stop for everyone on the case, because a deed has fixed the shares and there is nothing left to ask. Any other value leaves the chase running, deliberately — the clients' own recorded election is the file's evidence that they were advised.

- **The payment reminder never says how much.** Amounts and bank details live only in the portal, behind the magic link. That's deliberate: conveyancing is a big target for payment fraud, and a figure in an email is exactly what someone needs to make a fake "our bank details have changed" follow-up look real. If a client asks how much, point them at the portal rather than replying with a number.

## Related guides

- [Client portal — onboarding steps](/crm/guides/legal/client-portal-onboarding-steps)
- [Onboarding checklist](/crm/guides/legal/onboarding-checklist)
- [Email Quality Score — the send-time grade on outbound email](/crm/guides/legal/email-quality-score)
- [Anti-fraud and verification](/crm/guides/legal/anti-fraud-and-verification)
`,
    },
    {
        product: "legal",
        slug: "risk-management",
        title: "Risk management — grading a case and re-grading as it changes",
        one_line_description: "How the risk grade is set, when it's re-assessed, what each colour means in practice, and the MLRO loop.",
        category: "case-view",
        order: 30,
        last_screenshotted_at: "2026-05-29",
        tags: ["risk-assessment","mlro","holp","aml","vulnerability","roadblock","ongoing-risk-assessment"],
        content_hash: "c09895a198679914e0c7d252220f2bc7f19f8dcf6623c05be342cde44be4b48c",
        body_markdown: `## What it is

The case-level **risk grade** — *red*, *amber*, or *green* — that sits alongside the case and tells the team how much attention this matter needs. It's both an AML control (we're required to risk-rate every matter we take on) and a workflow control (red cases get different chase intervals, different authorisations, different scrutiny). The grade is owned by Sail Legal staff; the engine doesn't compute it for you — it just makes sure you record one, and reminds you to re-record one when the case has changed enough that the old grade is stale.

## When you'd use it

- A new case has just been instructed and you need to do the initial risk grading.
- A fact about the case has changed (vulnerable client identified, ID check came back with a flag, source of funds turned out to be more complex than you thought) and the existing grade no longer reflects the picture.
- An authoriser is signing off and wants to confirm the current grade.
- You're inheriting a case and want to know what to look at first.

## Walkthrough

### What the system gates — the **Ongoing Risk Assessment** roadblock

When a case is instructed, the engine fires the **Ongoing Risk Assessment** roadblock. This one is unusual in two ways:

- It **requires authoriser sign-off** to fully resolve, not just for the work to be done.
- It **blocks exchange**. The case cannot be marked ready for exchange while this is open.

The roadblock is satisfied by recording a current risk assessment with a grade and an explanation — the engine doesn't second-guess the grade you pick, but the authoriser will when they sign off.

### The risk assessment record

Each risk assessment row captures:

| Field | What you record |
| --- | --- |
| **Grade** | *Red*, *Amber*, or *Green*. (*Not set* is a fail state — the roadblock won't clear.) |
| **Explanation** | Free text, the *why*. This is the audit trail. "Green — sale of registered freehold, no debts on title, both clients passed ID and video calls, source of funds clear" is the right shape. |
| **Are any clients vulnerable?** | *Yes* / *No* / *Not set*. Treat *not set* as unfinished. |
| **Vulnerability explanation** | Required when vulnerability is *yes*; the specific picture and what we're doing about it. |

History is preserved — every assessment is a new row, so you can read back the path the grade has taken across the lifetime of the file.

### What each grade actually means

| Grade | Read as | Typical case shape |
| --- | --- | --- |
| **Green** | Standard transaction. Defaults apply. | Vanilla sale or purchase, no debts, no probate, no AML flags, registered title, both clients verified cleanly, source of funds straightforward. |
| **Amber** | Something needs extra eyes — but proceed. | Probate sale, transfer of equity, cash purchase, unregistered title, one client overseas, PEPs result requires investigation, source of funds explainable but not obvious. |
| **Red** | Stop and decide before proceeding. | Suspected fraud (or actual potential-fraud flag on an ID check), unexplained third-party funds, persistent uncooperative behaviour from a client, sanctions hit, client identified as vulnerable with no decision yet on how to support them. |

The grade carries through to chase intervals and authorisation expectations — red cases get shorter chase cycles and a lower bar for escalation. Use the explanation to encode the specifics; the grade alone isn't a handoff signal.

### When to re-grade

A grade is **stale** the moment any of the inputs underneath it change in a material way. Re-grade when:

- A new client is added or removed.
- The ID check or PEPs result changes.
- The verification call surfaces concerns about vulnerability or coercion.
- The bank account picture changes — a third-party account, a foreign account, a new source of funds story.
- A title issue you didn't know about is revealed (debts, restriction, defective deed).
- A long-running delay has changed the risk picture (e.g. a green case where the client has now gone missing for months).

You don't edit the previous assessment. You add a new one — the history is the audit trail.

### MLRO and HOLP — when they come in

- **PEPs result requires investigation** — the MLRO decides. The Client Verification ID-checks item won't clear until the result has been reviewed.
- **Sanctions hit** — straight to the MLRO. Stop work.
- **Suspected money laundering** — the MLRO decides whether a SAR is required. Don't tip off the client.
- **Vulnerability sign-off** — when video calls surface vulnerability concerns, the HOLP sign-off lifts the related Client Verification checklist item.
- **Red-grade cases** — HOLP is typically the authoriser; expect questions on the explanation before sign-off.

The authoriser sign-off on the **Ongoing Risk Assessment** roadblock is the moment the grade is locked in for that point in time. Until that sign-off lands, the roadblock stays in *awaiting signoff*.

### What you do per case

- Set the initial grade once you have the full picture — not at point of instruction, but once ID, source of funds, and title-shape are visible.
- Write the explanation in plain English; it has to make sense to an authoriser who hasn't touched the file.
- Re-grade as the inputs change. A green case can become amber overnight if the source of funds story changes.
- Flag red cases verbally to the conveyancer or HOLP — don't rely on the roadblock chase alone.
- For vulnerable clients, the explanation should name the *support* being offered, not just the vulnerability itself.

## How it works under the hood

- The **Ongoing Risk Assessment** roadblock is the only roadblock that both *blocks exchange* and *requires authoriser sign-off*. That's deliberate — it's an AML control we cannot waive.
- The engine **doesn't grade the case for you**. It checks only that a current assessment exists and has been signed off — the grade itself is your call.
- **History is preserved.** Every new assessment is a new row, never an edit. The display is the latest, but the trail is intact.
- The risk-assessment record is **separate from the Client Verification gates**. Passing ID and video calls doesn't make a case green. A red grade survives clean ID checks if the underlying risk story warrants it.
- Sign-off on the roadblock is **point-in-time**. A material change after sign-off should produce a new assessment, which re-opens the roadblock until it's signed off again.

## Edge cases & gotchas

- **A green grade is not a default.** It's a positive statement that nothing material is outstanding. Don't tick green on instinct — write the explanation that justifies it.
- **The vulnerability question is asked separately for a reason.** You can have a green case with a vulnerable client; the support obligations attach to the vulnerability, not the grade. Both fields matter.
- **"It was green last month."** That's not an answer to "is it green today?" If the case has materially changed, re-grade.
- **PEPs *requires investigation* doesn't automatically mean red.** The MLRO decides. The grade can still be amber depending on the wider picture; don't pre-empt them.
- **The roadblock is in *awaiting signoff* while waiting for the authoriser.** It can look stuck — that's normal. Chase the authoriser, not the engine.
- **Closing the case** auto-suspends most roadblocks but **not** post-completion ones. The risk assessment roadblock doesn't keep firing on a closed case — but a re-opened case may surface it again, in which case re-grade for the new picture.
- **Don't bury the vulnerability explanation inside the main grade explanation.** Use the dedicated vulnerability field; it's the field that will be searched in any future review.

## Related guides

- [Anti-fraud and verification](/crm/guides/legal/anti-fraud-and-verification)
- [Bank account verification](/crm/guides/legal/bank-account-verification)
- [Onboarding — what the system asks, and what you confirm by hand](/crm/guides/legal/onboarding-checklist)
- [Roadblocks: how they work](/crm/guides/legal/roadblocks-how-they-work)
- [Case view overview](/crm/guides/legal/case-view-overview)
`,
    },
    {
        product: "legal",
        slug: "roadblock-client-verification",
        title: "Roadblock: Client Verification",
        one_line_description: "How to clear each item under the Client Verification roadblock.",
        category: "roadblocks",
        order: 30,
        last_screenshotted_at: "2026-05-29",
        tags: ["roadblocks","client-verification","troubleshooting"],
        content_hash: "6601803be6ee428fc37113590aa4f39c61f5686e9606ee01252c3bdffb10c8c5",
        body_markdown: `## What it is

Client Verification is the system's catch-all for client-side checks: ID, video calls, vulnerability, contact details, terms of engagement, dual-rep consent, and (for purchases) the chosen payment method. It fires as soon as a case is instructed and stays open until the case has completed. Every checklist item targets a specific gate; missing any one of them blocks exchange.

## When you'd use this guide

- You're trying to clear a Client Verification item and can't tell what the engine is waiting on.
- An item won't go green even after you've done what looks like the work.
- A reviewer asks why a case can't be signed off as ready for exchange.
- You're picking up a part-onboarded file and want to know which gates have moved.

## The checklist

### Client ID checks passed

Every client who needs an ID check has a *valid* passing check that covers them up to completion.

- **Where to go**: open the case, **Details** tab, **People** block. Each client row shows their ID check status. If a check is missing or expired, run a new check from the *Run ID check* action on the client row.
- **Common stuck-states**:
  - **Check expired before the expected completion date** — even a passed check fails this item if it won't cover until completion. Run a fresh one.
  - **One client passed, another didn't** — the item is all-or-nothing. Check every client of the verifiable types (client, joint owner, executor, personal representative, LPA). Company executors don't need a personal check.
  - **Potential-fraud flag** — a failed check with the *potential fraud* tag does not clear by re-running on email. Escalate to HOLP.

### All clients passed initial verification call

Each client who needs verifying has a logged video call with a *pass* outcome that covers them through to completion.

- **Where to go**: **Details** tab → **People** block → client row → **Verification calls** button. Book or record a video call against each client.
- **Common stuck-states**:
  - **Call passed eleven months ago for a completion still three months out** — same expiry logic as ID checks. Book a fresh one.
  - **Company-executor-only case** — the item passes automatically; no personal call needed.

### All clients had no vulnerability concerns on call

Every passed video call records the vulnerability question as *none*. If a single call flags concerns, the item stays open until the relevant authoriser reviews.

- **Where to go**: **Details** tab → **People** block → client row → **Verification calls** → open the call → vulnerability question.
- **Common stuck-states**:
  - **A flagged call is never overwritten by a clean later call**. The item only clears once the flagged call's record is signed off — speak to the authoriser, don't book another call expecting it to wash through.

### Client emails and phone validated

Every client who can log into the portal has their own verified email address and their own verified phone number. The client proves each one themselves — email by clicking a magic link, phone by entering a code we text them.

- **Where to go**: **Details** tab → **People** block → client row. The roadblock's evidence panel also names each person and which of the two they're still missing.
- **Common stuck-states**:
  - **A client with no email or phone on file** — no email means no magic link, and no mobile means no SMS code. If the client genuinely has neither (e.g. elderly or assisted client), an authoriser / MLRO can record an authorised exemption with a mandatory explanation from the client card (**Details** tab → **People** block → client row → **Exempt from digital contact**). This clears the item for that client while preserving the regulatory AML audit trail.
  - **Adding a helper doesn't automatically satisfy the client's row** — contact verification is per person. Adding an authorised third party helps with facilitation, but the client still requires an authorised digital contact exemption if they have no digital channels of their own.
  - **A client changed their contact details** — re-verify on a video call before changing anything, and expect the item to reopen until the new address or number is verified.
  - **A stale phone still counts** — any verified number on that person satisfies it, not just their primary one. If they can no longer reach an old mobile, only your judgement catches that.

### Clients agreed to terms of engagement

At least one portal-capable client has agreed to the TOE in the client portal.

- **Where to go**: **Details** tab → **Client portal** block (or check the *agreed at* field on the client row). The client agrees in the portal — there's no staff-side tick.
- **Common stuck-states**:
  - **Client hasn't logged into the portal yet** — they need to onboard first. Re-send the portal invite.
  - **Company-executor-only case** — the item never fires because companies can't agree to a personal TOE.

### All clients consented to dual rep

Only fires on dual-rep cases. Every client who needs to consent has done so.

- **Where to go**: **Details** tab → **Conflicts** block. Each client's dual-rep consent shows there.
- **Common stuck-states**:
  - **You added a new client after others consented** — the new client needs to consent individually before the item clears.

### Client set purchase payment method

Only on purchase cases. The *purchase payment method* dropdown is set to something other than *unknown* (cash, mortgage, etc.).

- **Where to go**: **Details** tab → **Purchase** block → *payment method* dropdown.

## Edge cases & gotchas

- **The roadblock disappears the moment the case is recorded as completed.** This is deliberate — historic completed cases shouldn't be polluted by a TOE item that was never tickable. If a completed case wrongly shows the roadblock missing, check the *has completed* milestone.
- **ID and video call expiry is reckoned against the latest of completion deadline / expected completion**. A long-running case can flip an item back to red as the completion date approaches. That's the engine working as designed.
- **A potential-fraud flag is a HOLP escalation**, not something a case handler clears alone. Don't re-run the check from an email request — book a video call first.
- **Vulnerability concerns persist across calls.** A subsequent clean call doesn't unset the flag. Authoriser review is the only path.

## Related guides

- [Roadblocks: how they work](/crm/guides/legal/roadblocks-how-they-work)
- [Anti-fraud and verification](/crm/guides/legal/anti-fraud-and-verification)
- [Onboarding — what the system asks, and what you confirm by hand](/crm/guides/legal/onboarding-checklist)
- [Client portal — onboarding steps](/crm/guides/legal/client-portal-onboarding-steps)
- [Roadblock: Title Checks](/crm/guides/legal/roadblock-title-checks)
- [Roadblock: Exchange Ready](/crm/guides/legal/roadblock-exchange-ready)
`,
    },
    {
        product: "legal",
        slug: "sail-homes-legal-interface",
        title: "Sail Homes / Sail Legal interface — who does what",
        one_line_description: "Where the Sail Homes (estate agency) handover meets the Sail Legal (conveyancing) workflow, with the stages and open policy questions.",
        category: "reference",
        order: 30,
        last_screenshotted_at: "2026-05-29",
        tags: ["sail-homes","sail-legal","interface","handover","probate-sale","policy"],
        content_hash: "5d0f4f87843f2d74846e4f529b82ab9225b19c007ff262ef77eef76139cea0c9",
        body_markdown: `## What it is

When Sail Homes (estate agency) and Sail Legal (conveyancing) both touch the same probate property, work passes between them at several points. This guide is the reference for what each side owns at each stage — and flags the stages where the firm hasn't yet locked down a single owner.

Most of this is **process knowledge the case engine doesn't drive**. The two sides still keep separate records, and most of the handover is people talking to people.

One part is now automated. You can **point a case at the Sail Homes listing for the same property**, and once you do, the offer details flow across on their own. That's the "Sail Homes listing" section below.

## When you'd use it

- A handover question between Sail Homes and Sail Legal needs answering — who orders searches, who emails the PR.
- You're new and trying to work out the boundary between the two teams.
- You're updating a policy and need to know which decisions are still open.

## Walkthrough

### The glossary you need to follow this

| Term | Meaning |
| --- | --- |
| **Executor / Personal Representative (PR)** | The person acting for the estate. |
| **Grant of Probate (GOP)** | Court authority to act when there is a Will. |
| **Letters of Administration (LOA)** | Court authority to act when there is no Will. |
| **TA6** | Standard seller property information form. |
| **TA10** | Fixtures, fittings and contents form. |
| **LPE1** | Leasehold Property Enquiries pack (from freeholder / managing agent). |
| **Memorandum of Sale (MoS)** | One-page summary issued after offer accepted. |
| **EPC** | Energy Performance Certificate. |
| **Office copies** | Land Registry Title Register and Title Plan. |
| **Sail Homes file** | The sales record in the Sail Homes CRM. |
| **Sail Legal matter** | The legal case record in the Sail Legal CRM. |

### The stages

**Stage 0 — Valuation-only lead.** Many cases start as a valuation for inheritance tax or to help the family decide what to do next. Outcomes vary: instruction to sell, instruction to transfer to a beneficiary, or a decision to auction. **Open policy**: who captures the valuation-only lead, who chases the outcome, what evidence converts a valuation into an instruction.

**Stage 1 — Instruction and creation of records.** Once instructed, a Sail Homes file is created in the Sail Homes CRM and a Sail Legal matter is created in the Sail Legal CRM. **Open policy**: which team creates each record. How they're joined up is now answered — see "Sail Homes listing" below.

**Stage 2 — File opening.** Sail Homes obtains: office copies, documents referred to in the office copies, EPC, GOP or LOA if available, requests for local / drainage / environmental searches, a note on what we're waiting for, and a note on who will be on the GOP or LOA.

**Search-cost rule** — typically passed on to the buyer. If the buyer's solicitor rejects our searches, the cost falls on the seller. This is written into the contract with the seller for whom Sail Legal does the conveyancing.

**Open policy**: stage 2 names file-opening "obtains searches" but stage 7 also says "Sail Homes orders searches once on market". Single owner and single timing needed.

**Stage 3 — Email to the executor / PR.** Confirms the legal file is open, confirms valuation photos have been reviewed, asks whether clearance help is needed, shares a link to TA10. **Contention**: probate sales are sold as seen and TA6 / TA10 are typically not provided — sharing a TA10 link conflicts with that position. **Open policy**: confirm the firm's stance on TA6 / TA10 for probate sales.

**Stage 4 — Email to the referrer.** Asks for the referrer's case reference, where completion funds should be paid, an update on GOP / LOA and who will be named, the account number for any charge, and a death certificate for any predeceased person. The Sail Legal matter is updated with what comes back. **Open policy**: which team sends this email.

**Stage 5 — Onboarding.** Reviewing title, obtaining a death certificate for any predeceased person, obtaining TA10 (conflict with Stage 3 note), verifying ID for executors, video call and title check, requesting LPE1 cost if leasehold, obtaining redemption statement if there's a charge.

**Stage 6 — Marketing preparation and going live.** Sail Homes checks whether an EPC has been obtained — if not, requests via Vibrant. Waits for the client to confirm marketing photos. Prepares listings for the portals, shares a marketing report. The property goes live **once probate has been applied for or granted**.

**Stage 7 — Searches.** Sail Homes orders searches for both the Sail Homes file and the Sail Legal matter once on the market. **Conflict** with stage 2 — resolve.

**Stage 8 — Offer accepted.** If the case has a Sail Homes listing on it, most of this happens by itself: the purchase price, the buyer's solicitor, the buyer's details and the estate agent block are filled in, and the Memorandum of Sale is attached to the case. Sail Homes uploads a quote invoice. A Slack message confirms offer acceptance and any exchange deadline.

Without a Sail Homes listing on the case, **none of that happens and someone has to key it in by hand**. That's the main reason to add the listing.

**Open policy**: who shares and uploads the MoS, what the "quote invoice" is for and to whom it's addressed, central method for routine updates vs Slack vs email.

**Stage 9 — Exchange deadlines.** Current targets:

| Scenario | Target to exchange | Counted from | Set automatically? |
| --- | --- | --- | --- |
| GOP on file at acceptance, confirmed cash buyer | 14 days | Offer acceptance | Yes |
| GOP on file at acceptance, any other buyer | 28 days | Offer acceptance | Yes |
| Offer accepted before the GOP issued | 14 days | The grant being marked received | Yes |
| Leasehold | 6 weeks | — | **No — not built** |

**Only a confirmed cash buyer gets the short 14-day window.** Mortgage, bridging loan, and a payment method nobody has recorded yet all default to 28 days — quoting a buyer more time than they need costs nothing, whereas quoting 14 days to someone who was never going to manage it puts us behind a deadline we set ourselves.

**The deadline writes itself onto the legal file** — it shows up in the case's Contract section as the first row of the exchange deadline's history, from the same rule as the buyer-solicitor email, so the two can't disagree. **It fills at offer acceptance** when the grant is already on file, and **otherwise the moment you mark the grant received**.

**Changing it takes a reason.** Use the *Add exchange deadline* button, pick the new date and say why it moved. The old date stays in the table, with who changed it and when, so anyone can see what the other side was told and why that changed. **Your own date is never overwritten** — and neither is a deletion, because once you have had a say the rule leaves the case alone. And **a blank deadline before the grant issues is correct**, not a gap.

**Open policy**: the 6-week leasehold target has never been in the buyer-solicitor email and is not implemented — a leasehold probate sale gets the ordinary 28/14-day deadline. Also unconfirmed: whether searches must be back for the 14-day cash route, and whether the 28-day mortgage count should run from grant issue date rather than offer acceptance.

**Stage 10 — Completion.** Once completion dates are agreed, Sail Homes books key handover and final meter readings. Final Sail Homes invoice is added to the Sail Legal file.

**Stage 11 — Communications.** **Open policy**: conveyancers are currently copied into a large volume of emails that Slack often duplicates. The agreed direction is one central method of communication, a weekly Tuesday team meeting (Keri's pipeline review is Monday), documented process, realistic timescales, and reduced duplication.

**Stage 12 — Outcomes other than a sale.** Some estates don't sell — title may be transferred to a beneficiary after the grant, or the property may go to auction. **Open policy**: ownership of transfer-to-beneficiary quotes and chasing, ownership of the auction route, where those documents are filed.

### Open policy questions at a glance

| Topic | Decision needed |
| --- | --- |
| Valuation-only leads | Who captures, who chases, what evidence promotes to instructed. |
| Record creation | Who creates the Sail Homes file vs Sail Legal matter. (How they're joined up is settled — add the Sail Homes listing to the case.) |
| PR email | Which team sends; whether TA6 / TA10 are ever provided on probate sales. |
| Referrer email | Which team sends. |
| Searches | Single owner, single timing, single master storage location. |
| Offer accepted | Who shares and uploads MoS; what the quote invoice is for. |
| Exchange deadlines | Confirm "from when" trigger and pre-conditions for each target. |
| Communications | Confirm central method, confirm Tuesday meeting, confirm task split. |
| Transfers and auctions | Quote ownership, chasing, and document storage. |

## Edge cases & gotchas

- **Probate is the gating event** for going live and for the 28/14-day deadlines. A deadline can't exist before the GOP issues — there is nothing to count from, which is why the field stays blank until then.
- **Slack duplicating email is noise**, not redundancy. Pick one channel per topic and stick to it.
- **The Sail Homes file and Sail Legal matter are two different records.** Adding a document to one does not put it on the other — check both before saying something "is on file". The one exception is the offer data described in stage 8, and only when the case has a Sail Homes listing on it.
- **A sale with no Sail Homes listing on the case reads as legal-only.** It won't show the sailboat, it won't appear on the Sail Homes report, and the seller gets sent to the wrong review page. If you know Sail Homes sold it, add the listing.
- **Search costs are pushed to the buyer** by default. If the buyer's solicitor rejects our searches, the seller pays — flag this to the seller immediately.

## Sail Homes listing (joining the two records)

**What it does.** On the case, open the property section. If it's a sale, you'll see **Add Sail Homes listing**. Search by address, pick the listing, confirm.

**What you get.** The case starts counting as a Sail Homes sale. It shows the sailboat, it appears on the Sail Homes report, and the seller gets the Sail Homes review page instead of the Sail Legal one. If the listing already has an accepted offer, the offer details land on the case straight away.

**It won't overwrite your work.** It only fills boxes that are still empty. Anything a case handler has typed stays exactly as it is.

**Two things to watch:**

- If the search says a listing is **already on another case**, that usually means you've picked the wrong one. Check first. The exception is a matter that transferred to a new case — the old case keeps the listing too, and that's normal.
- **Removing a listing is not an undo.** The case stops counting as a Sail Homes sale, but the buyer's details and the Memorandum of Sale stay on the case. If they don't belong there, take them off yourself.

**Only sale cases.** There's no way to add a listing to a purchase, transfer or remortgage, so the option won't appear.

**A completed or lost listing still works.** You can add it for the record. You just won't see the sailboat, because that only shows for listings that are live.

## Related guides

- [Sale process](/crm/guides/legal/sale-process)
- [Onboarding checklist](/crm/guides/legal/onboarding-checklist)
- [Service level guide — residential conveyancing](/crm/guides/legal/service-levels)
- [Glossary — acronyms](/crm/guides/legal/glossary-of-acronyms)
`,
    },
    {
        product: "legal",
        slug: "training-registered-and-unregistered-land",
        title: "Training — Registered and unregistered land (Session 3)",
        one_line_description: "Triggers for first registration, the 15-year rule, classes of title, the three parts of the register, and overriding interests.",
        category: "training-sessions",
        order: 30,
        last_screenshotted_at: "2026-05-29",
        tags: ["training","registered-land","unregistered-land","classes-of-title","overriding-interests","lra-2002"],
        content_hash: "72a8c8785fcf30318170b4d1510eb6a87ef8754eaf2cab1489ed56a86cf476c3",
        body_markdown: `## What it is

The detail of how the two systems actually work — what triggers first registration, the classes of title the Land Registry assigns, what each part of the register contains, and which third-party rights bind a buyer **without** appearing on the register.

This is **training material, not currently driven by the case engine**.

## When you'd use it

- You need to know which Land Registry events trigger first registration of unregistered land.
- A class of title (qualified, possessory, good leasehold) appears on a file and you want to know what it means.
- An overriding interest has come up and you need to remember which ones bind without registration.

## Walkthrough

### Why two systems still exist

Historically, conveyancing was based on **unregistered** title — proof of ownership by chain of deeds. The **Land Registration Act 1925** introduced the registered system; the **Land Registration Act 2002** is now the principal statute. Since **1 December 1990** registration has been **compulsory throughout England and Wales** on the events listed below.

In the unregistered system the owner proves title by showing the documents (an abstract or epitome going back at least 15 years). In the registered system, **registration itself is proof of ownership**.

### Events which trigger first registration

To convert unregistered to registered land, one of these must occur:

- A qualifying **conveyance of the freehold**.
- A qualifying **grant of a lease for a term of more than 7 years**.
- A qualifying **assignment of a lease with more than 7 years still to run**.
- A **first legal mortgage** of the freehold, or of a lease with more than 7 years to run on the date of completion.
- **Franchises** (e.g. right to hold a market) and **profits in gross** (e.g. right to fish).

A conveyance, lease or assignment is "qualifying" if made for valuable or other consideration, by way of gift, or in pursuance of a court order.

Only the **estate in land** is registrable.

### The unregistered "15-year rule"

When selling or mortgaging unregistered land, the owner must deduce title for at least **15 years** before the date of the contract or new mortgage. The chain must start with a **good root of title**.

### Classes of title

| Freehold | Leasehold |
| --- | --- |
| Absolute title | Absolute leasehold |
| Qualified title | Good leasehold |
| Possessory title | Qualified leasehold |
| | Possessory leasehold |

Titles can be **upgraded** if criteria are met — the most common upgrade is **good leasehold to absolute leasehold**, usually by producing evidence of the superior freehold title.

### The three parts of the register

| Part | What it contains |
| --- | --- |
| **Property Register** | Estate (freehold/leasehold), postal address, title plan reference, particulars of rights benefiting the land; for leases — parties, date, term, rent. |
| **Proprietorship Register** | Class of title, full name and address of the registered proprietor, date of registration, price paid, restrictions, pre-Dec-2003 cautions/inhibitions, indemnity covenants. |
| **Charges Register** | Easements binding the land, restrictive covenants, legal charges, equitable charges, notices. |

The register is wholly **electronic**. Proof of title is by viewing the records or obtaining an **official copy** (no more than 6 months old).

### What a registered estate is subject to on a disposition

- Registered charges or other interests protected by **notice** in the register.
- **Overriding interests**.
- **Minor interests**, but only if protected by entry on the register.

### Overriding interests — the ones that bind without being on the register

The LRA 2002 lists overriding interests; the practically important ones:

- **Legal easements and profits a prendre**.
- **Adverse possession rights** acquired or being acquired under the Limitation Acts (squatter's rights).
- **Rights of those in actual occupation**.
- **Leases not exceeding 7 years**.

Before exchange (or completion of a remortgage), make enquiries to identify potential overriding interests. Found one? Resolve it — e.g. if the property has an occupier other than the seller, get them to sign the contract to release any interest.

### Minor interests

Must be protected by an entry on the register to bind. Pre-LRA-2002 those entries were cautions, notices, inhibitions or restrictions. Since LRA 2002 only **Restrictions** or **Notices (Agreed or Unilateral)** can be used.

### Charges priority

- To **own** a registered title, the buyer must be **registered as proprietor** — date of registration matters, not date of completion.
- The same applies to charges — they take **priority from the date of registration**, not the date of creation.
- Most lenders require their charge to be registered as the **first charge** (per the UK Finance Handbook).
- Priority periods on searches must be **maintained**.

## Edge cases & gotchas

- **Good leasehold can usually be upgraded to absolute** by producing the freehold title — worth pursuing on cases where a future sale might be affected.
- **Overriding interests survive even without notice to a buyer** — actual occupation is the trap.
- **A pre-LRA-2002 caution or inhibition** may still be on the register. They can't be created any more but the existing ones still affect the title.
- **The 15-year rule starts with a good root** — a gift or assent is a less acceptable root than a conveyance on sale.

## Related guides

- [Training — Essential background law (Session 2)](/crm/guides/legal/training-essential-background-law)
- [Training — Investigation of title and searches (Session 5)](/crm/guides/legal/training-investigation-of-title)
- [Land Registry register entries — catalogue](/crm/guides/legal/title-register-entries)
`,
    },
    {
        product: "legal",
        slug: "internal-pif",
        title: "Internal PIF — the new in-portal Property Information Form",
        one_line_description: "Who sees the new PIF, the reduced version for personal representatives, how multi-sig and the Risk Report work, and when the JotForm fallback still kicks in.",
        category: "legal-practice",
        order: 35,
        last_screenshotted_at: "2026-05-29",
        tags: ["pif","property-information-form","baspi","sale","signatures","risk-report","chimnie","introducer-flag","limited-title","personal-representative","executor"],
        content_hash: "1b215fb13adbaf960dd6a8c7ca7cd4331fed04a49d05391b9de6f63c0e913ade",
        body_markdown: `## What it is

The **internal PIF** is Sail Legal's in-portal Property Information Form — the BASPI-compliant seller questionnaire that replaces the long-standing JotForm-based PIF step on consumer onboarding. Sellers fill it in directly in the portal, every required signer signs, and the system produces both a PDF for the buyer's solicitor and a **Risk Report** for the case handler.

The PIF is shared per-case — any seller on the case can edit it — but **every required signer must sign before the step counts as complete**. The set of structured columns under the form is queryable (so risk rules can cross-reference answers), and Chimnie public-records data is pulled automatically to prefill the form where it can.

For the next while the new PIF runs **behind a per-introducer feature flag**. When the flag is off, the seller sees the legacy JotForm step instead. Both paths are alive in the codebase deliberately.

## When you'd use it

- A seller has reached the property-information step on a case where the new PIF is enabled, and you need to help them through it.
- A seller is selling as a personal representative (an executor or administrator of an estate), and you want to understand the shorter form they get and the caveats on their PDF.
- The PIF has been signed by one seller but a second seller is still outstanding, and you're working out why the step isn't ticked.
- An edit went in after signing and the signatures have all been invalidated.
- The Risk Report flags an amber or red that needs handler attention before the file goes to the buyer's solicitor.
- A seller is on a case whose introducer doesn't yet have the PIF flag on, and the JotForm fallback path is what they'll see.

## Walkthrough

### When the new PIF appears

The new PIF is **the default for every sale opened on or after the go-live date (15 June 2026)**. Two things decide whether a case is on it:

- **Transaction type is sale** — purchases and remortgages never see a PIF at all.
- **The date / the per-case setting.** By default a sale opened on or after go-live uses the new PIF; one opened before it stays on the legacy TA forms. Staff can override this per case with the **Use BASPI** setting (see below).

**Why the go-live date matters.** We don't want *in-flight* cases — files already part-way through the old TA6/TA7/TA10 process — to suddenly jump onto the new PIF and lose that progress. So older cases keep the legacy forms by default. (This used to be switched on per introducer; that's been retired — it's now the date plus the per-case setting.)

**The Use BASPI setting.** In the **CASE MANAGEMENT** block on a case you'll find **Use BASPI** with three positions:

- **Default** — follow the date rule (the resting state for almost every case).
- **Yes** — force this case onto the new PIF / BASPI, even if it was opened before go-live. Use this to bring an older case onto the BASPI flow when it needs it.
- **No** — force this case back onto the legacy TA forms, even if it was opened after go-live.

The form a seller gets depends on the **title guarantee** on the property:

- **Full title** — the full PIF. The seller has lived in the property and can answer the whole questionnaire.
- **Limited title** — a **reduced PIF**. This is the personal-representative case: an executor or administrator selling an estate they never lived in. They get only the questions anyone can answer by looking at the property, and the rest is handled for them (see the next section).

### What the seller sees

The PIF is a **single scrollable page** in the client portal. No bespoke wizard, no nested navigation. The BASPI sections stack top-to-bottom, the current section is in focus, and completed sections collapse to a header with an *edit* link.

The page is split into two surfaces of input:

- **Structured questions** — yes/no toggles, dates, picklists. These map to typed columns the system queries against (knotweed, flooding, subsidence, EPC rating, and so on).
- **Free-text follow-ups** — the *"please give details"* textareas. Stored separately, not pulled into the risk rules.

Address, seller name(s), tenure, title number, lease term, ground rent, service charge and management company are **never asked** — they're already on the case and rendered straight from the existing data at PDF time. If the case is missing one of these, the PIF page asks for it once and writes it back to the case (two-way sync).

**Some questions offer "I don't know".** These are the ones whose answer is a fact about the property or the title rather than something only the seller could know — how the property is owned, the council tax band, the EPC rating, the lease figures, what's staying in the kitchen. Any seller can use it, not just a personal representative. It counts as a complete answer, so it never blocks them from signing, and it prints on the BASPI as "not known" rather than a "no" the seller never gave.

Questions that are genuinely the seller's own call — what they're leaving behind, whether anything might delay the sale — carry no "I don't know", and neither do the few facts anyone selling a property has to hand, like whether it's a house or a flat.

### Selling as a personal representative (limited title)

When the sale is under a **limited title guarantee**, the seller is a personal representative — an executor or administrator who never lived in the property and has no personal knowledge of its history. Before, these sellers saw no questionnaire at all. Now they get a **reduced PIF** that lets them produce a complete BASPI without being asked things they could never honestly answer.

Here's what changes for them:

- **They're only asked what they can see.** The form is trimmed to the handful of questions anyone can answer from a look round the property — property type, number of bedrooms, what's staying in the kitchen, whether it's occupied, the tenure, and how the owners hold it between them. If the property is empty, the tenancy questions aren't asked at all.
- **And they can say "I don't know" to most of those.** How the owners hold the property is the one executors get stuck on most — sole owner, joint tenants or tenants in common is rarely something a personal representative knows, and it used to stop them submitting. They can now answer "I don't know" and move on.
- **Where the title already answers it, we fill it in.** A Form A restriction on the title means tenants in common, so we pre-fill that rather than asking. If the title check hasn't run yet and the seller answers "I don't know", the PIF tab tells you what the title says once it has — so that one comes off your list without a call.
- **Everything else is recorded as "not known".** Each question they can't answer is marked *"not known — sold by personal representative"*. This is an honest "we don't know", never a positive "no".
- **The estate basics are filled in for them.** A few facts are always true for an estate sale, so the form sets them automatically: not in a chain, no dependent purchase, all owners agree, and completion as soon as possible.
- **Restrictions and charges come from our own title check.** Where our title-check data gives a clear answer, those questions are pre-answered. Where it doesn't, they're left for the seller, same as any other question.
- **They can still answer anything they happen to know.** The optional questions sit in expandable *"answer if you know"* sections. If the executor opens one and gives a real answer, that answer is kept and treated like any other — the normal risk rules apply to it.

A short banner at the top of their form reassures them that the shorter version is the correct, honest form for an estate sale — they're not skipping anything they're on the hook for.

### Chimnie auto-prepopulation

When the seller opens the PIF step, a server-side **Chimnie** lookup fires automatically against the existing case address. No consent screen, no address picker — the case already knows the property. Anything Chimnie can answer (council, conservation area status, flood risk indicators, EPC, planning history hints) is prefilled into the relevant PIF fields.

Sellers are free to override Chimnie's prefill — they're the source of truth, Chimnie is a starter. The prefill is cached per property identity so re-opening the form doesn't re-run the lookup. From the seller's point of view: most of the basics are already answered when they land on the page, and they're filling in the gaps and the *"please give details"* prose.

### The BASPI PDF and the personal-representative cover note

On submit the system produces the BASPI PDF for the buyer's solicitor. On a full-title sale it carries the seller's answers as given.

On a **limited-title sale** the PDF still prints **every question** — nothing is silently dropped. The questions the seller couldn't answer show as *"Not known — sold by personal representative"*, and the PDF opens with a short **cover note** that explains why: the seller is a personal representative with no personal knowledge of the property, so "not known" is an honest disclosure, not a representation that the answer is "no". The note tells the buyer's conveyancer and surveyor to check those matters themselves. This heads off the common worry that the BASPI looks incomplete.

### Multi-signer signing and the invalidation rule

The step is complete only when **every required signer has signed**. The set of required signers is the seller-side title set — clients on the case, joint owners, executors, personal representatives, LPAs, company executors. Buy-side roles, introducers, and conveyancers are never signers.

Each signature is captured with an eIDAS-style audit trail per signer — timestamp, the name they typed, IP address, user agent, screen size, timezone, language. One row per signer per signing event.

The **invalidation rule** is the part to internalise: *any edit to the PIF after a signature has been recorded invalidates that signature*. The system tracks the last-edited timestamp on the PIF row, and a signature only counts if it was recorded **at or after** the most recent edit. So if Sarah signs at 10am and her co-owner amends a knotweed answer at 11am, **Sarah needs to re-sign** before the step is complete again. There is no warning before the edit — the signers are dropped, and you'll see them re-prompted to sign on their next visit.

Practically: get the form to a settled state *before* the first signer signs. If late changes are unavoidable, accept that everyone signs again.

### The Risk Report

Every PIF save runs the risk rules and produces a **Risk Report** — red/amber/green flag projection of what the PIF actually says. The Risk Report shows on a new tab on the case page (CRM-side, not visible to the seller) and is rendered to a PDF stored as a case document on submit.

What the report flags:

- Domain concepts with structured rule logic — flooding history, subsidence claims, neighbour disputes, planning enforcement, leasehold breaches, etc.
- Each flag carries a typed rule code so future risk-catalogue joins are stable.

A **limited-title PIF naturally raises few flags** — a question recorded as "not known" can't trip a risk rule, so the report only reflects what the personal representative actually answered. That's expected, not a gap.

For v1 the Risk Report is **not wired into the roadblock engine**. It's a display + a PDF, not an automatic blocker. The case handler reads it and decides what to do; nothing in the engine fires off the back of an amber. That's a deliberate v1 posture; the catalogue work will change it later.

### What the CRM PIF tab shows you

The case PIF tab shows the seller's answers section by section, the Risk Report flags, and the signing state. On a **limited-title case** it also carries a banner telling you the sale is by a personal representative, and each question the seller couldn't answer is shown as **"Not known — PR"**. So at a glance you can tell a genuine "no" from an honest "we don't know".

**The tab also lists what the seller told us they didn't know.** This is your to-do list, and it's short — it only covers questions they were actually asked, not the ones we filled in for them. **Look these up in the title or the management pack.** Don't go back to the client; they've already told you they can't help.

### How the PIF satisfies the contract-ready roadblock

On a case running the new PIF flow, a completed BASPI is what the **contract-ready roadblock** looks for — a single *PIF received* item. It **replaces the old TA6 / TA7 / TA10 received items**, which no longer apply on PIF-flow cases. So you chase one thing (the BASPI), not three TA forms. On older cases that aren't on the PIF flow, the TA items behave exactly as before.

### The JotForm fallback

When the introducer's PIF feature flag is **off**, the seller sees the previous JotForm-based step instead. The CRM, the document storage, and the step gating are all unchanged on that path. The new PIF and the JotForm step **never appear together** on the same case.

What you do as staff on a JotForm-fallback case:

- Recognise it from the portal step's appearance — it links out to a JotForm rather than rendering an in-page form.
- The completed JotForm comes back as a PDF in the case documents, same as before.
- There's no Risk Report on that path — risk-flagging only fires on the new PIF.
- If the introducer turns its flag on partway through a case's life, the case stays on whatever path it started on; we don't swap forms mid-flight.

### What you do per case

- **Read the Risk Report once it's submitted.** It's the system's first pass at "what to chase the seller on" before sending the file to the buyer's solicitor.
- **Treat a re-prompt to sign as a real signal**, not a glitch. The system is telling you the form was edited after signing — read the diff before sending the new signing request out.
- **Don't push a seller into the PIF before they have the answers** — half-finished sections are a worse outcome than a slightly delayed start. Better one settled pass than three rounds of re-signing.
- **On a personal-representative case, expect the short form and the "not known" answers** — that's the correct output, and the PDF cover note explains it to the buyer's side. There's nothing to chase the seller for on the questions we answered on their behalf.
- **Work the "didn't know" list on the PIF tab.** Those are the ones the seller was asked and couldn't answer, so they're real gaps — but the answers come from the title or the management pack, not from another call with the client.
- **On a JotForm-fallback case, the legacy playbook applies.** No Risk Report, no Chimnie prefill, no signing audit — same workflow we had before the new PIF existed.

## How it works under the hood

- The PIF is **shared per-case**, not per-user. Any seller on the case can edit it; the system records the last-edited timestamp, not per-user drafts.
- **Signature validity is anchored to the last-edited timestamp.** A signature row is "live" only when its signed-at is at or after the form's last-edited time. Any edit invalidates every prior signature.
- **Limited title drives the reduced form.** The property's title guarantee decides which version a seller gets; everything the personal representative can't answer is stored as a "not known" marker rather than a real answer, which is why those questions raise no risk flags and print with a caveat on the PDF.
- **A seller's own "I don't know" is stored exactly the same way** as one we filled in for them. That's deliberate — it's the same fact either way, so it prints the same caveat and raises no flag. The only difference is on the staff tab, which separates out the ones the seller was actually asked so you know what's worth looking up.
- **Chimnie lookups are cached** by property identity. Re-opening the PIF on the same property doesn't re-call (and re-bill) Chimnie. A fresh lookup only happens on a new property. An address Chimnie can't match is cached too, so an unmatched property isn't re-bought on every visit — that one is re-checked after 30 days, because a property it can't match today may be matchable later.
- **There's a daily ceiling on Chimnie lookups.** It sits well above normal demand and exists to stop a runaway loop billing for a whole day. If it's ever reached, prefill quietly stops rather than failing the page, and the team is alerted.
- **The Risk Report is recomputed on every PIF save** — not on submit. So an amber that the seller resolves mid-form genuinely disappears from the report; the report is always current to what's actually in the PIF.
- **The risk rule codes are a stable enum**, not free-text strings. This is laying the rails for a future cross-cutting risks catalogue spanning title check, PIF, searches and others — but the catalogue itself isn't here yet.

## Edge cases & gotchas

- **"The step is still showing as incomplete after both sellers signed."** Check the last-edited timestamp on the PIF against the signed-at on each signer. An edit after the last signature drops the signatures and the step re-opens.
- **A signer's name on the audit trail doesn't match the case record.** The signer types their own name on signing — typos happen. The audit row is the source of truth for *what they typed*; the case record is the source of truth for *who they are*. Cross-check before relying on the signature in correspondence.
- **Chimnie prefills the wrong council / wrong flood band.** Sellers are the source of truth. Have them override the field and explain in the free-text answer; the PDF goes out with their answer, not Chimnie's.
- **"The PIF opened with nothing prefilled."** Three innocent explanations before you treat it as a fault: Chimnie has no record of that address, the property is new to us and the lookup hasn't run yet, or the daily lookup ceiling has been reached. None of them blocks the seller — they fill the form as normal, and the prefill is a head start, not a dependency.
- **A limited-title BASPI looks "empty" to the buyer's side.** That's by design — the personal representative can't answer from knowledge, the cover note explains it, and "not known" is the honest disclosure. Point the other side to the cover note rather than re-opening the form.
- **The Risk Report is information, not an engine signal.** Don't expect a red flag to spawn a roadblock automatically — for v1 it doesn't. You read it and decide what to chase.
- **An old case isn't on the new PIF, but you need it to be.** Set **Use BASPI** to **Yes** in the CASE MANAGEMENT block. That forces it onto the new PIF / BASPI flow regardless of when it was opened. (Leaving it on **Default** means it follows the date rule, so a pre-go-live case stays on the legacy TA forms.)
- **"An older case is showing a PIF / a BASPI job and I didn't expect it."** Check two things: when the case was opened, and the **Use BASPI** setting. If it was opened before go-live and Use BASPI is **Default**, it should be on the legacy forms — flag it. If it was opened on or after go-live, or someone set Use BASPI to **Yes**, the new PIF (and the BASPI contract-ready job in place of TA6/7/10) is correct — even on a limited-title estate sale. To push it back to the legacy forms, set **Use BASPI** to **No**.
- **The PIF doesn't replace the TR1.** Property *information* is BASPI; the transfer of title is the TR1 and is a separate step. Don't conflate the two.

## Related guides

- [Sale process](/crm/guides/legal/sale-process)
- [Client portal — onboarding steps](/crm/guides/legal/client-portal-onboarding-steps)
- [Risk management](/crm/guides/legal/risk-management)
- [Roadblocks: how they work](/crm/guides/legal/roadblocks-how-they-work)
`,
    },
    {
        product: "legal",
        slug: "client-portal-onboarding-steps",
        title: "Client portal — onboarding steps and what staff can see",
        one_line_description: "Each step the client sees in the portal, what the system records, and how staff help when the client gets stuck.",
        category: "case-view",
        order: 40,
        last_screenshotted_at: "2026-05-29",
        tags: ["client-portal","onboarding","id-check","video-call","payment","toe","pif","dual-rep"],
        content_hash: "96ad05c8e139e9414fa9be35c490e9b03ca247a65608abf71b720c15783ec901",
        body_markdown: `## What it is

The portal is the surface the client uses to do everything we need from them during onboarding — ID, terms of engagement, payments, tax questions, property information, bank details. The crucial thing to know is that the portal **shows the client one step at a time**: they don't see future steps until they finish the current one. Staff can see the full list and can see each step's current status, but they can't reach into the portal and pre-fill a step on the client's behalf.

That one-step-at-a-time design is load-bearing for how you help a stuck client. You can't tell them "do steps 2, 3, and 5" — the client only sees step 2. The right help is "finish what's in front of you and the next one will appear", and "here's why this step is hard, let's solve that".

The second thing to know is that **a step belongs to the person looking at it**. It asks *is there something for you to do?* — not *is this finished for the case?* So on a joint case one client can be several steps ahead of the other, and a step showing as done for one of them tells you nothing about the other.

## When you'd use it

- A client is stuck on a step and you need to talk them through it.
- A client says they "can't see" a step you know is required — usually they're not on it yet.
- A new onboarding-team joiner wants to understand the order and what each step does.
- You're trying to work out whether onboarding is genuinely stalled or just paced.

## Walkthrough

### The order of steps the portal reveals

Steps are revealed in a fixed sequence. Whichever is the earliest *active* step is the one the client sees in front of them. Steps further down only become visible when the earlier ones are *completed* or *deferred*. Steps that don't apply to this case (no tax questions on a sale, no mortgage questions on a cash purchase) are skipped entirely.

| # | Step | Asks the client to | System records on completion |
| --- | --- | --- | --- |
| 1 | **Agree to the Terms of Engagement** | Read and agree to Sail Legal's standard TOE in-portal. Only shown to people who are a party to the engagement themselves — clients, executors, personal representatives, and LPAs. Authorised third parties, beneficiaries, registered third parties, and giftors skip this step (they're not bound by the TOE in their own right). | Timestamp against the client's record on the case. |
| 2 | **Consent to Dual Representation** | (Dual-rep cases only.) Acknowledge we represent both sides and consent to proceed. | Per-client consent flag on the case. |
| 3 | **Who are the clients?** | Confirm the list of people we have on the case is right. | The confirmed client list. |
| 4 | **Related transaction** | (Sale or purchase only.) Tell us about the linked sale / purchase if there is one. | Linked-transaction details on the case. |
| 5 | **Verify your identity** | Run through the standard ID check flow with their document and a selfie. Clients and personal representatives see the other parties' checks here too, so they can help someone through theirs. The step still only waits on their own check. An authorised third party has no check of their own, so their step waits on the people they help. | A new ID check row with the pass / fail status — feeds Client Verification. |
| 6 | **Book your video call with us** | Pick a slot for the standard verification call. (Re-appears as *missed appointment* if they no-show.) | A booked call row tied to this client. |
| 7 | **Make a payment** | Pay the upfront amount on the quote. | Payment status against the case ledger. |
| 8 | **Tell us how you'll be paying** | (Purchases.) Cash, mortgage, gift, mix. | Purchase payment method on the case. |
| 9 | **Tell us about your remortgage** | (Remortgages.) The remortgage shape and lender. | Remortgage details on the case. |
| 10 | **Tell us your mortgage account number** | (Cases with charges on title.) Confirm the account number for each charge. | Account numbers against each charge. |
| 11 | **Answer tax questions** | (Purchases.) The SDLT supplementary questions. | SDLT answers on the case. |
| 12 | **Tell us your joint ownership wishes** | (Purchases and transfers with two or more clients.) Joint tenants vs tenants in common preference. **Not shown when the case's *joint ownership held as* is set to *on trust*** — a deed already fixes the shares, so there is nothing for the client to elect. | Joint-ownership preference against each client. |
| 13 | **Create a Declaration of Trust** | (Joint owners electing tenants in common.) Trigger the referral to our DoT partner. Shown to clients and executors; giftors are not asked. **Not shown when the case is set to *on trust*** — those clients already have a declaration of trust. | Referral status on the case. |
| 14 | **Tell us which bank account to send money to** | (Sales and remortgages.) Submit one or more bank accounts for verification. On a case with a payable introducer, the client can instead pick the introducer here, so funds route back to the introducer without the client entering any details. | Bank account rows + the case-level lock — see [Bank account verification](/crm/guides/legal/bank-account-verification). |
| 15 | **Complete the Property Information Forms** | (Sales of full-title-guarantee property, on legacy introducers.) Fill in the external property questionnaires. | Completion flags against the form bundle. |
| 16 | **Property Information Form** | (Sales of full-title-guarantee property, on the new internal PIF.) Walk through the BASPI questionnaire in-portal. | The internal PIF row + signature(s). |
| 17 | **Confirm you're proceeding without legal advice** | (Transfer-of-equity departing owners only.) Read and agree the **ILA waiver** — that we act for them for Land Registry registration only, with no legal advice, and they proceed without independent advice. Shown only to the departing owner (a registered third party with ownership status *departing owner*) once staff set their *ILA or disclaimer* choice to **Signed disclaimer**, on transfer cases instructed after the feature's go-live date. | Evidential fields (agreed-at, signed name, browser) against the client's record **and** a frozen waiver PDF on the *ILA Waiver* document tile, which resolves the *Pre-Completion Ready* item. |
| 18 | **Energy Performance Certificate** | (Sales where no valid EPC could be found or the existing one has expired.) Choose who'll arrange a new EPC — **Sail Legal sorts it**, or **I'll arrange it myself**. Shown only to sellers, only when the case's EPC status is *not found* or *expired*. | The *who arranges* choice on the case's EPC record. If they pick **us**, a staff *Arrange new EPC* roadblock item appears for the case handler to chase. |

Two flow notes that catch people out:

- **Someone else's ID check never holds a client up.** A client who has passed their own check moves straight on to payment and everything after it, even if their co-seller hasn't started. Their own view of the co-seller's status goes quiet until they've finished their own steps, at which point it comes back (see below).
- **Deferred steps** sit below the current active step. A step is deferred when the client started it but couldn't finish (typically *Related transaction* on a purchase where they don't yet know their sale address). The portal nudges them to come back, and they can click in to finish without finishing the strict-ordered next step first.
- **The other client's unfinished check comes back at the end.** Once someone has done everything we need from them, that card is pulled to the top of the finished view, right under the congratulations card. A line next to it tells them we're chasing the others and they needn't do anything.
- **All steps complete** drops the client into a *what happens next* card — a short, plain rundown of how their matter now proceeds. The card is tailored to the case: a probate sale reads differently to a divorce transfer to a purchase, and the wording flexes to the reader's role (client, executor, giftor, receiving beneficiary). It also explains that the onboarding team is still finishing prep behind the scenes and names the case handler who takes over once the case is ready. From this point, reminder emails go quiet because there's nothing left to reveal.
- **The completion email** goes out **once**, the first time that person finishes their own steps — same rundown as the card, so the client has it in their inbox. It's sent from the case handler and signed by the onboarding team. It is deliberately once-only: if onboarding reopens (staff add or reset a step) and the client completes again, the email does **not** resend. Note this is per-person, so anyone who finishes early — a giftor, a beneficiary, or simply the quicker of two joint sellers — gets theirs while the other is still going.

### What staff can see for each step

Staff see the full list and a status pill against each step — *active*, *completed*, *deferred*, or *not required for this case*. Staff can also see the *output* of each step (ID check images and pass/fail, payment receipts, signed TOE timestamp, etc.) on the case page. What staff **cannot** do is:

- Pre-fill a step on the client's behalf.
- Push a future step to the client out of order.
- See exactly what the client sees right now (other than knowing which step is *active*).

### Helping a stuck client

When a client says "I can't find where to do X":

1. Check which step is currently *active* on their case. That's the only step they can see.
2. If X is *that* step, talk them through it. Frame the help around the friction in that step — ID document type, payment method, bank login for the 2FA validation, etc.
3. If X is a *future* step, explain that the portal reveals one thing at a time and they'll get to it. Don't promise a workaround that doesn't exist.
4. If X is someone else's ID check, point them at *Completed steps*. They can open it from there and help that person through it. Their own progress isn't waiting on it.
5. If X is a *deferred* step, point them at the deferred-steps card at the bottom of the portal.

When a step is genuinely blocking and the client cannot do it (e.g. an ID document they don't have, a payment they cannot fund right now), the right move is usually a phone call rather than emails about the portal — the friction is rarely the portal itself.

### The reminder email rhythm

The system sends per-step reminder emails so clients don't drift. The emails are deliberately scoped to **the one step the client can see right now** — not a list of everything outstanding — because enumerating future steps would surface things the portal hasn't revealed yet. If a reminder email says *one thing*, that's by design.

One rhythm worth knowing. When a client has nothing left to do themselves but could help someone else — usually a co-seller's ID check — they get a *could you nudge them* email instead. It goes less often than a normal reminder. It also pauses while they still have steps of their own, and starts again once they're done. That way we're not asking someone for a favour and chasing them at the same time.

For onboarding emails generally, see also [Managing the case inbox](/crm/guides/legal/managing-case-inbox).

## How it works under the hood

- The portal computes the **chronological step list** for each client on each visit; what they see is the earliest *active* step. The list is filtered by case shape (transaction type, mortgage involvement, dual rep status, etc.) so the client never sees a step that doesn't apply to their case.
- The **status of a step** is derived from the underlying case data — payment received, TOE timestamp set, ID check row exists with a final outcome, etc. Staff can't set a step's status by hand; you'd change the underlying field that the step reads. The data it reads is always that person's own, which is why one client's step can be done while another's is not.
- Reminder emails compute the **full** list of outstanding things from the same data, but pick the single one whose order matches the client's current portal step. That's why "we sent the reminder but the email only mentions one of three things" is correct behaviour, not a bug.
- The portal does have a **deferred** state, distinct from *not yet active*. Deferred means the client started the step and pressed *come back later*. Future steps that are simply gated still appear as *not started*.
- The **completion email** fires the moment the portal first sees that person's steps are all done, and is stamped once against their case membership so it can never send twice — reopening onboarding and re-completing does nothing. The *what happens next* wording (card and email) is chosen from the case's transaction type and reason for sale, plus that person's role, so you don't set it by hand.

## Edge cases & gotchas

- **Two clients on one case see different step lists.** Each client's view is computed from their own role on the case (joint owner vs executor vs LPA vs authorised third party), their own ID-check state, their own dual-rep consent, etc. Don't assume both clients are seeing the same step — and don't assume a helper (authorised third party) ever sees the same list as the person they're helping.
- **"Verify your identity" can show as done while another client's check is outstanding.** That's correct, not a stale status. The step reports on that client's own check. The case still can't exchange until everyone has passed, so nothing about the compliance position changes.
- **A client says "I've already done this".** Their ID check or video call may have expired (12 months), or the case shape may have changed and re-fired the step. Check the underlying record.
- **The video call step only appears if the client has a real call to book or has already booked / missed one.** A case with no verification call configured will not show the step at all — staff sometimes assume it's broken.
- **Property information forms vs PIF.** Two different steps based on the introducer's feature flag. Legacy introducers see the JotForm-based PIF; new introducers see the internal BASPI walkthrough. Both can appear on a sale of full-title-guarantee property.
- **All steps done = quiet portal.** Once the congratulations card shows, staff sometimes worry "is the client still engaged?" — they are; there's just nothing left for them to do. If something later needs them (e.g. completion-day questions), a new step will appear and a new reminder will fire.
- **The portal is not a chat surface.** Telling the client "reply in the portal" is wrong — the portal is one-way forms, not messaging. Use email for back-and-forth.

## Related guides

- [Onboarding — what the system asks, and what you confirm by hand](/crm/guides/legal/onboarding-checklist)
- [Anti-fraud and verification](/crm/guides/legal/anti-fraud-and-verification)
- [Bank account verification](/crm/guides/legal/bank-account-verification)
- [Risk management](/crm/guides/legal/risk-management)
- [Managing the case inbox](/crm/guides/legal/managing-case-inbox)
- [Roadblocks: how they work](/crm/guides/legal/roadblocks-how-they-work)
`,
    },
    {
        product: "legal",
        slug: "confidential-email-handling",
        title: "Confidential email handling — the parallel queue and who can see it",
        one_line_description: "What makes an email confidential, why it lives in its own tab, and the cross-over rules with the other inbox surfaces.",
        category: "communications",
        order: 40,
        last_screenshotted_at: "2026-05-29",
        tags: ["confidential","inbox","triage","private-inbox","hr","complaints","attachments"],
        content_hash: "f928a4a4eb02e20ff1f0f7188a78c2adbc498067ce77b5784ae64dedfb6fa94f",
        body_markdown: `## What it is

**Confidential** is a parallel slice of the legal inbox — a separate tab in the Private Inbox / Triage surface, fed by emails that arrived through a designated confidential routing path. Confidential emails do not show in the standard Untriaged / Unresolved / Resolved tabs at all. They live on their own.

Two things make an email confidential:

- It came in via the **confidential routing address** (the \`@confidential.saillegal\` variant of a staff member's email). Anything sent to or from that address pattern is flagged confidential at ingest.
- The staff member is on the email's recipient list. Confidential emails are only visible to the staff members they were actually addressed to — not to the wider firm.

Use the confidential queue for material that shouldn't show up in the general triage flow: HR matters, internal complaints, anti-money-laundering escalations, anything where wider visibility would be a problem.

## When you'd use it

- An external party has sent something sensitive to your confidential address and you need to action it without that thread showing in the general inbox.
- You're writing internally about a complaint or HR matter and need the thread held outside the standard surfaces.
- You're trying to find a confidential email that came in last week — search the Confidential tab, not the regular Inbox tabs.
- A confidential email arrived with an attachment and you need to file it onto a case (which requires extra steps).

## Walkthrough

### The routing — what makes an email confidential

Every staff Sail Legal email address has a **confidential twin** that lives at the \`@confidential.saillegal\` domain instead of the regular one. So if Edwina's address is \`edwina@saillegal.com\`, her confidential address is the matching \`@confidential.saillegal\` variant. Mail to the confidential address gets the **confidential** flag set at the moment we crawl it from Gmail — based purely on the address pattern in the From / To / CC / BCC headers.

When you send a confidential email out, the system rewrites the recipient's address into its confidential twin if both addresses belong to Sail Legal staff. The thread stays confidential through every reply — it doesn't accidentally leak back into the regular inbox on the next round.

### Who can see a confidential email

Visibility is **recipient-bound**, not role-bound. A confidential email is visible to the staff members whose **user IDs are recorded on the email's recipient list**. Other staff members — even other authorisers — do not see it.

There's no separate "access list" to manage, no admin grant. If you weren't on the To / CC / BCC of a confidential email, you won't see it in your Confidential tab. The privacy boundary is the recipient list itself.

The roles allowed to *use* the Confidential surface at all are:

- **Sail Legal non-authoriser staff**
- **Sail Legal authoriser staff**
- **Admin**

Anyone else hitting the Confidential view gets blocked. But being one of those roles doesn't give you visibility into other people's confidential mail — only your own.

### How it appears in the UI

The **Private Inbox** surface has four tabs visible in the menu bar:

| Tab | What it holds |
| --- | --- |
| **Untriaged** | Standard untriaged inbox — confidential emails never land here. |
| **Unresolved** | Your queue of standard unresolved emails. Confidential excluded. |
| **Resolved** | Your resolved standard emails. Confidential excluded. |
| **Confidential** | The parallel queue. Every confidential email you're on the recipient list for. |

The Confidential tab carries its own unread count on the menu bar. The four tabs all live in the same Private Inbox surface but operate on different filtered views of the email table.

### What you can do on a confidential email

The action surface in the Confidential view is **deliberately narrower** than the regular inbox view:

| Action | Available in Confidential? |
| --- | --- |
| **Reply** | Yes — opens compose with confidential routing preserved. |
| **Forward** | Yes — same compose surface. |
| **Resolve / archive / mark irrelevant** | No — the lifecycle flags used by triage don't apply. |
| **Assign to a case** | No — confidential emails are intentionally not assignable. |
| **File attachments to the case document store** | Not directly — the paperclip requires a case-link, which confidential mail doesn't carry. |

The reasoning is straightforward: case-assignment would surface the email on a Case Inbox tab that anyone with access to the case can read. That defeats the privacy boundary. Confidential mail is held outside the case-bound surfaces deliberately.

### Filing a confidential attachment onto a case

Sometimes a confidential email carries an attachment that genuinely needs to land on a case (e.g. a complaint summary that should be in the case file). The flow is **manual**, on purpose:

- Download the attachment from the confidential email.
- Open the relevant case directly.
- Upload the attachment via the case's documents tab as a fresh upload, choosing the appropriate document type.

The original email stays in the Confidential queue. The attachment is now on the case, with the lineage held only in whatever note you record alongside it. There's no automated "file confidential attachment to case" path — by design.

### Composing a confidential email outbound

To send confidentially:

- Compose using the confidential address in the recipient field — when staff are on either side of the conversation, the system will route via the confidential domain automatically.
- The compose form recognises a confidential recipient and adjusts the routing accordingly.

There's no "make this confidential" toggle in the compose UI — confidentiality is determined by who you're writing to, not by a separate flag.

### What you do per confidential email

- **Read it in the Confidential tab.** Don't paste content into a case-bound surface; you'd be unwinding the privacy posture.
- **Reply in-thread to keep the confidential routing alive.** Starting a fresh thread from a personal address breaks the chain.
- **If a confidential thread accumulates material that genuinely belongs on a case**, copy what's needed onto the case manually and explain in a note. Don't try to merge.
- **Don't assume your colleagues can see what you're seeing** — they almost certainly can't. If a confidential matter needs another pair of eyes, CC them deliberately into a new confidential email; they won't see yours otherwise.

## How it works under the hood

- Confidentiality is **a flag on the email row**, set at ingest from the recipient address pattern. It is not a separate table or separate inbox infrastructure.
- The standard inbox queries explicitly filter out confidential rows. The Confidential queue queries explicitly require them. The two views never overlap.
- **Visibility is enforced at query time** by \`user_ids\` membership on the email row — not by role alone. A non-recipient staff member querying for the email will not find it.
- **The lifecycle flags (resolved-at, case-status, assigned-to-user-id) exist on confidential rows too**, but the triage actions that write to them are not wired up. So a confidential email never "resolves" in the lifecycle-flag sense.
- **Case-assignment is structurally possible** but operationally not surfaced. The Confidential viewer doesn't expose the assign action, and case-linked confidential rows would create access surprises for case readers.

## Edge cases & gotchas

- **"I can see Edwina's confidential email in my Confidential tab."** Only if you were on the To / CC / BCC. The Confidential tab is filtered by recipient — not "all confidential" — even for authorisers and admins.
- **The Confidential count looks wrong.** It's the unread count for the emails the current user is on the recipient list of. Other people's confidential mail isn't included in your count.
- **A reply leaks back into the standard inbox.** Usually because the reply came from a personal address (not the confidential twin), and the original confidential pattern wasn't preserved. The outbound auto-routing only fires when the system can recognise both ends.
- **The paperclip is greyed out on a confidential email.** That's deliberate — confidential mail isn't case-assigned, so there's no case to file an attachment against. Manual upload to the case is the correct path.
- **"I want to forward this to a teammate to action."** Fine — forward via the confidential address so the new thread stays inside the privacy boundary. Forwarding to a regular address would break the chain.
- **Search across all inboxes will not surface confidential emails to non-recipients.** The recipient-bound visibility is enforced on search too. If a colleague says *"I can't find a confidential email you mentioned"*, the right answer is to forward it to them confidentially rather than coaching them on search.
- **The Confidential queue is not where you'd hold a tense client thread that you'd rather other staff didn't see.** Client correspondence belongs on the case Inbox; the Confidential queue is for material that shouldn't be visible to the firm at all. Don't blur the two.

## Related guides

- [Triage inbox and email filing](/crm/guides/legal/triage-and-email-filing)
- [Managing the case inbox — reply, forward, file attachments](/crm/guides/legal/managing-case-inbox)
- [Complaints process](/crm/guides/legal/complaints-process)
- [Risk management](/crm/guides/legal/risk-management)
`,
    },
    {
        product: "legal",
        slug: "roadblock-quote",
        title: "Roadblock: Quote",
        one_line_description: "How to clear each item under the Quote roadblock — quote created, quote sent.",
        category: "roadblocks",
        order: 40,
        last_screenshotted_at: "2026-05-29",
        tags: ["roadblocks","quote","onboarding","troubleshooting"],
        content_hash: "1efb1af070341aa4f8504f961464be83ed6c7e7fe233c9935f89964d262cedec",
        body_markdown: `## What it is

Quote fires on any case that has been upgraded from a property-report enquiry into a full matter and hasn't yet completed. It tracks the basic quoting hygiene: there's a quote on the case, and the quote has been sent to the client.

It is **not authoriser-gated** but it does **block exchange** — a case without a sent quote can't exchange.

## When you'd use this guide

- A case is showing the Quote roadblock and you can't tell what's missing.
- A quote was sent but the engine still flags one of the items.
- You're triaging early-stage cases and want to know what counts as "quoted".

## The checklist

### Quote created

A quote has been added to the case with the same transaction type as the case itself.

- **Where to go**: **Details** tab → **Quote** block → *Create quote*. Pick the transaction type that matches the case (sale / purchase / transfer / remortgage).
- **Common stuck-states**:
  - **A quote was added but with the wrong transaction type** — the engine reads the quote's type, not the case's. Replace the quote with one of the correct type.
  - **The case is property-report-only** — Quote shouldn't fire at all on those. If it has, check whether the case has been upgraded by mistake.

Some charges depend on the specific property and no rate card can predict them — chancel insurance, a coal search, a salt search. **Add Item on the Quote block puts one on the current quote.** Every add or remove **writes a new quote with its own PDF and keeps the old one**, so a figure a client has already seen never changes underneath them.

- **Only items an admin has set up as *only added by hand* appear in the picker.** If the one you need isn't listed, it needs a rate card first.
- **The controls disappear once the case is instructed** — from then on the ledger is the record of what the client owes, so add the charge there instead.

### Quote sent

The quote has been sent to the client and marked as sent in the CRM. **Emailing a quote to a client from the case now stamps the sent date automatically** and clears this item — whether you send a new email, a reply, or a forward.

- **Where to go**: usually nothing to do — **attach the quote when you email the client** from the case and the *quote sent to client* date populates on send. To set it by hand, **Details** tab → **Quote** block → *quote sent to client* → **Yes**.
- **Common stuck-states**:
  - **The quote only went to non-clients** (introducer, broker, the other side) — the auto-stamp only fires when a client on the case is a recipient in the To, CC, or BCC fields. Email the client, or set the date by hand.
  - **The quote was sent from outside the CRM entirely** — the engine doesn't see that. Set *quote sent to client* to Yes by hand.
  - **A revised quote was re-sent** — the date reflects the *first* send and doesn't move on later ones. That's deliberate; it's the compliance-relevant date. Flip the toggle by hand if you genuinely need to reset it.
  - **The item went green and then re-opened on its own** — **adding or removing an item on the quote clears the sent date**, because the client is holding a PDF whose total no longer matches. Send them the new quote and the item clears again.

## Edge cases & gotchas

- **Quote stops creating on completed cases.** A case that completed without a properly-recorded quote won't show the roadblock retrospectively — that's deliberate, to avoid completed cases sitting permanently red on legacy data.
- **Quote blocks exchange.** Both items must be green before the case can go through Exchange Ready / Pre-Completion Ready.
- **Quote is upstream of Chase Instruction.** Get Quote green first; Chase Instruction only fires once the quote has been sent.

## Related guides

- [Roadblocks: how they work](/crm/guides/legal/roadblocks-how-they-work)
- [Onboarding — what the system asks, and what you confirm by hand](/crm/guides/legal/onboarding-checklist)
- [Roadblock: Chase Instruction](/crm/guides/legal/roadblock-chase-instruction)
- [Roadblock: Client Verification](/crm/guides/legal/roadblock-client-verification)
`,
    },
    {
        product: "legal",
        slug: "risk-report",
        title: "The Risk Report — every risk grade added in the period",
        one_line_description: "The MI report listing each risk grade set during the selected months — the grade, who set it, when, and the case details.",
        category: "compliance-and-policies",
        order: 40,
        last_screenshotted_at: "2026-07-22",
        tags: ["risk-report","mi-reports","risk-grade","mlro","compliance","reports"],
        content_hash: "06e90dd5e940e41cdffe81b9bd68bfdbb5107b2ce91977e1ba294a51c619c155",
        body_markdown: `## What it is

The **Risk Report** is one of the management-information (MI) reports on the Sail Legal **Reports**
page. It's a **log of risk-grading activity**: every risk grade that was **added during the period
you pick**, with the grade, the explanation, who set it, when, and the case it belongs to.

It reads the **grading events**, not the current caseload. So a case only appears if someone
recorded a risk grade for it in the selected months — and if a case was **graded twice** in the
period (an initial grade then a re-grade), it shows up as **two rows**. That's deliberate: the
re-grade is the story the report is there to tell.

It is a *reporting view over* the risk grades — **it doesn't set or change any grade**. How grades
are decided, re-graded, and signed off is the subject of a different guide: [Risk management —
grading a case and re-grading as it changes](/crm/guides/legal/risk-management). This report is
where you read the grading activity across the book.

> Two other things share the word "risk" and are **not this report**: the case-level **risk grade**
> itself (set on the case — see the risk-management guide), and the **PIF Risk Report** (the
> red/amber flags a seller's Property Information Form throws — see [Internal
> PIF](/crm/guides/legal/internal-pif)). This guide is the **MI report** only.

## When you'd use it

- You're doing a **monthly compliance or MLRO review** and want every grade set in the month, with
  who set it.
- You want to see the **re-grades** — cases whose risk picture changed during the period.
- You want a **CSV** of the period's grading activity for a management pack or an audit.

## Walkthrough

### Running the report

1. Open the **Reports** page in the Sail Legal CRM.
2. Set the **period** at the top — the month (or month range) you want. The Risk Report reads
   *grades added within that window*, so the period defines exactly which grading events appear.
3. Find **Risk Report** in the alphabetical list and open it. It loads as an **on-screen table**.
4. To pull the data out, use the **download** button to export the same rows as **CSV**.
5. **← Back to reports** returns you to the list to pick another.

### What a row is

**One row per risk grade added** in the selected period. A single case can produce several rows
(one per time it was graded or re-graded in the window); a case that wasn't graded at all in the
period doesn't appear, even if it's open. Rows are ordered **oldest grade first**. Cases flagged as
created-in-error or as property reports are left out; otherwise the case's state doesn't matter — a
grade set on a since-closed or completed case still counts as activity in the period.

### Reading the columns

![The Risk Report table on the reports page — one row per risk grade added in the period, showing the case reference, the grade, who set it and when, and the case details](/static/guides/legal/risk-report/risk-report-table.png)

| Column | What it tells you |
| --- | --- |
| **Case ref** | The case's Klyant matter reference — the first column, so you can tie the grade back to the file. |
| **Id** | The case's internal id (kept for joins/exports). |
| **Risk grade** | The grade that was set — *red*, *amber*, or *green*. |
| **Risk grade explanation** | The *why* the grader wrote at the time — the justification for that grade. |
| **Risk added at** | When the grade was recorded. This is what the period filters on. |
| **Graded by** | The staff member who set the grade. |
| **Status** | Where the case is now in its lifecycle. |
| **Instructed** | When the case was instructed. |
| **Transaction type** | Sale, purchase, remortgage, transfer, and so on. |
| **Case handler** | The handler the case is assigned to. |
| **Purchase payment method** | How a purchase is being funded (where relevant). |
| **Closed before completion / Completed** | The terminal dates, if the case has reached one. |
| **Property address(es)** | The property (or properties) on the case. |
| **Client(s)** | The named clients on the case. |
| **Risk added at was estimated** | *True* when the *risk added at* timestamp was backfilled rather than captured live — older, historical grades can carry an estimated date. |

## How it works under the hood

- **The report is per grading event, not per case.** It reads the risk-assessment rows, so a case
  re-graded three times in the month appears three times. There's no collapse to a single latest
  grade — seeing the re-grades is the point.
- **The period filters on when the grade was set**, not on when the case was open. A long-running
  case with no new grade this month simply won't appear; a case graded on the 3rd will, even if it
  completed on the 10th.
- **Who set it is on every row.** *Graded by* is the accountability column — the review can see who
  made each call.
- **The grades come from the case, not the report.** Nothing you do on this page changes a grade;
  fix a wrong grade on the case itself (which records a new grading event), then re-run the report.

## Edge cases & gotchas

- **A case can appear more than once — that's correct.** Two rows for one case means it was graded
  twice in the window. Don't dedupe it in your head; the second row is a re-grade you want to see.
- **No grade this month, no row.** The report won't show an open case that hasn't been graded in
  the period. To find open cases that have *never* been graded, that's a caseload question for the
  case list, not this report.
- **The explanation is only as good as what was written.** A one-word explanation behind an *amber*
  tells a reviewer nothing. The report surfaces the text the grader wrote — thin explanations show
  up here.
- **"Risk added at was estimated" flags backfilled dates.** Where it reads *true*, treat the
  timestamp as approximate — it was reconstructed for a historical grade, not captured when the
  grade was set.
- **Pick the right period.** Widen or narrow the From/To months and the set of grading events
  changes with it.
- **Don't confuse it with the Complaints Report or the PIF Risk Report.** Different reports,
  different sources. This one is grading activity across cases.

## Related guides

- [Risk management — grading a case and re-grading as it changes](/crm/guides/legal/risk-management)
- [Internal PIF](/crm/guides/legal/internal-pif)
- [Recording a complaint on the satisfaction tab](/crm/guides/legal/recording-a-complaint)
`,
    },
    {
        product: "legal",
        slug: "training-taking-instructions",
        title: "Training — Taking instructions and procedure to exchange (Session 4)",
        one_line_description: "What to ask the client at the outset, conflicts and arms-length issues, surveys and mortgage offers, and the steps that lead up to exchange.",
        category: "training-sessions",
        order: 40,
        last_screenshotted_at: "2026-05-29",
        tags: ["training","instructions","conflicts","surveys","mortgage-offer","file-care"],
        content_hash: "45b2bd32ce4727b9e38088b3d5693926e1a97b4800a84740764397979ee66f7a",
        body_markdown: `## What it is

How to take instructions at the start of a transaction — what to ask, what to evidence, and how to set up the file so nothing gets missed. Plus the conduct issues (conflicts, arms-length transactions, money laundering) and the structured route to exchange.

This is **training material, not currently driven by the case engine**, but it overlaps heavily with the **Client Verification** roadblock and the onboarding flow.

## When you'd use it

- A new joiner is learning the firm's intake routine.
- You're handling a transaction with unusual parties (related buyers, private mortgagee) and want a reminder on the rules.
- You're reviewing a file before exchange and want a checklist against the standard steps.

## Walkthrough

### What "taking instructions" is actually for

The point isn't to fill in a form. It's to get enough information that **the whole transaction** can run cleanly — not just the first two or three steps. Done properly it stops issues being missed, lets you give accurate advice from day one, and sets the file up to move at pace.

Where instructions arrive by phone or via an introducer, **confirm in writing** to the client.

### Universal intake — every case

- Has the firm acted for this client before? (Affects client-care advice.)
- Full names, address, contact numbers, email.
- **Identity evidence** for AML and mortgage fraud — questionnaire completed.
- Property address and tenure.
- Estate agents involved — particulars; holding deposit paid?; deadline for exchange?
- Other side's solicitor / conveyancer.
- Agreed price.
- Other agreed terms — chattels, rights of way, access for repairs.
- Fixtures and fittings list — apportionment between property and chattels.
- Related sale or purchase?
- Who occupies the property? (Third-party rights, vacant possession.)
- Unusual location — canal, railway, etc. (Search implications.)
- Terms of business letter; summary of costs; flag likely additional costs (leasehold, management).
- Deposit on exchange — 5%, 10%, or none?
- Specific client concerns — extension plans, restrictions in deeds.

### Acting for the seller — extras

- Did we act on the original purchase? Pull old files from storage.
- EPC in place?
- Where are the title deeds? If mortgaged, get full lender name, address, account number to request deeds. Check for any pre-registration documents the client holds.
- Second or subsequent mortgagees? Get details and redemption figures.
- **Sale proceeds destination** — if not the client, escalate to **MLRO**.
- Protocol forms completed; planning consents, building reg consents, double-glazing warranties, other guarantees supplied.
- Multiple properties? **Capital Gains Tax** implications?

### Acting for the buyer — extras

- Intended use of the property (planning, restrictions).
- EPC received?
- Funding source — cash or mortgage? Trace **all** sources of funds. Refer to MLRO if concerned.
- Deposit arrangements — 100% mortgage, gifted deposit, deposit guarantee scheme.
- Survey advice (see below).
- Co-ownership advice if more than one buyer.
- Currently renting? **Remind not to give notice until exchange.**
- SDLT advice — payable, additional dwellings, first-time buyer relief, etc.

### Acting for buyer and lender

- Comply with the **CLC Code of Conduct** on conflicts. The Code permits acting for both a buyer and an institutional lender.
- **Do not act for both a buyer and a private mortgagee** — usually relatives lending money. The private mortgagee should be separately represented.
- Comply with the **UK Finance Handbook** (especially Part 2).

### Good faith, arms length, conflicts

| Term | Meaning |
| --- | --- |
| **Good faith** | Done honestly, even if negligently. |
| **At arm's length** | Parties far enough apart to avoid undue influence — full value given. Lender language for "a proper sale at correct price, separately represented, usual procedures". |
| **Not at arm's length** | E.g. two companies in the same group; related parties. May still be possible to act if no conflict. |
| **Conflict of interest** | Governed by **CLC Code of Conduct Outcomes 3.1, 3.3, 3.5** — client's best interests, informed decisions, awareness of limitations. If a conflict arises mid-transaction, **stop acting for all parties involved**. |

Acting for both parties when not at arms length (related parties, trustees/beneficiaries) is permissible if you have **written confirmation from all parties** and a **different casehandler** represents each side.

### Surveys

The buyer carries the **caveat emptor** risk. Three options:

| Survey | What it does |
| --- | --- |
| **Valuation report** | Primarily for the lender. Limited use to the buyer. |
| **HomeBuyer report** | Compromise between valuation and full survey. RICS criteria. |
| **Full structural survey** | Comprehensive. Expensive. Best for older properties. |

Advise the client on options and limitations; leave the choice to them. The surveyor owes the client a duty of care — negligent survey, client can sue.

Always **read the report** and act on issues. Write to the client on any legal issues the surveyor flags.

### Mortgage offers

- Comply with the **UK Finance Handbook** Part 2 entry for the lender.
- Lender is your client too — keep them informed of relevant factors.
- The independent financial adviser is responsible for suitability of the mortgage to the borrower — but you still need to read the offer and check conditions.

### Conduct and care of files (the firm's rules)

- Follow firm instructions on file running, retention, storage.
- All correspondence scanned to the case management system.
- Original signed documents (mortgage deed, transfer, lease) stored with post-completion.
- Detailed file notes of every call (client, broker, other side, third parties). Confirm decisions in writing.
- Emails imported to the CMS; outgoing emails sent from the CMS off the correct file.
- Be on guard for fraud and money laundering — discuss any concern with MLRO.
- Diarise date-sensitive issues; check files at least **2 days before exchange or completion**.
- Refer **conflict, ID, or title issues to a supervisor BEFORE acting**.
- Always take **authority to exchange** from all clients.

### Steps leading to exchange

**Acting for the seller:**

1. Prepare draft contract (in duplicate) → buyer's conveyancer with completed protocol forms (with copy guarantees, consents) and proof of title (official copies or epitome).
2. Obtain redemption statements from existing lenders.
3. Answer enquiries.
4. Once contract approved, send engrossment to client for signature.
5. Obtain estate agent's account.
6. Draft completion statement.
7. Discuss completion date with client.
8. Hold signed contract pending exchange.

**Acting for the buyer (three Sail Legal stages):**

- **Stage 1** — raise enquiries/requisitions, prepare draft transfer, report to client on contract and title, run pre-contract searches, approve draft contract.
- **Stage 2** — report on local search; identify other searches.
- **Stage 3** — on mortgage offer, check survey, prepare mortgage deed and final statement, report to client on mortgage offer and conditions.

Once client acknowledges Stage 3, discuss completion date. File is ready for exchange.

## Edge cases & gotchas

- **Don't act for both a buyer and a private mortgagee** — even a parent lending to a child. Separately represented.
- **Conflict appearing mid-transaction** — stop acting for everyone affected, not just one side.
- **Notice on rented accommodation** — clients give notice too early and end up homeless on a delayed exchange. Repeat the warning.
- **Deeds held by a dematerialised lender** — the lender doesn't hold any paper. Check whether the client has pre-registration documents themselves.

## Related guides

- [Training — Investigation of title and searches (Session 5)](/crm/guides/legal/training-investigation-of-title)
- [Onboarding checklist](/crm/guides/legal/onboarding-checklist)
- [Sale process](/crm/guides/legal/sale-process)
- [Purchase process](/crm/guides/legal/purchase-process)
`,
    },
    {
        product: "legal",
        slug: "transfer-of-equity-process",
        title: "Transfer of equity — the process",
        one_line_description: "What a transfer of equity is, the common scenarios, and the step-by-step legal flow.",
        category: "legal-practice",
        order: 40,
        last_screenshotted_at: "2026-05-29",
        tags: ["transfer-of-equity","toe","divorce","joint-tenants","tenants-in-common","sdlt"],
        content_hash: "69cbde9063c8900193a9407d43854b82bd9a008398028afcd840308dabe8efa6",
        body_markdown: `## What it is

A **Transfer of Equity** (ToE) is the legal process of changing who's on the title of a property by adding or removing an owner. Triggers: divorce, marriage, gifting, court orders, estate planning. The property doesn't change hands at full value — only the named proprietors change.

**System view** — a transfer doesn't go through *Exchange Ready* (no exchange of contracts). It goes through **Pre-Completion Ready** directly, with items for client authority, redemption statements, indemnities, and the SDLT routing. *Client Verification* still fires for all parties (including any donor / second proprietor). Don't expect to see *Contract Ready Pack* or *Exchange Ready* on a pure transfer file — if you do, the case type is wrong on the case record.

## When you'd use it

- You're running a divorce / separation buyout and need to know the moving parts.
- You're adding a spouse or partner to a title.
- You're handling a gift of equity within a family.
- You need the SDLT decision tree for these transactions.

## Walkthrough

### Common scenarios

**Joint ownership → sole ownership.** Most common in divorce. One owner buys out the other. The departing owner comes off the Proprietorship Register. If there's a mortgage, the remaining owner refinances solo and the departing owner is released from liability.

**Sole ownership → joint ownership.** Adding a spouse or partner. The new co-owner is added to the title, a new joint mortgage is usually issued. Ownership can be equal or unequal — unequal needs a **Declaration of Trust**.

**Changing the ownership shape.**
- **Joint tenants** — equal shares; on death of one, the property auto-transfers to the survivor (right of survivorship).
- **Tenants in common** — shares can be unequal; each owner can pass their share by Will.
- **On trust** — a declaration of trust or trust deed fixes the shares, so the owners have no election to make. Use this for transfers into a lifetime trust and for any estate-planning structure where a deed decides the split. Setting the case to **on trust** stops the portal asking the clients to choose and stops the Declaration of Trust referral, because they already have one. The register never shows the shares either way: it records the trustees plus a **Form A restriction**, and the percentages live only in the deed.
- Severing joint tenancy → file an **SEV form** with Land Registry.

### Mortgage lender considerations

- The lender's consent is required when keeping an existing mortgage.
- The lender can refuse, or require an affordability assessment on the remaining borrower.
- For a court-ordered transfer (divorce settlement), the lender must still agree before we proceed.
- Where the mortgage stays put, **many lenders don't want a new deed at all**. They keep the charge and release the departing borrower themselves. Nationwide calls it a transfer of title. Santander and Virgin Money do the same. The offer letter says so.
- When that happens, **answer No to "Mortgage offer lender requires a new mortgage deed"** on the Details tab, Mortgage offer block. **That stops the deed being asked for** anywhere on the case.

### Management company consent

If the property is leasehold or on a managed estate, landlord/management-company **consent to transfer** is often needed too. Link the company on the **Details** tab → **Management companies** block (reuse a previous one — its consent-to-transfer instructions and fees are recorded there), then mark **consent obtained** (or *not required* if the lease/title doesn't gate the transfer on it). That clears the pre-completion checklist item.

### Step 1 — Title check and documentation

- Obtain office copies. Review the Title Register.
- Identity verification on all parties. Issue initial legal documents to clients.

### Step 2 — Draft the transfer (TR1)

- All details correct before execution.
- Mortgage in play → liaise with the lender on conditions.

### Step 3 — Send transfer documents

- Send a copy of the TR1 to any co-owners and their solicitors for signing.
- Offer independent legal advice where appropriate.

> **Departing owner — independent legal advice (ILA) waiver.** Where we e-sign the transfer, HM Land Registry requires every party to be represented by a conveyancer, so we now act for the **departing owner** too — but for **Land Registry registration only, with no legal advice**. Set the departing owner's *ILA or disclaimer* choice to **Signed disclaimer** on the case party, and they're shown an in-portal **ILA waiver** step. When they agree it, a frozen PDF is saved to the **ILA Waiver** document tile (this resolves the matching *Pre-Completion Ready* checklist item), and the advised client sees an *"Acting on a transfer of equity"* section in their Terms of Engagement. The waiver only shows on transfer cases instructed after the feature's go-live date; older cases keep the offline-signed upload.

### Step 4 — Mortgage offer + compliance

- Review the mortgage offer. Comply with the **CML Handbook**.
- Address discrepancies in names or addresses promptly.

### Step 5 — SDLT

SDLT applies when the transaction involves a mortgage or financial consideration **above the threshold**. Exemptions:

- Court-ordered transfers (e.g. divorce).
- Gifts without a mortgage.
- Transfers from an estate.

### Step 6 — Land Registry registration

- Submit the TR1, SDLT1 (if applicable), and supporting documents.
- Get the updated Title documents and forward to the mortgage lender.

## Key financial considerations

### SDLT calculation

- Based on **half the outstanding mortgage plus any additional payments**.
- Transactions under **£40,000** are exempt from SDLT.
- Court-ordered transfers or estate settlements → no SDLT.

### Indemnity insurance for solvency

- Required when a person transfers their share and may face insolvency within five years.
- Protects lenders from losses if the transfer is later challenged in bankruptcy proceedings.

## Final steps and compliance

- Confirm the new owner has proper title documents and is mortgage-compliant.
- Register any restrictions with Land Registry if applicable.
- Advise clients on any additional responsibilities — independent legal advice is the usual one to flag.

## Edge cases & gotchas

- **A court order doesn't automatically bind the lender.** Even with a court order in a divorce settlement, the lender's consent is still required to release the departing owner. If consent is refused, the order may need to be revisited.
- **Tenants in common with unequal shares needs a Declaration of Trust.** Don't assume "50:50" without documenting otherwise.
- **The departing-owner solvency window** for indemnity insurance is five years from the transfer date. Don't skip it where the departing owner's finances are wobbly.
- **SDLT exemption needs evidence** — copy of the court order, evidence of the estate, etc. Don't claim the exemption without it.
- **"No new deed" is an answer, not a workaround.** If the lender keeps its charge, record it on the mortgage offer block. Don't file another document under the mortgage deed tile to clear a checklist item. It can end up at Land Registry as a charge we aren't registering.

## Related guides

- [Onboarding checklist](/crm/guides/legal/onboarding-checklist) — matrimonial transfer specifics on intake.
- [Sale process](/crm/guides/legal/sale-process)
- [Land Registry register entries — catalogue](/crm/guides/legal/title-register-entries)
`,
    },
    {
        product: "legal",
        slug: "assent-transfers",
        title: "Assent transfers — moving property out of an estate",
        one_line_description: "The legal process for transferring property from a deceased owner to a beneficiary.",
        category: "legal-practice",
        order: 50,
        last_screenshotted_at: "2026-05-29",
        tags: ["assent","probate","deceased-owner","executor","personal-representative","as1"],
        content_hash: "deba628b0453ad72b8defe5a7e1da2040408a99d6d000c18558a1007e02f184f",
        body_markdown: `## What it is

An **assent transfer** is the legal process of moving property ownership from a deceased estate to a beneficiary. The Personal Representative (intestacy) or Executor (Will) carries it out, following the Will or the rules of intestacy.

## When you'd use it

- A client has inherited a property and wants to put it in their name.
- Executors need to transfer a property to one or more beneficiaries before sale (or as the end-state itself).
- You're handling a deceased estate and the property isn't being sold.

## Walkthrough

### Two scenarios

**Under a Will.** The Executor named in the Will oversees the transfer to named beneficiaries. **Grant of Probate** is required first.

**Under intestacy.** No Will. The Personal Representative (Administrator) manages the estate and the transfer. **Letters of Administration** is the equivalent court authority.

### Step 1 — Grant of Representation

- Decide which is needed — Grant of Probate (Will) or Letters of Administration (intestacy).
- Settle all estate debts, including inheritance tax, **before transfer**.

### Step 2 — Title check and documentation

- Obtain office copies. Review the Title Register.
- Check for restrictions or encumbrances that could delay the transfer.

### Step 3 — Draft the assent (AS1)

- Complete the **AS1 form** to transfer legal ownership from the deceased's estate to the beneficiary.
- If the property is **unregistered**, an **FR1 form** is required for first registration.

### Step 4 — Send transfer documents

- Send the AS1 to the beneficiary for signing.
- If there are multiple beneficiaries, clarify how they're holding the property — **joint tenants** or **tenants in common**.

### Step 5 — SDLT

- Generally **not payable** on an assent.
- **Becomes payable** if the beneficiary takes on an outstanding mortgage, or if there's additional financial consideration.
- If payable, calculation is based on the mortgage amount / consideration.

### Step 6 — Registration with Land Registry

- Submit AS1, supporting documents (Grant of Probate / Letters of Administration), and an **ID1 form** where a party can't verify their identity through a law firm.
- Any restrictions must be removed or updated as necessary. A **Form A restriction** can only be removed once the trust of the registered owners has ended — done by submitting **RX3** plus a **statement of truth (ST3)** signed by the Personal Representative or Executor.
- Obtain updated Title documents for the new owner.

## Key financial considerations

### Inheritance tax and debts

- **IHT must be settled** before the transfer can take place. We **cannot advise** on IHT — refer the client to their probate lawyers or practitioners. Also advise them to take Capital Gains Tax advice.
- Outstanding estate debts must be cleared before distribution. **Registered charges must be repaid** before completing the assent.

### Indemnity insurance for potential claims

- Where there's uncertainty about the Will or intestacy, indemnity insurance may be advisable.
- Protects against potential claims from other parties disputing the estate.

## Final compliance checks

- All legal requirements met.
- Beneficiary has proper documentation.
- Beneficiary advised to seek independent legal or financial advice where appropriate.

## Edge cases & gotchas

- **Form A restriction on the title** is the single most common blocker. Plan the **RX3 + ST3** route early — you can't lift it until the trust has ended.
- **Unregistered property** needs first registration via **FR1**, on top of the assent. Don't miss this step on older country titles.
- **IHT is not our advice to give.** Clients sometimes ask — point them at their probate lawyer or HMRC. We can flag that it needs settling before transfer, but no further.
- **SDLT lurks** when there's a mortgage being taken on by the beneficiary, or any payment moving in the transaction. The "no SDLT on an assent" rule of thumb has these exceptions.

## Related guides

- [Transfer of equity — the process](/crm/guides/legal/transfer-of-equity-process)
- [Executors and LPAs](/crm/guides/legal/executors-and-lpas)
- [Dealing with property and Will trusts](/crm/guides/legal/property-and-will-trusts)
- [Land Registry register entries — catalogue](/crm/guides/legal/title-register-entries)
`,
    },
    {
        product: "legal",
        slug: "roadblock-title-checks",
        title: "Roadblock: Title Checks",
        one_line_description: "How to clear the Title Checks roadblock once a title has been added to the case.",
        category: "roadblocks",
        order: 50,
        last_screenshotted_at: "2026-05-29",
        tags: ["roadblocks","title-checks","troubleshooting"],
        content_hash: "f2012d5738d335f9a96901f6f5efea3caa03f521337a6d9d4a07db943f52147d",
        body_markdown: `## What it is

Title Checks fires as soon as a title has been added to an instructed case. It's the engine's way of saying "we've got a title (or several) — none of them has been fully checked yet". The single item under it stays open until every title's *title check status* is something other than *pending*.

## When you'd use this guide

- A new title was just added and you want to know what's blocking it from going green.
- A case has multiple titles and only some have been checked — the roadblock is still red.
- You believe a title is fully checked but the roadblock won't clear.

## The checklist

### All title checks completed

Every title on the case has had all of its checks answered.

- **Where to go**: open the case and set a block to **Title Checks**. Every title on the case is listed. Open one and work down the checks.
- **What moves it**: answering the checks. Each title card shows a counter — **0/48** means none of its 48 checks are answered yet. Fill them all in and that title is done. There's no status to set by hand and no button to press. The counter is the whole story.
- **The register is right there.** The title check opens with the register, the plan and any deeds we hold, plus the documents the register refers to. You don't need to dig through the document store to answer the checks.
- **Common stuck-states**:
  - **One title was added later and still has checks outstanding** — the item is all-or-nothing across every title on the case. Check each one, not just the lead title.
  - **One check left.** The counter has to reach the total. A single unanswered check on a single title keeps the roadblock open.
  - **The number of checks is different on each title.** A leasehold title asks more than a freehold one, and an unregistered title asks a different set again. Don't expect the totals to match.

## Edge cases & gotchas

- **The roadblock only creates after a title has been added.** Pre-instruction cases or instructed cases with no title yet won't show it.
- **Adding a new title to an already-checked case** reopens the roadblock. That's intended — every title needs checking individually.
- **Title Checks blocks exchange.** Until every title is fully answered the case cannot exchange.
- **There's no pass or fail.** The roadblock only asks whether someone has answered every check. If the answers show a problem, that's a different conversation — your risk grade and the exchange-ready review pick it up. It won't hold this roadblock open.
- **Unknown is not an answer.** Almost every check offers *Yes*, *No* and *Unknown*, and leaving one on *Unknown* does not move the counter. The same goes for a blank date or an empty description. If the counter is stuck and you're sure you've been through the title, look for the ones still sitting on *Unknown*.
- **The downstream contract-pack items** (e.g. *All documents referred to in titles and deeds obtained*) live on the *Contract Ready Pack* roadblock, not here. This roadblock only asks the engine to confirm someone has looked at every title.

## Related guides

- [Roadblocks: how they work](/crm/guides/legal/roadblocks-how-they-work)
- [Title register entries](/crm/guides/legal/title-register-entries)
- [Training — investigation of title](/crm/guides/legal/training-investigation-of-title)
- [Training — registered and unregistered land](/crm/guides/legal/training-registered-and-unregistered-land)
- [Roadblock: Contract Ready Pack](/crm/guides/legal/roadblock-contract-ready)
- [Roadblock: Exchange Ready](/crm/guides/legal/roadblock-exchange-ready)
`,
    },
    {
        product: "legal",
        slug: "will-trusts-tenants-in-common-death",
        title: "Tenants in common — one owner has died",
        one_line_description: "The two routes for updating the title when a tenant in common dies, with the trust implications and Land Registry forms for each.",
        category: "legal-practice",
        order: 50,
        last_screenshotted_at: "2026-05-29",
        tags: ["tenants-in-common","will-trust","deceased-proprietor","form-a","dpj","ap1","restrictions"],
        content_hash: "60588e7e40e4c093a04ff8aec7895ebcc0c8ec7bf516338838bb42c8abcedd48",
        body_markdown: `## What it is

A registered proprietor has died and the property was held as **tenants in common**. The surviving owner stays on title in some form, but the deceased's beneficial share now sits in a Will Trust. You have two legitimate routes to reflect this at Land Registry — and choosing the wrong one for the family's circumstances costs them on a later sale.

This is **process knowledge, not currently driven by the case engine**. Roadblocks won't fire route-specific items for you; this guide is for the conveyancer reasoning out which route to take and what to evidence.

## When you'd use it

- A surviving co-owner instructs you to update the title after their partner's death.
- The title contains a **Form A restriction** (a tenants-in-common giveaway).
- The Will leaves the deceased's share into a life interest or property trust.

## Walkthrough

### Step 1 — Confirm the title position

Before doing anything else, confirm:

| Check | What you're looking for |
| --- | --- |
| Tenure of co-ownership | Joint tenants or tenants in common? |
| Form A restriction | Already on the register? Usually confirms tenants in common. |
| The Will | Who inherits the deceased's share, are there trustees named, is there a life interest, are beneficiaries adults with capacity. |

If it's a **beneficial joint tenancy**, the survivorship rule applies and the surviving owner can sell solo — different process, see [Sale process](/crm/guides/legal/sale-process).

### The two available routes

**Route 1 — Add trustees/beneficiaries to the title.** Surviving owner remains on title; additional trustees or beneficiaries join as joint proprietors to reflect the trust.

| Aspect | Route 1 |
| --- | --- |
| Best when | Sale or remortgage likely in future; trustees want direct legal control; life interest trust in place. |
| Advantage | At least two proprietors remain on title — overreaching is straightforward on a future sale. |
| Disadvantage | More parties needed to sign in future. |
| Land Registry forms | **AP1**, **DJP** (remove deceased proprietor), **TR1** or **Assent** as appropriate, restriction applications if needed. |

**Route 2 — Sole registered proprietor + protect the trust by restriction only.** Surviving owner stays on title alone; the trust interest is protected by restrictions instead of by joint ownership.

| Aspect | Route 2 |
| --- | --- |
| Best when | Surviving owner intends to remain in the property for life; no plan to sell or remortgage; family wants a simpler title. |
| Advantage | Title stays simple; surviving owner remains sole registered proprietor. |
| Disadvantage | On a later sale, the sole proprietor cannot give a clean receipt — a **second trustee must be appointed** at that point to overreach. |
| Restrictions | Keep the existing Form A. Optionally add a **Form B** or bespoke restriction requiring trustee consent or trust-compliance certification. |

### Route 2 — what you MUST get on the file

Where the surviving proprietor wants to remain sole owner on the register, you can't proceed without:

- **Written confirmation from all trustees and all beneficiaries** with an interest under the Will Trust. The confirmation should set out that they understand the surviving owner stays sole registered proprietor, that the deceased's name will be removed, and that their beneficial interest will be protected by restriction only.
- **A detailed attendance note** capturing the advice given, the risks explained, the alternative discussed, and the client's instructions.

Skip either and the firm carries the risk if the family argues about the share years later.

### Required documents (both routes)

- Official copy death certificate.
- Official copy of the Will.
- AML-compliant identification.
- Office copy entries and title plan.

### What clients need to understand

Whichever route, advise in writing that:

- **The trust still exists.** Removing the deceased from the register does not remove the beneficiaries' interest.
- **Restrictions only protect, they don't transfer.** Restrictions do not move ownership to the beneficiaries or trustees.
- **A future sale will involve the trustees.** They may need to consent, sign, or be appointed.
- **After the surviving owner's death**, executors and trustees may need to work together before any sale can proceed.

## Edge cases & gotchas

- **Form A restriction on the title is the strongest signal** it was tenants in common — even if the deed wording is silent.
- **Route 2 looks simpler today and costlier later.** If the family is on the fence about selling, Route 1 keeps options open.
- **Escalate to a supervisor** where beneficiaries disagree, minors are involved, the Will is unclear, there is no surviving trustee, capacity is in doubt, a sale or remortgage is imminent, or the trust wording is complex.
- **Don't accept an oral assurance** that beneficiaries are happy — get the written consents on file.

## Related guides

- [Sale process](/crm/guides/legal/sale-process)
- [Transfer of equity — the process](/crm/guides/legal/transfer-of-equity-process)
- [Land Registry register entries — catalogue](/crm/guides/legal/title-register-entries)
- [Executors and LPAs](/crm/guides/legal/executors-and-lpas)
`,
    },
    {
        product: "legal",
        slug: "training-investigation-of-title",
        title: "Training — Investigation of title and searches (Session 5)",
        one_line_description: "How to deduce and investigate title (registered and unregistered), particular problem areas, and the searches you must do before exchange.",
        category: "training-sessions",
        order: 50,
        last_screenshotted_at: "2026-05-29",
        tags: ["training","title-investigation","searches","requisitions","indemnity","caveat-emptor"],
        content_hash: "889ace16f863403ab20e468a88727cb425568a6f024137944df49e6ce79d1b2b",
        body_markdown: `## What it is

How a buyer's solicitor satisfies themselves that the seller actually owns what they say they own, the particular pitfalls (PRs, attorneys, charities, companies, trustees in bankruptcy), and which searches to run before exchange.

This is **training material, not currently driven by the case engine**, though the **Title Checks** and **Contract Ready Pack** roadblocks operationalise much of it.

## When you'd use it

- A new joiner is learning what title investigation actually involves.
- You're looking at a tricky title — sale by an attorney, charity, PR — and want a refresher.
- A search has come back with an adverse entry and you need to think about whether indemnity insurance is the right call.

## Walkthrough

### What "deducing" and "investigating" mean

- **Deduction of title** — the seller demonstrates to the buyer that they own the land and can convey it.
- **Investigation of title** — the buyer confirms that the seller does own it and can convey it.

Burden is on the seller to provide evidence of **good title** — legal title vested in the seller, or in someone they can compel to convey or transfer.

### Unregistered land — deduction by abstract or epitome

The seller supplies an **abstract of title** or **epitome of title** showing the chain of ownership.

Insist on title from at least **15 years before the contract date**, starting with a **good root**:

| Good root criterion | What it means |
| --- | --- |
| Deals with the whole legal and equitable interest | Not a partial dealing. |
| Adequate, identifiable description | You can tell what's being sold. |
| Casts no doubt on the title | E.g. not dependent on an earlier missing document. |
| At least 15 years old at the contract date | |

Best roots: **conveyance on sale** or **legal mortgage**. Less acceptable: assents, deeds of gift — push for an earlier conveyance.

When investigating, ask:

- Root older than 15 years?
- Chain complete from root to current seller, with no missing links?
- Any name changes? — get evidence.
- Any deaths? — death certificates or Grants from the Probate Registry.
- Documents properly executed, witnessed, stamped? Description accurate?
- Should it have been registered earlier? (Omitted registration risk.)
- Searches cover all previous and current owners?
- Mortgages discharged? Vacating receipts endorsed? If not — undertaking from seller's solicitor to discharge.

### Registered land — the register is conclusive

Pre-2002 the seller had to supply office copies plus filed plan plus copies of noted documents. Under LRA 2002 the buyer doesn't need to insist — but a prudent buyer (and the 5th Edition Standard Conditions of Sale) does.

Official copies must be **no more than 6 months old**.

No need to seek a good root — the register provides clear, unequivocal evidence of ownership. If a proprietor has died, a copy death certificate is still required.

### Purpose

Make sure the seller's title is **good and marketable** and free from encumbrances that would adversely affect use and enjoyment.

### New builds

The developer deduces title covering the whole estate. Usually registered already; the builder provides official copies and a title plan (sometimes a Form 102 certifying the plot is part of the seller's title). Watch for:

- Land Registry approval of the **estate layout plan** (Form CI) — avoids boundary disputes, eases pre-completion searches.
- Adequate **easements in the transfer deed** to the buyer.

### Sales of part

| Check | Why |
| --- | --- |
| Lender consent if mortgaged | The lender's security is being split. |
| Accurate plan | Land Registry will reject a sloppy one. |
| Easements and restrictive covenants | Granted to / reserved from the part being sold. |

### Particular problem areas

**Mortgagee selling.** Confirm power of sale **exists**, has **arisen**, and has become **exercisable**.

**Personal Representative selling.** Either the PR is registered as proprietor, or a sealed Grant of Probate / Letters of Administration is produced. All proving PRs must sign the purchase deed. A **sole proving PR can give a valid receipt** for purchase money (unlike a sole trustee) — overreaching applies.

**Sole-surviving co-owner.** Determine whether it was beneficial joint tenancy or tenancy in common.

- **Joint tenancy** — survivorship applies; remaining co-owner sells solo, with a death certificate. In registered land, no restriction on the register.
- **Tenancy in common** — Form A restriction on the register; **second trustee** must be appointed to overreach.

**Attorney selling.** Power of Attorney must be valid and **no more than 12 months old** (unless a Lasting Power of Attorney). Certified copy on file. Different types: general, specific, enduring, lasting, trustee — each gives different authority. **Do not accept a spouse-to-spouse Power of Attorney.**

**Charity selling.** Either consent from the Charity Commissioners, or the charity is exempt. The transfer must contain a certificate confirming either consent or exemption. In registered land, any restriction on the register must be acted upon.

**Company selling.** Generally proceed as if an individual was selling (good-faith purchaser protection). Conduct a **company search** to confirm the company is still trading and not in winding-up.

**Trustee in Bankruptcy selling.** In addition to usual title evidence, obtain the **bankruptcy order** and the **order appointing the Trustee**.

**Court of Protection deputy selling.** Obtain the guidance notes from the Court setting out the requirements.

### Indemnity insurance — when

If you can't fix a title defect (defective easement, breach of covenant), and you can't get a Deed of Grant or retrospective consent, **defective title indemnity insurance** (with client and lender agreement) covers losses arising from the defect.

### Requisitions

Traditionally raised after exchange — under the Law Society Conveyancing Protocol and Standard Conditions of Sale they're now raised **before exchange**.

Sample requisitions:

- "Please provide evidence of the seller's change of name from Smith to Jones."
- "The root refers to an earlier conveyance with restrictive covenants — please provide a copy."
- "The seller says the property has a right of way over the rear passageway — the deeds contain no evidence. Provide evidence or confirm indemnity insurance will be supplied on completion."

**Don't exchange until every requisition is answered to your satisfaction.**

### Pre-exchange searches

The standard list, for registered land:

- Local search (LLC1 + CON29R).
- Environment search.
- Official search (**OS1** for whole title, **OS2** for part).
- Bankruptcy search.
- Plus searches specific to the property (see below).

For unregistered land:

- Local search.
- **Index map** search.
- **Commons** search.
- Environment search.
- Bankruptcy search.
- Official search.
- Plus specifics.

**Specifics** — pick from this list as appropriate to the property:

| Search | When |
| --- | --- |
| Drainage and water (CON29DW) | Always — public vs private sewers/supply. |
| Commons registration | If property never built on, recently developed, abuts village green/common/grass verge. |
| Coal mining | Always check the Law Society directory — Kent has historic mining too. |
| Company | If seller is a company. |
| Highways | Useful adjunct to local search. |
| Limestone mining | Dudley, Sandwell, Walsall, Wolverhampton. |
| Clay mining | Devon, Cornwall, Dorset. |
| Brine extraction | Cheshire, Greater Manchester (now in coal). |
| Tin mining | Devon, Cornwall. |
| New Forest | New Forest (Verderers — outside Commons Registration Act). |
| Rivers | Environment Agency — bank repairs, floods. |
| Canals | British Waterways — rights of way on towpaths. |
| Railways | Network Rail — adjacent or over tunnel. |
| Rent registration | If tenanted. |
| Chancel repairs | Electronic search or indemnity. |
| Agricultural Credits Dept | Working farmer's land. |
| Environmental | Law Society recommends in every case. |

Consider a **physical inspection** of the site — watercourses, boundary discrepancies vs deeds and OS maps.

### Preliminary enquiries (caveat emptor)

Where the Protocol applies, the seller supplies replies via the **Property Information Forms**. Read them carefully; raise additional enquiries only where specific to the property — pre-printed Additional Enquiries forms go against the Protocol's spirit.

The buyer must receive copies of all enquiries and replies before exchange.

### Caveat emptor

Let the buyer beware. The seller has only a limited duty of disclosure. The buyer must:

- Carry out a survey.
- Conduct appropriate searches.
- Raise enquiries and requisitions.

…before committing to exchange.

## Edge cases & gotchas

- **Don't accept a spouse-to-spouse Power of Attorney.** Conflict territory — the granting spouse should be separately represented.
- **A PR can give a valid receipt as a sole signatory** — but a sole trustee can't. Don't confuse them.
- **Form 102 (or its replacement) certifies a plot is part of the seller's title** — useful evidence on a new build but not a substitute for full official copies.
- **Indemnity insurance is a last resort**, not a quick fix. Try Deed of Grant, retrospective consent, or fixing the underlying issue first.
- **Pre-printed additional enquiries** — frowned on under the Protocol. Make every additional enquiry specific to the property.

## Related guides

- [Training — Registered and unregistered land (Session 3)](/crm/guides/legal/training-registered-and-unregistered-land)
- [Training — Exchange of contracts (Session 6)](/crm/guides/legal/training-exchange-of-contracts)
- [Land Registry register entries — catalogue](/crm/guides/legal/title-register-entries)
- [Failed environmental search](/crm/guides/legal/failed-environmental-search)
`,
    },
    {
        product: "legal",
        slug: "triage-and-email-filing",
        title: "Triage inbox and email filing — assign, resolve, archive",
        one_line_description: "Triage states and actions, plus the priority filter and overdue clock for working the inbox.",
        category: "case-view",
        order: 50,
        last_screenshotted_at: "2026-05-29",
        tags: ["triage","inbox","email","assignment","resolve","archive","case-assignment","private-inbox","priority","needs-attention","overdue"],
        content_hash: "bebff1ff6ef8424094db4e3f6166f96c8fcd77772487908e6199530c5dc5a222",
        body_markdown: `## What it is

The triage workflow turns the firehose of inbound legal email into something filed and actionable. Every email that lands at Sail Legal sits in one of a small number of states, and the triage UI is the surface that moves it between them. The two ideas you need to keep separate are:

- **Case-assignment** — is this email tied to a case?
- **User-assignment** — is this email sitting in a specific staff member's personal queue?

These are independent. An email can be tied to a case *and* sitting in your queue. An email can be assigned to you with no case attached. Don't conflate the two — the rest of the model only makes sense once they're pinned apart.

## When you'd use it

- You're doing inbox duty and an unfamiliar email has landed in the untriaged tab.
- You're covering a colleague's inbox and need to see their unresolved emails.
- You want to file an email onto a case you can identify.
- You want to keep an email in your own queue without yet picking a case for it.
- An email is junk / not for us / a mis-routed reply, and you want it out of every active surface.
- A previous handler resolved an email but you need it back in the active queue.

## Walkthrough

### The three lifecycle flags on every email

Every email row carries three independent flags. Read the state by reading all three.

| Flag | What it tells you |
| --- | --- |
| **Who it's assigned to** | The staff member whose Private Inbox this email shows in. Empty = unassigned, lives in the triage Untriaged tab. |
| **Whether it's resolved** | A timestamp. Set means "done, hide from active inboxes". Clear means active. |
| **Case relevance** | An enum: *unassigned*, *assigned*, *irrelevant*. *Assigned* means it's on a case. *Irrelevant* is overloaded — it covers both archived-as-junk and "assigned to a person but explicitly not for any case". |

Combine those three flags and you get the inbox tabs you see in the UI.

### The triage tabs

| Triage tab | What's in it |
| --- | --- |
| **Untriaged / Unassigned** | Anyone's responsibility. No user assignment, not resolved. The default firehose. |
| **Unresolved (mine)** | In your queue, not yet resolved. Includes emails on cases and emails with no case. |
| **Resolved** | Set as done. Hidden from active inboxes unless you unresolve. |
| **Confidential** | A separate audience-restricted slice. Treat as a parallel queue. |
| **Staff Inboxes** | Choose a staff member to see their unresolved emails. From your Inbox page, open the My Inbox selector and choose **Staff Inboxes** to jump here. |
| **Archive** | Marked *irrelevant* + resolved. Hidden from active inboxes, but searchable. |

### Cutting through the firehose: the importance filter

Above the email list — on the Untriaged and Unresolved tabs — there's a **Show:** switch with three options, each with a live count:

- **Needs attention** — the emails most likely to need you first: chasing, complaints, onboarding problems, anything an unhappy client has sent, a Land Registry requisition, and the like. The triage inbox opens on this.
- **All** — everything in the tab, nothing set aside.
- **Noise** — the safe-to-leave end: marketing, supplier blasts, auto-replies, obvious spam. A quick place to sweep up and archive.

Every row also carries a small coloured dot so you can read the priority at a glance without switching: **amber** for needs-attention, **grey** for noise, nothing for the ordinary middle. The dot is worked out for you from what the email is about, its tone, and whether it looks like junk — it's not something you set.

Two things to hold onto:

- The filter **hides nothing** — it only narrows what's on screen. Switch back to *All* whenever you want.
- The priorities are a **starting sort, not a verdict.** A "Noise" email is occasionally something real, so skim before a bulk archive.

### The overdue clock

A small **red clock** next to the date means one thing: an email from someone outside the firm that **we haven't replied to within 24 working hours.** Weekends don't count — an email that lands Friday afternoon isn't overdue until Monday afternoon.

"Replied" means a reply has actually gone out on that thread — not that someone read it or filed it. So the clock stays until a reply is sent, and clears the moment one is.

Read it as a service-level nudge: a queue full of red clocks is a queue full of clients waiting. It only ever shows on emails to us that we haven't answered — never on your own outbound mail, and never once a reply is on the thread.

### The actions you take

Each action below is a specific move between states. The terminology matters because the audit trail records each one.

#### Assign to a case (and a person), and resolve

Use when you can identify the case the email belongs to **and** the work it needs is done now.

- The email is linked to the chosen case.
- It moves into the chosen person's Resolved queue.
- It will surface on the case's Inbox tab.

#### Assign to a case (and a person), keep it open

Use when you've identified the case but the email still needs work — usually you're keeping it in your own queue or routing it to a colleague.

- The email is linked to the chosen case.
- It moves into the chosen person's *Unresolved* queue.
- The case-assignment surfaces in the case Inbox too.

#### Assign to a person only — no case

Use when the email is in someone's working area but isn't really *about* a case (a query, an internal coordination, something we'll deal with off-case).

- The email enters the chosen person's *Unresolved* queue.
- Case relevance is recorded as *irrelevant* — meaning "not on a case" rather than "junk". This is the overloaded use of *irrelevant* — don't read it as archive.
- The email is *not* linked to any case.

#### Resolve

Use when whatever the email needed is done.

- Resolution timestamp set against the email.
- The email drops out of active queues; still searchable, still on its case Inbox if it had one.

#### Archive

Use when the email is genuinely not for us — junk, mis-routed reply, scam, unsubscribed list mail.

- Case relevance set to *irrelevant*.
- Resolution timestamp set (so the email actually disappears from assignee queues — the archive sticks).
- Any case-link is severed.
- The original assignee is preserved on the row for audit ("who had this before it was archived").

#### Disassociate from a case

Use when the email was linked to the wrong case (or it shouldn't have been linked at all).

- Case-link is removed. The user-assignment is unchanged.
- The email continues to live in whatever queue it was already in.

### The case-suggestion popout

When you go to assign an email to a case, the triage UI suggests candidate cases for you. The suggestions come from rules like *the from-address matches a contact on a case*, *the subject mentions the property address*, *this thread already touched this case*. Two suggestion shapes worth knowing:

- **Currently assigned to case** — a pill showing the email is *already* on a case. It's shown in the same list as the suggestions but isn't a suggestion in the predictive sense; it's the existing assignment surfaced as a single-click confirmation.
- **Ranked suggestions** — the cases the engine thinks this email is about, with the strongest signal first.

You can ignore the suggestions and pick a case by search instead. The audit trail records which case you chose, not which one was suggested.

### What you do per email

- **Start on Needs attention.** The triage inbox opens there for a reason — clear the essential work before dropping into the wider list.
- **Chase your red clocks.** A clock means someone outside the firm has been waiting more than a working day for a reply.
- **Read enough to know who it's about and what it needs.** If the answer is "I don't know", route it to the conveyancer who's likely closer.
- **If it's clearly a case email, assign it now.** Don't sit on it in the untriaged tab.
- **If you're acting on it yourself, take it into your queue** rather than leaving it floating — it stops two people from picking it up independently.
- **Resolve when the action is done**, not when you've read it. Read ≠ done.
- **Archive aggressively for junk.** Untriaged is a firehose; every junk item that stays in there costs someone reading time.

## How it works under the hood

- Case-assignment and user-assignment **live on different rows in different places** — that's why they don't auto-cascade. Disassociating from a case doesn't clear the user-assignment, and resolving doesn't sever the case-link.
- *Irrelevant* on the case-relevance field is **two things in one flag** — archived junk *and* assigned-to-a-person-no-case. The combination of case-relevance + assignee + resolution tells you which it is. If you write any custom report off this, filter on the combination, not on case-relevance alone.
- **Archive sets the resolved timestamp** as well as the case-relevance flag. That's why a junk email genuinely disappears from the assignee's Unresolved view rather than lingering as a *zombie unassigned-but-still-shown* row.
- **Case-assignment is many-to-many in the data model** but operationally treated as at-most-one. The triage UI surfaces the single current case; if you find an email with two case-links, it's a data quirk to flag.
- The **case-suggestion engine** runs at view time. It doesn't *learn* from your past choices — the rules are static, even if the strength scores update with new data on the case.

## Edge cases & gotchas

- **"I resolved it but it's still in someone's queue."** Resolution drops it from *active* views. If the colleague is viewing the Resolved tab, it's still listed there. Resolution is not deletion.
- **"I archived it but it's still on the case Inbox."** Archive severs the case-link going forward but the case Inbox snapshots what's currently linked — give the page a refresh, and the archived email will no longer appear there.
- **Two people working the untriaged tab.** No locking. Make sure you're not duplicating effort by talking to each other or splitting by sender domain.
- **"Assign to person only" looks like archive in some reports.** Because *irrelevant* is overloaded, naive filters can hide legitimate assigned-no-case emails. Always combine with the assignee + resolution flags.
- **A confidential email is a separate audience.** Don't assign confidential mail into a non-confidential person's queue without checking they have the right access.
- **The case-suggestion list can be wrong.** It's rules, not magic. Trust your reading of the email over the top suggestion if they disagree.
- **Email expiry / time-out.** Triage rows have an assigned-to expiry that hands an email back to the untriaged tab if you don't act on it. Don't claim emails you're not actually going to work on.

## Related guides

- [Managing the case inbox](/crm/guides/legal/managing-case-inbox)
- [Case view overview](/crm/guides/legal/case-view-overview)
- [Dashboard overview](/crm/guides/legal/dashboard-overview)
- [Anti-fraud and verification](/crm/guides/legal/anti-fraud-and-verification)
`,
    },
    {
        product: "legal",
        slug: "cautions-on-deceased-owner",
        title: "Cautions on title — deceased sole proprietor",
        one_line_description: "How to handle a caution against dealings when the registered owner has died and you're assenting the property to a beneficiary.",
        category: "legal-practice",
        order: 55,
        last_screenshotted_at: "2026-05-29",
        tags: ["cautions","deceased-proprietor","assent","as1","ap1","probate"],
        content_hash: "b7ee7f81a8ab30282ef194cf3af227e92391be395dec36606837d90fa9b18056",
        body_markdown: `## What it is

A **caution against dealings** is a pre-Land-Registration-Act-2002 entry that gives the cautioner two rights: to be notified of certain applications, and to object. It does **not** prevent registration, does not give the cautioner ownership, and is not a charge or restriction. The Land Registry's role is procedural — they don't decide ownership unless there's a genuine legal dispute.

This is **process knowledge, not currently driven by the case engine**. The roadblocks won't tell you what to do with a caution — this guide does.

## When you'd use it

- The registered owner has died and you're transferring or assenting the property to a beneficiary.
- The title shows a caution in favour of a third party (often a family member who is not a personal representative).
- A buyer's solicitor is asking what the caution means for completion.

## Walkthrough

### Step 1 — Confirm estate authority before doing anything with the caution

Before going near the Land Registry:

- Obtain the **death certificate**.
- Obtain the **Grant of Probate** or Letters of Administration.
- Confirm the property passes to the beneficiary under the Will (or intestacy rules).

No application proceeds without proper estate authority. **Do not attempt to cancel or remove the caution at this stage** — the presence of the caution does not prevent the assent being lodged.

### Step 2 — Lodge the assent

Proceed in the normal way:

- Prepare **Form AS1** (assent).
- Lodge **Form AP1**.
- Attach Grant of Probate, death certificate, and identity evidence (**ID1 / ID2** where required).

### Step 3 — Land Registry serves notice on the cautioner

This is automatic. The cautioner is given an opportunity to object. **No action from the conveyancer required at this point** — it's a statutory notification process.

### What an objection actually does

This is the part that trips clients up.

| Objection outcome | What happens |
| --- | --- |
| Cautioner does not object | Registration proceeds. Beneficiary is registered as proprietor. The caution may remain but has no practical effect. **Most common outcome.** |
| Cautioner objects but has no registrable interest | Land Registry rejects the objection. Registration proceeds. |
| Cautioner objects with a genuinely registrable interest | Application is stayed. Parties may try to resolve; if not, the matter can go to the First-tier Tribunal (Property Chamber). |

**Crucial point for client advice**: an objection does not stop registration — it only pauses it while a genuine dispute is determined. The cautioner cannot block registration simply by objecting. The burden is on them to prove a registrable proprietary interest.

### Valid vs invalid objections

| Valid (registrable) interests | Invalid objections |
| --- | --- |
| Beneficial interest under a trust | Family disagreement |
| Proprietary estoppel | Moral claims |
| Charging or equitable interest | Verbal promises without evidence |

### After registration

Once the beneficiary is registered:

- The caution often becomes obsolete.
- The new registered proprietor may apply to cancel it.
- If cancellation is opposed, the same objection process applies.
- In practice, many clients leave historic cautions in place unless selling or remortgaging.

## Edge cases & gotchas

- **Don't promise registration timescales** to the client — the notification window can stretch things.
- **Warn the client in writing** that the cautioner will be notified. They should not be surprised by a phone call from the cautioner's solicitor.
- **An objection from a registrable interest stays the application** — your job at that point is to report and hold pending instructions, not to fight the objection on the cheap.
- **Cancelling a dormant caution later** is usually easier when the family is no longer in dispute. Pick your moment.

## Related guides

- [Sale process](/crm/guides/legal/sale-process)
- [Sale with deceased owner and deceased PR](/crm/guides/legal/deceased-owner-deceased-pr)
- [Executors and LPAs](/crm/guides/legal/executors-and-lpas)
- [Assent transfers](/crm/guides/legal/assent-transfers)
`,
    },
    {
        product: "legal",
        slug: "deceased-owner-deceased-pr",
        title: "Sale when both owner and personal representative have died",
        one_line_description: "Why the PR's Grant of Probate cannot be used to sell the original owner's property, and the DBN Grant you need instead.",
        category: "legal-practice",
        order: 56,
        last_screenshotted_at: "2026-05-29",
        tags: ["probate","dbn-grant","deceased-pr","intestacy","letters-of-administration"],
        content_hash: "238bde8a8b20f47d85097c95afe8ed98b92d36bd1755814836bcf679e6c6a6c3",
        body_markdown: `## What it is

A sale where the registered owner died (often intestate), a personal representative was appointed, then the personal representative died too — with the estate still partly administered. The PR's Grant of Probate gives authority over **their own** estate, not the original owner's. You need a different Grant.

This is **process knowledge, not currently driven by the case engine**. The roadblocks won't flag this scenario for you — it's on the conveyancer to spot the chain-of-representation issue.

## When you'd use it

- The property is still registered in the name of the original deceased owner.
- The original PR has died, and there's a Grant of Probate for the PR's own estate.
- The family or the next-named person wants to push the sale forward using the PR's Grant.

## Walkthrough

### Why the PR's Grant cannot be used

The PR's Grant of Probate gives authority to deal with **the PR's estate** — not the original deceased's estate. You cannot use it to sell or transfer the property still registered in the original deceased's name. The Land Registry will reject any application that tries to. Proceeding without the right authority risks legal and negligence exposure.

### What you need instead — a DBN Grant

**Grant of Letters of Administration De Bonis Non Administratis** (commonly called a **DBN Grant**). This is a special Grant that appoints a new personal representative to finish administering the original deceased's estate.

### When a DBN Grant is required

| Scenario | DBN Grant needed? |
| --- | --- |
| Owner died **without** a Will, PR died before completing estate | Yes |
| Owner died **with** a Will, executor died before completing estate | Yes |
| PR or executor died **after** fully completing administration | No |

### Step-by-step

1. **Review the chain of representation.** Who died first? Was a Grant obtained? Did the PR actually deal with the property, or did they die mid-administration?
2. **Check the Land Registry title.** Is the property still in the original deceased's name? If yes, the chain is broken and a DBN Grant is the route.
3. **Advise the client in writing.** Let them know a DBN Grant is required before the sale can proceed. Do not progress the sale until the correct Grant is in place.
4. **Wait for the DBN Grant** before lodging any application or contracting to sell.

### Who applies for the DBN Grant

Usually the next person entitled under the intestacy rules (or under the original Will if testate). They may need their own probate solicitor — Sail does not do contentious probate.

## Edge cases & gotchas

- **The PR's Grant on its own is a dead end** for the property — even if it looks superficially valid. Don't attempt the Land Registry application "just to see".
- **Don't contract to sell** before the DBN Grant is issued. The seller cannot give good title without it.
- **Escalate to your team leader** the moment you spot this scenario — it has timing implications for any buyer in place.

## Related guides

- [Sale process](/crm/guides/legal/sale-process)
- [Executors and LPAs](/crm/guides/legal/executors-and-lpas)
- [Assent transfers](/crm/guides/legal/assent-transfers)
- [Cautions on title — deceased sole proprietor](/crm/guides/legal/cautions-on-deceased-owner)
`,
    },
    {
        product: "legal",
        slug: "managing-case-inbox",
        title: "Managing the case inbox — reply, forward, file attachments",
        one_line_description: "How to read a case's email thread, compose from the case, and file attachments into the document store.",
        category: "case-view",
        order: 60,
        last_screenshotted_at: "2026-05-29",
        tags: ["case-inbox","email","attachments","document-store","reply","forward","clear-review","email-coach","unresolved","resolve"],
        content_hash: "ffa56e650fc55e5041c422fac46f1da495d57464e4e30b8ef7ce284757b8447a",
        body_markdown: `## What it is

The **Inbox** tab on a case page. It shows every email linked to the case, in chronological order, with the reply / forward / file-attachments actions sitting next to each one. It's the place you actually do legal work over email: you read the thread, you compose from inside the case so outgoing email is tied to the case automatically, and you file inbound attachments straight into the case's document store rather than leaving them buried in email history.

The case Inbox is downstream of the Triage workflow. An email shows here because someone — or the automated case-suggestion — assigned it to this case. If you're wondering where an email *came from*, see [Triage and email filing](/crm/guides/legal/triage-and-email-filing).

## When you'd use it

- You want to see every email exchange on a case in one place.
- You're replying to or forwarding a client / counterparty email and you want the outgoing message linked to the case.
- An attachment has come in and you want to file it into the document store.
- An email landed on the wrong case and you need to send it back to triage.
- You want to compose a new email from scratch with the case context already loaded.

## Walkthrough

### Reading the case thread

The Inbox tab lists every email that's currently linked to the case. Each row shows the sender, subject, snippet, and a timestamp. Click any row to expand the email in the right pane — full body, recipients, attachments. Threading is by case, not by RFC message-id — emails belonging to the same logical thread but landed via different routes will still show in the case Inbox as long as they're each linked to the case.

What you'll see for each email:

| Action | What it does |
| --- | --- |
| **Reply** | Open the compose pane with the recipients and subject pre-filled and the original thread quoted. Sent reply is linked to this case automatically. |
| **Forward** | Open the compose pane with the original body quoted but recipients blank. Forwarded email is linked to this case. |
| **File attachments** | Open the file-attachments overlay — file any attachments on this email straight into the case document store. |
| **Disassociate** | Send the email back to triage; it stops appearing on the case Inbox. The user-assignment is unchanged. |
| **Mark as resolved** | Clear the email from the handler's Legal inbox without leaving the case. Only offered on mail that is still unresolved. |

The disassociate action is the one you reach for when an email landed on the wrong case. It doesn't archive — the email goes back into the triage flow so the right case can be picked.

An incoming email that nobody has dealt with yet carries a small amber envelope icon on the right of its row, next to the date — hover it for *Unresolved*. It means someone still owes a reply — it's the same email the case's AI estimate is counting when it says *1 unresolved inbox email*, and clicking that line is what brings you here. Open it and the double-tick marks it resolved, which clears it from whoever's Legal inbox it was sitting in. **The email stays on the case** afterwards, it just loses the icon — this tab is the case's whole mail history, not a to-do list that empties. Resolving is not reversible from here, and the undo only lasts a moment, so read before you tick. Outgoing mail never carries the icon: nobody owes a reply to an email we sent.

Next to that envelope you'll see a set of initials — **whose inbox the email is sitting in**. That's who you're clearing the work from when you tick it. Hover for the full name and whether they still hold it: *claimed until 10 Oct* means it's live in their queue, *claim lapsed 9 Mar 2026* means their hold ran out and the email has been on the shared expired list ever since. **A lapsed claim is the common case, not the exception** — usually the sign that an email needs picking up by someone, rather than left sitting with the name on the row.

Initials only appear on the unresolved incoming rows. Mail we sent has nobody assigned to it, and resolved mail is finished work, so there's nothing useful to name on either.

### Composing from inside the case

Composing from the case Inbox is the standard route for any outgoing legal email tied to the matter. The compose form pre-loads the case context — the right participants on the recipient picker, the case reference in the subject, links into the portal where relevant. Outgoing email composed this way is automatically tied to the case and shows on the Inbox tab from the moment it's sent.

Two AI tools live in the compose form:

- **CLEAR Review** — the mid-draft rewrite tool, behind the lightbulb icon in the compose toolbar. Returns a rewritten body plus feedback against five rubric dimensions (Connect, Lay out, Explain, Assure, Resolve). Use it when you've drafted something and want a second pass before sending.
- **Email Quality Score** — the send-time score that fires automatically when you press send. Grades the draft against three dimensions and shows you a verdict. If the verdict is *Send confidently*, the email goes; if it's *Worth a tweak* or *Reconsider*, you see a modal and choose to revise or send anyway.

The Email Quality Score is graded per recipient role — the rubric calibrates for client, estate agent, conveyancer on the other side, or other. For client recipients, the AI knows the recipient's ownership status (new owner, remaining owner, departing owner) so the empathy expected on a transfer of equity is different from the empathy expected on a fresh sale.

For the underlying CLEAR principles and the email coach copy framework, see the email coaching material in the broader Communications category.

### Sending documents out — links or attached files

Attaching a case document, redemption statement or quote to an outgoing email is one thing; how it reaches the recipient is another. By default it **goes out as a download link** — the file name appears in the email, the recipient clicks it, the file downloads in a new tab. Nothing is attached to the message itself.

That's on purpose. We can switch a link off later if we need to pull a document back. It also means a redemption statement with a lender's bank details isn't sitting in someone's mailbox for good, going out again with every forward.

Some people never get the file that way. Their mail system rewrites or strips the link, or their firewall blocks it. When that happens, open the attachments panel on the compose form and **switch to Attached files**:

| Mode | What the recipient gets |
| --- | --- |
| **Download links** (default) | The file names as links. Nothing attached to the email. |
| **Attached files** | The same documents as normal email attachments, in the format they were saved in. A Word file arrives as a Word file — nothing is turned into a PDF. |

Either way, the email body lists the file names. So the case record always shows what you sent.

**The switch is per email.** It goes back to *Download links* every time you compose. If one firm's system keeps eating links, you'll flip it on each message you send them. It works the same on a new email, a reply and a forward.

Attached files have a size limit — about 28MB for the whole email. Go over it and the panel turns red and Send is switched off, until you take a file out or go back to links.

### Filing attachments into the document store

When an inbound email carries a relevant attachment (a redemption statement, a search result PDF, an ID document, a property questionnaire), file it from the case Inbox rather than downloading-and-reuploading. From the email row, hit **File attachments** to open the overlay.

In the overlay you pick:

- Which attachments to file (one, several, or all).
- The document type each one is being filed as — redemption statement, search result, ID image, etc. The type drives where it shows on the case and what roadblock checklist items it can clear.
- (Optional) a note recording why this attachment is being filed.

Filed documents land on the case's documents tab and are visible to the engine — many Contract Ready Pack and Pre-Completion Ready checklist items are cleared by the presence of a correctly-typed document.

The **paperclip icon** that opens this overlay is only live when the email is *currently linked to a case* — which on the case Inbox is always true. The same overlay is reachable from the Private Inbox when an email has a current case-assignment.

### Forwarding without losing the trail

Forwarding from the case Inbox keeps the forwarded email linked to the case — so if a counterparty's email needs to go to a search provider or surveyor, the forward shows up on the case Inbox alongside the original. This is the right way to delegate a piece of work without losing the audit trail.

### What you do per case

- Treat the Inbox tab as the canonical case correspondence history. Don't paste email content into roadblock chats as a substitute.
- Compose outgoing from the case Inbox, not from your personal mail client. The automatic case-link is the whole point.
- File attachments at the moment you read the email. Filing later, after several emails have flowed past, is the most common reason a redemption statement (or similar) ends up "lost" on the case.
- Leave outgoing documents on links unless a recipient has actually told you the link didn't work. Switching to attached files is the fix for a broken link, not the default way to send.
- When a link has failed, re-send with attached files from the case Inbox — never from your own mail client. Sending it yourself is what loses the audit trail.
- Disassociate ruthlessly when an email landed on the wrong case. Leaving it in place corrupts the case history for everyone who comes after.

## How it works under the hood

- The case Inbox **reads from the same email store** as the Private Inbox and the Triage queues — the difference is the filter. Case Inbox is "emails currently linked to this case"; Private Inbox is "emails currently in my queue".
- **Sent email from the case is linked automatically** at send-time, not after. So a sent email shows on the Inbox tab even before any reply arrives.
- The **file-attachments overlay** is shared infrastructure across Private Inbox, Triage, and Case Inbox — same flow, same document-type picker. The paperclip lives wherever the email has a current case-link.
- **Disassociate is non-destructive** — the email row itself isn't deleted; the link to the case is. The email continues to live wherever it is in triage / Private Inbox land.
- The Email Quality Score is **always persisted on send** — even when the AI couldn't grade the draft (template, too short, AI unavailable). That row records the verdict and whether the sender pressed "Send anyway" on a flagged draft.
- **Attached files come straight from the document store** at send-time. Nothing is copied, changed or re-uploaded, so the recipient gets exactly the file that sits on the case. Files the virus scanner has flagged are never attached.
- The mode **isn't saved against the sent email**, but **you can tell which one was used** from the sent copy on the Inbox tab. Links mode shows the file names as links; attached mode shows them as plain text.

## Edge cases & gotchas

- **"I sent the email but it's not on the case Inbox."** It usually is — give the page a refresh. If it's still missing, you might have composed from your personal mail client instead of from the case; the case-link only happens when the compose form knows the case.
- **A reply lands in triage, not the case Inbox.** If the original was disassociated, or the reply's threading doesn't match, the reply will land untriaged. Pick it up in triage and assign to the case.
- **Filed attachment doesn't appear on the documents tab.** Usually because the document type was wrong on filing — the document landed under a different bucket. Re-file with the right type.
- **CLEAR Review and the Email Quality Score are different tools.** CLEAR rewrites a draft; the Quality Score grades it. Don't conflate the two — and don't expect CLEAR's feedback to match the Quality Score verdict.
- **"Send anyway" is recorded.** Overriding an amber or red Quality Score verdict is logged against the email — there are no silent overrides. Use the override deliberately, not as a reflex.
- **Attachments on a sent email are *not* re-filed by the system.** If you sent a document you've already filed from elsewhere, the sent copy is just the email; it doesn't double-up the document store.
- **"They say they never got the document."** Check which mode you used first. Links mode is the usual cause when a firm's mail system strips web addresses. **Re-send with Attached files** rather than assuming the email never arrived.
- **The mode doesn't stick to a person or a firm.** Every compose starts on links — including a reply in a thread where you already switched once. There's no "this firm always needs attachments" setting yet.
- **A file you attached isn't in the email.** Either the virus scanner flagged it, or you were over the size limit. Going over the limit stops the send and tells you. A flagged file is simply left out.
- **Enquiries don't change with the mode.** They're written into the email body as text either way. The switch only covers documents, redemption statements and quotes.
- **Disassociated emails can be found through search**, not by browsing the case Inbox — once disassociated they're gone from this tab.
- **Resolving here is the same act as resolving in the Legal inbox.** It's one flag on the email, not a per-screen one, so the email leaves the assignee's inbox even though you did it from the case. If the case is in chase you'll be asked about chase state first, exactly as you would be in the inbox.
- **Initials on a row are not a promise anyone is working on it.** Most unresolved mail has a lapsed claim, so the name tells you who it landed with, not who is dealing with it. Hover before you assume — and if it has lapsed, treat it as unowned and either pick it up or hand it to someone.
- **The name can be someone who has left.** A departed colleague's initials still show rather than a blank, because knowing who had the email is what tells you who to ask next.
- **A resolved email keeps no resolve button.** That's deliberate — the row stays visible on the case, and re-ticking it would overwrite the record of when it was actually dealt with.

## Related guides

- [Triage inbox and email filing](/crm/guides/legal/triage-and-email-filing)
- [Case view overview](/crm/guides/legal/case-view-overview)
- [Client portal — onboarding steps](/crm/guides/legal/client-portal-onboarding-steps)
- [Anti-fraud and verification](/crm/guides/legal/anti-fraud-and-verification)
`,
    },
    {
        product: "legal",
        slug: "mortgage-offer-report",
        title: "Mortgage offer report — process",
        one_line_description: "Working a mortgage-offer job through the CRM, escalation, and the report to the client.",
        category: "legal-practice",
        order: 60,
        last_screenshotted_at: "2026-05-29",
        tags: ["mortgage-offer","lender","retention","escalation","coda"],
        content_hash: "9d3e764df15b5e32c79cd9b8e995561c1fdb69e4552390a2b96bd11933e81b49",
        body_markdown: `## What it is

The process for reviewing a mortgage offer once it lands on a case, escalating anything risky, generating the mortgage report, and sending it to the client with the deed.

## When you'd use it

- A new mortgage offer has been logged on a case and a job has appeared for you.
- You're about to send the offer to the client and need to check you've covered everything.
- The offer contains something unusual (a retention, an unusual condition) and you're deciding whether to escalate.

## Walkthrough

### 1. Open the job

When the mortgage offer is logged onto the CRM, a new job appears on your day view. Two ways to open it:

- Activate the job by selecting **Go to Case**, or
- Search for the case using the CRM search, then under *Details*, select **Mortgage Offer**.

### 2. Work through the offer

In the **Details** section you'll find the **Mortgage Offer** job. Complete every detail field — the job only closes once they're all in.

### 3. Mortgage offer checklist

Complete the **mortgage offer checklist** and add it to the CRM.

If anything needs escalating to **Edwina Homfray-Davies**, record it in the **Risk Management screen** — what's been referred and a Grade. Example: a retention typically gets **Amber**. Save your comments.

**Do not proceed to completion** until you've received authorisation from Edwina Homfray-Davies.

### 4. Generate the report

Use **CODA** to generate the mortgage report.

Once the report is generated, **mark it as sent** to the client by ticking the box next to the head-and-shoulders button.

### 5. Send to client

Email the **search report** to the client, attaching a copy of the **mortgage deed**.

### 6. Digital vs hard-copy mortgage deed

Check the **lender panel spreadsheet** for the lender's signing requirement:

- **Digital signature accepted** — the client can e-sign and you're done.
- **Hard copy required** — ask the client to print the mortgage deed you emailed and post the original to head office.

## Edge cases & gotchas

- **Retentions trigger escalation, every time.** Don't run a retention case to completion without Edwina's sign-off in writing — log it, tag it Amber, wait for the auth.
- **Lender-panel spreadsheet is the source of truth on digital vs hard-copy.** Don't assume — different lenders flip their position on this regularly.
- **Forgetting to tick the "sent to client" box** is the most common cause of stale mortgage-offer jobs on the day view. The job won't clear until that's ticked.

## Related guides

- [Purchase process](/crm/guides/legal/purchase-process)
- [Dashboard overview](/crm/guides/legal/dashboard-overview) — where the mortgage-offer job appears.
`,
    },
    {
        product: "legal",
        slug: "mortgagee-protection-and-renters-rights-act",
        title: "Mortgagee protection clauses and the Renters' Rights Act 2025",
        one_line_description: "How to review leases that lack a mortgagee protection clause now that the assured-tenancy risk has been removed.",
        category: "legal-practice",
        order: 60,
        last_screenshotted_at: "2026-05-29",
        tags: ["leasehold","mortgagee-protection","renters-rights-act","indemnity","forfeiture","uk-finance-handbook"],
        content_hash: "03ac8b583534b6e5e5605f635f64e6920d3762d83258edbcb6467069f8f9da15",
        body_markdown: `## What it is

Long residential leases sometimes lack an express **mortgagee protection clause** — a provision requiring the landlord to notify the lender before forfeiture and giving the lender a chance to remedy. Historically lenders treated this as a title defect and often demanded a deed of variation or indemnity policy. The **Renters' Rights Act 2025** has materially shifted that risk.

This is **process knowledge, not currently driven by the case engine**. Title checks may flag the missing clause, but the judgement on how to report it is on the fee earner.

## When you'd use it

- Reviewing a long residential lease that doesn't contain a mortgagee protection clause.
- A lender's instruction is asking about the clause and you need to know what to report.
- Deciding whether indemnity insurance is warranted.

## Walkthrough

### What the clause does

A mortgagee protection clause typically:

- Requires the landlord to **notify the lender before forfeiture**.
- Gives the lender an **opportunity to remedy** breaches or arrears.
- Protects the lender's security interest if the tenant defaults.

Without it, the historic concern was that forfeiture could in theory extinguish the lender's security without prior notice — particularly where ground rent crossed the £250 (London £1,000) threshold and the lease became an assured shorthold tenancy under the Housing Act 1988.

### What the Renters' Rights Act 2025 changed

Long residential leases exceeding 21 years are **no longer treated as assured tenancies** for Housing Act 1988 purposes. Practical effects:

- Landlords can no longer use the Housing Act 1988 mandatory possession regime for these leases.
- The principal historic lender concern has been substantially reduced.
- Practical forfeiture risk affecting lender security is now low.

The absence of a mortgagee protection clause therefore no longer carries the risk it used to.

### What to review in the lease

Even with the legislative change, run through these checks:

| Area | What to look for |
| --- | --- |
| **Forfeiture clause** | Grounds for forfeiture, notice periods for arrears, whether re-entry is immediate or conditional, whether section 146 of the Law of Property Act 1925 is referenced. |
| **Notice provisions** | Section 196 of the Law of Property Act 1925 incorporated, formal written notice requirements. |
| **Length of lease** | Exceeds 21 years, residential in nature — engages the Renters' Rights Act 2025 effect. |
| **Ground rent** | Current level, review provisions, any wider marketability concerns. |

Notice provisions don't substitute for a full mortgagee protection clause, but they help demonstrate that formal notice procedures are contemplated before enforcement.

### UK Finance Handbook still bites

Read Part 2 of the lender's section in the **UK Finance Mortgage Lenders' Handbook**. Some lenders still refer specifically to mortgagee protection clauses and defective leases and want to be told. Report the absence where the lender's instructions require it — but couch it with the impact of the Renters' Rights Act 2025.

### Suggested wording for the lender report

> "The lease does not contain an express mortgagee protection clause. However, the lease exceeds 21 years and accordingly is no longer treated as an assured tenancy following the Renters' Rights Act 2025. The lease also incorporates notice provisions under section 196 of the Law of Property Act 1925. We consider the practical risk to lender security to be low."

### Indemnity insurance — when it's still needed

Default position: **often no longer necessary** solely because of the missing clause. Still consider it where:

- The lender expressly requires it.
- Lender underwriting comes back insisting.
- The lease has unusual forfeiture wording that materially increases risk.

If the lender expressly requires a policy, the lender instruction has to be complied with — the legal landscape doesn't override the contract.

### Client reporting

Keep it proportionate. The client report should explain:

- The lease lacks a mortgagee protection clause.
- Historically this raised lender concerns.
- The Renters' Rights Act 2025 has substantially reduced the risk.
- The matter has been assessed and is considered low risk.
- Lender requirements have been satisfied or addressed.

Don't alarm the client unnecessarily where the practical risk is low.

### Current risk position at a glance

| Risk area | Assessment |
| --- | --- |
| Technical title defect | Low |
| Practical forfeiture risk | Low |
| Risk to lender security | Low |
| Marketability risk | Low to moderate (depending on lender) |
| Need for deed of variation | Usually not required |
| Need for indemnity insurance | Often no longer needed unless lender insists |

## Edge cases & gotchas

- **Lender practice is still catching up** with the legislation. Don't assume every lender's policy is current — read the Part 2 entry.
- **Excessive ground rent** is a separate concern from the mortgagee-protection question — see [Escalating ground rent](/crm/guides/legal/escalating-ground-rent).
- **File the audit trail** — lease review, lender report, lender response, rationale for any decision not to insure, and the client report. This is what protects the firm.
- **A deed of variation is rarely proportionate now** — don't reach for it as the default fix.

## Related guides

- [Escalating ground rent](/crm/guides/legal/escalating-ground-rent)
- [Training — Leasehold fundamentals (Session 9)](/crm/guides/legal/training-leasehold-fundamentals)
- [Sale process](/crm/guides/legal/sale-process)
`,
    },
    {
        product: "legal",
        slug: "roadblock-contract-ready",
        title: "Roadblock: Contract Ready Pack",
        one_line_description: "How to clear each item under the Contract Ready Pack roadblock on a sale.",
        category: "roadblocks",
        order: 60,
        last_screenshotted_at: "2026-05-29",
        tags: ["roadblocks","contract-ready","sale","troubleshooting"],
        content_hash: "272dd3ad2361b804b90a0fb3d93441e2e5d39313a83696964887ec820150a66b",
        body_markdown: `## What it is

Contract Ready Pack is the sale-only roadblock that tracks the documents and milestones we need to assemble the contract pack — the bundle that goes out before exchange. It fires on instructed sale cases and contains a mix of protocol forms (TA6, TA7, TA10), documents referred to in the title, searches, EPC, leasehold pack (LPE1), freehold management pack (FME1), tenancy agreements, probate documents, and charge / management-company housekeeping.

Items clear as the documents land and the relevant fields are set, but the roadblock is **authoriser-gated** and **blocks exchange** — once every item is green it sits *awaiting signoff* until someone in the sign-off pool approves it.

## When you'd use this guide

- You're assembling the contract pack and want to know what the engine is still waiting on.
- A document was uploaded but the matching item won't go green.
- You're trying to work out which items live here vs Contracts or Exchange Ready.

## The checklist

### All documents referred to in titles and deeds obtained

Every property on the case has its *all documents referred to in titles and deeds obtained* title-check answer set to **yes** or **some missing**.

**Some missing clears this item.** A document referred to in the register that was never lodged at HMLR can't be obtained by anyone, so the pack is built around the gap — describe it in *Missing deed or title documents description* and move on. **Only no and unknown hold it open** — either what is obtainable hasn't been obtained yet, or nobody has checked.

- **Where to go**: **Details** tab → **Property** block → each property → title check answers.

### EPC Received

The case has a settled EPC position — **the case holds an EPC, or doesn't need one**. Three routes clear it: a valid (non-expired) certificate has been found and attached, the certificate is in the case's **EPC** document slot however it got there (emailed in by the agent and filed by hand counts), or the case handler has marked the property as not requiring one (e.g. a plot of land).

We look the EPC up automatically against the government *Get Energy Performance of Buildings Data* register when the case is instructed, matching on the property's **UPRN**, and file the official certificate PDF into the case's EPC document slot. Everything is managed from the **Details** tab → **EPC** section: **Re-run lookup**, **search by postcode** and attach the right certificate, **No EPC available** (when none exists), or **Mark as not required**.

The automatic lookup never concludes that a property has no EPC. Most cases it can't match simply have no UPRN on file, which tells us nothing about whether a certificate exists — so it leaves the status as **No automatic match** and shows you the certificates registered at the postcode to pick from. Only you can record **No EPC available**, and only that triggers a chase for a new one. If the panel says *Certificate on file*, the EPC is already on the case and there is nothing to do.

Don't reach for **Mark as not required** to clear an EPC you already hold — it permanently records that the property needs no EPC, blocks every future lookup, and leaves the PIF's Energy section empty.

- **Where to go**: **Details** tab → **EPC** section.

### Arrange a new EPC

Appears only when there's no valid EPC, none on file, and the seller has asked **us** (rather than themselves) to arrange a new one. Resolves when you tick **"we've sorted a new EPC"** on the EPC section once a replacement assessment has been booked; the fresh certificate later flips the EPC status back to valid.

- **Where to go**: **Details** tab → **EPC** section → *Mark new EPC as sorted*.

### LPE1 ordered (leasehold)

Cases with at least one leasehold title where **Is there a management company?** is **Yes**. Resolves once every management company linked to the case has a **pack ordered** date against it.

An LPE1 is served on the landlord or managing agent, so **the item only appears when there is one to serve it on** — a seller who owns the freehold reversion of their own leasehold title has nobody to ask. **Answering No takes the item away.**

- **Where to go**: **Details** tab → **Management companies** block → *Pack ordered*.

### LPE1 received (leasehold)

Follows on once the LPE1 has been ordered — the pair shows one row at a time, never both. Every LPE1 document slot on the case is uploaded and received.

- **Where to go**: **Documents** tab → *LPE1* slot.

### FME1 received

Freehold properties on cases where a management company exists. Every FME1 slot is uploaded and received.

- **Where to go**: **Documents** tab → *FME1* slot.

### AST or tenancy agreement received

Cases sold subject to an AST. Every AST and tenancy agreement slot is uploaded and received.

- **Where to go**: **Documents** tab → *AST* / *Tenancy agreement* slots.

### Grant of probate or letters of administration received

Probate cases. Every grant / letters slot is uploaded and received.

- **Where to go**: **Documents** tab → grant slots.

### Grant of probate or letters of administration applied for

Probate cases. Either the *grant application date* is recorded, or the grant / letters are already received.

- **Where to go**: **Details** tab → **Probate** block → *Grant applied for* date.

### Will received

Probate cases where a will slot has been created. Every will slot is uploaded and received.

- **Where to go**: **Documents** tab → *Will* slot.

### Death certificate received

Probate cases where a death certificate slot has been created. Every slot is uploaded and received.

- **Where to go**: **Documents** tab → *Death certificate* slot.

### TA6 received

Full-title-guarantee properties. Every TA6 (Property Information Form) slot is uploaded and received.

- **Where to go**: **Documents** tab → *TA6* slot.

### TA7 received

Full-title-guarantee leasehold properties. Every TA7 (Leasehold Information Form) slot is uploaded and received.

- **Where to go**: **Documents** tab → *TA7* slot.

### TA10 received

Full-title-guarantee properties. Every TA10 (Fittings and Contents Form) slot is uploaded and received.

- **Where to go**: **Documents** tab → *TA10* slot.

### Local authority search received

Cases where searches have been ordered. Every local authority search slot is uploaded and received.

- **Where to go**: **Documents** tab → *Local authority search* slot.

### Environment search received

Cases where searches have been ordered. Every environment search slot is uploaded and received.

- **Where to go**: **Documents** tab → *Environment search* slot.

### Drainage search received

Cases where searches have been ordered. Every drainage search slot is uploaded and received.

- **Where to go**: **Documents** tab → *Drainage search* slot.

### Management company details obtained

Cases with a management company. The management company's name and at least one contact channel (email or phone) is filled in.

- **Where to go**: **Details** tab → **Management company** block.

### Account numbers received for all charges

Cases with at least one charge. Every charge row has a non-empty account number.

- **Where to go**: **Details** tab → **Charges** block → each charge.

### First redemption statement received for all charges

Cases with at least one charge. Every charge has a redemption statement received against it.

- **Where to go**: **Details** tab → **Charges** block → each charge → *Redemption statement received* tick.

### Auction pack sent

Auction sales only — the item appears once *Auction status* is set to **Yes** in the Contracts tab. Answer *Auction pack sent* **Yes** once the pack has actually gone out to the auctioneer.

- **Where to go**: **Contracts** tab → *Auction status* → the auction fields that appear below it → *Auction pack sent*.

## Edge cases & gotchas

- **Contract Ready Pack only fires on sales, not on purchases.** Buyer-side work the equivalent doesn't have a *Contract Ready Pack* roadblock — the relevant pack-receipt items move to *Exchange Ready* on the purchase side.
- **Contract Ready Pack blocks exchange.** Every applicable item must be green before the case can go through Exchange Ready.
- **An uploaded document with no *received* date won't clear the item.** Uploading the file isn't the signal; ticking *received* against the slot is. Confirm both.
- **Title-guarantee status drives which TA forms fire.** A *limited* or *no guarantee* title won't trigger TA6 / TA10. If you expected those items and don't see them, check the title-guarantee dropdown.
- **Probate items reappear here and on Pre-Completion Ready.** The same documents are shared. Once you've cleared them on this roadblock, the matching items on Pre-Completion Ready will already be green.
- **Ticking *Auction pack sent* tells the introducer.** On a case that reached us through ConveyNext the tick reports the matching milestone straight back to them, so don't record it ahead of the pack actually going — there's no way to retract it.
- **Some items the legacy onboarding SOP mentions are deliberately not on this roadblock** — name-differs / marriage certificates, LPA / lost capacity, planning permissions, building regs. There's no reliable signal for them yet; staff judgement runs them outside the engine.

## Related guides

- [Roadblocks: how they work](/crm/guides/legal/roadblocks-how-they-work)
- [Sale process](/crm/guides/legal/sale-process)
- [Roadblock: Title Checks](/crm/guides/legal/roadblock-title-checks)
- [Roadblock: Contracts](/crm/guides/legal/roadblock-contracts)
- [Roadblock: Exchange Ready](/crm/guides/legal/roadblock-exchange-ready)
- [Training — investigation of title](/crm/guides/legal/training-investigation-of-title)
- [Training — leasehold fundamentals](/crm/guides/legal/training-leasehold-fundamentals)
`,
    },
    {
        product: "legal",
        slug: "training-exchange-of-contracts",
        title: "Training — Exchange of contracts (Session 6)",
        one_line_description: "Section 2 LP(MP)A 1989, particulars and conditions, deposits, the three exchange formulae, insurance, and the legal effects.",
        category: "training-sessions",
        order: 60,
        last_screenshotted_at: "2026-05-29",
        tags: ["training","exchange","formulae","deposit","standard-conditions","insurance"],
        content_hash: "5bf47b7976214f21e42a6884a5dfc2eea5a73eed0169226640f5506c59c6ac93",
        body_markdown: `## What it is

The mechanics of exchange — what makes a contract for land valid, the structure of the contract, deposit handling, the three methods of exchange (including the Law Society formulae for telephone exchange), insurance, and what changes legally at the moment of exchange.

This is **training material, not currently driven by the case engine**, though it lines up directly with the **Exchange Ready** roadblock.

## When you'd use it

- A new joiner is learning the formal mechanics of exchange.
- You're about to do your first telephone exchange and need the formula details.
- A client asks what changes legally at exchange — and whether they can still pull out.

## Walkthrough

### What makes a land contract valid

Under **section 2 of the Law of Property (Miscellaneous Provisions) Act 1989**, a contract for the sale or disposition of land must:

- Be **in writing**.
- **Incorporate all expressly agreed terms** in one document (or in exchanged counterparts).
- Be **signed by or on behalf of each party**.

If section 2 isn't satisfied, the contract is **void**. In conveyancing, both sides only sign their respective parts.

### The two sections of a contract

**Particulars of Sale**

- Names and addresses of parties.
- Extent of the property to be sold (postal address).
- Tenure (freehold or leasehold).
- Title number (if registered) or root of title (if unregistered).
- Reference to encumbrances.
- Purchase price.
- Contract rate.
- Date of contract and completion date.
- Signature space.

**Conditions of Sale** — split into:

- **General conditions** — for Protocol cases, the **Standard Conditions of Sale, 5th Edition**. Cover deposit, requisitions, completion arrangements, insurance, remedies for delay/breach.
- **Special conditions** — usually on the back page. Specific to the property; can override the Standard Conditions, cover repairs allowances, retentions, vacant possession or not.

### Deposit

Traditionally **10%** of the purchase price, paid on exchange as part-payment and as a guarantee the buyer will complete. Buyer defaults → seller can forfeit the whole deposit (subject to other remedies).

**Held as**:

| Capacity | Effect |
| --- | --- |
| **Agent** | Seller can use the deposit immediately on receipt. |
| **Stakeholder** | Held until completion. Default position under the Standard Conditions. |

A seller with a related purchase can use the deposit received on their sale **as a deposit on their purchase** — but only for that purpose.

Paid by solicitor's client account cheque, BACS, or telegraphic transfer.

### Mortgage offers — satisfy conditions before exchange

Once exchanged, both parties are legally bound. Any unsatisfied condition in the mortgage offer is a problem the buyer carries:

- Lender won't release funds.
- Completion delayed or fails.
- Buyer in breach of contract; penalties apply.

If you can't satisfy a condition before exchange, be **confident it will be satisfied before completion** — otherwise don't exchange.

### Before exchange — buyer's checklist

- Financial arrangements (mortgage offer) in place.
- Reported to client on transaction, title, easements, restrictions, mortgage offer conditions, search results.
- Holding the deposit.
- Satisfied with searches, enquiry replies, title queries, survey.
- Satisfied that title is good and marketable.
- Buildings insurance in place from exchange (or will be).
- Holding signed contract.
- Can comply with CLC Code of Conduct.
- AML satisfied.
- Completion date agreed.

### Before exchange — seller's checklist

- Up-to-date redemption statements held.
- Signed contract held.
- Completion date agreed.
- Client instructions on deposit acceptable on exchange.
- AML satisfied; ID held.

### The three methods of exchange

| Method | How |
| --- | --- |
| **In person** | Conveyancers meet, typically at seller's office; physically exchange contracts and the deposit cheque. Safest, rarely used. |
| **By post** | Buyer's conveyancer posts buyer's contract and deposit to seller's conveyancer, who dates both and posts back the seller's contract. **Not recommended for linked transactions** — risk of a party changing their mind in the time lag. |
| **By telephone** | Most common method today. Exchange occurs the moment the conveyancers agree contracts are exchanged on the call. Governed by Law Society formulae. |

### The Law Society telephone exchange formulae

Used because they involve professional undertakings — only between solicitors and/or licensed conveyancers.

| Formula | Used when |
| --- | --- |
| **A** | One party's conveyancer holds **both** signed contracts and the deposit. |
| **B** | Each conveyancer holds their **own** client's signed contract (buyer's conveyancer holds deposit). |
| **C** | A chain of transactions where deposit funds are sent directly to another firm further up the chain. |

After exchange, **confirm in writing the same day** — memorandum quoting formula, time/date of exchange, completion date, names of conveyancers involved.

### Buildings insurance

Under the Standard Conditions of Sale, **risk passes to the buyer on exchange**. The buyer must therefore have buildings insurance in place from exchange. Failure to advise = negligence if the client suffers loss.

The seller should **maintain their existing insurance until completion** in case damage occurs and the buyer defaults.

### Effects of exchange

The moment there's a valid contract:

- Beneficial ownership passes to the buyer.
- Buyer bears the risk of loss or damage (subject to the seller's duty to look after the property until completion).
- Seller acquires a lien over the property for the balance of the purchase money.
- Seller retains the legal estate as a qualified trustee for the buyer until the purchase deed is executed.

## Edge cases & gotchas

- **Don't exchange while a mortgage offer condition is unsatisfied** unless you're confident it will be by completion. Once exchanged the buyer carries the risk of non-compliance.
- **Buildings insurance from exchange** is on the buyer (Standard Conditions). Easy to forget on leasehold flats where the freeholder/management company insures — confirm which applies.
- **Telephone exchange involves undertakings** — only solicitors and licensed conveyancers. Don't let unqualified staff exchange.
- **Formula B with no deposit yet held** is a quick way to expose your firm to an undertaking you can't satisfy. Hold the deposit before agreeing exchange.

## Related guides

- [Training — After exchange (Session 7)](/crm/guides/legal/training-after-exchange)
- [Authority to exchange — scripts](/crm/guides/legal/authority-to-exchange)
- [Sale process](/crm/guides/legal/sale-process)
- [Purchase process](/crm/guides/legal/purchase-process)
`,
    },
    {
        product: "legal",
        slug: "escalating-ground-rent",
        title: "Escalating ground rent — when a long lease becomes an AST",
        one_line_description: "Why a long lease with ground rent over £250 (£1,000 in London) becomes an assured shorthold tenancy, and what to do about it.",
        category: "legal-practice",
        order: 62,
        last_screenshotted_at: "2026-05-29",
        tags: ["leasehold","ground-rent","ast","housing-act-1988","deed-of-variation","indemnity"],
        content_hash: "a3f0e49dd4bcd81f2b836c81f16c4527153f4ae0e34148e0960a27e7daffa04f",
        body_markdown: `## What it is

A long lease — 99+ years — with a ground rent **over £250 per annum** (or **over £1,000 in London**) falls within the Housing Act 1988 and is treated as an **assured shorthold tenancy** (AST). That sounds technical; the consequence is brutal: if the tenant falls three months behind on rent, the court has **no discretion** — it must order forfeiture, and both the homeowner and the lender lose everything.

This is **process knowledge, not currently driven by the case engine**. Title checks may flag the rent level, but the conveyancer makes the call on how to mitigate.

Note: the Renters' Rights Act 2025 has reduced this risk for many long leases — see [Mortgagee protection clauses](/crm/guides/legal/mortgagee-protection-and-renters-rights-act) — but ground-rent escalation in some leases still creates concerns that need addressing.

## When you'd use it

- Buying, remortgaging, or transferring a leasehold property.
- The ground rent is currently over £250 (or £1,000 London), or an escalation/review clause will take it above that level during the term.
- The lease has a forfeiture clause and there's a mortgage on the property.

## Walkthrough

### When the AST trap bites

| Ground rent | Status |
| --- | --- |
| Peppercorn / nominal (red rose, pint of Guinness, etc.) | No issue. |
| Fixed at £250 or less (London: £1,000 or less) | No issue. |
| Fixed at £250+ (London £1,000+) **for the whole lease** | AST applies. |
| Starts below £250 but escalates above during the term | AST applies **from the moment the rent exceeds the threshold**. |

This does not apply to tenants/property owners who are **limited companies** — but for individual homeowners with a mortgage, the AST consequence is real.

### Why this is dangerous

- **Forfeiture is mandatory** in the court if rent is 3+ months in arrears and overdue by 3+ months under Schedule 2 of the Housing Act 1988. The court has no discretion to grant relief.
- The **homeowner loses the property with no compensation**.
- The **lender loses its security and the whole of its mortgage**.
- The lessor has **no obligation to give the lessee first refusal** on the freehold if the long lease has become an AST.
- Properties with AST-converted leases are **worth less** than equivalent long leases that haven't converted.

**Example.** Ground rent £800 per year payable in two £400 instalments on 1 January and 1 July. Forfeiture hearing in April with the January £400 unpaid → the court must order forfeiture.

### What to do when you spot it

1. **Write to your client** explaining the situation and the risks to them and any current or future lender.
2. **Cash buyer with no mortgage?** Get them to sign a **disclaimer** confirming they understand the risks and will not hold the firm liable.
3. **Buyer with a mortgage?** Notify the lender.
4. **Push for a deed of variation** to bring the ground rent into a safe category (see options below).

### Acceptable ground-rent shapes (deed of variation targets)

| Option | What it means |
| --- | --- |
| Cap at £250 forever | Rent never exceeds £250. |
| £250 fixed for the entire term | No escalation at all. |
| £200 per annum reviewed every 20 years in line with RPI | Slow, predictable indexed growth. |
| Cap at 0.1% of the property value | Stays proportionate. |

### Sample mortgagee protection wording (if you get a variation)

When negotiating a variation, push for wording along these lines:

> Before re-entering the Property pursuant to this clause, or commencing any proceedings for forfeiture of this lease, the Landlord shall:
>
> - give notice of the breach complained of to any mortgagee of this lease of whom the Landlord has received notice; and
> - if the mortgagee confirms in writing to the Landlord within 28 days (or such longer time as may be reasonable) that it will remedy the breach, this clause shall include any breach of the grounds contained within Schedule 2 of the Housing Act 1988.

### If the lease cannot be varied

The lender may ask the landlord to **enter into an agreement to serve notice on the lender** if the property is to be repossessed under any Schedule 2 ground. Push for this as a fallback.

## Edge cases & gotchas

- **The threshold trips on the escalation, not just the starting rent.** A lease starting at £200 with RPI doubling every 10 years will breach £250 at some point — that's the moment it becomes an AST.
- **Limited-company owners are safe** from the AST consequence but most homeowners aren't.
- **The Renters' Rights Act 2025** removed the assured-tenancy treatment for long residential leases over 21 years. Pair this guide with [Mortgagee protection clauses](/crm/guides/legal/mortgagee-protection-and-renters-rights-act) to work out the lender's current actual exposure.
- **Disclaimers don't help the cash buyer if they later remortgage** — a future lender will refuse the title.

## Related guides

- [Mortgagee protection clauses and the Renters' Rights Act 2025](/crm/guides/legal/mortgagee-protection-and-renters-rights-act)
- [Sale process](/crm/guides/legal/sale-process)
- [Purchase process](/crm/guides/legal/purchase-process)
`,
    },
    {
        product: "legal",
        slug: "companies-house-charge",
        title: "Registering a charge at Companies House (MR01)",
        one_line_description: "Step-by-step for filing a company mortgage charge via Companies House WebFiling within the 21-day deadline.",
        category: "legal-practice",
        order: 64,
        last_screenshotted_at: "2026-05-29",
        tags: ["companies-house","mr01","charge","webfiling","corporate-borrower"],
        content_hash: "5696ff38e1829f08109b14b153d61d441ea8e3236b8bdb01a834aca179bc90a7",
        body_markdown: `## What it is

When a company borrows secured against property, the charge must be registered at **Companies House** within **21 days** of creation using **Form MR01** via WebFiling. Miss the deadline and the charge is void against a liquidator, administrator, or other creditor — the lender loses its priority.

This is **process knowledge, not currently driven by the case engine**. The roadblocks won't fire an MR01 item — it's a post-completion duty owned by the conveyancer.

## When you'd use it

- A corporate borrower has just completed and the lender's charge needs registering at Companies House.
- Buy-to-let or commercial purchase where the buyer is a limited company.
- Refinance of a company-owned property with a new lender.

## Walkthrough

### Before you start, gather

- **Company number** (from Companies House).
- **Authentication code** for that company's filing account.
- **Lender (chargee) details** — full name, address.
- **Secured amount or obligations** — the principal sum or the description of secured obligations.
- **Certified copy of the charge document** (executed mortgage deed).

### Filing steps

1. Log in to **Companies House WebFiling**.
2. Enter the company number and authentication code.
3. Select **Register a charge**.
4. Enter the **date of charge** (the date the mortgage deed was completed).
5. Enter the **lender (chargee) details**.
6. Describe the charge — fixed, floating, all assets, or specific property.
7. Enter the **secured amount or description of obligations**.
8. Upload the **certified copy of the charge document** where the system requires it.
9. Review every field carefully.
10. Pay the **£14 filing fee** and submit.
11. Save the **confirmation and receipt** to the case file.
12. Check the public Companies House register entry for the company to confirm the charge has been registered.

### The 21-day deadline

The charge must be **registered within 21 days of creation**. The clock starts on the date of completion of the mortgage deed.

Miss it and you cannot file late as of right — you'd need a court order to extend, which is expensive and uncertain.

## Edge cases & gotchas

- **Do not guess authentication codes.** Get them in advance from the company's secretary or director — guessing locks the account.
- **The 21 days is firm.** Diarise it the moment you exchange or as soon as completion is in sight.
- **The charge document must be a certified copy** — not the original, and not an unsigned draft.
- **A separate Land Registry application** (AP1 + the mortgage deed) is still required for the property itself. The MR01 is in addition, not instead of.

## Related guides

- [Purchase process](/crm/guides/legal/purchase-process)
- [Remortgage process](/crm/guides/legal/remortgage-process)
- [Sale process](/crm/guides/legal/sale-process)
`,
    },
    {
        product: "legal",
        slug: "splitting-land-into-plots",
        title: "Splitting land into two plots — Transfer of Part",
        one_line_description: "What's involved when an owner wants to divide a registered title into two separate plots at HM Land Registry.",
        category: "legal-practice",
        order: 66,
        last_screenshotted_at: "2026-05-29",
        tags: ["transfer-of-part","tp1","land-registry","plans","new-titles"],
        content_hash: "4b923ef8dec2178b9655dbfd5ebe3f62cc3dfcf15579a234258d5bdc2f1581aa",
        body_markdown: `## What it is

A **Transfer of Part** (TP1) is what you use when an owner wants to carve a single registered title into two separate plots — typically after inheriting land that they want to split between family members, or selling off part of a garden. The Land Registry creates new title numbers for each plot.

This is **process knowledge, not currently driven by the case engine**. The roadblocks don't currently model a TP1 — the conveyancer reasons through it as a standard transfer with plan complications.

## When you'd use it

- A client has inherited land and wants to divide it between beneficiaries.
- An owner wants to sell part of their garden to a neighbour.
- A developer is splitting a plot for a building project.

## Walkthrough

### Step 1 — Prepare a Land-Registry-compliant plan

You need a clear, scaled plan showing exactly how the land is being divided and which part goes to whom. Land Registry standards require:

- **Clear boundaries** with measurements where possible.
- **Each new plot marked** with colours or labels.
- **A north point and scale**.

If the client doesn't have a compliant plan, instruct a surveyor who specialises in Land Registry plans. The **RICS Find a Surveyor** directory is the standard starting point — search by postcode under "Land and measured surveys".

### Step 2 — Draft the legal documents

The **TP1** sets out:

- Who is transferring the land.
- Who is receiving each part.
- Any **rights or restrictions** — access routes, shared driveways, mutual covenants for boundary maintenance.

All parties sign the TP1.

### Step 3 — Submit to HM Land Registry

The application goes in with:

- The TP1.
- The Land Registry-compliant plan.
- Supporting evidence (death certificates, Grant of Probate, etc. where the original owner is deceased).

Land Registry registers the transfer and **creates new title numbers** for each plot. They update their records to show the land now exists as two separate legal titles.

### Step 4 — Pay the Land Registry fee

Fee depends on the value of the land being transferred. Confirm with the client before submitting.

## Edge cases & gotchas

- **A non-compliant plan will get the application rejected.** Don't try to draw the plan yourself — instruct a surveyor.
- **Easements and covenants need spelling out** in the TP1 — access, drainage, shared maintenance. Easy to forget on a friendly family split, hard to fix later.
- **Mortgage on the original title** — the lender must consent to the sale or transfer of part. Don't proceed without consent.
- **Stamp Duty Land Tax** may apply depending on the consideration. A gift or court-ordered split has different SDLT treatment from a sale — see [Transfer of equity](/crm/guides/legal/transfer-of-equity-process) for the decision tree.

## Related guides

- [Transfer of equity — the process](/crm/guides/legal/transfer-of-equity-process)
- [Sale process](/crm/guides/legal/sale-process)
- [Land Registry register entries — catalogue](/crm/guides/legal/title-register-entries)
`,
    },
    {
        product: "legal",
        slug: "failed-environmental-search",
        title: "Failed environmental search — contaminated land",
        one_line_description: "How to respond when an environmental search flags potential contamination and the seller's solicitor sends planning documents as evidence.",
        category: "legal-practice",
        order: 68,
        last_screenshotted_at: "2026-05-29",
        tags: ["environmental-search","contaminated-land","searches","indemnity","planning-condition"],
        content_hash: "e64b80e7b72f03b2d6f16f3a8dd1a559360850c41e40269b22dec3ac384ded1b",
        body_markdown: `## What it is

An environmental search has come back flagging potential contamination. The seller's solicitor has responded with planning permissions, building regulations approvals, and an assumption that everything was discharged because the development went ahead. **That response, on its own, is not enough to clear the search failure.**

This is **process knowledge, not currently driven by the case engine**. The Title Check / Contract Ready roadblocks will flag a missing or failed environmental search but won't tell you how to respond — that's on the conveyancer.

## When you'd use it

- An environmental search has identified potential contamination on or near the property.
- The seller's side has responded with planning history and you're working out whether to accept it.
- You're advising a client on whether to proceed, insure, or walk away.

## Walkthrough

### Why a planning response is not enough

- **Planning permission** focuses on development control — not on confirming the land is uncontaminated.
- **Building Regulations approval** confirms compliance with construction standards — not environmental safety.
- A discharged planning condition does **not** evidence that the land is free from designation under Part IIA of the **Environmental Protection Act 1990**.

In short: development happened, but no formal evidence has been provided that remediation was carried out or that the land isn't currently designated as contaminated.

### What to ask for

| Document | What it proves |
| --- | --- |
| Formal **discharge notice** from the local authority for the relevant planning condition | The condition was actually signed off, not just assumed discharged. |
| **Contamination or remediation reports** from planning or construction | Specific evidence of what was found and what was done about it. |
| Confirmation from the seller whether the site has been investigated or designated under environmental legislation | Direct yes/no on the Part IIA position. |

### Independent assessment

If the documentary response is thin, escalate to:

- An **environmental consultant** to review the search findings and assess risk.
- A **professional opinion from the search provider** — they will often give a follow-up assessment for a small additional fee.
- The **indemnity underwriter** — they will tell you what they would and wouldn't cover and on what terms.

### Indemnity insurance — proceed with care

Contaminated land indemnity policies are available, but they routinely **exclude known risks**. If the search has already identified the issue, a policy may not cover it. Don't sell indemnity to the client as a fix without checking the actual policy wording for what's excluded.

### What to say to the client

Reassure them that a flagged environmental search is **not uncommon** but needs proper assessment. Failing to investigate could leave them with remediation liabilities under Part IIA — or with a property they can't resell because the next buyer's solicitor will spot the same thing.

Frame the choice as:

- **Get the missing documents** — the cleanest path; everyone moves on.
- **Get an independent assessment** — slower but gives a defensible answer.
- **Indemnity insurance, if the underwriter will cover it** — pragmatic but read the exclusions.
- **Walk away or renegotiate** — viable if the seller won't engage.

## Edge cases & gotchas

- **A planning condition that "must have been discharged because the development was built" is not evidence of discharge.** Local authority records are the only proof.
- **Indemnity policies for known contamination are often unhelpful** — the exclusion eats the cover. Don't dress one up to the client as a fix when it isn't.
- **Lender requirements** — most lenders will refuse the case if contamination is unresolved. Check the **UK Finance Handbook** Part 2 entry before going far.
- **Document the decision trail** — the client's instruction to proceed (or not), the basis on which you proceeded, the steps you took. This is the file note that protects the firm.

## Related guides

- [Title check — what we flag and why](/crm/guides/legal/title-register-entries)
- [Purchase process](/crm/guides/legal/purchase-process)
- [Sale process](/crm/guides/legal/sale-process)
`,
    },
    {
        product: "legal",
        slug: "purchase-enquiries",
        title: "Purchase enquiries — standard wording",
        one_line_description: "The catalogue of standard enquiries we raise with the seller's solicitors on a purchase.",
        category: "legal-practice",
        order: 70,
        last_screenshotted_at: "2026-05-29",
        tags: ["purchase","enquiries","building-safety","new-build","indemnity"],
        content_hash: "e223848515229843ac0553e5d423adfe2bef76a90eb569d1e1d5caa6431123d0",
        body_markdown: `## What it is

The standard set of enquiries we raise with the seller's solicitors on a purchase. Use this as a reference when drafting your enquiry letter — pick the relevant items, leave the rest. Includes the standard responses we give to common pushbacks.

## When you'd use it

- You're drafting the enquiry letter after receiving the draft contract papers.
- You've spotted an issue in the title / property info forms and want the standard wording.
- The seller's side has pushed back on something and you need the standard response.

## Walkthrough

### Opening

> *"Thank you for your letter enclosing the contract papers. We have now had the opportunity of considering the papers provided."*

**Contract** — *"We approve the contract as amended, and look forward to receiving the engrossment."*

**Transfer Deed and Requisitions** — *"Please find enclosed the Transfer Deed for your approval. If you are happy with the same, please use it as your engrossment. We also enclose the Completion Information and Undertakings Form for your completion and return."*

### Standard enquiries

1. **Boundaries** — Please provide the seller with a copy of the title plan and ask them to confirm whether the boundaries outlined on it are in accordance with the property's physical boundaries on the ground, and there is no additional land which should be included as part of the sale.

2. **Restrictive covenants** — Please confirm that you have specifically advised your client about all of the restrictive covenants which affect the property and confirm whether you or they are aware of any past, present, or potential future breaches.

3. **Rights of way / easements** — Please confirm that the seller has been able to exercise their rights set out in the Transfer / Conveyance throughout their ownership without obstruction or hindrance.

4. **Heating service** — Please provide a copy of the heating service inspection report referenced in point 12.3(d) of the Property Information Form.

5. **Mortgagee protection (leasehold)** — The lease does not provide a mortgagee protection provision in the event of re-entry. Please provide a Deed of Variation or an Indemnity Insurance in respect of the same. Where an Indemnity Insurance is to be provided, please obtain the seller's confirmation of any Statements of Fact / Assumptions to the proposed Policy.

6. **Missing deed** — Please confirm that at their own expense, the seller will obtain missing-deed indemnity insurance with regards to entry XXX of the XXX Register.

7. **Executor without capacity** — We note that the seller is acting as executor of the estate, however we understand that the executor has lost mental capacity and that a lasting power of attorney is in place. **An attorney appointed under a lasting power of attorney cannot act in the capacity of executor or personal representative.** To enable us to proceed, please confirm and provide evidence of the basis on which the estate is currently being administered, in particular:
   - Whether there is more than one executor and, if so, whether the remaining executor(s) are acting; or
   - If the incapacitated executor was the sole executor, whether an application has been made to the court for the appointment of a substitute personal representative (for example under section 50 Administration of Justice Act 1985), and whether a court order has been obtained; and
   - Confirmation of who currently has legal authority to sell and transfer the property on behalf of the estate.

   Please also confirm when you anticipate being able to provide documentary evidence of the above, **as this is a prerequisite to exchange**.

8. **Rentcharge entry** — Has the seller been called upon to make any payments in respect of entry C:1 of the title? If so, please provide further details to include to whom the rent is paid, how the payment is made, and evidence that the payment has been paid up to date. If this is not possible, please explain why and confirm that you will at your client's own expense put in hand a suitable indemnity policy which will be in effect on or before completion.

9. **Conservatory access** — With regards to the conservatory added to the property, how do the sellers gain access from the house to the addition — was a walkthrough created from the property, or is access gained via the back door?

10. **Conservatory paperwork** — Please ask the seller to complete the Conservatory Questionnaire.

11. **Conservatory guarantees** — Please provide any guarantees in respect of the Conservatory.

12. **Solar panels** — Please ask the seller to complete the enclosed Solar Panel Enquiries Form.

13. **Occupier signing the contract** — Please confirm that the occupier will sign the contract.

14. **Criminal activity** — In view of the case of *Sykes v Taylor-Rose 2004 EWCA Civ 299*, have there been any crimes committed at the property that you or the seller may be aware of?

15. **Nearby development** — There appears to be a large open space within the vicinity of the property. Does the seller know if there is any intention of developing this area?

16. **Services not disconnected** — Please confirm that the services have not been disconnected and will not be prior to completion.

17. **Water and sewerage charging** — What is the current basis for charging for sewerage and water services at the property? Our search result is silent on this point.

18. **Sewer near building** — Our drainage search indicates that there is a public sewer within 100 feet of a building within the boundaries of the dwelling. Please ask the sellers to confirm that to their knowledge this has not been built over.

19. **Roof renewal** — Since 1 January 2006, has more than 25% of the roof at the property been renewed or replaced? If so, please supply Building Regulations from the Local Authority.

20. **Impermeable garden surface** — Since 1 October 2008, has any part of the garden been covered by more than 5 square metres of impermeable surface such as tarmac or impermeable concrete? If so, please provide the requisite planning permission.

21. **Decking height** — When was the decking installed at the property, is it over 30 cm high, and does it cover more than 50% of the garden?

22. **TA10 attached to contract** — Please confirm that you will attach a copy of the TA10 to your client's part of the contract.

23. **Sewer within boundary** — We note from our drainage search that there is a sewer within the property boundary. We enclose a copy of the map and would be grateful if you can confirm that this has not been built over. If it has, please provide the build-over agreement.

24. **Surface water not draining to public drain** — Please ask the seller to answer:
    - Does the property have a soakaway?
    - If so, where is it located?
    - Have there been any instances of flooding as a result?
    - What measures have been put in place to protect the property from flooding because of this issue?

25. **CIL** — Our Local Search result has revealed that the property is subject to a Community Infrastructure Levy. Please ask the seller(s) whether they have had to make any financial contributions and obtain confirmation from the Local Authority that there are no outstanding obligations.

26. **Failed environmental search** — Our environmental search result has not received a pass certificate. Please provide further information to enable us to forward to the search provider to overturn the result to a pass. If you are unable to provide further evidence, we will require an allowance of **£160.00** from the purchase price to cover the cost of the necessary environmental report. We attach a draft indemnity policy for the same.

27. **Building Safety Act remediation** — Please provide confirmation as to whether the building has been or will be remediated under the Building Safety Act 2022.

28. **Leaseholder Deed of Certificate** — Please provide a copy of any executed leaseholder Deed of Certificate (in the form set out in the Building Safety (Leaseholder Protections) (England) Regulations 2022) and confirmation that it has been submitted by the relevant Leaseholder to the Landlord.

29. **Landlord's Certificates** — Please provide a copy of the Landlord's Certificates, signed by the Landlord in the form set out in the Building Safety (Leaseholder Protections) (England) Regulations 2022.

### New-build enquiries

1. Long-stop date to be agreed as part of the contract.
2. Snagging procedure and timescales for insertion into the contract.
3. Developer's duty to notify of any pre-exchange changes — confirm you've advised them and will notify us of any changes you become aware of.
4. White-goods guarantees left at the property on completion.
5. Estimated build completion date.
6. Services plan.
7. NHBC dealing with full building regulations and final certificates.
8. Management company membership: (a) is there a membership application? (b) is the client required to become a director?
9. NHBC authentication code.
10. Connection of mains services on completion — seller or buyer?
11. Gas / electric supplier names and addresses on completion.
12. Burglar alarms / security devices: guarantees, maintenance, instruction manual, access code.
13. Land Registry approval of the Transfer form — date of approval.
14. Properties currently occupied within the development.
15. Section 106 agreements: (a) legal and financial conditions discharged? (b) not enforceable against owners / occupiers / tenants?
16. Section 38 Agreement in place?
17. Seller meets the cost of service charge for void units.

### AML / source of funds statement

> *"We are proceeding on the basis that you have undertaken anti-money-laundering verification in respect of your client, using the risk-based approach in accordance with the current guidance for the legal profession, and the documentation provided establishes a link between your client and the property. We further expect that in accordance with the Law Society's Conveyancing Protocol, you will notify us prior to completion, if you receive instructions to remit the net proceeds of sale, outside the jurisdiction."*

### Building Safety Act follow-ups

- Confirmation as to whether the building has been or will be remediated under the Building Safety Act 2022. — *"The building will be remedied under the Building Safety Act 2022."*
- Please provide full details of the works to be done in accordance with the Act.
- I would further query why the Building Safety Act applies to this building if it is not more than 11 metres in height and less than 5 storeys.

### Standard close

> *"Please note that the buyer relies on the seller's solicitors' warranty that it does indeed act for the true owner of the property. We reserve the right to raise further enquiries and look forward to hearing from you."*

### Standard pushback responses

**Restrictive covenant breach — refusing indemnity**

> *"Please refer to Hepworth v Pickles 1893 in which the court recognised that where a breach has been allowed to occur without taking steps to enforce for 24 years, the covenant was not enforceable. Please also refer to the reading of the Limitation Act 1980 in which enforcement is probably not possible after 12 years. If you believe the case law is different, please advise and provide precedent. Due to the above, we will not provide an indemnity policy and therefore you should obtain one at your own expense if required."*

**Title plan boundaries pushback**

> *"Confirmed. However, Standard Condition 4.4 sets out what a seller needs to prove about the boundaries, and states that the seller need not prove the exact boundaries of the property or prove who owns fences, ditches, hedges or walls."*

## Edge cases & gotchas

- **Don't send all 29 enquiries on every file.** Pick the ones relevant to your specific title, leases, search results.
- **The executor-without-capacity enquiry is a prerequisite to exchange.** Don't let it slide.
- **Failed environmental search → £160 allowance** is the standard ask. Don't accept a number lower than the actual cost of remediation.
- **New-build enquiries are additive.** Run them on top of the standard 29, not instead.
- **Hepworth v Pickles** is the standard response to a restrictive-covenant indemnity request — keep it ready.

## Related guides

- [Purchase process](/crm/guides/legal/purchase-process)
- [Failed environmental search](/crm/guides/legal/failed-environmental-search)
- [Executor that loses capacity](/crm/guides/legal/executor-loses-capacity)
- [Mortgagee protection clauses + Renters' Rights Act](/crm/guides/legal/mortgagee-protection-renters-rights)
`,
    },
    {
        product: "legal",
        slug: "remortgage-process",
        title: "Remortgage process",
        one_line_description: "End-to-end remortgage flow — from initial docs to AP1 — anchored to the roadblocks the engine fires.",
        category: "legal-practice",
        order: 70,
        last_screenshotted_at: "2026-05-29",
        tags: ["remortgage","redemption","mortgage-offer","ap1","completion"],
        content_hash: "f10b857b376abdf8547496f27d75a673f4fcdf1dc829cbbc9e924192690ff495",
        body_markdown: `## What it is

A remortgage replaces one lender's charge on a property with another's. No buyer, no seller — but the title still goes through redemption of the old charge, registration of the new charge, and the standard pre-completion gates. Pace tends to be faster than a sale or purchase because there's no chain.

**System view** — a remortgage doesn't fire **Contract Ready Pack** or **Exchange Ready** because there's no contract or exchange of contracts. **Client Verification** fires for the borrower(s). **Title Checks** and **Pre-Completion Ready** are the load-bearing roadblocks. **Post-Completion** then fires for AP1 / SDLT / Companies House work.

## When you'd use it

- You're running a standalone remortgage instructed by the borrower.
- A purchase has fallen through and the client wants to remortgage existing property instead.
- A buy-to-let or company-owned property is being refinanced.

## Walkthrough

### How the system sees a remortgage

| Roadblock | Gates | Clears when |
| --- | --- | --- |
| **Client Verification** | Anything client-side. | ID checks pass, verification call done, due diligence complete. |
| **Title Checks** | Pre-completion. | Office copies in, title-check pass, restrictions dealt with. |
| **Pre-Completion Ready** | Completion-day prerequisites. | Redemption statement in, mortgage funds requested, mortgage deed signed where the lender wants one, K16 and OS1 done. |
| **Post-Completion** | After completion. | AP1 submitted, registration received, account reconciled. |

### Stage 1 — Open the file and verify the client

- Send initial documents to the client.
- Receive and log the client information form.
- Request **Thirdfort** ID verification; chase until ID + Thirdfort received.
- Complete client due diligence.
- Update the client.

### Stage 2 — Title and existing charge

- **Request office copies** and download the title.
- **Request the redemption statement** from the existing lender. If the property has a **Help to Buy charge**, you also need Help to Buy's consent to redeem — completion can't happen without it.
- If leasehold, contact the landlord or management company and confirm **notice fees**. Link the company on the **Details** tab → **Management companies** block (reuse one we've used before — its consent-to-remortgage instructions and fees are already recorded), and mark **consent obtained** (or *not required*) once sorted; that clears the pre-completion checklist item.
- Check the title for **restrictions** that need to be dealt with.
- Complete the title check.

### Stage 3 — Redemption statement check

When the redemption statement comes back:

- Check for an **Early Redemption Penalty**. If the penalty expires soon and the saving is material, **arrange completion for after expiry**.
- Send a copy to the client.

### Stage 4 — Mortgage offer

- Receive the mortgage offer.
- Refer any issues to the lender (e.g. lease term issues for short leases).
- Send the **mortgage report** to the client with a **draft completion statement**.
- Request a copy of the buildings insurance.

### Stage 5 — Signed documents back

- Receive the **mortgage confirmation form** and, where one is needed, the **signed mortgage deed**.
- Not every lender wants a new deed. **Some keep the existing charge** and release the departing borrower themselves. Nationwide, Santander and Virgin Money all do it, and the offer letter says so. Go to the Details tab, Mortgage offer block, and **answer No to "Mortgage offer lender requires a new mortgage deed"**. **The deed then stops being asked for.** No new deed tiles get raised. The pre-completion and post-completion deed items stop firing. Any deed already on the file is left out of the Land Registry application.

### Stage 6 — Pre-completion (Pre-Completion Ready)

- Review the file and set up for completion.
- Request **mortgage funds** from the new lender.
- Complete **K16** (bankruptcy search against the borrower) and **OS1** (Land Registry priority search).
- File signed off by **team leader or manager**.

### Stage 7 — Completion

- Complete the remortgage on the agreed date.
- Send completion letters to the client and old lender.

### Stage 8 — Post-completion

- Submit **AP1** to register the new charge and discharge the old one.
- Where the borrower is a company, file **MR01** at Companies House within 21 days — see [Companies House charge](/crm/guides/legal/companies-house-charge).
- Receive the completed registration; send a copy to the client and new lender.
- Reconcile the account.
- Close the file.

### Accounts completion pack — 48 hours before completion

The pack to accounts must include:

- Completion statement.
- Receipt for mortgage advance from new lender.
- Redemption statement.
- **CHAPS slip** for mortgage redemption to the old lender.
- Slips for any indemnity policies or leasehold fees.
- Chit for balance to the client.
- The bill.

### Wallets to keep on the file

| Wallet | Contents |
| --- | --- |
| **Risk Review** | Checklist, buildings insurance, landlord notice fees, indemnity policies on risk, any certificates of consent. |
| **Signed Documents** | Mortgage deed where one was required, mortgage report confirmation form, copy of AST if buy-to-let. |
| **ID** | Client information form, client due diligence form / ID checklist, copy client ID including Thirdfort. |
| **Title Bundle** | Office copies, title plan, any documents referred to in office copies. |

## Edge cases & gotchas

- **Help to Buy consent is non-negotiable** for a property with a Help to Buy charge — you cannot complete without it. Start the request early.
- **Early Redemption Penalty close to expiry** — push the completion date past it if the maths works.
- **Short lease terms** — refer to the lender before getting too far; some refuse below 80 unexpired years.
- **Don't file something else under the deed tile to clear the checklist.** Answer the deed question "No" instead. A document mis-filed as a mortgage deed can be sent to Land Registry as a charge on a case that isn't registering one. It won't stand up to an audit either.
- **The roadblocks are the source of truth.** If you've done something but the roadblock won't clear, the underlying case field isn't where the engine expects.

## Related guides

- [Companies House charge](/crm/guides/legal/companies-house-charge)
- [Sale process](/crm/guides/legal/sale-process)
- [Purchase process](/crm/guides/legal/purchase-process)
- [Transfer of equity — the process](/crm/guides/legal/transfer-of-equity-process)
`,
    },
    {
        product: "legal",
        slug: "roadblock-contracts",
        title: "Roadblock: Contracts",
        one_line_description: "How to clear each item under the Contracts roadblock on a sale.",
        category: "roadblocks",
        order: 70,
        last_screenshotted_at: "2026-05-29",
        tags: ["roadblocks","contracts","sale","troubleshooting"],
        content_hash: "f1d5a2eb661d6a31ac9a24fef9d4be3d1d3b316daf196f050dd0f4f2b78cb6c1",
        body_markdown: `## What it is

Contracts is the sale-only roadblock that holds the contract-drafting milestones — the draft contract itself, the draft transfer (TR1 / TP1 / TP2 / TR2), the other-side conveyancer's details and verification, the property details on the case, the contract details (price, deposit, auction status), the estate agent's details, the auction completion deadline (where relevant), and the other-side client details.

It fires once we have an MOS (memorandum of sale) received on the case, or where the case has been marked as having no estate agent. It is **not authoriser-gated** but it **blocks exchange**.

## When you'd use this guide

- A sale case has Contracts open and you can't tell which clerical field is missing.
- A draft contract was uploaded but the matching item won't go green.
- You're trying to work out which items live here vs Contract Ready Pack.

## The checklist

### Draft contract created

A draft (or already-signed) contract document exists on the case.

- **Where to go**: **Documents** tab → *Draft contract* / *Signed contract* slot.

### Draft Transfer created

A draft or signed TR1 / TP1 / TP2 / TR2 exists on the case.

- **Where to go**: **Documents** tab → relevant transfer slot.

### Draft contract sent

Only fires once a draft contract exists. The *sent draft contract* milestone is recorded.

- **Where to go**: **Details** tab → **Milestones** block → *Draft contract sent* date.

### Other side conveyancer details added

All other-side conveyancer fields are filled in: organisation name, contact name, phone, address, email.

- **Where to go**: **Details** tab → **Other side** block → *Conveyancer* sub-section.

### Other side conveyancer verified

Only fires once the OS conveyancer details are added. The *SRA / CLC number* is filled in.

- **Where to go**: **Details** tab → **Other side** block → *SRA / CLC number*.

### Property details added

**Every** property on the case has every property-details field filled in: tenure, postcode, building name or number, street name, *is registered*, *transfer of*, title-guarantee, title-class — plus *title number* and *last transfer date* if the title is registered, and *affected by the Building Safety Act* on leasehold titles. The case-level **Is there a management company?** question must also be answered, either way.

- **Where to go**: **Details** tab → **Property** block → each property's full details form.
- **Common stuck-states**:
  - **One property is complete but a sibling isn't** — the item stays open until every title is filled in. The evidence under it names each title, so the incomplete one is the one to open.
  - **Every title looks complete but the item won't tick** — check the management company question. Leaving it unanswered also silently hides the LPE1 and FME1 items, so nothing prompts you for the pack.

### Contract details added

The case has *price*, *deposit according to contract*, and *auction status* (not *unknown*) all set.

- **Where to go**: **Details** tab → **Contract details** block.

### Estate agent details added

Either *no estate agent* is **yes**, or all four estate agent fields (organisation, contact name, phone, email) are filled in.

- **Where to go**: **Details** tab → **Estate agent** block.

### Add completion deadline

Auction cases only. A *completion deadline* date is set.

- **Where to go**: **Details** tab → **Contract details** block → *Completion deadline*.

### Add other side client details

Either:
1. All other-side company details are filled in (company name, number, building name or number, city, country, postcode, street) and no individual OS clients are added, **or**
2. At least one OS client exists with both a legal first and last name —
plus the *all other side clients added and right to buy/sell checked* field is ticked.

- **Where to go**: **Details** tab → **Other side** block → *Clients* sub-section.
- **Common stuck-states**:
  - **You added an OS client with no last name** — the engine requires both first and last legal name to count. Empty surname keeps the item open.
  - **You filled in the OS company details but also added an OS client** — the company-only branch requires zero OS clients. Either move to the individual branch (give the OS clients full legal names) or remove the OS clients.

## Edge cases & gotchas

- **Contracts only fires once we have an MOS received** — or once *no estate agent* is set. On a private sale with no agent, tick *no estate agent* to bring the roadblock to life.
- **Contracts blocks exchange.** Every applicable item must be green before Exchange Ready can close.
- **Contracts overlaps Contract Ready Pack but isn't the same thing.** Contract Ready Pack handles the protocol forms and search documents; Contracts handles the contract / transfer drafting and the case-level details the contract is built from.
- **The "verify OS conveyancer" item depends on the OS conveyancer details being added first.** Don't expect it to appear on day one.

## Related guides

- [Roadblocks: how they work](/crm/guides/legal/roadblocks-how-they-work)
- [Sale process](/crm/guides/legal/sale-process)
- [Roadblock: Contract Ready Pack](/crm/guides/legal/roadblock-contract-ready)
- [Roadblock: Exchange Ready](/crm/guides/legal/roadblock-exchange-ready)
- [Auction referrals](/crm/guides/legal/auction-referrals)
`,
    },
    {
        product: "legal",
        slug: "training-after-exchange",
        title: "Training — After exchange (Session 7)",
        one_line_description: "What needs doing between exchange and completion, the purchase deed forms, mortgage deeds, and pre-completion searches.",
        category: "training-sessions",
        order: 70,
        last_screenshotted_at: "2026-05-29",
        tags: ["training","post-exchange","transfer-deed","tr1","tp1","as1","pre-completion-searches","os1","k15","k16"],
        content_hash: "c627e192a64f8e89807cef1902431924e46f9d5d305e4123342b0f1ddbe7b386",
        body_markdown: `## What it is

The interval between exchange and completion — what to do, what can go wrong (death, bankruptcy, liquidation of either party), the right purchase-deed form for the situation, the mortgage deed, and the pre-completion searches.

This is **training material, not currently driven by the case engine**, but the **Pre-Completion Ready** roadblock encodes the operational side.

## When you'd use it

- A new joiner is learning what happens in the four weeks before completion.
- A client party has died or been declared bankrupt between exchange and completion and you need to know how the contract is affected.
- You're picking the right deed (TR1 vs TP1 vs AS1) for a transaction.

## Walkthrough

### What's left to do after exchange

Almost all proof of title is now done before exchange. After exchange the workload is operational:

- Confirm the completion date to the client.
- Diarise completion — plan a **pre-completion check at least 2 days before**.
- If telephone exchange, prepare the memorandum.
- Honour any undertakings given on exchange (e.g. dispatch contract and deposit cheque).
- Buildings insurance in place.
- **Acting for seller** — notify estate agent of completion date; request invoice; contact existing lender(s) for redemption statement calculated to completion day.
- **Acting for buyer** — register an Agreed Notice to protect the contract if appropriate; prepare the Transfer deed for approval; run pre-completion searches; request funds from lender; ensure client funds top-up arrives in good time.

Normal gap between exchange and completion: **four weeks**. Can be longer or shorter by agreement. **Simultaneous exchange and completion** is technically possible — avoid it where you can; it's stressful for everyone.

### Unusual situations between exchange and completion

**Death of a contracting party.** Benefit and burden pass to the deceased's PRs — they're bound to complete.

- **One co-seller dies, joint tenancy** — surviving co-seller proceeds on survivorship.
- **One co-seller dies, tenancy in common** — surviving co-owner appoints a second trustee.
- **Buyer dies** — PR steps into the buyer's shoes and is bound to complete. Lender will likely withdraw the mortgage offer → PR seeks finance elsewhere.
- **One co-buyer dies** — remaining co-buyer still bound; finances may need rearranging.

**Bankruptcy of seller.** Buyer is only affected if the bankruptcy entry appears on the official search or office copies.

- **Sole seller bankrupt** — legal estate passes to the **Trustee in Bankruptcy**. Deal only through the Trustee. Trustee usually proceeds with the sale (must obtain market value — sales at undervalue are off the table).
- **Co-seller bankrupt** — Trustee acquires only an **equitable** interest in the bankrupt's share; legal estate unaffected. Contract proceeds with the Trustee joining the purchase deed to consent to the sale of the bankrupt's beneficial interest.

**Bankruptcy of buyer.** Benefit of contract passes to the Trustee, who may complete or disclaim as onerous.

- **Sole buyer bankrupt** — usually disclaimed.
- **Co-buyer bankrupt** — equitable interest in the contract passes to the Trustee. Mortgage offer almost certainly vitiated; matter usually doesn't proceed.

**Appointment of a liquidator.**

- **Selling company in winding-up** — liquidator can complete and executes the purchase deed.
- **Buying company in winding-up** — liquidator has power to complete or disclaim; usually doesn't proceed (no funds).

### The purchase deed

Transfers the legal estate from seller to buyer. The transfer of a legal estate is **void unless by deed** (except for an **Assent**).

**Buyer's conveyancer** prepares the draft (in duplicate). Contents follow the contract. Draft → seller's conveyancer for approval (may be approved subject to amendments). Once approved, buyer's conveyancer engrosses and arranges client execution → sent to seller's conveyancer for seller execution → held until completion.

**Time limits:**

| Stage | Deadline |
| --- | --- |
| Draft submitted | At least **12 working days** before completion |
| Approval | Within **4 working days** |
| Engrossment with seller's conveyancer | **5 working days** before completion |

Execution must be valid — Trustee in Bankruptcy, liquidator, charity, or PR transactions need specific attestation clauses. Refer to the **Land Registration (Execution of Deeds) Rules**.

### Which purchase-deed form

| Form | When |
| --- | --- |
| **TR1** | Whole of a registered title (freehold or leasehold). |
| **TP1** | Part of an existing registered title. |
| **TR1 (whole) or TP1 (part)** | Unregistered land being bought. |
| **Conveyance** | Freehold unregistered land. |
| **Lease** | New lease being granted (registered or unregistered). |
| **Assignment** | Leasehold unregistered land. |
| **Assent (unregistered) or AS1 (registered)** | PRs transferring to a beneficiary under a Will or intestacy. |

### Mortgage deed

A legal mortgage must be made **by deed**. Most lenders provide their own form; alternatively the Land Registry's **CH1** can be used.

The buyer's conveyancer fills in the blanks and has the deed signed and witnessed. Must be properly executed. On completion, the mortgage deed is **dated the same date as the purchase deed**.

### Requisitions on title (completion request form)

Before completion, the buyer's conveyancer sends a requisitions on title / completion request form (in duplicate). It covers:

- Mortgages to be redeemed by the seller — must be listed, and the seller's conveyancer undertakes to discharge them and provide evidence of discharge after completion.
- Where completion will take place.
- How the keys are collected.
- How the balance is to be paid.

### Pre-completion searches

**Buyer:**

- When acting for buyer and lender together, apply for **one** Land Registry search in the **lender's name** only — the buyer gets the benefit of search priority.
- Company search if buying from a company — checks ability to sell still subsists.
- Send certificate of title to lender, request release of mortgage funds in time for completion.
- Hold balance purchase funds plus costs and disbursements.
- Hold validly executed mortgage deed and related papers.
- Replies to requisitions and completion information satisfactory.
- Engrossment purchase deed sent to seller's conveyancer.
- Broker arrangement fee on file?

The specific searches:

| Land type | Searches |
| --- | --- |
| **Registered** | **OS1** (whole) or **OS2** (part); **K16** bankruptcy search against buyer (if also acting for lender); company search if buying from a company. |
| **Unregistered** | **K15** full land charges search against past and present estate owners post-1925; **K16** bankruptcy against buyer (if also acting for lender); company search if buying from a company. |

**Seller:**

- Redemption figures calculated to the completion date for any mortgages on the title.
- Prepare **DS1** or **END1** for sending to the lender on completion with redemption funds.
- Comply with undertakings in the completion information form / correspondence.
- Leasehold — up-to-date ground rent and service charge receipts; completion statement to buyer's conveyancer with apportionments.
- Confirm you hold the executed Transfer and title documents.
- Prepare client's final completion statement.
- Note the seller's new address.

### The 2-day pre-completion check

Plan to check a file — purchase or sale — **at least 2 working days before completion**. The gap lets you deal with last-minute issues without delay.

## Edge cases & gotchas

- **Simultaneous exchange and completion** is allowed but is a stress generator. Push for a gap whenever possible.
- **A spouse-bankrupt co-buyer** vitiates the joint mortgage offer — the matter usually collapses.
- **The mortgage deed is dated the same date as the purchase deed** on completion — don't pre-date it.
- **OS1 in the lender's name** gives the buyer the benefit too — saves a search fee.
- **K15 against past and present estate owners** for unregistered land — easy to miss a past owner.

## Related guides

- [Training — Completion and post-completion (Session 8)](/crm/guides/legal/training-completion-and-post-completion)
- [Sale process](/crm/guides/legal/sale-process)
- [Purchase process](/crm/guides/legal/purchase-process)
- [Assent transfers](/crm/guides/legal/assent-transfers)
`,
    },
    {
        product: "legal",
        slug: "auction-referrals",
        title: "Auction referrals — Bamboo and Rocket / Sail Homes",
        one_line_description: "How to run auction conveyancing files for Bamboo and Rocket — pack, premium, deposit, completion timelines.",
        category: "legal-practice",
        order: 80,
        last_screenshotted_at: "2026-05-29",
        tags: ["auction","bamboo","rocket","sail-homes","conditional","traditional"],
        content_hash: "1344dd6cf914b4da9733b82d18467ebb28f47427b83bbaed1238e1193c2d7ab1",
        body_markdown: `## What it is

How conveyancing assistants handle auction referral matters. Covers both auction routes we work with — **Bamboo** (Nick Bailey) and **Rocket / Sail Homes** (Matt Toon).

## When you'd use it

- A new auction matter has come in and you need to know what to request from the client.
- You're preparing the auction pack and want to confirm what's in it.
- An auction sale has happened and you need the completion timeline.

## Walkthrough

### Bamboo auctions (Nick Bailey)

**Overview** — Bamboo handles auction sales in partnership with various estate agents.

**Client payment** — Request **£550** from the client at the outset. Covers the auction pack (**£350 + VAT**) and associated disbursements (environmental + indemnity costs). Funds must be received before the auction pack is ordered.

**Auction pack** contains:
- Martello Environmental Report
- Stewart Title No-Search Indemnity Policy
- **Fixed cost: £350 + VAT**

**Buyer premium** — always charged to the buyer.
- Standard: **3% + VAT**
- Minimum: **£5,000 + VAT**

**Deposit** — **Bamboo does not collect the deposit**. When sending the contract pack, you must request the deposit from the buyer's solicitor.

**Auction types**:
1. **Traditional** — contracts exchanged at the auction. Completion due within **20 working days**.
2. **Conditional** — buyer pays a reservation fee. Contracts exchanged within **28 calendar days**, completion within another **28 calendar days**.

### Rocket / Sail Homes auctions (Matt Toon)

**Overview** — Rocket manages auction cases for Sail Homes and related sellers.

**Client payment** — same as Bamboo: **£550** to cover the auction pack (£350 + VAT) and disbursements. Funds in before the pack is ordered.

**Auction pack** contains:
- Martello Environmental Report
- Stewart Title No-Search Indemnity Policy
- **Fixed cost: £350 + VAT**

**Buyer premium** — always charged to the buyer.
- **Sail Homes cases**: 3% + VAT, minimum £5,000 + VAT.
- **Non-Sail Homes auctions**: varies, typically 3% + VAT (min £5,000 + VAT) or 1.5% + VAT (min £2,500 + VAT).

**Deposit** — Rocket does not collect the deposit. Request from the buyer's solicitor when the contract pack is sent.

**Auction types** — same as Bamboo:
1. **Traditional** — exchanged at auction. Completion within **20 working days**.
2. **Conditional** — reservation fee. Exchange within **28 calendar days**, completion within another **28 calendar days**.

### Recording the auction on the case

**Set Auction status to yes** and the Contracts tab gains an Auction section. **Put the auction date in there** — Conveynext sends it on referral, so on a Bamboo or Rocket case it usually arrives already filled in. The same section records the reservation fee, who covers the seller's legal and auction fees, and whether the auction pack has been sent.

**A banner appears on the case Overview** once the auction is **less than a week away with the pack still not marked as sent**. It stays up until the pack goes out, so a missed pack cannot sit quietly on a file.

## Edge cases & gotchas

- **Funds in before the pack is ordered.** Don't order the auction pack against an unfunded ledger — both routes are clear on this.
- **Deposit is not the auction house's job.** Both Bamboo and Rocket leave deposit collection to you. Don't assume it's been done.
- **Sail Homes auctions through Rocket follow the Sail Homes premium scale** — 3% + VAT, min £5,000 + VAT. Don't apply the lower 1.5% schedule by mistake.
- **The auction date arrives from Conveynext, and you can overwrite it.** It lands on the case with the referral and nothing re-syncs it afterwards, so if the auction house moves the date, change it on the case yourself.
- **Traditional auction is the fast one** — 20 working days to completion is tight. Stage the post-auction work accordingly.

## Related guides

- [Sale process](/crm/guides/legal/sale-process)
- [Onboarding checklist](/crm/guides/legal/onboarding-checklist)
`,
    },
    {
        product: "legal",
        slug: "roadblock-exchange-ready",
        title: "Roadblock: Exchange Ready",
        one_line_description: "How to clear each item under the Exchange Ready roadblock on a sale or purchase.",
        category: "roadblocks",
        order: 80,
        last_screenshotted_at: "2026-05-29",
        tags: ["roadblocks","exchange-ready","sale","purchase","troubleshooting"],
        content_hash: "a51f64c31dbd46acd7e687e3295d582c52b7f26206c803f17c04cbea16937326",
        body_markdown: `## What it is

Exchange Ready is the authoriser-gated roadblock that stands between a sale or purchase and the moment we exchange contracts. **It appears once the case is under offer** — not at instruction — and bundles together the pre-exchange housekeeping the legacy *ready-for-exchange sign-off* used to check: enquiries, title freshness, the mortgage block, SDLT, bank accounts, charges, ledger fees, client authorisations, the report to client, plus the authoriser's review itself.

**Before an offer is accepted this roadblock does not exist on the case.** Most of its checklist depends on having a buyer — enquiries, the enquiries report, the signed contract, the report on the contract, buildings insurance — so opening it earlier only produced a sign-off request nobody could action.

This is the roadblock that **requires authoriser sign-off** — clearing every clerical item still leaves the senior review item, which only the authoriser can mark.

## When you'd use this guide

- A case is otherwise ready to exchange and you're trying to work out what's still red.
- An item won't go green even after you think the work is done.
- You're an authoriser preparing to sign a case off.
- You want to know which items live here vs on Pre-Completion Ready, Contracts, or Client Verification.

## The checklist

### All enquiries resolved or marked not relevant

Every row on the **Enquiries** tab is either *resolved* or *not relevant*.

- **Where to go**: **Enquiries** tab. Work through the open rows.
- **Common stuck-states**:
  - **An enquiry was forgotten on a substituted property** — replacing a title doesn't clear stale enquiries against the previous one. Mark them *not relevant*.

### Replies-to-enquiries report received

Purchase only. The *replies to enquiries report* document is uploaded and marked as received.

- **Where to go**: **Documents** tab → *Replies to enquiries report* slot. Upload and tick *received*.

### Non-standard undertakings approved (or marked as none)

Either *has non-standard undertakings* is set to **no**, or it's **yes** and the undertakings have been approved.

- **Where to go**: **Details** tab → **Undertakings** block.

### Risk grade green and recent

The most recent risk assessment on the case is *green*.

- **Where to go**: **Details** tab → **Risk Management** panel → run or open the latest risk assessment.
- **Common stuck-states**:
  - **A red or amber assessment from earlier hasn't been refreshed** — run a new assessment. The item reads the latest one only.

### Recent-last-transfer risk acknowledged for every title

For every registered title, either the last transfer was more than 6 months ago, or the *recent last transfer risk acknowledged* tick is set on that property.

- **Where to go**: **Details** tab → **Property** block → each property row → *Recent last transfer risk acknowledged* checkbox.

### All charges and title restrictions marked as added

Sale, transfer, and remortgage only — not purchase. The *add charges and title restrictions* job is marked done.

- **Where to go**: **Details** tab → **Charges and restrictions** block → *Job done* checkbox.

### Search report received

Non-sale cases with searches ordered. A *search report* document is uploaded and marked as received.

- **Where to go**: **Documents** tab → *Search report* slot.

### Leasehold report received

Purchase cases with at least one leasehold property. The *leasehold pack report* document is uploaded and received.

- **Where to go**: **Documents** tab → *Leasehold pack report* slot.

### Mortgage offer report received

Cases involving a mortgage. The *mortgage offer report* document is uploaded and received.

- **Where to go**: **Documents** tab → *Mortgage offer report* slot.

### Mortgage lender identified

Mortgage cases. The *mortgage lender* field is set to a real lender (anything other than *unknown*).

- **Where to go**: **Details** tab → **Mortgage** block → *Lender* dropdown.

### Mortgage offer added

Mortgage cases. The *mortgage offer date* is recorded.

- **Where to go**: **Details** tab → **Mortgage** block → *Offer date*.

### Mortgage offer review questions answered

Mortgage cases. Every question on the mortgage offer review is answered (offer date, advance amount, retention amount, term, interest rate, early redemption penalty, names correct, address correct, amount matches purchase price, tenure matches contract, lender requirements checked, buildings insurance, occupier consent, matters to refer — and if matters-to-refer is *yes*, the details field is filled in).

- **Where to go**: **Details** tab → **Mortgage** block → *Offer review* form.
- **Common stuck-states**:
  - **You ticked *matters to refer: yes* but left the details box empty** — the engine reads both. Fill in the details or set the question back to *no*.

### Mortgage offer document received (and not expired)

Mortgage cases. A *mortgage offer* document is uploaded and marked as received.

- **Where to go**: **Documents** tab → *Mortgage offer* slot.

### Mortgage retention shared with client

Only fires when the lender has a retention amount > 0. The *mortgage offer retention explained to client* tick is set.

- **Where to go**: **Details** tab → **Mortgage** block → *Retention explained to client* checkbox.

### Early repayment charge answered for every charge

Cases with at least one charge. Every charge has *has early repayment charge* answered (yes or no).

- **Where to go**: **Details** tab → **Charges** block → each charge row.

### Client notified of early repayment charge

Only fires for charges where *has early repayment charge* is *yes*. The *client notified* tick is set against each such charge.

- **Where to go**: **Details** tab → **Charges** block → charge row → *Client notified* checkbox.

### Bank accounts verified (or marked won't be used)

Every bank account on the case is either *approved* with *all clients agree with this bank account* set, or marked *not verified — won't be used*.

- **Where to go**: **Details** tab → **Bank accounts** block.
- **Common stuck-states**:
  - **An account is approved but no client-agree row is recorded** — both are needed. Capture the client confirmation.

### All charges have account numbers (non-purchase)

Non-purchase cases. Every charge row has a non-empty account number.

- **Where to go**: **Details** tab → **Charges** block → each charge → *Account number*.

### SDLT return uploaded or Compass referral set

Purchase / transfer cases with a price of £40,000 or more. Either the case is marked *referred to Compass*, or *Compass referral not required* plus a draft or signed SDLT return is uploaded and received, or the case is marked *No SDLT return is required for this transfer*.

- **Where to go**: **Details** tab → **SDLT** block.

### SDLT declaration signed by every client

Same gate (purchase / transfer £40,000+). Every client has signed the SDLT declaration, unless the case is marked *No SDLT return is required for this transfer*.

- **Where to go**: **Details** tab → **SDLT** block → declarations.

### SDLT added to ledger

Same gate. A *stamp duty land tax* line is on the **Ledger** tab.

- **Where to go**: **Ledger** tab → *Add entry* → *Stamp duty land tax*.

### Clients authorised exchange and complete

Every case — but **gated**. This item only appears once every other team and chased item on Exchange Ready is resolved (enquiries, title, mortgage block, SDLT, charges, ledger fees, the report to client). That's deliberate: we don't ask the client to authorise exchange before our prep and the documents we're chasing are in. Until then the item is hidden; once the rest goes green it surfaces, and you record the *clients authorise exchange and complete* milestone after the [authority-to-exchange](/crm/guides/legal/authority-to-exchange) call. An outstanding *client* item (e.g. a signed contract still to come back) does **not** hold it back — only our own prep does.

- **Where to go**: **Details** tab → **Milestones** block → *Clients authorise exchange and complete*.

### All clients added and right to buy/sell checked

Every case. Every client expected to be on the case has been added, and the *right to buy/sell checked* field has been confirmed. This used to sit on Client Verification during onboarding; it now lives here as a ready-for-exchange assurance, so the authoriser affirms the full client roster and their right to sell at sign-off.

- **Where to go**: **Details** tab → **People** block. Add any missing clients via *Add person*, then tick *Right to buy/sell checked* on the case-level field.
- **Common stuck-states**:
  - **You added a client but forgot to tick the checkbox** — the engine treats the two as separate signals. Both must be in place.

### Joint ownership held as answered

Purchase / transfer / remortgage with more than one client. The *joint ownership held as* dropdown is set to something other than *unknown* (joint tenants or tenants in common).

- **Where to go**: **Details** tab → **People** block → *Joint ownership held as*.

### Apportionment question answered

Cases with a management company. The *requires apportionment statement* question is answered.

- **Where to go**: **Details** tab → **Management company** block.

### Dual rep lender conflict approved (if required)

Dual-rep mortgage cases where the lender doesn't allow dual rep. The *dual rep lender conflict approved to continue* milestone is set.

- **Where to go**: **Details** tab → **Conflicts** block.

### Dual rep consent obtained from all our-side clients

Dual-rep cases. Every our-side client has consented.

- **Where to go**: **Details** tab → **Conflicts** block.

### Leasehold fee added to ledger

Cases with a leasehold property. A *leasehold fee* ledger line exists.

- **Where to go**: **Ledger** tab.

### Land Registry fee added to ledger

Purchase / transfer / remortgage. A *land registry fee* ledger line exists.

- **Where to go**: **Ledger** tab.

### Electronic transfer fee added to ledger

Purchase and sale always; remortgage if a *mortgage redemption* ledger entry exists. An *electronic transfer* ledger line is added.

- **Where to go**: **Ledger** tab.

### Estate agent fee added to ledger

Sale cases with an estate agent (i.e. *no estate agent* is **false**). An *estate agent fee* ledger line exists.

- **Where to go**: **Ledger** tab.

### Deposit added to ledger

Purchase cases with a non-zero price and no related sale. A *money from client — deposit* ledger line exists.

- **Where to go**: **Ledger** tab.

### Draft completion statement uploaded

Every case. A *completion statement draft* document is uploaded and received.

- **Where to go**: **Documents** tab → *Completion statement draft* slot.

### Divorce document type selected

Divorce-as-reason cases on transfer / remortgage / purchase. The operator picks which divorce document the seller will provide.

- **Where to go**: **Details** tab → **Contract details** block → *Divorce document type*.

### Report sent to client

Purchase cases only. The *reports sent to client* milestone is recorded.

- **Where to go**: **Details** tab → **Milestones** block → *Reports sent to client*.

### Ready-for-exchange review complete

The capstone item. Either the *ready for exchange review* milestone is set, or an authoriser has signed the case off as ready for exchange.

- **Where to go**: **Details** tab → **Authority to exchange** block → authoriser sign-off action.
- **Common stuck-states**:
  - This is **not a clerical box-tick**. It's an authoriser sign-off. The case handler cannot mark it themselves.
  - **The roadblock will sit at *awaiting signoff*** until the authoriser reviews. From the doer's day view it will look quiet — that's expected.

## Edge cases & gotchas

- **Exchange Ready blocks exchange**. The engine treats it as a hard gate — until every applicable item is green, the case cannot be exchanged.
- **The roadblock requires authoriser sign-off**. Once you've cleared all the clerical items the roadblock flips to *awaiting signoff*, the doer's day view stops showing it, and only the authoriser can finish it.
- **Exchange Ready overlaps Contracts and Contract Ready Pack**. Other-side-client details, OS conveyancer, and the protocol forms (TA6 / TA7 / TA10) live on those siblings, not here. If those sibling roadblocks aren't clear, fixing items here doesn't unblock exchange on its own.
- **Transfer and remortgage cases don't get Exchange Ready** — they don't exchange. The equivalent gate is *Pre-Completion Ready*.
- **The shared "clients authorise exchange and complete" milestone** is one column on the case. It reads as exchange-flavoured but applies to non-exchanging cases too — see Pre-Completion Ready.
- **Mortgage offer document expiry** is read against the case dates: an offer that expires before completion fails this item.
- **A £40,000 price threshold** is what triggers the SDLT items — the statutory point at which a return becomes notifiable. Below it the SDLT items do not fire on this roadblock; SDLT may still need handling separately.

## Related guides

- [Roadblocks: how they work](/crm/guides/legal/roadblocks-how-they-work)
- [Authority to exchange](/crm/guides/legal/authority-to-exchange)
- [Sale process](/crm/guides/legal/sale-process)
- [Purchase process](/crm/guides/legal/purchase-process)
- [Mortgage offer report](/crm/guides/legal/mortgage-offer-report)
- [Roadblock: Client Verification](/crm/guides/legal/roadblock-client-verification)
- [Roadblock: Contract Ready Pack](/crm/guides/legal/roadblock-contract-ready)
- [Roadblock: Contracts](/crm/guides/legal/roadblock-contracts)
- [Roadblock: Pre-Completion Ready](/crm/guides/legal/roadblock-pre-completion-ready)
- [Roadblock: Completion Ready](/crm/guides/legal/roadblock-completion-ready)
`,
    },
    {
        product: "legal",
        slug: "training-completion-and-post-completion",
        title: "Training — Completion and post-completion (Session 8)",
        one_line_description: "Methods of completion, the Law Society Code for Completion by Post, SDLT, registration deadlines, delays and remedies.",
        category: "training-sessions",
        order: 80,
        last_screenshotted_at: "2026-05-29",
        tags: ["training","completion","post-completion","sdlt","registration","notice-to-complete","code-for-completion"],
        content_hash: "4964d2ec1c8c2ad7d3f71ce995eb487e1bdf32064f275e921f597891ff63d2d1",
        body_markdown: `## What it is

Completion day mechanics — when title actually passes, the three methods of completing, the Law Society Code for Completion by Post, SDLT submission, the registration deadlines, and what to do when one side delays.

This is **training material, not currently driven by the case engine**, though the **Completion Ready** and **Post-Completion** roadblocks track much of the operational side.

## When you'd use it

- A new joiner is learning what completion day involves.
- The other side has delayed and you need to know whether to serve a Notice to Complete.
- A client asks how SDLT is paid and how long it takes for registration.

## Walkthrough

### Purpose and effect of completion

For the client, completion is the "big day" — keys handed over (buyer) or mortgage redeemed and net proceeds received (seller).

For the conveyancer, completion is the date the contract is fulfilled. It's not the end of the transaction — registration and other tidy-up still to do.

**Effect on title:**

| Land type | When legal title passes |
| --- | --- |
| **Unregistered** | At completion. |
| **Registered** | When the buyer is **registered as proprietor** at HM Land Registry — not at completion itself. |

On completion the **contract merges with the purchase deed** in respect of matters they both cover — meaning, after completion, you can't generally sue on a contract term unless expressly preserved. Many contracts contain a **non-merger clause** to preserve the right to sue post-completion. Tort actions are unaffected.

### Date and time of completion

Date — inserted in the contract on exchange, agreed in prior negotiations. Chains must complete on the same day with synchronised dates.

**Final time** under the Standard Conditions: **2pm** on the day. After 2pm = interest payable and technically completion is deemed not to have taken place until the next working day. Most conveyancers complete pragmatically after 2pm — not all.

### Three methods of completion

| Method | What happens |
| --- | --- |
| **In person** | Buyer's conveyancer attends seller's office, hands over money, collects documentation. Safest, very rarely used. |
| **By agent** | Same as in person but a representative from another firm attends on behalf of buyer's conveyancer. Used where firms are far apart but personal attendance is needed (concerns about seller's conveyancer, deeds need inspecting). |
| **By post** | Most common. Money sent by telegraphic transfer; seller's conveyancer checks correct sum, posts title documents to buyer's conveyancer. Governed by the Law Society Code for Completion by Post. |

### Law Society Code for Completion by Post

Conveyancers must agree to adopt it. Under the Protocol, the Code must also be used unless otherwise agreed.

In short, the Code provides that:

- Seller's conveyancer acts as **agent for the buyer's conveyancer** (without a fee).
- Buyer's conveyancer sends **full instructions**.
- Seller's conveyancer confirms they're **authorised by the seller** to receive money.
- Seller's conveyancer confirms they're **agent for any mortgage lender** whose mortgage needs discharging.
- Seller's conveyancer **notifies the buyer's conveyancer of completion** and sends deeds out by first-class post or DX **the same day**.

### Essential completion issues

**Acting for the seller:**

- Hold all necessary deeds and documents, executed purchase deed, DS1 / END1 / vacating receipt.
- Give undertakings about redemption in the **Law Society approved form** wherever possible.
- If asked to act as agent on completion, consider conflicts — decline if any.
- In Protocol cases / where the Code applies, the Code incorporates professional undertakings.
- Seller is moving out — where will the keys be?
- On receipt of funds: notify client, estate agents (release keys), buyer's conveyancer.

**Acting for the buyer:**

- Check all searches exist and remain in force; client final inspection immediately before completion.
- Accept seller's undertaking on the mortgage? Preferably not if lender is not a member of the CML.
- Written confirmation that seller's conveyancer is agent of the lender at completion.
- In a long chain — by-pass arrangements so monies arrive in time.
- Proper safeguards (undertakings) in writing.
- Seller's conveyancer holds executed purchase deed **before** sending funds.

### Post-completion

**Seller:**

- Redeem all existing mortgages.
- Pay estate agents.
- Send purchase deed and title documents to buyer's solicitors.
- Final completion statement to seller with cheque for balance due.
- Final file check — no forgotten points.

**Buyer:**

- Confirm completion to buyer, introducer, new lender.
- Pay stamp duty (if applicable) and **submit the Stamp Duty Land Transaction return within 14 days** of completion.
- Submit application for registration to HM Land Registry within the **30-day priority period** of the Land Registry search result, or **within 2 months** of completion if unregistered.
- Once registration finalised, send documents to lender; post the buyer a copy of the Title Information Document.
- Final file check.

### Stamp Duty Land Tax — current rates

| Slice | Rate |
| --- | --- |
| Up to £250,000 | 0% |
| £250,001 to £925,000 | 5% |
| £925,001 to £1.5m | 10% |
| Over £1.5m | 12% |

The return must be submitted regardless of price. Tax payable is sent above the threshold — firm policy is to pay by BACS on the day of completion.

Once the Return is received, HMRC sends an acknowledgment certificate to the buyer's conveyancer. **That certificate must accompany the Land Registry application.**

### Registration deadlines

| Land type | Deadline | Danger if missed |
| --- | --- | --- |
| **Registered** | Within the **30-day priority period** of your OS1/OS2 | Buyer (and lender) interest may lose priority to another application. |
| **Unregistered** | Within **2 months** of completion | The transfer of the legal estate becomes **void**. |

Submit purchase deed, HMRC certificate, evidence of name/marriage/death changes, discharge documents, new mortgage deed, and (for unregistered) all title deeds for the last 15 years' chain of ownership. Add RX1 to register a restriction, DI to register a disclosable interest like a right of way.

### Delays and remedies

**Late completion** — defaulting party pays compensation. Standard Conditions: either party can claim compensation at the contract rate of interest on a daily basis until completion.

Time is **not of the essence** under the standard form. To make it of the essence, the ready party serves a **Notice to Complete** — must be ready, able and willing to complete before serving. The delaying party then has **10 working days** to complete.

| Delaying party fails | Available remedies |
| --- | --- |
| **Seller** | Buyer may rescind, recall deposit, sue for breach. |
| **Buyer** | Seller may rescind, forfeit deposit, resell, claim damages. |

**Other remedies:**

- **Specific performance** — equitable; available to either party where the contract hasn't been completed.
- **Damages** — common law rules apply; credit given for forfeited deposit if seller sues.
- **Rescission** — by contractual term (after Notice to Complete), agreement, or court order.
- **Vendor and Purchaser Summons** — apply to court for a decision on a specific point of dispute (e.g. good title shown?) without applying for specific performance.

## Edge cases & gotchas

- **2pm Standard-Condition cut-off** — most conveyancers complete pragmatically after but not all. Don't assume the other side will.
- **The Code for Completion by Post incorporates professional undertakings** — make sure you can deliver everything you've undertaken before completion.
- **Registration deadline for unregistered land is 2 months** — miss it and the legal estate transfer is **void**. The 30-day registered deadline is "only" a priority issue by comparison.
- **A Notice to Complete you can't back up** — if you serve it and you weren't actually ready, the other side may flip the position and serve one on you. Don't bluff.
- **SDLT certificate must accompany Land Registry application** — without it, the application fails.

## Related guides

- [Training — After exchange (Session 7)](/crm/guides/legal/training-after-exchange)
- [Sale process](/crm/guides/legal/sale-process)
- [Purchase process](/crm/guides/legal/purchase-process)
- [Companies House charge](/crm/guides/legal/companies-house-charge)
`,
    },
    {
        product: "legal",
        slug: "roadblock-shared-exchange-and-pre-completion-items",
        title: "Roadblock: Shared exchange + pre-completion items",
        one_line_description: "The 31 checklist items shared between Exchange Ready and Pre-Completion Ready — where to fix each one.",
        category: "roadblocks",
        order: 85,
        last_screenshotted_at: "2026-05-29",
        tags: ["roadblocks","exchange-ready","pre-completion-ready","troubleshooting","shared"],
        content_hash: "4a282508f71bb02f47dfa56c117973ac34d90439204705462cfb452989f3921f",
        body_markdown: `## What it is

A set of **31 checklist items** that appear on both **Exchange Ready** (sales and purchases) and **Pre-Completion Ready** (transfers and remortgages). They cover the pre-sign-off basics that apply to every kind of transaction: undertakings, title freshness, charges, mortgage offers, bank verification, SDLT, client authority, dual-rep, ledger fees, and the completion statement.

Treat this guide as the **lookup for the bulk** of the items you'll see on Exchange Ready or Pre-Completion Ready. The non-shared, roadblock-specific items (enquiries, replies-to-enquiries report, ready-for-exchange review) live in [Roadblock: Exchange Ready](/crm/guides/legal/roadblock-exchange-ready) and [Roadblock: Pre-Completion Ready](/crm/guides/legal/roadblock-pre-completion-ready).

## When you'd use this guide

- An item is open under Exchange Ready or Pre-Completion Ready and you're not sure what the system is asking for.
- An item is amber / red and you think you've already done the work — you want to know exactly where the engine is looking.
- A new joiner is on a sale/purchase or transfer/remortgage and wants the full list of "things that must be true before sign-off".

## Walkthrough

The items are grouped below by topic. They sort alphabetically inside the panel; this grouping is just for human readability.

### Undertakings

#### Non-standard undertakings approved (or marked as none)

Confirms there's no off-pattern undertaking lurking that hasn't been signed off.

**Where to fix it.** Open the case → **Details tab** → **Case management** section. Either mark *"no non-standard undertakings on this case"*, or — if there are some — record them and mark them as **approved by HOLP**.

### Title freshness at sign-off

#### Risk grade green and recent

Confirms the case's overall risk grade is green and the last assessment is recent enough to be trusted at sign-off.

**Where to fix it.** Details tab → **Risk Management** section. If the grade is amber/red, complete the outstanding actions. If it's green but stale, re-run the assessment to refresh the timestamp.

#### Recent-last-transfer risk acknowledged for every title

A title that transferred recently (in the last six months) is a fraud-risk signal — the engine wants you to acknowledge you've thought about it.

**Where to fix it.** Details tab → **Property** block → for each registered title, tick the *recent-last-transfer risk acknowledged* checkbox once you've reviewed the previous transfer date. Unregistered titles don't need this.

### Charges and restrictions

#### All charges and title restrictions marked as added

Forces you to confirm you've logged every charge and restriction the office copies show.

**Where to fix it.** Details tab → **Charges & restrictions** section. Add every entry from the title, then tick *"all charges added"* and *"all restrictions added"*.

#### All charges have account numbers (non-purchase only)

For sales / transfers / remortgages, every charge needs the account number so the redemption can be requested. Doesn't apply on purchases (we're not paying off anything on this side).

**Where to fix it.** Same section as above. For each charge that's still outstanding, fill in the account number.

#### Early repayment charge answered for every charge

Confirms that for every charge, you've recorded whether an early-repayment penalty applies.

**Where to fix it.** Same section. Each charge has a yes/no/unknown selector — choose one.

#### Client notified of early repayment charge

If you said yes to ERC, the client needs to have been notified. Acknowledge that you've done it.

**Where to fix it.** Same section, tick the *client notified* checkbox.

### Reports received

#### Search report received

Confirms the search report has been logged against the case.

**Where to fix it.** **Documents tab** → upload the search report and set the document type to *search report*.

#### Leasehold report received

Leasehold-only. Logs the leasehold information report (the LPE1 + lease review).

**Where to fix it.** Documents tab → upload the leasehold report under the *leasehold report* type. If the case has no leasehold property, the item doesn't fire in the first place.

#### Mortgage offer report received

When there's a mortgage, confirms the mortgage offer report (the one you draft to send the client) has been logged.

**Where to fix it.** Documents tab → upload the mortgage report under *mortgage offer report*. See [Mortgage offer report — process](/crm/guides/legal/mortgage-offer-report) for the full workflow.

### Mortgage offer

These items only fire when there's a mortgage involved (remortgage, or purchase / transfer with \`purchase_payment_method = mortgage\`).

#### Mortgage lender identified

Confirms we've recorded who the new lender is.

**Where to fix it.** Details tab → **Mortgage** block → lender field.

#### Mortgage offer added

The offer itself has been logged on the case.

**Where to fix it.** Details tab → **Mortgage** block → click *Add mortgage offer* and fill in the offer date.

#### Mortgage offer review questions answered

The full set of review questions on the mortgage offer (advance, retention, term, interest rate, ERC, names, address, amount-matches-price, tenure-matches-contract, lender requirements, buildings insurance, occupier consent, matters to refer).

**Where to fix it.** Details tab → **Mortgage offer** sub-section → work through every question. Any question left as *unknown* keeps the item amber.

#### Mortgage offer document received (and not expired)

Confirms we've got the actual offer document on file and that it isn't past its expiry date.

**Where to fix it.** Documents tab → upload under *mortgage offer*. If the offer has expired, request a new one from the lender and replace the document.

#### Mortgage retention shared with client

If the lender's offer included a retention, the client must have been told.

**Where to fix it.** Details tab → **Mortgage offer** sub-section → tick *retention shared with client*. If there's no retention, the item doesn't fire.

### Bank accounts

#### Bank accounts verified (or marked won't be used)

Every bank account on the case must either be *approved* (and the clients have agreed it's the right one) or marked *not-verified-as-will-not-be-used*.

**Where to fix it.** Details tab → **Bank accounts** block. For each account, either run the verification call and mark approved (with the clients-agree confirmation), or mark it as not being used for this case. See [Bank account verification](/crm/guides/legal/bank-account-verification) for the full protocol.

### SDLT (purchase + transfer cases over £40,000)

Only fires when \`transaction_type = purchase or transfer\` AND \`price_pence >= 4,000,000\` (so £40,000+ in human terms).

#### SDLT return uploaded or Compass referral set

Either the SDLT5 has been uploaded, or the case is marked as referred to Compass.

**Where to fix it.** Documents tab → upload the SDLT5, OR Details tab → set the *SDLT routing* to *Compass referral* on the case.

#### SDLT declaration signed by every client

Each client who needs to sign the SDLT declaration must have signed.

**Where to fix it.** Onboarding tab → **SDLT declaration** step — each client's status. Chase any outstanding signer.

#### SDLT added to ledger

The SDLT line item must be on the case ledger.

**Where to fix it.** **Ledger tab** → add an SDLT line item with the calculated amount.

### Client authority

> **Not a shared item anymore.** *Clients authorised exchange and complete* used to sit in this shared set, so it appeared from the moment the roadblock opened. It now lives on each roadblock separately ([Exchange Ready](/crm/guides/legal/roadblock-exchange-ready) and [Pre-Completion Ready](/crm/guides/legal/roadblock-pre-completion-ready)) and is **gated**: the item only surfaces once every team and chased item on that roadblock is resolved — so you're never asked to take the client's authority before our prep and the documents we're chasing are in. See those two guides for the detail.

#### All clients added and right to buy/sell checked

Every case. Every client expected to be on the case has been added, and the *right to buy/sell checked* field is confirmed. Moved here from Client Verification — it's a ready-for-sign-off assurance on the client roster and their right to sell, not an onboarding check. As a conveyancer (team) item it also gates the *clients authorised* ask above: that item won't surface until this one is ticked.

**Where to fix it.** Details tab → **People** block. Add any missing clients via *Add person*, then tick *Right to buy/sell checked* on the case-level field.

#### Joint ownership held as answered

For multi-client cases, how the clients will hold the property (joint tenants or tenants in common, plus share % if TIC) must be recorded.

**Where to fix it.** Details tab → **Property** block → *Joint ownership* selector. Pick one — the engine doesn't accept *unknown*.

#### Apportionment question answered

For leasehold (or anything else with apportionable charges), whether apportionments are being settled on completion must be answered yes/no.

**Where to fix it.** Details tab → **Apportionments** sub-section → yes/no selector.

### Dual rep

#### Dual rep lender conflict approved (if required)

Where we're acting for both sides AND a lender is involved, the conflict must have been approved.

**Where to fix it.** Details tab → **Dual rep** block → record the conflict approval. If the case isn't dual-rep, the item doesn't fire.

#### Dual rep consent obtained from all our-side clients

Every our-side client on a dual-rep case must have signed dual-rep consent.

**Where to fix it.** Onboarding tab → **Dual rep consent** step per client. Chase any outstanding signers.

### Ledger fees

Each of these is checking that a ledger line item of the right type exists. Add via **Ledger tab** → *Add line item*.

#### Leasehold fee added to ledger

Leasehold-only. The leasehold review fee.

#### Land Registry fee added to ledger

The LR registration fee on purchases / transfers / first-registrations.

#### Electronic transfer fee added to ledger

The CHAPS / electronic transfer fee covering completion-day money movement.

#### Estate agent fee added to ledger

Sales-only. The estate agent's commission.

#### Deposit added to ledger

Purchases-only. The deposit being held to exchange.

### Completion statement

#### Draft completion statement uploaded

A draft completion statement must be on file before sign-off.

**Where to fix it.** Documents tab → upload under *draft completion statement*. See the [sale process](/crm/guides/legal/sale-process) or [purchase process](/crm/guides/legal/purchase-process) for the wider workflow.

### Probate items (where they apply)

When the case has probate features, the relevant probate items also appear here — they're documented separately in [Roadblock: Probate items](/crm/guides/legal/roadblock-probate-items).

### Divorce

#### Divorce document type selected

For matrimonial transfers, whether the client has a court order, a separation agreement, or wants us to prepare a separation statement.

**Where to fix it.** Details tab → **Transfer of equity** sub-section → *Divorce document type* selector. See the [transfer of equity process](/crm/guides/legal/transfer-of-equity-process) guide for what each option requires.

## Edge cases & gotchas

- **The engine is item-level, not section-level.** Even if 30 of 31 items are green, the parent roadblock stays open. Hunt the last one.
- **Some items vanish on cases where they don't apply** — leasehold-only items don't appear on freehold cases, SDLT items don't appear under £40,000, dual-rep items don't appear on single-rep cases. If you can't find the item the day-view banner mentions, check whether the case shape has changed.
- **The *clients authorised exchange and complete* column reads as exchange-flavoured even on non-exchanging transactions.** Don't be thrown — for transfers/remortgages, that's just where the authority gets recorded. The legacy column name hasn't been renamed.
- **Mortgage offer document validity isn't checked against the completion date.** The item clears as long as a non-expired offer is on file. A real-world expiry between sign-off and completion is your call to spot.
- **"Recent-last-transfer" means six months.** If the last transfer was 5 months and 29 days ago, the item won't clear until you acknowledge the risk.

## Related guides

- [Roadblocks: how they work](/crm/guides/legal/roadblocks-how-they-work) — the engine overview.
- [Roadblock: Exchange Ready](/crm/guides/legal/roadblock-exchange-ready) — the sale/purchase wrapper around these items.
- [Roadblock: Pre-Completion Ready](/crm/guides/legal/roadblock-pre-completion-ready) — the transfer/remortgage wrapper.
- [Sale process](/crm/guides/legal/sale-process), [Purchase process](/crm/guides/legal/purchase-process), [Transfer of equity process](/crm/guides/legal/transfer-of-equity-process), [Remortgage process](/crm/guides/legal/remortgage-process) — the playbooks that get you to a state where these items can clear.
- [Bank account verification](/crm/guides/legal/bank-account-verification), [Mortgage offer report — process](/crm/guides/legal/mortgage-offer-report), [Authority to exchange contracts](/crm/guides/legal/authority-to-exchange) — referenced from individual items above.
`,
    },
    {
        product: "legal",
        slug: "executors-and-lpas",
        title: "Executors and LPAs — why an LPA can't sign a TR1",
        one_line_description: "Why a Lasting Power of Attorney is no use for estate administration, and what to do if an executor loses capacity.",
        category: "legal-practice",
        order: 90,
        last_screenshotted_at: "2026-05-29",
        tags: ["executor","lpa","lasting-power-of-attorney","tr1","probate","capacity"],
        content_hash: "6d2309199b59a1459247d93181ac1ce36a97da5301d6f25d3d6bee07528fc848",
        body_markdown: `## What it is

Executors and beneficiaries sometimes assume a Lasting Power of Attorney lets the attorney sign for the executor on probate matters. **It doesn't.** This guide explains why, and what to do when an executor genuinely can't sign — including the *Grant de Bonis Non* route.

## When you'd use it

- A beneficiary has produced an LPA and wants their attorney to sign the TR1.
- An executor has lost capacity after probate was issued and the estate is part-administered.
- A client is asking why we won't accept an LPA-signed transfer deed.
- A Land Registry requisition has come back questioning the executor's signature.

## Walkthrough

### 1. LPAs end on death

A Lasting Power of Attorney (Property & Financial Affairs) only applies **while the donor is alive**. It **ends automatically on death**. After death, authority comes from the Will and the Grant of Probate — not the LPA.

### 2. Executors cannot delegate via LPA

Executor powers are personal legal duties connected to administering an estate. They **cannot be delegated using a standard LPA**.

Under the Trustee Act 1925, an executor may only delegate duties using a **specific Trustee Power of Attorney** (a section 25 Trustee Act POA).

### 3. Why Land Registry rejects TR1s signed under an LPA

If a TR1 is signed under an LPA:

- The signature is **invalid**.
- Attorney powers under an LPA do not extend to executor duties.
- The Land Registry raises a requisition requiring the executor to **re-sign personally**.

### 4. What executors must do instead

- Executors **must sign the TR1 personally**.
- If the executor cannot sign due to illness or absence, a **Trustee Power of Attorney must be created BEFORE signing**.
- If no Trustee POA exists, the TR1 must be re-executed by the executor.

### 5. When a Trustee Power of Attorney is acceptable

A Trustee POA is valid only if:

- It specifically delegates executor duties **under the Trustee Act 1925**, AND
- It **predates the TR1** or other transfer deed.

Without both of those, Land Registry will not accept the attorney's signature.

### 6. Practical tips for executors

- Tell the conveyancer **early** if you may struggle to sign.
- Never rely on an LPA for estate dealings.
- Keep copies of the Grant of Probate and ID ready for property transactions.

If in doubt, consult the conveyancer before signing. Prevents delays and avoids Land Registry requisitions.

## When the executor has lost capacity after probate

If the original executor has lost capacity **after probate was issued**, the estate is part-administered. The LPA is no use. The correct procedure is:

### Apply for a "Grant de Bonis Non"

Apply for a **Grant of Letters of Administration (with Will) de bonis non administratis**. This new grant gives legal authority to whoever is next entitled to finish the administration.

**Who applies?** If the Will names no substitute executor, then the **residuary beneficiary** (or beneficiaries) with the greatest entitlement has priority to apply.

### Documents needed for the new grant

- Copy of the original grant.
- The Will.
- **Medical evidence** confirming the executor's incapacity.
- A statement explaining:
  - The grant was issued.
  - The executor has since lost capacity.
  - The estate remains unadministered in part (e.g. property not sold).

### Once the new grant is issued

The newly appointed personal representative (administrator with Will annexed):

- Gains full legal authority.
- Can sign the contract for sale.
- Can sign the TR1 or transfer deed.
- Manages estate funds.
- Completes remaining administration.

**The LPA attorney plays no role.** No authority to administer the estate or complete the sale.

## Edge cases & gotchas

- **The attorney is often the well-meaning family member** who has stepped in. Be diplomatic but firm: it has to be a Grant de Bonis Non, not the LPA.
- **Medical evidence of incapacity** is the rate-limiter on the new grant. Get the GP letter started as soon as the issue surfaces.
- **The Trustee POA must predate the TR1.** A POA created the same day or later is no good.
- **Residuary beneficiary priority** can produce surprises — the person currently doing the leg-work isn't always the one with legal priority to apply.

## Related guides

- [Assent transfers — moving property out of an estate](/crm/guides/legal/assent-transfers)
- [Executor that loses capacity](/crm/guides/legal/executor-loses-capacity)
- [Handling a sale when both the property owner and their personal representative have died](/crm/guides/legal/sale-after-pr-died)
- [Purchase enquiries — standard wording](/crm/guides/legal/purchase-enquiries) (executor-without-capacity enquiry)
`,
    },
    {
        product: "legal",
        slug: "roadblock-pre-completion-ready",
        title: "Roadblock: Pre-Completion Ready",
        one_line_description: "How to clear each item under Pre-Completion Ready on a transfer or remortgage.",
        category: "roadblocks",
        order: 90,
        last_screenshotted_at: "2026-05-29",
        tags: ["roadblocks","pre-completion-ready","transfer","remortgage","troubleshooting"],
        content_hash: "2a30ce647818dd87ef55b282b648b2c08bc3b9a0671ece82640b0409d742d9af",
        body_markdown: `## What it is

Pre-Completion Ready is the transfer / remortgage equivalent of *Exchange Ready*. Those transactions don't exchange — they go straight from instruction to completion — so this roadblock fronts the same authoriser-gated review, minus the exchange-only items (enquiries, replies-to-enquiries report, client report, ready-for-exchange review).

It fires on every instructed transfer or remortgage and stays open until every applicable item is green and an authoriser has signed it off.

## When you'd use this guide

- A transfer or remortgage is nearly ready to complete and you want to know what's left.
- An item won't clear and you can't tell what the engine is reading.
- You're an authoriser preparing to sign the case off.
- You want to know which items live here vs on Client Verification, Completion Ready, or Post-Completion.

## The checklist

### Non-standard undertakings approved (or marked as none)

Either *has non-standard undertakings* is **no**, or it's **yes** and the undertakings are approved.

- **Where to go**: **Details** tab → **Undertakings** block.

### Risk grade green and recent

The most recent risk assessment on the case is *green*.

- **Where to go**: **Details** tab → **Risk Management** panel.

### Recent-last-transfer risk acknowledged for every title

For every registered title, either the last transfer was more than 6 months ago, or the *recent last transfer risk acknowledged* tick is set.

- **Where to go**: **Details** tab → **Property** block → each property row.

### All charges and title restrictions marked as added

The *add charges and title restrictions* job is marked done.

- **Where to go**: **Details** tab → **Charges and restrictions** block.

### Search report received

If searches were ordered, a *search report* document is uploaded and received.

- **Where to go**: **Documents** tab → *Search report* slot.

### Mortgage offer report received

Mortgage cases. The *mortgage offer report* document is uploaded and received.

- **Where to go**: **Documents** tab → *Mortgage offer report* slot.

### Mortgage lender identified

Mortgage cases. The *mortgage lender* dropdown is set to a real lender.

- **Where to go**: **Details** tab → **Mortgage** block → *Lender*.

### Mortgage offer added

Mortgage cases. The *mortgage offer date* is recorded.

- **Where to go**: **Details** tab → **Mortgage** block → *Offer date*.

### Mortgage offer review questions answered

Mortgage cases. Every mortgage-offer review question on the case is answered. If *matters to refer* is **yes**, the details box must be filled in.

- **Where to go**: **Details** tab → **Mortgage** block → *Offer review* form.

### Mortgage offer document received (and not expired)

Mortgage cases. A *mortgage offer* document is uploaded and received.

- **Where to go**: **Documents** tab → *Mortgage offer* slot.

### Mortgage retention shared with client

Only fires when the lender has a retention amount > 0. The *retention explained to client* tick is set.

- **Where to go**: **Details** tab → **Mortgage** block.

### Early repayment charge answered for every charge

Cases with at least one charge. Every charge has *has early repayment charge* answered.

- **Where to go**: **Details** tab → **Charges** block.

### Client notified of early repayment charge

Only for charges where *has early repayment charge* is **yes**. The *client notified* tick is set.

- **Where to go**: **Details** tab → **Charges** block.

### Bank accounts verified (or marked won't be used)

Every bank account is either *approved* with *all clients agree* set, or marked *not verified — won't be used*.

- **Where to go**: **Details** tab → **Bank accounts** block.

### All charges have account numbers (non-purchase)

Every charge row has a non-empty account number.

- **Where to go**: **Details** tab → **Charges** block.

### SDLT return uploaded or Compass referral set

Transfer cases £40,000+. Either referred to Compass, or *Compass referral not required* plus a draft / signed SDLT return uploaded, or the case marked *No SDLT return is required for this transfer*.

- **Where to go**: **Details** tab → **SDLT** block.

### SDLT declaration signed by every client

Transfer cases £40,000+. Every client signs the SDLT declaration, unless the case is marked *No SDLT return is required for this transfer* — with no return there is nothing to declare.

- **Where to go**: **Details** tab → **SDLT** block.

### SDLT added to ledger

Transfer cases £40,000+. A *stamp duty land tax* line on the ledger, unless the case is marked *No SDLT return is required for this transfer* — an exempt transfer has nothing to remit.

- **Where to go**: **Ledger** tab.

### Clients authorised to complete

Every case — but **gated**. This item only appears once every other team and chased item on Pre-Completion Ready is resolved — including the first redemption statement for each charge, the mortgage block, charges and the draft completion statement. That's deliberate: we don't ask the client to authorise completion before our prep and the documents we're chasing (redemption statements especially) are in. Until then the item is hidden; once the rest goes green it surfaces and you record the milestone. On a non-exchanging transaction this reads as "clients authorised to complete". An outstanding *client* item does **not** hold it back — only our own prep does.

- **Where to go**: **Details** tab → **Milestones** block.
- **Common stuck-states**:
  - **The field is named for exchange in the codebase but applies here too** — staff just see *Clients authorised to complete* on transfer / remortgage. Same underlying tick.
  - **The item isn't showing yet** — that's the gate doing its job. Check the redemption statements and the rest of the team prep are in; the authority item appears once they are.

### All clients added and right to buy/sell checked

Every case. Every client expected to be on the case has been added, and the *right to buy/sell checked* field has been confirmed. Moved here from Client Verification — it's a ready-to-complete assurance on the client roster and their right to sell, not an onboarding check.

- **Where to go**: **Details** tab → **People** block. Add any missing clients via *Add person*, then tick *Right to buy/sell checked* on the case-level field.
- **Common stuck-states**:
  - **You added a client but forgot to tick the checkbox** — the engine treats the two as separate signals. Both must be in place.

### Joint ownership held as answered

Transfer / remortgage with more than one client. The *joint ownership held as* dropdown is set.

- **Where to go**: **Details** tab → **People** block.

### Apportionment question answered

Cases with a management company. The *requires apportionment statement* question is answered.

- **Where to go**: **Details** tab → **Management companies** block → select the company.

### Management company details obtained

Cases with a management company. At least one company is linked, each with a name and a contact channel (email or phone).

- **Where to go**: **Details** tab → **Management companies** block → **Add management company** (search a previous one, or create a new one).

### Management company consent obtained

Transfer / remortgage cases with a management company. Every linked company has consent to transfer/remortgage recorded, or is marked *not required* (use this when the lease/title doesn't gate the transfer on landlord consent).

- **Where to go**: **Details** tab → **Management companies** block → select the company → record *consent obtained*, or tick *consent not required*.

### Dual rep lender conflict approved (if required)

Dual-rep mortgage cases where the lender doesn't allow dual rep. The conflict is approved-to-continue.

- **Where to go**: **Details** tab → **Conflicts** block.

### Dual rep consent obtained from all our-side clients

Dual-rep cases. Every our-side client has consented.

- **Where to go**: **Details** tab → **Conflicts** block.

### Leasehold fee added to ledger

Leasehold properties. A *leasehold fee* line on the ledger.

- **Where to go**: **Ledger** tab.

### Land Registry fee added to ledger

Transfer / remortgage. A *land registry fee* line on the ledger.

- **Where to go**: **Ledger** tab.

### Electronic transfer fee added to ledger

Remortgage only if a *mortgage redemption* ledger entry exists. An *electronic transfer* line on the ledger.

- **Where to go**: **Ledger** tab.

### Draft completion statement uploaded

Every case. A *completion statement draft* document is uploaded and received.

- **Where to go**: **Documents** tab → *Completion statement draft* slot.

### Divorce document type selected

Divorce-as-reason transfer / remortgage. The operator picks the divorce document type.

- **Where to go**: **Details** tab → **Contract details** block → *Divorce document type*.

### Grant of probate or letters of administration received

Probate cases. Every grant of probate / letters of administration document slot is uploaded and received.

- **Where to go**: **Documents** tab → grant slots.

### Grant of probate or letters of administration applied for

Probate cases. Either the *grant application date* is recorded, or the grant / letters are already received (no need to backfill the application date once the grant is in).

- **Where to go**: **Details** tab → **Probate** block → *Grant applied for* date.

### Will received

Probate cases where a will slot has been created. Every will slot is uploaded and received.

- **Where to go**: **Documents** tab → *Will* slot.

### Death certificate received

Probate cases where a death certificate slot has been created. Every slot is uploaded and received.

- **Where to go**: **Documents** tab → *Death certificate* slot.

### First redemption statement received for all charges

Cases with at least one charge. Every charge has a redemption statement against it.

- **Where to go**: **Details** tab → **Charges** block → each charge row.

### Draft TR1 created

Transfer cases that aren't probate assents. A draft (or signed) TR1 — or TP1 / TP2 / TR2 for a transfer of part — has been created on the case. Generate it from the contracts block; the deed is produced for you and lands in the document store as *draft TR1*.

- **Where to go**: **Details** tab → **Contract details** block → *Create Draft Transfer Pack*.

### Draft AS1 created

Probate assents (seller reason for sale is *probate — AS1*). A draft (or signed) AS1 has been created on the case. Generated from the same *Create Draft Transfer Pack* button — on an assent it produces an AS1 instead of a TR1.

- **Where to go**: **Details** tab → **Contract details** block → *Create Draft Transfer Pack*.

### Signed TR1 received

Transfer cases that aren't probate assents, once a draft TR1 exists. Every *signed TR1* document slot is uploaded and marked received.

- **Where to go**: **Documents** tab → *Signed TR1* slot.

### Signed AS1 received

Probate assents, once a draft AS1 exists. Every *signed AS1* document slot is uploaded and marked received.

- **Where to go**: **Documents** tab → *Signed AS1* slot.

## Edge cases & gotchas

- **Pre-Completion Ready requires authoriser sign-off.** Cleared items flip the roadblock to *awaiting signoff* — only the authoriser can finish it. The doer's day view will go quiet.
- **Pre-Completion Ready blocks exchange** in the engine's model — but transfer / remortgage cases don't exchange, so what it really blocks is the case being signed off as ready to complete.
- **Pre-Completion Ready does not fire on sales or purchases** — those use Exchange Ready instead. The two roadblocks share a large body of items and aren't both on the same case.
- **The "clients authorised exchange and complete" milestone** is one field on the case shared with Exchange Ready. The display text changes but the underlying tick is the same — meaning if a case ever crosses transaction types mid-flight, the milestone carries.
- **Probate document slots only fire as items if they exist on the case.** A probate case with no will slot created yet won't be blocked on *Will received* — the case handler has to set the slot up first via the document store.
- **The signed-deed item only appears once a draft exists.** *Signed TR1 received* / *Signed AS1 received* won't show until the matching *Draft TR1 / AS1 created* item is done — drafting comes first. The signed slot is created automatically once the draft is received and the report has been sent to the client, so there's nothing to upload into before then.
- **A divorce transfer of equity is SDLT-exempt.** Where the transfer is made in connection with the ending of a marriage or civil partnership, no return is due at all — even where there is consideration or a mortgage is assumed. On those cases the SDLT block offers **No SDLT return is required for this transfer**, which clears all three SDLT items and the sign-off gate together. Choose it rather than uploading a return, posting a stamp-duty ledger line, or collecting declarations you don't need.
- **The ILA waiver has to sit on the *ILA Waiver* tile.** *ILA or disclaimer complete for every departing owner* reads that tile only. On a case that predates the rename, the signed waiver may be filed on the old **ILA Waiver (old tile)** — move it across first, then set the departing owner's choice. Setting the choice while the document is on the old tile leaves the item unresolved.
- **TR1 vs AS1 is decided by the case, not by you.** Standard transfers get the TR1 items; probate assents (seller reason for sale = *probate — AS1*) get the AS1 items instead. You'll only ever see one pair on a given case.

## Related guides

- [Roadblocks: how they work](/crm/guides/legal/roadblocks-how-they-work)
- [Transfer of equity — the process](/crm/guides/legal/transfer-of-equity-process)
- [Remortgage process](/crm/guides/legal/remortgage-process)
- [Roadblock: Client Verification](/crm/guides/legal/roadblock-client-verification)
- [Roadblock: Title Checks](/crm/guides/legal/roadblock-title-checks)
- [Roadblock: Completion Ready](/crm/guides/legal/roadblock-completion-ready)
- [Roadblock: Post-Completion](/crm/guides/legal/roadblock-post-completion)
`,
    },
    {
        product: "legal",
        slug: "training-leasehold-fundamentals",
        title: "Training — Leasehold fundamentals (Session 9)",
        one_line_description: "Lease anatomy, the practical review checklist, licence to assign, deed of covenant, ground rent, service charges, management companies, shared ownership.",
        category: "training-sessions",
        order: 90,
        last_screenshotted_at: "2026-05-29",
        tags: ["training","leasehold","lease-review","licence-to-assign","ground-rent","service-charge","management-company","shared-ownership"],
        content_hash: "fed694861d48734e42e648232c44d904a59e40eef1bc7629e96ae59bb7e628fc",
        body_markdown: `## What it is

The foundation session on leasehold conveyancing — what to look for in a lease, how alienation and licences work, the difference between flats and houses on insurance and repair, and the basics of management companies and shared-ownership leases.

This is **training material, not currently driven by the case engine**, though leasehold-specific items appear in **Contract Ready Pack** and **Title Checks**.

## When you'd use it

- A new joiner is reviewing their first lease and needs the structure.
- You're about to negotiate a Licence to Assign or Deed of Covenant.
- A client asks why their service charge is different from a friend's, or what a sinking fund is.

## Walkthrough

### What a lease is

An agreement creating an estate in land or property for a **finite term**. No two leases are identical — read every one in full.

**Residential types:** new lease, existing lease, underlease, sub-lease. Leases for houses are structurally different from leases for flats.

(This guide doesn't cover commercial leases.)

### Practical review checklist

| Check | What to look for |
| --- | --- |
| Accurate description of property | Is there a plan? |
| Rights | Right of way, right of support and protection, access for repairs. |
| Ground rent | Clear and acceptable — escalation clauses are the trap (see [Escalating ground rent](/crm/guides/legal/escalating-ground-rent)). |
| Lessee's covenants | Reasonable for the property type. |
| Lessor's covenants | Sufficiently comprehensive. |
| Insurance | House: usually lessee insures. Flat/maisonette: usually landlord or management company. |
| Maintenance | House: lessee responsible for all repairs. Flat/maisonette: best held by landlord or management company (often tenant-owned). |
| User restriction | Residential use only? Specific prohibitions? |
| Alienation | Reasonable / appropriate restrictions? Licence to Assign or Deed of Covenant required? |
| Mutual enforcement | Has the lessor covenanted to enforce covenants against one lessee at the request of another? |
| Forfeiture on bankruptcy | **Unacceptable** — no lender accepts it as security. Advise the client. |
| Leasehold enquiries | Acquire leasehold information pack if possible; current buildings insurance policy and schedule; last 3 years' service charge accounts; clear ground rent receipt; check no section 146 Notice served; notice fee. |
| Good leasehold title | Convert to absolute, or deduce the superior title (add a contract condition); consider indemnity. |
| Lease term remaining | Some lenders refuse below 60 years unexpired — check Part 2 of the UK Finance Handbook. |
| Garden | (Flats) front or rear — does either form part of the property being bought? |
| Report to client | Written report on all lease terms; client acknowledgment. |

### Licence to Assign

Common in alienation clauses — on assignment of a leasehold property, the seller must seek and obtain consent for the sale.

- Landlord's solicitor usually prepares the licence; **seller pays the fee**.
- Handed to the buyer on completion.
- Landlord may seek references — but consent **cannot be unreasonably withheld**.

### Deed of Covenant

Many leases (especially flats) require the buyer to enter into a Deed of Covenant with the landlord covenanting to observe and perform the lease.

This gives the landlord the ability to enforce the covenants via the Deed itself, in addition to other lease or statutory powers.

### Ground rent and service charge

**Ground rent** — charged in all leases. Anything from a peppercorn (legal fiction) to £500+ per annum. Can escalate — read the lease and tell the client. If the landlord doesn't demand ground rent, they can claim only **a maximum of 6 years' arrears** at any one time.

**Service charge** — rare on leasehold houses; common on flats and maisonettes. Covers repairs to the structure and common parts. Must be **reasonable**; tenants have the right to query. Read the lease carefully to confirm provisions are appropriate.

### Reserve and sinking funds

Set up by the landlord or management company for unexpected expenditure or anticipated major works (e.g. lift renewal). Funded by small annual contributions — smooths the impact of high-cost years on service charges.

### Management companies

Used to manage the block on behalf of landlord and/or tenants.

| Scheme | How it works |
| --- | --- |
| **Transfer of freehold** | Landlord transfers the reversion to the management company, which becomes the landlord. |
| **Concurrent lease** | Landlord grants a lease of the reversion to the management company — making the company the flat owner's landlord for the concurrent lease's duration. |
| **Variation — company joins the leases** | Company has no legal interest in common parts (problem). |
| **Variation — service charge to company** | Landlord covenants to repair but uses the company as agent. |
| **Right to Manage / Enfranchisement** | Tenants acquire the freehold reversion and form a management company to hold title and perform the landlord's role. |

When dealing with a management company:

- Obtain copy **Memorandum / Articles of Association**.
- Obtain copy **Share Certificate**.
- Have a **Stock Transfer form** signed (by seller; blank from your client).
- Obtain last 3 years' **audited company accounts**.
- Do a company search (Companies House check is sufficient).

### Post-completion

Serve a **Notice of Transfer and Charge** (in duplicate) on the landlord. (Notice fees normally apply.)

### Leasehold flats vs houses — quick reference

| Aspect | House | Flat / maisonette |
| --- | --- | --- |
| Insurance | Lessee | Landlord / management company |
| Repairs to structure/common parts | Lessee for all | Landlord / management company |
| Service charge | Rare | Common |
| Sinking / reserve fund | Rare | Common |
| Deed of Covenant on assignment | Less common | Common |

### Shared ownership leases

Introduced to provide affordable homes. Granted by Housing Associations. The tenant acquires a percentage of the ownership and pays rent on the rest. **Staircasing** lets them acquire further shares up to 100%.

## Edge cases & gotchas

- **Forfeiture on bankruptcy clause** — no lender will accept the lease as security. The client needs to know.
- **Lease term below 60–80 years** — some lenders won't lend. Check the UK Finance Handbook Part 2 entry early.
- **Ground rent escalation** — read the review clause carefully. See [Escalating ground rent](/crm/guides/legal/escalating-ground-rent) for the AST trap.
- **Service charge dispute history** — last 3 years' accounts usually reveal it. Worth asking the seller's solicitor for any disputes.
- **Management company with no interest in common parts** — caveat the report to the client; it can become a problem on a future sale.

## Related guides

- [Escalating ground rent](/crm/guides/legal/escalating-ground-rent)
- [Mortgagee protection clauses and the Renters' Rights Act 2025](/crm/guides/legal/mortgagee-protection-and-renters-rights-act)
- [Sale process](/crm/guides/legal/sale-process)
- [Purchase process](/crm/guides/legal/purchase-process)
`,
    },
    {
        product: "legal",
        slug: "executor-loses-capacity",
        title: "Executor that loses capacity — what to do",
        one_line_description: "When a will-named executor has lost mental capacity, the LPA is no use — here's the actual route.",
        category: "legal-practice",
        order: 95,
        last_screenshotted_at: "2026-05-29",
        tags: ["executor","capacity","lpa","dementia","court-order","section-50"],
        content_hash: "cf44b74d4408e16d50753d6683645a5cc5f3479200c36d58ece57fc791785ef2",
        body_markdown: `## What it is

A high-risk area: an executor has lost mental capacity during the administration of an estate. This guide is the conveyancing team's red-flag list and decision tree for getting authority sorted before exchange.

## When you'd use it

- A client mentions the executor "has dementia" or "isn't with us anymore mentally".
- A family member says "their attorney is dealing with the sale".
- An LPA has been produced as authority to sell estate property.
- A sole executor is incapacitated and there's no court order.

These are **stop points.** Escalate immediately.

## Walkthrough

### Key legal principles

**An executor's authority comes from the Will**, not from the Grant of Probate. They must have mental capacity to act.

**If an executor loses mental capacity:**

- They cannot continue to act as executor.
- They cannot validly sell or transfer estate property.
- Any documents they purport to sign as executor are **invalid**.

Loss of capacity does **not** automatically pass authority to anyone else.

### LPA — the common misunderstanding

> **An attorney appointed under a Lasting Power of Attorney CANNOT act as an executor.**

Why:

- An executor's role is a **personal appointment under a Will**.
- An LPA only authorises the attorney to act **for the donor personally**.
- It does not extend to fiduciary roles such as executor or personal representative.

**An LPA does not solve the problem.**

### How the estate can keep moving

Depends on whether there are other executors.

#### A. More than one executor named

- The remaining executor(s) with capacity may continue to act.
- The incapacitated executor effectively steps aside.

**Evidence required:**

- Copy of the Will showing multiple executors.
- Confirmation that the acting executor(s) have capacity.
- Grant of Probate (if issued) showing acting executor(s).

#### B. Sole executor has lost capacity

- There is **no one** with authority to administer the estate.
- A **court application is required**.
- Usual route: application to court for the appointment of a substitute personal representative, commonly under **section 50 Administration of Justice Act 1985**.

**Until the court order is made:**

- The property cannot be sold.
- Contracts must not be exchanged.

### Conveyancing red flags

Fee earners escalate immediately if:

- An executor is described as having dementia or lacking capacity.
- A seller says "their attorney is dealing with the sale".
- An LPA is produced as evidence of authority to sell estate property.
- The executor is the sole executor and no court order is available.

**These are stop points.**

### Evidence required before exchange

Before exchange, you must have clear documentary authority showing who can sell:

- The Will.
- Grant of Probate.
- Court order appointing a substitute personal representative (if applicable).
- Confirmation of which personal representative(s) are executing the transfer.

**Do not rely on assurances or explanations without documents.**

### Practical steps for fee earners

- Identify early who is selling and in what capacity.
- Ask direct enquiries if capacity is in doubt.
- Do not assume an LPA is sufficient.
- Do not exchange until authority is proven.
- Escalate to a supervisor if unsure.

## Risk to the firm

Proceeding without a properly authorised personal representative may result in:

- Void or voidable transfer.
- Title defects for the buyer.
- Professional negligence claims.
- Breach of lender requirements.

This is a **non-negotiable compliance issue.**

## Edge cases & gotchas

- **A medical letter ≠ a court order.** Even with clear medical evidence of incapacity, you still need the court appointment of a substitute PR before exchange.
- **Family doing things informally** is the most common trap. The well-meaning relative who's been "handling everything" has no legal authority unless the documents say so.
- **A "Grant de Bonis Non"** is the correct mechanism when incapacity hits *after* probate was issued — see the LPA guide.
- **Section 50 applications** take real time. Surface them at intake, not the week before exchange.

## Related guides

- [Executors and LPAs — why an LPA can't sign a TR1](/crm/guides/legal/executors-and-lpas)
- [Assent transfers — moving property out of an estate](/crm/guides/legal/assent-transfers)
- [Handling a sale when both the property owner and their personal representative have died](/crm/guides/legal/sale-after-pr-died)
`,
    },
    {
        product: "legal",
        slug: "anti-fraud-and-verification",
        title: "Anti-fraud and verification — the system gates and the human ones",
        one_line_description: "How ID checks, video calls, validated contact details, and locked bank details defend a case, plus the moves fraudsters try.",
        category: "legal-practice",
        order: 100,
        last_screenshotted_at: "2026-06-29",
        tags: ["fraud","aml","id-check","video-call","verification","bank-account","holp","mlro","pep","sanctions"],
        content_hash: "16fe575af8e2449ca6c6dd1132279cc21a74aaff08219960b48f0c0ebd77c8f5",
        body_markdown: `## What it is

The collection of system gates and human routines that stop the wrong person from impersonating a client, moving money, or changing where money goes. Most of the controls are wired into the **Client Verification** roadblock plus the **bank-account verification lock** — but a real-world defence relies just as much on staff knowing the patterns fraudsters use, so they don't accidentally clear a gate they shouldn't.

The right mental model is: **the engine is your first line, not your only line**. The system gives you green lights when the data says it should, but a confident phone caller, a plausible-looking email, and a small change to a sort code is the standard fraud package — and that's deliberately *not* something the engine can catch on its own.

## When you'd use it

- A client wants to change their bank details, email, or phone number — especially close to completion.
- A verification call doesn't feel right and you're deciding whether to push back.
- A new joiner is asking why we make clients do video calls, ID checks, *and* phone verification.
- A PEP or sanctions match has come back and you need to see who the client matched and decide what to do.
- HOLP or MLRO has flagged a case and you need to retrace which controls have fired.
- You're closing the loop after a near-miss and want to know what would have caught it.

## Walkthrough

### What the system gates — the **Client Verification** roadblock

When a case is instructed, the engine fires **Client Verification** and adds a checklist for every client who is one of the verifiable types (client, joint owner, executor, personal representative, LPA). The checklist items, and what makes each one clear:

| Checklist item | What it actually means |
| --- | --- |
| **Client ID checks passed** | Every client who needs an ID check has a *valid* one — passed identity document, passed address, passed PEPs & sanctions, and the check covers up to expected completion / completion deadline. ID checks expire after 12 months. |
| **All clients passed initial verification call** | A logged video call with a *pass* outcome on file for every client who needs one. Same 12-month expiry, same "must cover until completion" rule. |
| **All clients had no vulnerability concerns on call** | The vulnerability question on every passed video call is set to **none**. If any call records concerns, the item stays open until the relevant authoriser has reviewed. |
| **Client emails and phone validated** | Every client who can log into the portal has **their own verified email address and their own verified phone number**. The client proves each one themselves — clicking a magic link, then entering a texted code. |
| **Clients agreed to terms of engagement** | At least one portal-capable client has agreed to the TOE in the portal. |
| **All clients consented to dual rep** | Only fires on dual-rep cases. Every client who needs to consent has done so. |
| **Report PEP to HOLP — get sign-off** | Appears only after a client has confirmed that one of their screening matches *is* them, which moves their PEPs & sanctions status to **Fail**. Clears once you've reported the PEP to the HOLP, got sign-off, and moved the status off Fail. |

You don't tick any of these off by hand. You do the work (book the call, run the ID check, get the client into the portal) and the engine notices the state has moved and clears the item.

### The fraud flag on an ID check

The ID check pipeline records a separate **potential fraud** flag when the identity document check fails for a specific reason — the most common being a manipulated selfie. The check still appears as a fail, but the row is marked as *potential fraud*. Treat any potential-fraud flag as an automatic conveyancer / HOLP escalation; don't unlock anything, don't accept a "send me a new ID" reply on email, and don't take a phone call as the resolution.

### PEP and sanctions matches — what we show, and the client's declaration

When the screening returns a possible PEP or sanctions match, we now pull the matched people into the case rather than only flagging it for investigation. Open the client's ID check and you'll see a **Comply Advantage matches** list under the PEPs & sanctions status. Each match shows the person's **photo** and **name**, their **date of birth** and **country**, the **category** (PEP or sanction) and a **match score**, and — for a PEP — their **political role**. It also spells out **why they matched**, for example on exact name and year of birth. Sanction matches are listed first because they're the serious ones, and the **Open report to investigate** link is still there for the full Comply Advantage detail.

![PEP and sanctions matches shown under a client's ID check in the CRM](/static/guides/legal/anti-fraud-and-verification/pep-sanctions-matches.png)

Flagged clients are also asked to help. During onboarding, a client whose check came back as needing investigation gets a **Confirm an identity check result** step. It shows them the same list and asks whether any of the records are actually them:

- **None of these are me** — the client signs a short declaration (we keep a PDF of it on the case) and their status stays as needing investigation. This does **not** clear the gate — a staff member still reviews the match and sets the pass.
- **One of these is me** — the client's PEPs & sanctions status moves to **Fail**, and the **Report PEP to HOLP — get sign-off** item appears on Client Verification.

The rule to hold onto: **the client's declaration never sets the pass**. Most matches are just a name-and-birthday coincidence. The declaration clears those quickly, and flags the rare real one. You — or HOLP — always make the final call.

### Video calls — required beyond the basic checklist

The roadblock only requires *a* passed video call per client. Sail Legal's policy adds video calls on top of that for the situations where the standard call isn't enough on its own. The conveyancer initiates these — they are not separate checklist items and the engine will not roadblock you into doing them.

| Situation | Why the extra video call exists |
| --- | --- |
| Mortgage fee cases | A separate party is paying us; we need to see the client agreeing in real time. |
| Matrimonial transfers of equity | Coercion risk on a separation; eye-to-eye check that both parties consent. |
| Unregistered titles | Higher-than-average impersonation risk because there's no land registry photo of the title. |
| Suspected vulnerability | Confirm capacity and that the client isn't being pressured. |
| Client tries to change contact details | The single most common social-engineering vector — see below. |
| Clients living abroad | Higher likelihood of impersonation, harder to verify by other means. |
| Cash purchases | Source-of-funds becomes the only AML control; need to see the client. |
| Suspected bankruptcy | Title impact + counterparty-fraud risk. |
| Transactions at undervalue (parent-to-child gifts) | Pressure / coercion check. |
| Uncooperative ID verification | Treat reluctance as a signal, not an excuse. |
| Seller offering incentives or cash back | Mortgage-fraud red flag. |
| Deposit paid by someone other than the buyer | Source-of-funds opacity. |
| Bank mortgage requiring a Certificate of Execution | The mortgage conditions require an eye-to-eye witness. |

### How fraudsters try to get past us

The patterns repeat. Recognising them is most of the defence.

| Pattern | What it looks like | The right response |
| --- | --- | --- |
| **Bank-detail change near completion** | Email arrives saying "we've changed bank, please send to this new account" — frequently with a plausible explanation (closed old account, moving banks). | Refuse to act on email alone. Trigger a video call. Confirm via the phone number we already have on file, not the one in the email signature. |
| **Contact-detail change just before exchange** | Client emails to ask us to update their phone number or email "for the closing weeks". | Treat as a fraud red flag in its own right. Re-verify on a video call before changing anything. The validated-contact gate gets reset. |
| **Email impersonation** | A look-alike domain or a compromised real address sends instructions that match house style. | Read the address character-by-character. If it doesn't match the address on the case record, escalate. |
| **Phone push payment fraud** | Caller claims to be the client, knows enough public detail to sound plausible, asks us to do something on email-only authority. | Hang up. Call back on the number on file. The caller's number is meaningless. |
| **Coached client on a video call** | Client is reading from a script, eyes flicking off-camera, unusually rigid answers. | Pause the call. Ask an unscripted question. If anything still feels off, fail the call and escalate to conveyancer / HOLP. |
| **Selfie manipulation on the ID step** | ID check returns a fail with the *potential fraud* flag. | Do not re-run the check on an email request. Conveyancer / HOLP decide whether to redo it, on a video call. |
| **Last-minute "small" change** | Anything urgent + small + just-before-money-moves. Urgency is the manipulation lever. | The greater the urgency, the slower we move. Always escalate. |

### The validated email and phone gate

This one is **per person, not per case**. Every member who can log into the portal — client, joint owner, executor, personal representative, attorney, and any authorised third party — needs **their own verified email and their own verified phone**. Deceased parties, introducers and company executors are excluded, because there's nobody personal to validate.

Only the client's own actions set either one. **Email is proved by clicking a magic link**; **phone by entering a code we text them**. Logging a phone verification call doesn't set it, and one member's verified details never stand in for another's — so adding a helper to a case does nothing for the client's own row.

Two things follow:

- **There is no override, and ticking the item by hand won't hold.** The engine recalculates the checklist on the next change to the case and takes the tick straight back off. If a client genuinely has neither an email nor a phone, that blocks exchange and needs an MLRO or HOLP decision recorded on the file — not invented contact details.
- **A contact-detail change request is itself a fraud signal.** Re-verify on a video call before changing anything, and expect the item to reopen once the new address or number is unverified.

Where a client can't manage the portal but has capacity, the route is an **authorised third party** who helps them through it — with the client's own permission, given on the recorded call. The helper still doesn't verify on the client's behalf: the address and handset must be the client's, with the helper beside them. See [Verifying identity when the standard check won't work](/crm/guides/legal/verifying-identity-when-the-standard-check-wont-work).

### The bank-account verification lock

Sale, purchase, and remortgage cases all use the case's bank-account list as the destination for client money. The system protects that list two ways:

- Each bank account row has a **verification status** — *unsubmitted*, *pending*, *approved*, *rejected*, *not verified as will not be used*.
- The case as a whole carries a **client changes to bank accounts locked** flag. Once locked, the client can't change their bank details from the portal — only staff can, after re-verification.

For the full flow, see [Bank account verification](/crm/guides/legal/bank-account-verification).

### What you do per case

Beyond clearing the roadblock checklist, on every case:

- Read the ID check images for yourself. Don't rely on the pass/fail alone — names, dates of birth, and addresses must match the case record.
- When a PEP or sanctions match comes back, read the matched people in the **Comply Advantage matches** list — and never let the client's "none of these are me" declaration stand in for your own review. Set the pass yourself.
- On the verification call, ask one question that isn't scripted. Coached clients fall over on those.
- Lock the bank account list the moment it's verified. Don't leave it open "in case".
- Re-verify on a video call before *any* contact-detail or bank-account change, no matter how trivial. There's no such thing as a small one.
- If something feels off and you can't quite name what, escalate to the conveyancer or HOLP. The cost of one extra call is nothing; the cost of being wrong is the entire transaction.

## How it works under the hood

- The **ID check pipeline** is independent of the **video call** pipeline — they're separate checklist items and one passing doesn't waive the other.
- The **ID check** gathers three separate signals: the document + selfie check, the address residency check, and the PEPs / sanctions screening. Any PEPs or sanctions match lands as *requires investigation* and stays there until a human reviews — it doesn't auto-fail.
- When there's a match, the matched people are pulled into the case and listed under the client's ID check (photo, name, date of birth, country, category, score, political role, and why they matched — sanction first), so you can see who they matched without leaving the CRM. The link out to the full Comply Advantage report is still there.
- The flagged client is asked, in onboarding, to confirm whether any match is them. **None of these are me** records a signed declaration PDF on the case and leaves the status as *requires investigation*; **one of these is me** moves the status to *Fail* and raises the **Report PEP to HOLP — get sign-off** item. The client's answer never sets the pass — that's always a staff action.
- ID checks expire **12 months after they pass**, and on this case specifically must remain valid until the expected completion or completion deadline. The checklist item will go red again as completion approaches if the existing check won't cover it.
- A **manipulated selfie** failure sets the *potential fraud* flag on the ID check row. That flag does not clear automatically.
- The **vulnerability checklist item** ties to the vulnerability question on the video call. If even one passed call shows concerns, the item stays open until the right person has signed off.
- The **email and phone validation check** is recorded against each person, not against the case. It reads every portal-eligible member and needs a verified email plus at least one verified phone for each of them, so the roadblock evidence panel names exactly who is missing which. The phone side is satisfied by any verified number on that person, not specifically their primary one — so a stale mobile they can no longer reach will still count, and only staff judgement catches that.
- The **bank account lock** is a timestamp on the case. While set, the client portal hides the *edit bank details* surface. Staff can clear it, but that's a deliberate action with a paper trail.

## Edge cases & gotchas

- **"The ID check passed but the name on the document is wrong."** A pass on the document and selfie check doesn't mean the name matches the case record. The ID check pipeline cross-checks legal name + date of birth, and a name mismatch returns a specific failure — but read the document for yourself in any case the client's name on file may have drifted from the document.
- **PEPs / sanctions *requires investigation* is not a fail, and a client declaration doesn't clear it.** It's a "human decide" state. A client saying "none of these are me" records their declaration but leaves the status where it is — only a staff pass clears the Client Verification ID checklist item, and on a sanctions match that's an MLRO / HOLP call.
- **"One of these is me" sends the case to HOLP.** When a client confirms a match is them, the status moves to Fail and the **Report PEP to HOLP — get sign-off** item appears. Don't proceed until HOLP has signed off and you've moved the status off Fail.
- **Vulnerability flag persists across calls.** Once a call records concerns, the checklist item stays open until cleared by sign-off — it is not unset by a subsequent call where the question is set to *none*.
- **A revalidated bank account doesn't unlock the case-level lock by itself.** Locking and unlocking the case is a separate action; verifying individual accounts to *approved* doesn't change the lock state.
- **Video call expiry mirrors ID check expiry.** A call that passed eleven months ago doesn't satisfy the checklist for a completion in two months — the engine reads expiry against the latest of completion date / expected completion / completion deadline.
- **Company executors don't need a personal ID check or video call** — that's handled by a separate process. The Client Verification roadblock allows company-executor-only cases to pass the relevant items even with no personal check on file.
- **"The client says they can't do a video call."** Refusal or persistent dodge of a video call is itself the answer. Don't proceed; escalate.
- **You can't override the email and phone gate, and a helper doesn't satisfy it.** It's per person and it recalculates itself. An authorised third party clears their own row only — never the client's.
- **A relative "handling everything" is worth a second look.** Helping an elderly client through the portal is normal and fine. Holding their mailbox, their phone and their bank details is how coercion looks from the outside — ask the client, alone on the call, and check the proceeds go to an account in their own name.
- **"The change came from the client's real email address."** Email accounts get compromised. Treat *any* bank or contact-detail change as if the email could be the attacker, even when the address matches the one on file.

## Related guides

- [Verifying identity when the standard check won't work](/crm/guides/legal/verifying-identity-when-the-standard-check-wont-work)
- [Bank account verification](/crm/guides/legal/bank-account-verification)
- [Risk management](/crm/guides/legal/risk-management)
- [Onboarding — what the system asks, and what you confirm by hand](/crm/guides/legal/onboarding-checklist)
- [Client portal — onboarding steps](/crm/guides/legal/client-portal-onboarding-steps)
- [Roadblocks: how they work](/crm/guides/legal/roadblocks-how-they-work)
`,
    },
    {
        product: "legal",
        slug: "roadblock-completion-ready",
        title: "Roadblock: Completion Ready",
        one_line_description: "How to clear each item in Completion Ready — the completion-day funds-flow gate.",
        category: "roadblocks",
        order: 100,
        last_screenshotted_at: "2026-05-29",
        tags: ["roadblocks","completion-ready","funds-flow","troubleshooting"],
        content_hash: "974f6334736252d795652341897c97632cf9d3058c471fcbdd0ee0d544ec3158",
        body_markdown: `## What it is

Completion Ready is the completion-day funds-flow roadblock. It fires once a case reaches *ready for exchange* or *exchanged* status and tracks the money in / out milestones between then and actual completion: funds in from lender and client, funds out to the seller's solicitor, proceeds in from the buyer's solicitor, redemption statements ready, the divorce document (where relevant).

It is **not authoriser-gated** — items clear themselves as you record the dates and confirmations on the case.

## When you'd use this guide

- You're working a case in the run-up to completion and want to see what's outstanding.
- Funds are moving but one of the items isn't going green.
- You're trying to work out which items live here vs Pre-Completion Ready or Post-Completion.

## The checklist

### Funds requested from lender

Mortgage-involved cases only (purchase / transfer with a mortgage, or remortgage). The *mortgage funds requested from lender* date is recorded.

- **Where to go**: **Details** tab → **Funds** block → *Funds requested from lender* date.

### Funds received from lender

Mortgage-involved cases. The *mortgage funds received from lender* date is recorded.

- **Where to go**: **Details** tab → **Funds** block → *Funds received from lender* date.

### Funds requested from client

Purchase / transfer / remortgage. The *funds requested from client* date is recorded.

- **Where to go**: **Details** tab → **Funds** block → *Funds requested from client* date.

### Funds received from client

Purchase / transfer / remortgage. The *funds received from client* date is recorded.

- **Where to go**: **Details** tab → **Funds** block → *Funds received from client* date.

### Redemption statement(s) received for completion

Cases with at least one charge to redeem. Every charge on the case has a redemption statement file attached.

- **Where to go**: **Details** tab → **Charges** block → each charge row → *Redemption statement received* tick.
- **Common stuck-states**:
  - **A statement was received earlier but has now expired against the completion date** — the engine reads "statement file attached" only; validity against the completion date is a follow-up check the case handler does by eye. Don't assume green here means the statement is in date.

### Seller solicitor paid

Purchase cases only. The *seller solicitor paid* date is recorded.

- **Where to go**: **Details** tab → **Funds** block → *Seller solicitor paid* date.

### Seller solicitor payment confirmed

Purchase cases only. The *seller solicitor received payment* date is recorded.

- **Where to go**: **Details** tab → **Funds** block → *Seller solicitor confirmed receipt* date.

### Proceeds of sale received from buyer's solicitor

Sale cases only. The *proceeds of sale received from buyer's solicitor* date is recorded.

- **Where to go**: **Details** tab → **Funds** block → *Proceeds of sale received* date.

### Divorce document received

Divorce-as-reason cases on transfer / remortgage / purchase where a divorce document type has been chosen. A scanned copy of the chosen divorce document (or a generic court order with a received date) is uploaded.

- **Where to go**: **Documents** tab → the divorce document slot matching the type chosen on **Contract details**.
- **Common stuck-states**:
  - **The case handler chose *final order* on contract details but uploaded the file under *court order*** — the generic *court order* slot is recognised as a fallback (it's the legacy upload location). Either path works.

## Edge cases & gotchas

- **Completion Ready creates at *ready for exchange* and stays live through *exchanged*.** Once the case status becomes *completed* the roadblock stops generating.
- **Completion Ready does not block exchange** (unlike Pre-Completion / Exchange Ready). It's a funds-flow tracker for the days around completion, not a sign-off gate.
- **Completion Ready does not require authoriser sign-off.** Items clear as you record dates.
- **The "Payments due chased" idea** lives on Post-Completion, not here — and is currently disabled in the engine pending a deterministic signal. Don't expect this roadblock to chase outstanding client funds for you.
- **A redemption statement going green here is not the same as it being valid.** The engine checks for a file; staff must check the statement's validity window against the completion date by eye.

## Related guides

- [Roadblocks: how they work](/crm/guides/legal/roadblocks-how-they-work)
- [Training — completion and post-completion](/crm/guides/legal/training-completion-and-post-completion)
- [Roadblock: Exchange Ready](/crm/guides/legal/roadblock-exchange-ready)
- [Roadblock: Pre-Completion Ready](/crm/guides/legal/roadblock-pre-completion-ready)
- [Roadblock: Post-Completion](/crm/guides/legal/roadblock-post-completion)
`,
    },
    {
        product: "legal",
        slug: "verifying-identity-when-the-standard-check-wont-work",
        title: "Verifying identity when the standard check won't work",
        one_line_description: "The decision tree for helpers, attorneys, expired documents, no-ID clients, re-screens and manual passes.",
        category: "legal-practice",
        order: 101,
        last_screenshotted_at: "2026-06-20",
        tags: ["aml","id-check","manual-pass","re-screen","id1","mlro","expired-document","no-id","authorised-third-party","capacity"],
        content_hash: "2fce323e993131bd44ec60d3cc789dc5c1ef1903f39288dfae57a233019e5afa",
        body_markdown: `## What it is

The playbook for every case where a client **can't** sail through the normal digital ID check. The normal route is: the client does the Stripe biometric (document + selfie), we screen them for PEPs/sanctions (Comply Advantage) and verify their address (Melissa), and the **Client Verification** roadblock clears. This guide is about what to do when that route stalls — a client who can't work the technology, a client who has lost capacity, an expired passport, a check that lapsed at 12 months, a genuine fraud signal, or a client with no photo ID at all.

The guiding principle, straight from our AML policy and LSAG: an exception isn't a dead end, but every exception decision must be **recorded and escalated** — not waved through. The system gives you the tools to record a defensible decision (re-screen, manual pass, the fail reasons on screen). The harder cases (no ID, ID1) are a documented **manual** process the system deliberately doesn't automate.

## When you'd use it

- A client can't manage the app or the portal — they're elderly, or a family member is helping them.
- A client has lost capacity and somebody is holding a power of attorney.
- A client's ID check has hit its 12-month expiry and you don't want to drag them through a full re-do.
- A check **failed** and you need to know *why* before deciding what to do.
- A client's only photo ID has expired and they can't get a new one in time.
- A client has **no** passport or driving licence at all.
- HMLR wants an ID1 form, or the client can't complete the digital route.

## Walkthrough

Work top-down. The first question is not *what ID do they have?* — it's *can this client act for themselves at all?*

### 1. Start here: does the client have capacity?

This fork decides everything that follows, and the two branches are not interchangeable.

- **They have capacity, they just can't work the technology** (elderly, hard of hearing, no smartphone, doesn't use email). Go to step 2 — an **authorised third party** can help them through it.
- **They have lost capacity.** An authorised third party is *not* the answer and cannot be made to be. Somebody must be acting under a **registered power of attorney** or a court appointment. Go to step 3.

Getting this wrong either way is serious. Adding a helper for a client who can't consent to that helper is meaningless — the client couldn't authorise them. Treating an attorney as just a helper skips the authority question.

### 2. The client has capacity but can't self-serve → authorised third party

Before you reach for ID1 or a manual pass, ask the cheaper question: **can somebody help them do the normal check?** A 90-year-old with a valid passport and a willing grandson doesn't need an exception route — they need a helper and a spare afternoon.

**Get the client's permission first, on the recorded video call.** This is the part people skip and it's the part that matters. Ask the client directly, in their own words:

- Do you want *[named person]* to help you with this?
- Are you happy for us to deal with them about your sale?

It has to come from the client, not from the helper saying they look after things. Minute the answer in the call note, naming the person. That note is the authority for everything the helper then does.

Once you have it, add the helper to the case as an **Authorised third party**.

**What that unlocks.** From their own login they can start the client's ID check on their own screen and hand the phone over for the selfie, upload the client's proof of address, and receive the ID-check reminders on the client's behalf.

**What it deliberately doesn't unlock.** They can't agree the terms of engagement, sign the property information forms, consent to dual rep, sit the verification call in the client's place, or be paid the sale proceeds. Those stay with the client, by design. A helper helps; they don't become a party to the transaction.

**Assisted, not substituted — the line that matters.** Adding a helper **does not verify the client's own email and phone**. Every person who can log into the portal needs **their own verified email and their own verified phone**. The helper's details verify the helper, and nobody else.

So the client needs an email address and a mobile that are really theirs, with the helper sitting beside them. The link arrives at the client's address, and the client clicks it with help. The code arrives on the phone in the client's hand. That's assisted, and it's defensible. If the helper controls the mailbox and holds the handset, we've verified the helper, not the client — don't record that as the client's verification.

If the client has neither an email nor a phone and never will, say so out loud rather than inventing details. The item can't be cleared, and it will block exchange. That needs an MLRO or HOLP decision recorded on the file.

### 3. The client has lost capacity → this is an authority question first

Stop before you think about ID at all. "Their attorney is dealing with the sale" is a **stop point**, not a shortcut, and the documents come before anything else:

- **Is the power of attorney registered?** An enduring power can be used unregistered only while the donor still has capacity. Once capacity is gone it must be **registered with the Office of the Public Guardian** to be used at all. "I hold a full EPA" is not the same statement as "I hold a registered EPA" — ask for the sealed, registered instrument and keep a certified copy.
- **Are attorneys appointed jointly, or jointly and severally?** If jointly, every one of them must act.
- **Does the title work?** Check whether a second trustee is needed to give a valid receipt for the money.
- **Where are the proceeds going?** They must go to an account in the client's name or one the attorney properly operates for them. Anywhere else is an MLRO escalation.
- **Get the medical evidence of incapacity** on file. You need it for the vulnerability record regardless.

Only once authority is proven do you deal with identity. And you deal with **two people, not one**. The attorney does the full standard check in their own right. They're the one actually transacting, so there's no exception for them. The client still needs their own due diligence, which will usually be step 8(c) below.

A client who has lost capacity is a **vulnerable client**. Set the vulnerability field on the risk assessment in its own right and name the support being offered, not just the diagnosis.

### 4. The check just expired (identity still good) → it re-screens itself

An ID check is valid for **12 months**. What expires at 12 months is the **screening** (PEPs/sanctions lists and address change over time) — *not* the proof of identity. A face matched to a valid passport eleven months ago is still that person.

So you usually don't need to do anything. A daily job automatically **re-screens** a passed check about a week before it expires, as long as the case is still live and the client's ID document is still in date. A re-screen:

- creates a fresh check that **carries the proven identity forward** (no new biometric),
- re-runs Comply Advantage and Melissa,
- sets a new 12-month expiry.

The client never sees an "expired" prompt. If the re-screen turns up a new PEPs hit or an address problem, the usual roadblocks open exactly as they would for a fresh check.

The auto re-screen only fires when:

- the case is **active** — closed cases and cases paused at the client's request (abeyance) are skipped, so we don't spend on dead or parked work,
- the ID **document is still in date** — if it has expired, or we never captured an expiry date (older checks), the check is left to lapse and the client does a full check instead.

You can also trigger a re-screen by hand: open the client's ID check and click **Re-screen**. Use this when a check has already lapsed, or after rescuing a failed check (see step 6). A re-screened check shows **"Identity was carried forward from an earlier check"** instead of document images, because no new images were captured.

### 5. The ID document itself has expired → full check

If the **document** the identity was proven against has since expired, you can't carry it forward — the auto re-screen skips it and the client falls back to the normal flow: they get the "your ID check has expired" prompt in the portal and do a full Stripe check against current ID. Nothing for you to do but chase them as normal.

### 6. A check failed → read the reason first

When a check fails, the CRM shows **why** under the identity status, in a **"Why this failed:"** box — for example *"The ID document had expired"* versus *"The selfie didn't match the document (possible fraud)"*. This is the most important signal on the page, because it tells you which route applies:

- **Salvageable** (e.g. document expired, unsupported device/country, client didn't finish): you can rescue the case — re-screen and, if appropriate, set a manual pass (below).
- **Fraud signal** (selfie didn't match, document couldn't be verified): **do not** proceed. The check is flagged for fraud review; refer to the MLRO and consider whether a SAR is needed.

A check that's flagged for fraud is also labelled **(Fraud risk)** in the ID-check dropdown until someone resolves the review, so it's hard to miss when you open the case.

**The commonest failure fixes itself now.** Where the reason is *"the name didn't match"* or *"the date of birth didn't match"*, the client mistyped their details before doing the check — they aren't unverifiable, they just told us "Jim" when their passport says "James". When that client next opens the identity step in the portal, their name, date of birth and address open up already expanded, above a prompt naming the field that didn't match and telling them to correct it before trying again. So on a name or date-of-birth mismatch, **chase, don't intervene** — the client has everything they need to fix it and retry. Only reach for the exception routes below if they come back a second time.

**Audit trail for corrected details.** Whenever a client (or staff member) edits legal name, date of birth, or correspondence address, every change is permanently recorded in the legal details audit log with the previous value, new value, who made the edit, and the ID check status at that moment. The CRM displays this history directly beneath the check fail reasons, giving the MLRO and case handlers a clear view of what details changed and when.

Note that a name or date-of-birth mismatch is only ever recorded when Stripe **accepted** the document. If the document itself failed first — blurry photo, expired, abandoned halfway — we never got as far as comparing the name, so the absence of a mismatch reason doesn't mean the details are right. The client sees a general "check your details" prompt in that case for the same reason.

### 7. Manual pass — the recorded exception

When a client genuinely can't complete a standard check but you're satisfied (after the steps below) that they are who they say they are, set the identity document status to **Manual pass**. It clears the same gates a normal pass does, but it's a deliberate, recorded decision:

- Only staff with **roadblock sign-off** permission can set it.
- A **reason is required** — record what you relied on (e.g. *"Expired passport plus video call and proof of renewal; MLRO approved 20 Jun"*).
- It's **blocked while a fraud review is open** — you must first resolve the fraud review to *not fraud*, otherwise the manual pass won't save.

A manual pass is "as good as a pass, by hand". Use it for the expired-document and no-standard-ID routes once the additional checks are done and the MLRO has signed off.

**Screen them first.** When Stripe never completed — say the client's passport is expired so they can't get to the end of the app — the check sits with everything **Unknown** and there's nothing to carry forward, so **Re-screen** doesn't apply. Instead, on that check use the **Run screening** button: it runs Comply Advantage and Melissa **in place**, against the name, date of birth and address on file, and fills in the PEPs/sanctions and address results — without touching the identity status. Do this *before* you manual-pass, so the authoriser is signing off with the screening results in front of them, not blind.

The button only appears while a check is unverified and hasn't been screened yet, and it stays **disabled until the client's legal name, date of birth and address are all filled in** (either the client supplies them or you type them into the member's details) — screening on half-complete data isn't worth having. Once it has run, the results show as normal and the button disappears, so a check is only ever screened once. Any legal staff can run it; recording the **Manual pass** afterwards still needs sign-off permission.

### 8. The three "can't do the digital check" routes

Once you've ruled out a helper (step 2), "no ID" splits three ways — and the policy treats them differently:

**(a) Has photo ID but can't do the app** and there's nobody who can help them. Use the firm's **Manual AML / ID1 process**: the client attends a local solicitor or barrister, who completes an **ID1 form**, certifies a copy of their passport or photo driving licence and certified proof of address, and posts the originals to us. We then **independently verify the certifier** with a Law Society (or equivalent regulator) check and keep evidence on file. A mandatory video call and MLRO sign-off complete it.

**(b) Only an expired photo ID.** This is steps 4–7 above — re-screen plus, where needed, a manual pass with MLRO approval.

**(c) Genuinely no photo ID at all.** Use the **LSAG alternative-evidence route**. The inability to produce standard ID is not, by itself, a reason to refuse — but it raises risk. Gather as much independent evidence as the client's circumstances allow (birth or marriage certificate, HMRC/DWP/benefits letter, GP or care-home-manager letter, social worker or court-appointed deputy confirmation, bank statement, council tax). Run PEPs/sanctions and an address check on the data you do have, complete a video call, record the full reasons on the case, and get MLRO approval before proceeding. In the CRM this is the **Run screening** button (step 7) followed by a **manual pass** with the reasons in the reason field.

Where the client has lost capacity and can't sit a video call at all, that's an exception in its own right: record why it's impossible, get independent confirmation of identity from somebody responsible for their care, and put the whole thing to the MLRO before you pass anything.

### 9. ID1 is for HM Land Registry — not a substitute for our AML

Since March 2026, if the client *or the verifier* lacks a current valid passport, HMLR identity must be done on form **ID1** (Practice Guide 67). Treat ID1 as **one piece of evidence**, never as an AML pass on its own — a case can need ID1 for HMLR even when our own CDD is already satisfied, and vice versa. Always complete our own risk-based CDD, screening, and MLRO escalation separately.

Where somebody is signing under a power of attorney, it's the **attorney's** identity that HMLR needs, because they're the one executing the deed.

## How it works under the hood

- **Authorised third party** is a member type on the case. It carries the helper permissions (start another member's ID check, upload their proof of address, get their reminders) and deliberately carries none of the party obligations — no terms of engagement, no property forms, no dual-rep consent, no video call, and it can't be selected as a destination for money.
- **Email and phone verification is per person, not per case.** Each portal-eligible member needs their own verified email and their own verified phone. Email is proved by clicking a magic link; phone by entering a texted code. Nothing else sets either, and one member's details never satisfy another's.
- **Re-screen** creates a new ID check that copies the prior identity status (and its document expiry and any manual-pass reason) forward, links back to the check it came from, and re-runs the Comply Advantage + Melissa screening. The automatic version is a daily job that only fires shortly before expiry, when the document is still in date and the case is still active.
- **Run screening** runs Comply Advantage + Melissa on an existing check in place — no new row, no identity change — and is only offered on an unverified check that hasn't been screened yet (that "not yet screened" test is also what stops it running twice). It's guarded so it can't run until the client's name, date of birth and correspondence address are present.
- **Manual pass** is one of the identity document statuses. The roadblock that gates exchange treats it exactly like a pass; saving it enforces sign-off permission, a non-empty reason, and the open-fraud-review block.
- **Fail reasons** are the reasons Stripe gives us (plus our own name / date-of-birth mismatch markers), saved on the check and shown with friendly wording. The client portal reads the same markers to decide which "check your details" prompt to show on a failed check — so what you see in the CRM and what they see in the portal come from one source.
- **Document expiry** is read from Stripe when a check passes, and used only to decide whether a re-screen is allowed — it never invalidates a check that already passed.
- **Legal details audit trail** captures every edit to legal name, date of birth, and correspondence address made from either the client portal or the CRM. Each entry logs the field, old and new values, actor (client vs staff), and ID check status at that moment, displayed under the check fail reasons in the CRM.

## Edge cases & gotchas

- **An authorised third party is not an attorney, and an attorney is not a helper.** The helper route needs a client with capacity who can authorise them. The attorney route needs a registered power of attorney. Neither substitutes for the other.
- **The helper's permission has to come from the client, on the call, in their own words.** A helper asserting that they handle the client's affairs is not authority. If the client can't give it, you're in the capacity branch.
- **Adding a helper doesn't clear the email and phone gate.** That's per person. The helper's verified details satisfy the helper's own row and nothing else.
- **There is no override on email and phone verification, and ticking the item by hand won't hold** — the engine recalculates on the next case change and clears the tick straight back off. If a client truly has neither channel, escalate it as a recorded exception; don't fabricate contact details to get a green light.
- **An elderly client plus a helpful relative is also the shape undue influence takes.** It's usually innocent. Show on the file that you asked the client, alone on the call, whether this is what they want — and check the proceeds are going to an account in the client's own name.
- **An attorney can start the client's ID check from their own login.** Like an authorised third party, an attorney can generate the donor's ID verification link and upload proof of address from their portal. However, an attorney cannot instruct the case in the portal — staff do that on the case.
- **A re-screen is not a re-verification.** It refreshes screening only. If you actually doubt the person's identity, do a full check, not a re-screen.
- **Don't re-screen an expired document forward.** Both the nightly job and the **Re-screen** button block expired documents: re-screening cannot carry an expired document forward into a clean pass. If an exception is justified by policy, record a manual pass with MLRO sign-off and the reason written out instead.
- **Manual pass is an MLRO decision, not a shortcut.** The reason field is the audit trail — write it as if a regulator will read it, because they might.
- **Never manual-pass a fraud signal.** Resolve the fraud review honestly first; a selfie mismatch means it may not be them — and the system won't let you manual-pass until the review is cleared.
- **Don't confuse the two expiries.** "ID check expired" (the 12-month screening clock) is routine and self-healing. "ID document expired" (the passport itself) means a full check.
- **The "check your details" prompt only appears on a failed check, never an expired one.** An expired check passed at the time, so the details were right — telling that client to check what they typed reads as an accusation and sends them hunting for a mistake that isn't there.
- **The auto re-screen won't rescue every case.** It skips closed and paused cases, and any check where the document has expired or has no recorded expiry date — those still need a full check or a hand-driven decision.
- **ID1 ≠ done.** Completing ID1 satisfies HMLR, not our AML obligations.

## Related guides

- [Anti-fraud and verification — the system gates and the human ones](/crm/guides/legal/anti-fraud-and-verification)
- [Client Verification roadblock](/crm/guides/legal/roadblock-client-verification)
- [Risk management](/crm/guides/legal/risk-management)
- [Executors and LPAs](/crm/guides/legal/executors-and-lpas)
- [When an executor loses capacity](/crm/guides/legal/executor-loses-capacity)
`,
    },
    {
        product: "legal",
        slug: "roadblock-post-completion",
        title: "Roadblock: Post-Completion",
        one_line_description: "How to clear each item under Post-Completion — registration, ledger finalisation, retentions, invoice, post.",
        category: "roadblocks",
        order: 110,
        last_screenshotted_at: "2026-05-29",
        tags: ["roadblocks","post-completion","registration","troubleshooting"],
        content_hash: "711a594cd78953770500f5374fbb358a7a55605ee5eaa27c59b0100623eff366",
        body_markdown: `## What it is

Post-Completion is the after-completion housekeeping roadblock. It fires once the case is recorded as completed and tracks two parallel sub-flows: the **Land Registry registration** sequence (submit → resolve any requisitions / cancellation → registration complete), and the **finance and admin** wrap-up (cashroom slips, payouts, ledger finalisation, retentions settled, Sail Legal invoice paid, post forwarded or shredded).

It is **not authoriser-gated** and does not block exchange (we've already exchanged and completed). It does keep the case visible until everything is closed off.

## When you'd use this guide

- A completed case still has Post-Completion open and you can't tell what's left.
- An item won't go green even after you think you've done the work.
- You're trying to work out which items live here vs Completion Ready.

## The checklist

### Registration submitted to Land Registry

Purchase / transfer / remortgage cases (the transactions where we're the registering party). The *registration submitted* date is recorded.

- **Where to go**: **Details** tab → **Registration** block → *Submitted* date.

### Requisitions resolved

Only fires when Land Registry has raised requisitions. The *registration requisitions resolved* date is recorded.

- **Where to go**: **Details** tab → **Registration** block → *Requisitions resolved* date.

### Registration cancellation resolved

Only fires when Land Registry has cancelled the submission. The *registration resubmitted* date is recorded.

- **Where to go**: **Details** tab → **Registration** block → *Resubmitted* date.

### Registration complete

Purchase / transfer / remortgage. The *registration complete* date is recorded — i.e. Land Registry has confirmed the registration is done.

- **Where to go**: **Details** tab → **Registration** block → *Complete* date.

### Case ledger finalised

Every post-completion case. The *ledger finalised* date is recorded.

- **Where to go**: **Ledger** tab → *Finalise* action.

### Slip added to cashroom for every paid ledger entry

Cases with at least one active ledger entry. Every entry that's marked paid (either *paid at* or *user says paid*) has a *slip added to cashroom* date.

- **Where to go**: **Ledger** tab → each paid row → *Slip added to cashroom* date.

### All ledger items paid out

Cases with at least one active ledger entry. Every non-deleted entry has either *paid at* or *user says paid* recorded.

- **Where to go**: **Ledger** tab → each row.

### Retentions settled

Cases with a retention on the ledger — any *Money retained by us* or *Money retained by other side* entry. Every one of them has been ticked as settled.

- **Where to go**: **Ledger** tab → **Retentions** section → tick *Settled*.
- The same section is where you record **what the money is for** and **when to start chasing**. Both are free to leave blank, but a retention with no reason is exactly what the CLC audit picked us up on — fill them in when you create the entry.

### Sail Legal invoice paid

Every post-completion case. The *funds transferred to office account for Sail Legal invoice* date is recorded.

- **Where to go**: **Ledger** tab → Sail Legal invoice row.

### Post forwarded or shredded

Every post-completion case. The *post forwarded or shredded* milestone is recorded.

- **Where to go**: **Details** tab → **Admin** / **Closing** block → *Post forwarded or shredded* milestone.

### Payments due chased

This item is currently **disabled in the engine** — there's no deterministic signal for "chased". The funds-in chasing happens on Completion Ready before completion; once completed there is no engine-tracked equivalent. The item will not appear on cases until a signal is wired up.

## Edge cases & gotchas

- **Post-Completion does not auto-suspend on a closed case the way other roadblocks do.** Closed cases keep this roadblock alive specifically because the housekeeping needs finishing. Once *final closing review* is set, its items update only when someone works the case — any case edit, or the **Refresh roadblocks** button — rather than on the background refresh cycle.
- **The one case where it does suspend is a case closed awaiting a new one.** That matter never completed, so there is no post-completion housekeeping to finish — anything outstanding belongs to the replacement case.
- **Purchase / transfer / remortgage cases only get the registration items.** Sales don't register — the other side does. Sale cases will only see the finance / admin items.
- **The cashroom slip and paid-out items only count active (non-deleted) ledger entries.** Deleting an unfinished entry will clear it from the calculation.
- **Settled is not the same as *Payment sent*, and ticking one does not tick the other.** On a retention we hold, payment sent means we released the money. On a retention the *other side* holds, payment sent means we sent it **to** them — the moment the retention starts, not ends. That is why settling is its own tick rather than being read off the payment status.
- **Retentions booked before late October 2026 start out unsettled**, including ones that were really settled years ago. They were checked against the client account in Klyant and the ones holding nothing were ticked off; anything still holding money was deliberately left for someone to confirm by hand.
- **Sale cases don't see a *Registration complete* item** — there's nothing for us to register. This is by design.

## Related guides

- [Roadblocks: how they work](/crm/guides/legal/roadblocks-how-they-work)
- [Training — completion and post-completion](/crm/guides/legal/training-completion-and-post-completion)
- [Roadblock: Completion Ready](/crm/guides/legal/roadblock-completion-ready)
- [Roadblock: Pre-Completion Ready](/crm/guides/legal/roadblock-pre-completion-ready)
`,
    },
    {
        product: "legal",
        slug: "roadblock-ongoing-risk-assessment",
        title: "Roadblock: Ongoing Risk Assessment",
        one_line_description: "How the Ongoing Risk Assessment roadblock works on an instructed case.",
        category: "roadblocks",
        order: 120,
        last_screenshotted_at: "2026-05-29",
        tags: ["roadblocks","ongoing-risk-assessment","troubleshooting"],
        content_hash: "cc37f87066d229934afcb0afe8767c7df5faed12491435212000f31d029639be",
        body_markdown: `## What it is

Ongoing Risk Assessment fires on every instructed case. It's the engine's anchor for the running risk-grade conversation on a case — separate from the *Risk grade green and recent* checklist item on Exchange Ready / Pre-Completion Ready, which reads the same data but exists to gate sign-off.

This roadblock currently has **no predefined checklist items**. It exists as a holder for the risk conversation: the chat thread, the audit trail of grade changes, and the ad-hoc items staff add via the \`#add-checklist-item\` chat command.

It is **authoriser-gated** and **blocks exchange** — but with no built-in items the practical effect is that staff drive it by hand.

## When you'd use this guide

- A case has Ongoing Risk Assessment open and you can't see any checklist items.
- You want to know why this roadblock looks different to the others.
- An authoriser has flagged the risk grade and you're working out the next move.

## The checklist

There are no predefined items. Use the roadblock for:

### Running risk-grade decisions

The latest risk grade lives on the **Risk Management** panel on **Details**. Run a fresh assessment whenever the case shape changes (new lender, new client, fresh source-of-funds question, fresh title concern).

- **Where to go**: **Details** tab → **Risk Management** panel → *Run assessment*. The new grade lands and the Ongoing Risk Assessment roadblock's chat captures the audit trail.

### Ad-hoc tracking

Use the \`#add-checklist-item\` command in the roadblock's chat to capture one-off things you need to track that the predefined library doesn't cover.

- **Where to go**: open the roadblock → chat box → type \`#\` → arrow to *add-checklist-item*.

## Edge cases & gotchas

- **The roadblock is authoriser-gated even with no items**. This is by design — the engine treats it as needing authoriser sign-off before it counts as resolved, so it'll sit *awaiting signoff* once staff are ready.
- **It blocks exchange**. Don't ignore an Ongoing Risk Assessment that's open or awaiting sign-off — Exchange Ready / Pre-Completion Ready can't close while this is live.
- **The *risk grade green and recent* item on Exchange Ready / Pre-Completion Ready** reads the latest risk assessment record. Running an assessment from inside Ongoing Risk Assessment's flow updates the same record — so the two roadblocks move in lockstep.
- **Suspends with the rest of the library on terminal cases**. A closed / abandoned case will mute this roadblock alongside everything except post-completion.
- **Configuration note for staff reading widely**: the codebase file for this roadblock declares it with the same export name as Client Verification by mistake (both use the variable name \`ClientVerification\`). It does not affect behaviour but is worth flagging as a follow-up.

## Related guides

- [Roadblocks: how they work](/crm/guides/legal/roadblocks-how-they-work)
- [Risk management](/crm/guides/legal/risk-management)
- [Roadblock: Client Verification](/crm/guides/legal/roadblock-client-verification)
- [Roadblock: Exchange Ready](/crm/guides/legal/roadblock-exchange-ready)
- [Roadblock: Pre-Completion Ready](/crm/guides/legal/roadblock-pre-completion-ready)
`,
    },
    {
        product: "legal",
        slug: "roadblock-probate-items",
        title: "Roadblock: Probate Items",
        one_line_description: "How the probate-related items appear and clear across the relevant roadblocks.",
        category: "roadblocks",
        order: 130,
        last_screenshotted_at: "2026-05-29",
        tags: ["roadblocks","probate","troubleshooting"],
        content_hash: "fa8f3d2203ad7c21800fc602b63c71f72fe9d749b7da72f8299ede9490b15211",
        body_markdown: `## What it is

There isn't a standalone *Probate Items* roadblock in the engine — instead, four probate-related checklist items are **shared between Contract Ready Pack (sales) and Pre-Completion Ready (transfers and remortgages)**. They cover the documents we need from a probate case: grant of probate (or letters of administration), evidence that the grant has been applied for, the will, and the death certificate.

This guide is the single place to understand how those items behave, since they don't have a roadblock of their own.

## When you'd use this guide

- A probate case is showing a grant / will / death-certificate item and you want to understand the gate.
- You can't find a "probate roadblock" — this is why.
- You want to know when each item fires and on which sibling roadblock it lives.

## The probate items

Each item only fires when the case's *seller reason for sale* is a probate variant (*probate*, *probate AS1*, *probate DJP*, *probate concessionary*).

### Grant of probate or letters of administration received

Every grant / letters slot on the case is uploaded and marked as received.

- **Where to go**: **Documents** tab → *Grant of probate* slot or *Letters of administration* slot.
- **Lives on**: Contract Ready Pack (sale) and Pre-Completion Ready (transfer / remortgage).

### Grant of probate or letters of administration applied for

Either the *grant application date* is recorded on the case, or the grant / letters are already received. Once the grant is in, there's no need to backfill the application date.

- **Where to go**: **Details** tab → **Probate** block → *Grant applied for* date.
- **Lives on**: Contract Ready Pack and Pre-Completion Ready.

### Will received

Probate cases where a *will* document slot has been created. Every will slot is uploaded and received.

- **Where to go**: **Documents** tab → *Will* slot.
- **Lives on**: Contract Ready Pack and Pre-Completion Ready.
- **Stuck-state**: the item won't fire at all until a will slot has been created on the case. Spot-check that the slot exists if you expect the item to be present.

### Death certificate received

Probate cases where a *death certificate* slot has been created. Every slot is uploaded and received.

- **Where to go**: **Documents** tab → *Death certificate* slot.
- **Lives on**: Contract Ready Pack and Pre-Completion Ready.

## Edge cases & gotchas

- **There is no dedicated *Probate* roadblock.** The items live on the sibling roadblocks of the relevant transaction type. If you're hunting for "probate roadblock" in the day view, it doesn't exist — look on Contract Ready Pack (sale) or Pre-Completion Ready (transfer / remortgage).
- **The will and death certificate items only fire when a slot exists** — uploading a file to a slot that hasn't been created on the case isn't possible, so the operator's first job on a probate case is creating those slots.
- **A probate case that isn't flagged as probate won't trigger any of these items.** The *seller reason for sale* field is the gate — set it correctly at intake.
- **Items intentionally deferred** include per-deceased-proprietor evidence selection (no UI yet), executor ID verification (covered by Client Verification), lifetime mortgage redemption, CGT advice, and co-executor disagreement. Those run on staff judgement outside the engine for now.

## Related guides

- [Roadblocks: how they work](/crm/guides/legal/roadblocks-how-they-work)
- [Assent transfers](/crm/guides/legal/assent-transfers)
- [Executors and LPAs](/crm/guides/legal/executors-and-lpas)
- [Executor loses capacity](/crm/guides/legal/executor-loses-capacity)
- [Deceased owner — deceased PR](/crm/guides/legal/deceased-owner-deceased-pr)
- [Cautions on deceased owner](/crm/guides/legal/cautions-on-deceased-owner)
- [Roadblock: Contract Ready Pack](/crm/guides/legal/roadblock-contract-ready)
- [Roadblock: Pre-Completion Ready](/crm/guides/legal/roadblock-pre-completion-ready)
`,
    }
];
